/**
 * Bharat Charts PHP — full shell: cx-workspace + right wl-dock + tipNet TV.
 * Source of truth: TypeScript (built via frontend/esbuild → public/assets/js/charts.js).
 */
// @ts-nocheck — chart shell typed incrementally; session/bars modules are strict.
import {
  IST_OFFSET_MS,
  isMcxSymbol,
  sessionOpenMin,
  sessionCloseMin,
  resMinutes,
  floorBarTimeMs,
  istDayStartMs,
  inSessionNow,
  inSessionAt,
  isPreOpenAt,
  stripOpenBucket,
  setFnoClosingAuctionSymbols,
  hasFnoClosingAuction,
  SESSION_FNO_CLOSE_MIN,
} from "./session";
import {
  toTvBars,
  mergeBars,
  barIsFlatPlaceholder,
  aggregateTvBars,
  sanitizeTipBars,
  samePriceRegime,
} from "./bars";
import { CandleBuilder, UNIVERSAL_INTRADAY_TFS } from "./candle_builder";
import { initLayouts, VALID_GRIDS, panesForGrid } from "./layouts";
import { initAlerts } from "./alerts";
import { initPush } from "./push";
import { initHeatmap } from "./heatmap";

(function () {
  "use strict";

  var cfg = window.__BC || {};
  var TIP_URL = cfg.tipUrl || "/api/tip.php";
  var WL_URL = cfg.watchlistUrl || "/api/watchlist.php";
  var LIB_PATH = cfg.libPath || "/charting_library/";
  // Slice tip payload — 0 used to mean "full shelf" (~45KB) and amplified tip storms.
  var TIP_COUNTBACK = 400;
  // Paint full tip (~45 trading days hot shelf). Year depth comes from history scroll-back.
  var FIRST_PAINT_BARS = 8000;
  /** Persist tip MEM — hold full warehouse window (~1y of 5m). */
  var alertCtl = null;
  var heatmapCtl = null;
  var TIP_SESSION_BARS = 25000;
  var TICKS_URL = cfg.ticksUrl || "/api/ticks.php";
  var LIVE_WS_URL =
    cfg.wsUrl ||
    (location.protocol === "https:" ? "wss://" : "ws://") + location.host + "/ws/live";
  /** In-memory tip/history pool keyed by symbol|resolution — must exist before getBars. */
  var histCache = {};
  var histWarmPending = {};
  /** Tip bars the last symbol switch downloaded, for the getBars call that follows it. */
  var switchHandoff = null;
  /** In-flight tip for the symbol switch, so getBars does not start a second tip (3s cooldown). */
  var switchOpenTip = null;
  /** symbol|res → {p: tipNet promise, at} started on hover/press, reused by the click's switch. */
  var tipPrefetch = {};
  /**
   * Tip load control — hover used to burn nginx tip budget → Cloudflare Total Requests spike.
   * Priority: click/warm=0, deepen/cold=1, hover=2 (hover disabled by default).
   * Max 1 in-flight + per-symbol cooldown stops TATAELXSI-style retry storms.
   */
  var tipFly = 0;
  // One in-flight tip — parallel tips (2) melted FPM under load (504s, load≃40).
  var tipMaxFly = 1;
  var tipWait = [];
  var tipHoverPauseUntil = 0;
  var tipBackoffUntil = 0;
  /** symbol(upper) → last tip attempt ms */
  var tipSymAt = {};
  /** Background hover and watchlist candle warm-up. Off: one tip.php for the symbol the user opens. */
  var TIP_HOVER_ENABLED = false;
  var TIP_COOLDOWN_CLICK_MS = 3000;
  var TIP_COOLDOWN_DEEPEN_MS = 12000;
  var TIP_COOLDOWN_429_MS = 60000;

  function tipPump() {
    while (tipFly < tipMaxFly && tipWait.length) {
      tipWait.sort(function (a, b) {
        return a.prio - b.prio;
      });
      var job = tipWait.shift();
      tipFly++;
      Promise.resolve()
        .then(job.start)
        .then(
          function (v) {
            tipFly--;
            try {
              job.resolve(v);
            } catch (_) {}
            tipPump();
          },
          function (e) {
            tipFly--;
            try {
              job.reject(e);
            } catch (_) {}
            tipPump();
          }
        );
    }
  }

  function tipSchedule(prio, startFn) {
    return new Promise(function (resolve, reject) {
      tipWait.push({ prio: prio, start: startFn, resolve: resolve, reject: reject });
      tipPump();
    });
  }

  function tipNoteRateLimited(symbol) {
    tipHoverPauseUntil = Date.now() + 60000;
    tipBackoffUntil = Date.now() + 30000;
    if (symbol) {
      tipSymAt[String(symbol).toUpperCase()] = Date.now() + TIP_COOLDOWN_429_MS;
    }
    // Drop hover + deepen queue — keep only click/warm (prio 0).
    tipWait = tipWait.filter(function (j) {
      return j.prio === 0;
    });
  }

  function tipSymReady(symbol, mode) {
    var k = String(symbol || "").toUpperCase();
    if (!k) return true;
    var at = tipSymAt[k] || 0;
    var now = Date.now();
    // Future stamp = 429 hard cool-off.
    if (at > now) return false;
    var need =
      mode === "hover"
        ? TIP_COOLDOWN_DEEPEN_MS
        : mode === "deepen" || mode === "cold"
          ? TIP_COOLDOWN_DEEPEN_MS
          : TIP_COOLDOWN_CLICK_MS;
    return now - at >= need;
  }

  function tipSymMark(symbol) {
    var k = String(symbol || "").toUpperCase();
    if (k) tipSymAt[k] = Date.now();
  }

  /** Custom bar replay (TV Advanced Charts has no native Bar Replay). */
  var barReplay = {
    active: false,
    selecting: false,
    playing: false,
    symbol: "",
    resolution: "",
    bars: [],
    index: 0,
    speed: 1,
    timer: null,
    hoverTime: null,
    hoverMs: null,
    pickShapeId: null,
    pickLineTime: null,
    pickReadyAt: 0,
    crossSub: null,
    mouseSub: null,
    hostEl: null,
    /** Visible-range span (seconds) captured before truncate / Live — soft-fit preserves zoom. */
    savedSpanSec: 0,
    /** Exact {from,to} at pick time — restore after truncate so zoom/pan does not jump. */
    savedRange: null,
    /** Bar spacing + right offset at pick — keep the same visual scale after wipe. */
    savedSpacing: 0,
    savedRightOffset: 0,
  };

  function barReplayCutMs() {
    if (!barReplay.active || !barReplay.bars || !barReplay.bars.length) return 0;
    var cut = barReplay.bars[barReplay.index];
    if (!cut) return 0;
    var t = Number(cut.time);
    if (!(t > 0)) return 0;
    return t < 1e12 ? t * 1000 : t;
  }

  function barReplayClampBars(bars, symbol, resolution) {
    if (!bars || !bars.length) return bars || [];
    if (!barReplay.active) return bars;
    if (symbol && String(symbol).toUpperCase() !== String(barReplay.symbol || "").toUpperCase()) return bars;
    if (resolution != null && String(resolution) !== String(barReplay.resolution)) return bars;
    var cut = barReplayCutMs();
    if (!(cut > 0)) return bars;
    var out = [];
    for (var i = 0; i < bars.length; i++) {
      var t = Number(bars[i].time);
      if (!(t > 0)) continue;
      if (t < 1e12) t *= 1000;
      if (t <= cut + 500) out.push(bars[i]);
    }
    return out.length ? out : bars.slice(0, 1);
  }

  /** Live LTP / reseed must not touch the chart while this symbol is in bar replay. */
  function barReplayBlocksLive(symbol, resolution) {
    if (!barReplay.active) return false;
    // Any TF / alias of the replay symbol — live must not touch that series at all.
    if (symbol && !sameChartSymbol(symbol, barReplay.symbol)) return false;
    return true;
  }

  /** Drop queued live chart jobs for the replay symbol so a pending flush cannot fire later. */
  function barReplayClearPendingLive() {
    try {
      var pending = TickHub && TickHub._pendingChart;
      if (!pending) return;
      Object.keys(pending).forEach(function (guid) {
        var job = pending[guid];
        if (job && job.lis && sameChartSymbol(job.lis.symbol, barReplay.symbol)) {
          delete pending[guid];
        }
      });
    } catch (_) {}
  }

  function barReplayApply(symbol, resolution, bars) {
    if (!barReplay.active || !bars || !bars.length) return bars;
    if (String(symbol || "").toUpperCase() !== String(barReplay.symbol || "").toUpperCase()) return bars;
    if (String(resolution) !== String(barReplay.resolution)) return bars;
    return barReplayClampBars(bars, symbol, resolution);
  }

  /** Small densify of a truncated window only (never the full multi-week tip). */
  function densifySessionBars(bars, resolution, symbol) {
    if (!bars || bars.length < 2 || bars.length > 2500) return bars || [];
    var stepMs = resMinutes(resolution) * 60 * 1000;
    if (!(stepMs > 0) || stepMs >= 86400000) return bars.slice();
    var out = [];
    var prev = null;
    for (var i = 0; i < bars.length; i++) {
      var cur = bars[i];
      var t = Number(cur.time);
      if (!(t > 0)) continue;
      if (t < 1e12) t *= 1000;
      if (prev) {
        var pt = Number(prev.time);
        if (pt < 1e12) pt *= 1000;
        var nextT = pt + stepMs;
        var filled = 0;
        while (nextT < t - stepMs * 0.4 && filled < 80 && out.length < bars.length + 400) {
          var pShifted = new Date(pt + IST_OFFSET_MS);
          var nShifted = new Date(nextT + IST_OFFSET_MS);
          var sameDay =
            pShifted.getUTCFullYear() === nShifted.getUTCFullYear() &&
            pShifted.getUTCMonth() === nShifted.getUTCMonth() &&
            pShifted.getUTCDate() === nShifted.getUTCDate();
          if (!sameDay) break;
          if (!inSessionAt(symbol, nextT)) break;
          var px = Number(prev.close);
          if (!(px > 0)) break;
          var syn = { time: nextT, open: px, high: px, low: px, close: px, volume: 0 };
          out.push(syn);
          prev = syn;
          pt = nextT;
          nextT += stepMs;
          filled++;
        }
      }
      var bar = {
        time: t,
        open: Number(cur.open),
        high: Number(cur.high),
        low: Number(cur.low),
        close: Number(cur.close),
        volume: Number(cur.volume) || 0,
      };
      out.push(bar);
      prev = bar;
    }
    return out;
  }

  function barReplayChart() {
    try {
      var w = barReplayWidget();
      if (w && typeof w.activeChart === "function") return w.activeChart();
    } catch (_) {}
    return null;
  }

  function barReplayWidget() {
    try {
      var w = widgetForPane(activePane || "sector");
      if (w) return w;
    } catch (_) {}
    return widget || (paneWidgets && paneWidgets.sector) || null;
  }

  function barReplayHostEl() {
    var pane = activePane || "sector";
    var id =
      pane === "sector"
        ? "tv_sector_chart"
        : pane === "stock"
          ? "tv_stock_chart"
          : "tv_" + pane + "_chart";
    return (
      document.getElementById(id) ||
      document.querySelector('.cx-pane.is-active-pane .cx-chart') ||
      document.getElementById("tv_sector_chart") ||
      null
    );
  }
  var navT0 = performance.now();
  var switchT0 = navT0;
  try {
    performance.mark("ki-shell-script");
  } catch (_) {}
  // Drop stale tip/session caches that painted tip→Sep11 holes before
  // tip+5m-pool first paint / scroll-without-bridgeReady wait (v93).
  try {
    if (sessionStorage.getItem("bc_bridge_v") !== "130") {
      var rm = [];
      for (var i = 0; i < sessionStorage.length; i++) {
        var k = sessionStorage.key(i);
        if (k && k.indexOf("bc_tip_") === 0) rm.push(k);
      }
      rm.forEach(function (k) {
        sessionStorage.removeItem(k);
      });
      sessionStorage.setItem("bc_bridge_v", "130");
      try {
        if (window.BcIdb && typeof window.BcIdb.clear === "function") window.BcIdb.clear();
      } catch (_) {}
    }
  } catch (_) {}
  var activeSymbol = cfg.symbol || "NIFTY 50";
  var activeRes = "5";
  // Boot straight onto the saved layout's chart so the restore doesn't swap symbols mid-load.
  (function seedFromSavedLayout() {
    try {
      var lays = JSON.parse(localStorage.getItem("bc_charts_layouts_v1") || "[]");
      if (!Array.isArray(lays) || !lays.length) return;
      var actId = localStorage.getItem("bc_charts_active_layout");
      var lay = null;
      for (var i = 0; i < lays.length; i++) {
        if (lays[i] && lays[i].id === actId) lay = lays[i];
      }
      if (!lay) lay = lays[0];
      if (!lay) return;
      if (lay.interval) activeRes = String(lay.interval);
      var urlSym = new URLSearchParams(location.search).get("symbol");
      var nav0 = performance.getEntriesByType && performance.getEntriesByType("navigation")[0];
      if (nav0 && (nav0.type === "reload" || nav0.type === "back_forward")) urlSym = "";
      var laySyms0 = [lay.sector, lay.stock].concat(lay.extras || []).map(function (s) {
        return String(s || "").toUpperCase();
      });
      if (urlSym && laySyms0.indexOf(urlSym.trim().toUpperCase()) >= 0 && urlSym.trim().toUpperCase() !== String(lay.sector || "").toUpperCase()) urlSym = "";
      if (!(urlSym && urlSym.trim()) && lay.sector) {
        activeSymbol = String(lay.sector);
        var title = document.getElementById("cx-left-title");
        if (title) title.textContent = activeSymbol;
      }
    } catch (_) {}
  })();
  var widget = null;
  /** Multi-grid panes (Layout settings). sector = primary TV widget. */
  var paneWidgets = {
    sector: null,
    stock: null,
    extra0: null,
    extra1: null,
    extra2: null,
    extra3: null,
    extra4: null,
    extra5: null,
  };
  var paneSymbols = {
    sector: activeSymbol,
    stock: null,
    extras: [null, null, null, null, null, null],
  };
  var activePane = "sector";
  var currentGrid = "1";
  var sharedDatafeed = null; // unused — each widget gets createDatafeed() (see getSharedDatafeed)
  var layoutCtl = null;
  /** Suppress save toasts during initial page hydrate. */
  var pageSaveQuiet = true;
  setTimeout(function () {
    pageSaveQuiet = false;
  }, 2800);
  /** Chart layout autosave (indicators, drawings, grid, symbols). */
  function kickAutosave(reason) {
    if (pageSaveQuiet) return;
    if (layoutCtl && typeof layoutCtl.scheduleAutosave === "function") {
      layoutCtl.scheduleAutosave(reason || "");
    }
  }
  /** Toast for prefs already written to localStorage (watchlist, favorites). */
  var _pageSaveToastT = null;
  function pageSaved(label) {
    if (pageSaveQuiet) return;
    // Autosave header badge removed — keep a lightweight console hint for debug only.
    try {
      if (label && /need more bars|chart not ready|replay/i.test(String(label))) {
        var hint = document.getElementById("cxReplayHint");
        var text = hint && hint.querySelector(".cx-replay-hint-text");
        if (text) {
          text.textContent = String(label);
          hint.hidden = false;
          clearTimeout(pageSaved._t);
          pageSaved._t = setTimeout(function () {
            if (hint && !barReplay.selecting) hint.hidden = true;
          }, 2800);
        }
      }
    } catch (_) {}
  }
  var pendingChartStates = {
    sector: null,
    stock: null,
    extras: [null, null, null, null, null, null],
  };
  var themeSwitchBusy = false;
  var chartTheme =
    (function () {
      try {
        return localStorage.getItem("ki_charts_theme") === "dark" ? "dark" : "light";
      } catch (_) {
        return "light";
      }
    })();
  var recreatePrimaryWidget = null;
  var firstCandle = false;
  var switchPending = null;
  var switchTraceSeq = 0;
  window.__BC_SWITCH_TRACE = window.__BC_SWITCH_TRACE || [];
  var wlData = { indices: [], sectors: [], fno: [], nifty50: [], nifty500: [], custom: [] };
  var wlNifty500N = 0;
  var wlNifty500Loading = false;
  /** symbol(upper) → public logo URL for TV legend + watchlist. */
  var logoBySym = {};
  function rememberLogo(sym, url) {
    var k = String(sym || "").trim().toUpperCase();
    if (!k || !url) return;
    logoBySym[k] = String(url);
  }
  function logoUrlFor(sym) {
    var s = String(sym || "").trim();
    if (!s) return "";
    return logoBySym[s.toUpperCase()] || logoBySym[s] || "";
  }
  /** Public logo URL for a rendered row — prefers index map, else on-demand API (404 → hide). */
  function logoSrcForRow(sym, label, explicit) {
    if (explicit) return String(explicit);
    var hit = logoUrlFor(sym) || logoUrlFor(label);
    if (hit) return hit;
    var full = String(sym || label || "")
      .replace(/^OPT:/i, "")
      .trim()
      .toUpperCase();
    if (!full || full.length < 2) return "";
    return "/api/logo.php?s=" + encodeURIComponent(full);
  }
  function logoUrlsForTv(sym) {
    var u = logoUrlFor(sym);
    return u ? [u] : undefined;
  }
  function ingestLogosFromRows(rows) {
    (rows || []).forEach(function (r) {
      if (!r || !r.logo_url) return;
      rememberLogo(r.symbol || r.label, r.logo_url);
      rememberLogo(r.label, r.logo_url);
      (r.stocks || []).forEach(function (s) {
        if (s && s.logo_url) {
          rememberLogo(s.symbol || s.label, s.logo_url);
          rememberLogo(s.label, s.logo_url);
        }
      });
    });
  }
  function loadLogosIndex() {
    // Allow short browser cache (API max-age=60) — do not cache-bust every load.
    return fetch("/api/logos-index.php", { credentials: "same-origin" })
      .then(function (r) {
        return r.json();
      })
      .then(function (j) {
        if (!j || !j.ok || !j.logos) return;
        Object.keys(j.logos).forEach(function (k) {
          rememberLogo(k, j.logos[k]);
        });
      })
      .catch(function () {});
  }

  /** Sync tip snapshot from sessionStorage (written by storeCachedBars). */
  function sessionGetTip(cacheKey) {
    try {
      var raw = sessionStorage.getItem("bc_tip_" + cacheKey);
      if (!raw) return null;
      var bars = JSON.parse(raw);
      return Array.isArray(bars) && bars.length ? bars : null;
    } catch (_) {
      return null;
    }
  }

  /** Local tip good enough for first paint (IDB / session) before network. */
  function localTipPaintable(bars, symbol, resolution) {
    if (!bars || bars.length < 8) return false;
    if (!(tipBarsFresh(bars) || tipBarsSameSession(bars, symbol))) return false;
    if (!memEdgeTrusted(bars, symbol, resolution)) return false;
    return barMatchesLtp(bars[bars.length - 1], symbol);
  }
  var wlSrc = "default"; // default | indices | sectors | fno | nifty50 | nifty500 | custom
  /** Sectors tab: list of sectors, or drill-in stocks for one sector. */
  var wlSectorView = "list"; // list | stocks
  var wlActiveSector = null;
  var wlSectorStockRows = [];
  /** Multi custom lists (Dhan-style). Legacy single array migrated once. */
  var CUSTOM_WL_KEY = "bc_charts_custom_wl";
  var WL_LISTS_KEY = "bc_charts_wl_lists_v1";
  /** User-created pages beside the locked Default page. */
  var WL_MAX_LISTS = 5;
  var WL_MAX_SYMS = 100;
  /** Locked Default page — always opened first; order is fixed (not Chg% sorted). */
  var DEFAULT_WL_ID = "default";
  var DEFAULT_WL_LABELS = ["NIFTY 50", "FIN NIFTY", "BANK NIFTY", "SENSEX", "GIFT NIFTY", "CRUDE OIL"];
  var SYSTEM_WL = [
    { id: DEFAULT_WL_ID, name: "Default", system: true, group: "market" },
    { id: "indices", name: "Indices", system: true, group: "market" },
    { id: "sectors", name: "Sectors", system: true, group: "market" },
    { id: "fno", name: "F&O stocks", system: true, group: "market" },
    { id: "nifty50", name: "Nifty 50", system: true, group: "universe" },
    { id: "nifty500", name: "Nifty 500", system: true, group: "universe" },
  ];
  function isSystemWlId(id) {
    return SYSTEM_WL.some(function (S) {
      return S.id === id;
    });
  }
  var wlLists = []; // [{id,name,symbols:[]}]
  var wlActiveListId = DEFAULT_WL_ID;
  var wlPinCustomId = "my-list"; // target for +/✓ from system lists
  /** Watchlist Chg% sort: 'desc' (high→low) or 'asc' (low→high). */
  var wlChgSort = "desc";
  try {
    wlChgSort = localStorage.getItem("ki_charts_wl_chg_sort") === "asc" ? "asc" : "desc";
  } catch (_) {}

  function newListId() {
    return "c_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  }
  function normalizeSymList(arr) {
    var out = [];
    var seen = {};
    (arr || []).forEach(function (s) {
      var t = String(s || "").trim().toUpperCase();
      if (!t || seen[t]) return;
      seen[t] = true;
      out.push(t);
    });
    return out.slice(0, WL_MAX_SYMS);
  }
  /** Keep user pages that hold symbols or were renamed; drop the old empty fixed slots. */
  function migrateCustomLists(lists, legacySyms) {
    var out = (lists || []).filter(function (L, i) {
      return L && ((L.symbols && L.symbols.length) || !isDefaultCustomName(L.name, i));
    });
    if (!out.length && legacySyms.length) {
      out.push({ id: newListId(), name: "List 1", symbols: legacySyms.slice() });
    }
    return out.slice(0, WL_MAX_LISTS);
  }
  function loadWlListsState() {
    var lists = [];
    var activeId = DEFAULT_WL_ID;
    var pinId = "";
    var legacy = [];
    try {
      var _cw = JSON.parse(localStorage.getItem(CUSTOM_WL_KEY) || "[]");
      legacy = Array.isArray(_cw) ? normalizeSymList(_cw) : [];
    } catch (_) {}
    try {
      var raw = JSON.parse(localStorage.getItem(WL_LISTS_KEY) || "null");
      if (raw && Array.isArray(raw.lists)) {
        lists = raw.lists.map(function (L) {
          return {
            id: String(L.id || newListId()),
            name: String(L.name || "List").slice(0, 40),
            symbols: normalizeSymList(L.symbols),
          };
        });
        if (raw.pinCustomId) pinId = String(raw.pinCustomId);
      }
    } catch (_) {}
    lists = migrateCustomLists(lists, legacy);
    var ids = {};
    lists.forEach(function (L) {
      ids[L.id] = true;
    });
    if (!ids[pinId]) pinId = lists.length ? lists[0].id : "";
    // Every page load opens the Default page (saved activeId is intentionally ignored).
    wlLists = lists;
    wlActiveListId = activeId;
    wlPinCustomId = pinId;
    wlSrc = activeId;
  }
  function persistWlLists() {
    try {
      localStorage.setItem(
        WL_LISTS_KEY,
        JSON.stringify({
          activeId: wlActiveListId,
          pinCustomId: wlPinCustomId,
          lists: wlLists,
        })
      );
      // Keep legacy key in sync with pin list for older code paths.
      var pin = findCustomList(wlPinCustomId);
      localStorage.setItem(CUSTOM_WL_KEY, JSON.stringify(pin ? pin.symbols : []));
      pageSaved("WL saved");
    } catch (_) {}
  }
  function findCustomList(id) {
    var want = String(id || "");
    for (var i = 0; i < wlLists.length; i++) {
      if (wlLists[i].id === want) return wlLists[i];
    }
    return null;
  }
  function activeCustomList() {
    if (wlSrc === "custom") return findCustomList(wlActiveListId);
    return findCustomList(wlPinCustomId) || wlLists[0] || null;
  }
  function customWatchlistSymbols() {
    var L = activeCustomList();
    return L ? L.symbols.slice() : [];
  }
  loadWlListsState();

  function tvSettingsAdapter() {
    var PREFIX = "bc_tv_setting_";
    var initial = {};
    try {
      for (var i = 0; i < localStorage.length; i++) {
        var k = localStorage.key(i);
        if (k && k.indexOf(PREFIX) === 0) {
          initial[k.slice(PREFIX.length)] = localStorage.getItem(k) || "";
        }
      }
    } catch (_) {}
    return {
      initialSettings: initial,
      setValue: function (key, value) {
        try {
          localStorage.setItem(PREFIX + key, String(value));
          // Favorites / studies / drawings → full chart autosave; skip noisy misc keys.
          var k = String(key || "");
          if (/favorit|favourite|studyTemplate|chartproperties|linetools|drawings/i.test(k)) {
            kickAutosave("settings");
          }
        } catch (_) {}
      },
      removeValue: function (key) {
        try {
          localStorage.removeItem(PREFIX + key);
          var k = String(key || "");
          if (/favorit|favourite|studyTemplate|chartproperties|linetools|drawings/i.test(k)) {
            kickAutosave("settings");
          }
        } catch (_) {}
      },
    };
  }

  var FAV_IND_KEY = "bc_indicator_favs_v1";
  var DEFAULT_FAV_INDS = [
    { name: "Moving Average Exponential", short: "EMA" },
    { name: "Moving Average", short: "SMA" },
    { name: "Relative Strength Index", short: "RSI" },
    { name: "MACD", short: "MACD" },
    { name: "Bollinger Bands", short: "BB" },
    { name: "VWAP", short: "VWAP" },
    { name: "Stochastic", short: "Stoch" },
    { name: "Average True Range", short: "ATR" },
  ];
  var PICKABLE_INDS = DEFAULT_FAV_INDS.concat([
    { name: "Volume", short: "Vol" },
    { name: "Commodity Channel Index", short: "CCI" },
    { name: "Momentum", short: "Mom" },
    { name: "On Balance Volume", short: "OBV" },
    { name: "Parabolic SAR", short: "PSAR" },
    { name: "Ichimoku Cloud", short: "Ichi" },
  ]);
  function loadFavIndicators() {
    try {
      var raw = JSON.parse(localStorage.getItem(FAV_IND_KEY) || "null");
      if (Array.isArray(raw) && raw.length) {
        return raw
          .map(function (r) {
            return {
              name: String(r.name || "").trim(),
              short: String(r.short || r.name || "").trim().slice(0, 10),
            };
          })
          .filter(function (r) {
            return !!r.name;
          });
      }
    } catch (_) {}
    return DEFAULT_FAV_INDS.slice();
  }
  function saveFavIndicators(list) {
    try {
      localStorage.setItem(FAV_IND_KEY, JSON.stringify(list || []));
      pageSaved("Favs saved");
    } catch (_) {}
  }
  var favIndicators = loadFavIndicators();

  function applyFavoriteStudy(studyName) {
    var w = widget || paneWidgets.sector;
    if (!w || typeof w.activeChart !== "function") return;
    try {
      var chart = w.activeChart();
      if (!chart || typeof chart.createStudy !== "function") return;
      chart.createStudy(studyName, false, false);
      kickAutosave("indicator");
    } catch (e) {
      log("fav.study.err", e && e.message ? e.message : e);
    }
  }

  function renderFavDock() {
    var list = document.getElementById("bcFavList");
    if (!list) return;
    if (!favIndicators.length) {
      list.innerHTML = '<div class="bc-fav-hint" style="padding:0.6rem">No favorites yet — tap ＋</div>';
      return;
    }
    list.innerHTML = favIndicators
      .map(function (f, i) {
        return (
          '<div class="bc-fav-item">' +
          '<button type="button" class="bc-fav-apply" data-fav-apply="' +
          escAttr(f.name) +
          '" title="Add ' +
          escAttr(f.name) +
          '">' +
          escHtml(f.short || f.name) +
          "</button>" +
          '<button type="button" class="bc-fav-x" data-fav-remove="' +
          i +
          '" title="Remove">×</button>' +
          "</div>"
        );
      })
      .join("");
  }

  function wireFavDock() {
    var fab = document.getElementById("bcFavFab");
    var panel = document.getElementById("bcFavPanel");
    var addBtn = document.getElementById("bcFavAdd");
    var list = document.getElementById("bcFavList");
    if (!fab || !panel) return;
    renderFavDock();
    fab.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      var open = panel.hidden;
      panel.hidden = !open;
      fab.setAttribute("aria-expanded", open ? "true" : "false");
    });
    document.addEventListener("click", function () {
      panel.hidden = true;
      fab.setAttribute("aria-expanded", "false");
    });
    panel.addEventListener("click", function (e) {
      e.stopPropagation();
    });
    if (list) {
      list.addEventListener("click", function (e) {
        var apply = e.target.closest("[data-fav-apply]");
        if (apply) {
          applyFavoriteStudy(apply.getAttribute("data-fav-apply"));
          return;
        }
        var rm = e.target.closest("[data-fav-remove]");
        if (rm) {
          var idx = parseInt(rm.getAttribute("data-fav-remove"), 10);
          if (!isNaN(idx)) {
            favIndicators.splice(idx, 1);
            saveFavIndicators(favIndicators);
            renderFavDock();
          }
        }
      });
    }
    if (addBtn) {
      addBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        var names = PICKABLE_INDS.map(function (p, i) {
          return i + 1 + ". " + p.short + " — " + p.name;
        }).join("\n");
        var pick = window.prompt(
          "Add favorite (number or full indicator name):\n\n" + names,
          "1"
        );
        if (pick == null) return;
        var chosen = null;
        var n = parseInt(String(pick).trim(), 10);
        if (n >= 1 && n <= PICKABLE_INDS.length) chosen = PICKABLE_INDS[n - 1];
        else {
          var want = String(pick).trim().toLowerCase();
          for (var i = 0; i < PICKABLE_INDS.length; i++) {
            if (
              PICKABLE_INDS[i].name.toLowerCase() === want ||
              PICKABLE_INDS[i].short.toLowerCase() === want
            ) {
              chosen = PICKABLE_INDS[i];
              break;
            }
          }
          if (!chosen && want) chosen = { name: String(pick).trim(), short: String(pick).trim().slice(0, 8) };
        }
        if (!chosen) return;
        var exists = favIndicators.some(function (f) {
          return f.name.toLowerCase() === chosen.name.toLowerCase();
        });
        if (!exists) {
          favIndicators.push(chosen);
          saveFavIndicators(favIndicators);
          renderFavDock();
        }
      });
    }
  }

  function log() {
    try {
      var msg = ["[bc-php]"].concat([].slice.call(arguments)).join(" ");
      console.log(msg);
      sendDebugLog(msg);
    } catch (_) {}
  }

  function sendDebugLog(msg) {
    try {
      if (String(msg).indexOf("ERROR") < 0 && String(msg).indexOf("tip.err") < 0) return;
      var fd = new FormData();
      fd.append("msg", msg);
      fd.append("sym", activeSymbol);
      navigator.sendBeacon("/api/debug-log.php", fd);
    } catch (_) {}
  }

  window.onerror = function(msg, url, lineno, colno, err) {
    log("ERROR", msg, "line:" + lineno, err && err.stack ? err.stack.substring(0, 100) : "");
    return false;
  };

  window.addEventListener("unhandledrejection", function(e) {
    log("UNHANDLED_REJECTION", e.reason && e.reason.message ? e.reason.message : e.reason);
  });

  function lb(type, msg, data) {
    try {
      if (window.KiLogbot && typeof window.KiLogbot.mark === "function") {
        window.KiLogbot.mark(type, msg, data || {});
      }
    } catch (_) {}
  }

  function frontTrace(type, data) {
    try {
      var entry = {
        seq: ++switchTraceSeq,
        at: new Date().toISOString(),
        ms: Math.round(performance.now()),
        type: String(type || ""),
        symbol: activeSymbol,
        resolution: String(activeRes),
        data: data || {},
      };
      window.__BC_SWITCH_TRACE.push(entry);
      if (window.__BC_SWITCH_TRACE.length > 120) window.__BC_SWITCH_TRACE.shift();
      console.debug("[bc-switch]", entry);
      lb("frontend." + entry.type, entry.symbol, entry);
      return entry;
    } catch (_) {
      return null;
    }
  }

  function ocLog(msg, data) {
    try {
      var line =
        "[oc] " +
        String(msg || "") +
        (data ? " " + JSON.stringify(data) : "");
      // bridge.skip fires dozens of times on pan — console only, no beacon spam.
      if (String(msg || "") === "bridge.skip") {
        console.debug(line);
        return;
      }
      console.info(line);
      frontTrace("oc." + String(msg || "event"), data || {});
      var fd = new FormData();
      fd.append("msg", line);
      fd.append("sym", (data && data.symbol) || activeSymbol || "");
      navigator.sendBeacon("/api/debug-log.php", fd);
    } catch (_) {}
  }

  /** Last NSE session open/now buckets (skip Sat/Sun so TV session filter does not blank the chart). */
  function lastSessionBuckets(resolution, symbol) {
    var stepMs = Math.max(60, resMinutes(resolution || activeRes)) * 1000;
    var now = Date.now();
    var cursor = now;
    for (var i = 0; i < 5; i++) {
      var ist = cursor + 5.5 * 3600000;
      var d = new Date(ist);
      var dow = d.getUTCDay(); // 0 Sun .. 6 Sat
      if (dow !== 0 && dow !== 6) {
        var openIst =
          Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 9, 15, 0) - 5.5 * 3600000;
        if (isMcxSymbol(symbol)) {
          openIst =
            Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 9, 0, 0) - 5.5 * 3600000;
        }
        var closeMin = isMcxSymbol(symbol) ? 23 * 60 + 30 : 15 * 60 + 30;
        var closeIst =
          Date.UTC(
            d.getUTCFullYear(),
            d.getUTCMonth(),
            d.getUTCDate(),
            Math.floor(closeMin / 60),
            closeMin % 60,
            0
          ) - 5.5 * 3600000;
        var openBucket = Math.floor(openIst / stepMs) * stepMs;
        var curBucket = Math.floor(Math.min(cursor, closeIst) / stepMs) * stepMs;
        if (curBucket < openBucket) curBucket = openBucket;
        return { openBucket: openBucket, curBucket: curBucket, stepMs: stepMs };
      }
      // Step back one calendar day
      cursor -= 24 * 3600000;
    }
    // Fallback: plain now
    var openBucket2 = Math.floor(now / stepMs) * stepMs;
    return { openBucket: openBucket2, curBucket: openBucket2, stepMs: stepMs };
  }

  /** Instant 1–2 bar seed from live LTP so TV can flip symbol before tip/hist returns. */
  function quoteSeedBars(symbol, resolution, ltpHint) {
    var oq =
      (TickHub.lastQuotes && (TickHub.lastQuotes[symbol] || TickHub.lastQuotes[String(symbol).toUpperCase()])) ||
      null;
    var ol = oq && Number(oq.ltp) > 0 ? Number(oq.ltp) : Number(ltpHint) || 0;
    if (!(ol > 0) && isOptSymbol(symbol)) {
      ol = 0.05;
    }
    if (!(ol > 0)) return null;
    var bkt = lastSessionBuckets(resolution || activeRes, symbol);
    var openBucket = bkt.openBucket;
    var curBucket = bkt.curBucket;
    // Cash: flat O=H=L=C at LTP. Day high/low from WL painted a giant wick that tip then
    // "healed" with resetData — felt like flickering / needing refresh for clean candles.
    if (!isOptSymbol(symbol)) {
      return dedupeBarsByTime([
        { time: curBucket, open: ol, high: ol, low: ol, close: ol, volume: 1 },
      ]);
    }
    var oo = oq && Number(oq.open) > 0 ? Number(oq.open) : ol;
    var oh = oq && Number(oq.high) > 0 ? Number(oq.high) : Math.max(oo, ol);
    var olo = oq && Number(oq.low) > 0 ? Number(oq.low) : Math.min(oo, ol);
    oh = Math.max(oh, oo, ol);
    olo = Math.min(olo, oo, ol);
    var bars = [
      { time: openBucket, open: oo, high: Math.max(oo, oh), low: Math.min(oo, olo), close: oo, volume: 1 },
    ];
    if (curBucket !== openBucket) {
      bars.push({
        time: curBucket,
        open: oo,
        high: oh,
        low: olo,
        close: ol,
        volume: 1,
      });
    } else {
      bars[0].high = oh;
      bars[0].low = olo;
      bars[0].close = ol;
    }
    return dedupeBarsByTime(bars);
  }

  function dedupeBarsByTime(bars) {
    if (!bars || !bars.length) return bars || [];
    var byT = {};
    for (var i = 0; i < bars.length; i++) {
      var b = bars[i];
      if (!b || !(Number(b.time) > 0)) continue;
      byT[String(b.time)] = b;
    }
    return Object.keys(byT)
      .map(function (k) {
        return Number(k);
      })
      .sort(function (a, b) {
        return a - b;
      })
      .map(function (t) {
        return byT[String(t)];
      });
  }

  function setMetric(id, value, cls) {
    var el = document.getElementById(id);
    if (!el) return;
    el.textContent = value;
    var wrap = el.parentElement;
    if (!wrap) return;
    wrap.classList.remove("ok", "bad", "warn");
    if (cls) wrap.classList.add(cls);
  }

  function tipNet(symbol, resolution, mode) {
    mode = mode || "warm";
    // Never use refresh=1 on the click path — it blocked the UI while the URL already changed.
    // Background deepen uses warm tip (Redis/L1) or history.php (Postgres) instead.
    if (mode === "switch") {
      mode = "warm";
    }
    // hover < deepen < click/warm — click must not wait behind a hover tip storm.
    var prio = mode === "hover" ? 2 : mode === "cold" || mode === "deepen" ? 1 : 0;
    if (mode === "hover" && (!TIP_HOVER_ENABLED || Date.now() < tipHoverPauseUntil)) {
      return Promise.reject(new Error("tip_hover_paused"));
    }
    if (prio > 0 && Date.now() < tipBackoffUntil) {
      return Promise.reject(new Error("tip_backoff"));
    }
    if (!tipSymReady(symbol, mode)) {
      return Promise.reject(new Error("tip_sym_cooldown"));
    }
    tipSymMark(symbol);
    var fetchRes = nativeSourceRes(resolution);
    var url =
      TIP_URL +
      "?symbol=" +
      encodeURIComponent(symbol) +
      "&resolution=" +
      encodeURIComponent(fetchRes) +
      "&countback=" +
      TIP_COUNTBACK;
    var logMode = mode;
    return tipSchedule(prio, function () {
    var t0 = performance.now();
    var ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    var abortTimer = null;
    if (ctrl) {
      // Hard cap so getBars never sits on a hung FPM worker for 30s.
      abortTimer = setTimeout(function () {
        try {
          ctrl.abort();
        } catch (_) {}
      }, 1500);
    }
    return fetch(url, {
      credentials: "include",
      cache: "no-store",
      signal: ctrl ? ctrl.signal : undefined,
    })
      .then(function (res) {
      if (abortTimer) clearTimeout(abortTimer);
      if (!res.ok) {
        if (res.status === 429) tipNoteRateLimited(symbol);
        throw new Error("HTTP " + res.status);
      }
      return res.json().then(function (data) {
        var clientMs = Math.round(performance.now() - t0);
        var bars = (data && data.bars) || [];
        var cache = (data && data.cache) || "";
        if (/DEMO/i.test(cache) || (data && data.fallback === "DEMO")) {
          throw new Error("tip_demo_blocked");
        }
        var tv5 = stripOpenBucket(toTvBars(bars), fetchRes, symbol);
        if (tv5.length) {
          commitFreshNative(symbol, fetchRes, tv5);
        }
        try {
          var tipPretty = data && data.symbol ? String(data.symbol).trim() : "";
          var tipWire =
            (data && data.wire ? String(data.wire).trim() : "") ||
            (data && data.security_id ? "OPT:" + String(data.security_id).trim() : "") ||
            (/^OPT:/i.test(String(symbol || "")) ? String(symbol).trim() : "");
          if (tipPretty && tipWire && /^OPT:/i.test(tipWire)) {
            registerOptLabel(tipWire, tipPretty);
            // Keep URL + header on the pretty strike name after an OPT:sid deep link.
            if (/^OPT:/i.test(String(symbol || "")) && tipPretty !== symbol) {
              try {
                if (String(activeSymbol || "").toUpperCase() === String(symbol).toUpperCase()) {
                  activeSymbol = tipPretty;
                  var elTip = document.getElementById("cxActiveSym");
                  if (elTip) elTip.textContent = tipPretty;
                  var titleTip = document.getElementById("cx-left-title");
                  if (titleTip) titleTip.textContent = tipPretty;
                  history.replaceState(null, "", "/charts?symbol=" + encodeURIComponent(tipPretty));
                }
              } catch (_) {}
            }
          }
        } catch (_) {}
        var wantMin = resMinutes(resolution);
        var out =
          fetchRes === String(resolution) || wantMin === 5
            ? tv5
            : aggregateTvBars(tv5, wantMin, symbol);
        if (wantMin !== 5 && fetchRes === "5") {
          cache = (cache || "FILE") + "+AGG" + wantMin;
        }
        log("tipNet", logMode, "n=" + out.length, "cache=" + cache, clientMs + "ms", "src=" + fetchRes, "want=" + resolution);
        if (logMode === "cold") {
          lb("tv.getBars.ok", symbol, {
            ms: clientMs,
            server_ms: data && data.ms,
            n: out.length,
            cache: cache,
            resolution: resolution,
            mode: "cold",
          });
          lb("tv.speed", symbol, {
            ms: clientMs,
            cache: cache,
            mode: "cold",
            verdict: clientMs <= 250 ? "fast" : clientMs <= 800 ? "ok" : "slow",
          });
        }
        return {
          bars: out,
          bars5: tv5,
          cache: cache,
          client_ms: clientMs,
          ok: !!(data && data.ok && out && out.length),
        };
      });
    })
      .catch(function (err) {
        if (abortTimer) clearTimeout(abortTimer);
        var msg = err && err.message ? String(err.message) : String(err || "");
        if (/HTTP 429/.test(msg)) tipNoteRateLimited(symbol);
        // 504 / abort = FPM saturated — back off so the tab stops feeding the storm.
        if (/HTTP 504|HTTP 502|HTTP 503|aborted|AbortError/i.test(msg)) {
          tipBackoffUntil = Math.max(tipBackoffUntil, Date.now() + 8000);
        }
        throw err;
      });
    }); // tipSchedule
  }

  function histNet(symbol, resolution, fromMs, toMs, optCapMs?) {
    var fromSec = Math.floor((fromMs || 0) / 1000);
    var toSec = Math.floor((toMs || Date.now()) / 1000);
    // VC Stocks: history is always the continuous 5m pack; higher TFs derive from it.
    var fetchRes = nativeSourceRes(resolution);

    var url =
      "/api/history.php" +
      "?symbol=" +
      encodeURIComponent(symbol) +
      "&from=" +
      fromSec +
      "&to=" +
      toSec +
      "&resolution=" +
      encodeURIComponent(fetchRes);

    var t0 = performance.now();
    var fetchP = fetch(url, { credentials: "include", cache: "no-store" }).then(function (res) {
      if (!res.ok) throw new Error("HTTP " + res.status);
      return res.json().then(function (data) {
        var clientMs = Math.round(performance.now() - t0);
        var bars = (data && data.bars) || [];
        var cache = (data && data.cache) || "";
        if (/DEMO/i.test(cache)) {
          throw new Error("hist_demo_blocked");
        }
        var tvNative = stripOpenBucket(toTvBars(bars), fetchRes, symbol);
        // Merge into exact-TF cache (server already rolled). Keep 5m pool when src is 5.
        if (tvNative.length && fetchRes === "5") {
          persistFiveAndDerive(symbol, tvNative);
        } else if (tvNative.length) {
          // Don't merge hist into replay symbol MEM past the cut.
          if (!(barReplay.active && sameChartSymbol(symbol, barReplay.symbol))) {
            var nk = subKey(symbol, fetchRes);
            histCache[nk] = mergeBars(tvNative, histCache[nk] || []);
            storeCachedBars(nk);
          }
        }
        var wantMin = resMinutes(resolution);
        var src = histCache[subKey(symbol, fetchRes)] || tvNative;
        var out =
          wantMin === resMinutes(fetchRes)
            ? src
            : aggregateTvBars(src, wantMin, symbol);
        // Slice aggregated bars to the requested window (ms).
        if (out.length && (fromMs || toMs)) {
          out = out.filter(function (b) {
            return b.time >= (fromMs || 0) && b.time <= (toMs || Date.now() + 86400000);
          });
        }
        if (wantMin !== resMinutes(fetchRes) && fetchRes === "5") {
          cache = (cache || "HIST") + "+AGG" + wantMin;
        }
        log("histNet", "n=" + out.length, "cache=" + cache, clientMs + "ms", "src=" + fetchRes, "want=" + resolution);
        lb("tv.speed", symbol, {
          ms: clientMs,
          cache: cache,
          mode: "history",
          n: out.length,
          verdict: clientMs <= 250 ? "fast" : clientMs <= 800 ? "ok" : "slow",
        });
        return {
          bars: out,
          bars5: fetchRes === "5" ? tvNative : null,
          cache: cache,
          client_ms: clientMs,
          ok: !!(data && data.ok && out.length),
        };
      });
    });
    return fetchP;
  }


  function markCandle(extra) {
    if (firstCandle) return;
    firstCandle = true;
    var tipAfter = Math.round(performance.now() - switchT0);
    if (tipAfter < 0) tipAfter = 0;
    var grade = tipAfter <= 250 ? "ok" : tipAfter <= 800 ? "warn" : "bad";
    setMetric("mTip", tipAfter + "ms", grade);
    if (extra && extra.cache) setMetric("mCache", String(extra.cache));
    try {
      performance.mark("ki-first-candle");
    } catch (_) {}
    if (window.__kiFirstCandleTipMs == null) {
      window.__kiFirstCandleTipMs = tipAfter;
    }
    lb("tv.speed", activeSymbol, {
      ms: tipAfter,
      cache: (extra && extra.cache) || "",
      verdict: grade === "ok" ? "fast" : grade === "warn" ? "ok" : "slow",
      mode: extra && extra.local ? "warm" : "cold",
    });
    lb("page.first_candle", activeSymbol, {
      tip_after_libs_ms: tipAfter,
      first_candle_ms: Math.round(performance.now() - navT0),
      cache: (extra && extra.cache) || "",
    });
    frontTrace("candle.paint", {
      elapsed_ms: tipAfter,
      cache: (extra && extra.cache) || "",
      pending_gen: switchPending && switchPending.gen,
    });
    switchPending = null;
  }

  function hideEmpty() {
    var el = document.getElementById("cx-left-empty");
    if (el) el.hidden = true;
  }

  // TradingView's realtime subscription model is symbol+resolution scoped and
  // keyed by subscriberUID (a chart can hold multiple independent live
  // subscriptions) — see charting-library-docs/datafeed-api/datafeed-subscriptions.
  // A single global window._tvRealtimeCallback only happens to work while
  // exactly one symbol is ever subscribed; on a symbol switch, TradingView's
  // own widget.setSymbol() re-subscribes internally, but our separate
  // window.liveStream (the EventSource) was never told to reconnect for the
  // new symbol — so it would keep delivering the OLD symbol's ticks into
  // whatever callback is currently registered. Route through a proper
  // subscriber map instead, matched by symbol ticker + resolution, exactly
  // like TradingView expects a real streaming datafeed to behave.
  var realtimeSubscribers = {};
  // Per symbol|resolution key, the last time we actually delivered to
  // TradingView — lets us log NEW BAR vs BAR UPDATE and catch any
  // backwards-time bug immediately instead of relying on TV's own rejection.
  var lastDeliveredTime = {};
  // Last bar returned by getBars() on the cold/first request, per
  // symbol|resolution — this is what a live stream's first bar must
  // continue from (same time = update, time+interval = valid new bar).
  // TV tutorial: lastBarsCache — keyed symbol|resolution (not ticker alone).
  var lastHistoricalBar = {};
  /** TV onResetCacheNeededCallback per listenerGUID — fire after tip heal. */
  var resetCacheNeededCbs = {};
  /** OpenAlgo-style builders keyed symbol|resolution. */
  var candleBuilders = {};
  /**
   * Anti-flicker: first getBars onResult committed this key's tip edge.
   * Value = Date.now() when committed. Live seed + forceEdgeReset wait on this.
   */
  var paintCommittedAt = {};
  /** Pending subscribeBars seeds waiting for paint commit: key → [{guid,cb,ticker,res,bar}]. */
  var pendingLiveSeeds = {};
  /** Grace ms after first paint — suppress resetData / seed double-paint. */
  var PAINT_GRACE_MS = 2200;
  /**
   * Last bar TradingView actually holds per symbol|resolution (getBars edge or last onTick).
   * TV silently ignores realtime bars older than this, so every send is checked against it.
   */
  var tvEdge = {};
  /** Forming candle built from live ticks, per symbol|resolution. */
  var liveBar = {};
  /** Cumulative day volume when the forming candle opened (volume = cum - this). */
  var liveCumAtOpen = {};
  /** Per-key live counters: ticks in, emits, drops by gate, last price seen. */
  var liveStat = {};
  /** First-request getBars in flight (key → started ms); live waits until TV has history. */
  var getBarsPending = {};
  /** Option charts: candles built from ticks since the last history load (key → time → bar). */
  var optLiveBars = {};
  /** Option heal repaint in flight: its OPT-MEM repaint skips the refetch + second reset. */
  var optHealReset = {};
  window.__bcLive = { stat: liveStat, tvEdge: tvEdge, liveBar: liveBar, pending: getBarsPending, optLive: optLiveBars };
  /** Read by the staging candle-eval bot. What the chart is holding right now. */
  window.__bcCandleProbe = function () {
    var symbol = String(activeSymbol || "");
    var resolution = String(activeRes || "");
    var key = subKey(symbol, resolution);
    var bars = histCache[key] || [];
    var today = [];
    try {
      today = barsTodaySession(bars, symbol);
    } catch (_) {}
    function slim(b) {
      if (!b) return null;
      return {
        t: Number(b.time) || 0,
        o: Number(b.open),
        h: Number(b.high),
        l: Number(b.low),
        c: Number(b.close),
      };
    }
    var lb = liveBar[key] || null;
    return {
      symbol: symbol,
      resolution: resolution,
      n: bars.length,
      today: today.length,
      last: slim(today.length ? today[today.length - 1] : bars.length ? bars[bars.length - 1] : null),
      todayTail: today.slice(-8).map(slim),
      live: lb
        ? { t: Number(lb.time) || 0, o: Number(lb.open), h: Number(lb.high), l: Number(lb.low), c: Number(lb.close) }
        : null,
      edge: tvEdge[key] || null,
      paintedToday: tvPaintToday[key] == null ? null : tvPaintToday[key],
      ws: !!window.__kiWsConnected,
      at: Date.now(),
    };
  };
  /** Ring-buffer for multi-grid live debugging. HUD only with ?gridlog=1 or localStorage bc_grid_log=1. */
  var gridLiveLog = [];
  var gridLiveLogEl = null;
  var gridLiveLogWant =
    /(?:\?|&)gridlog=1\b/.test(String(location.search || "")) ||
    (function () {
      try {
        return localStorage.getItem("bc_grid_log") === "1";
      } catch (_) {
        return false;
      }
    })();
  window.__bcGridLog = gridLiveLog;
  window.__bcGridDump = function () {
    var subs = [];
    try {
      Object.keys(realtimeSubscribers || {}).forEach(function (guid) {
        var sub = realtimeSubscribers[guid];
        if (!sub) return;
        var tick = (sub.symbolInfo && (sub.symbolInfo.ticker || sub.symbolInfo.name)) || "";
        subs.push({
          guid: String(guid).slice(0, 12),
          symbol: tick,
          res: String(sub.resolution),
          hasRaw: typeof sub.rawCallback === "function",
          edge: tvEdge["g:" + guid] ? tvEdge["g:" + guid].close : null,
        });
      });
    } catch (_) {}
    var listeners = [];
    try {
      Object.keys((TickHub && TickHub.listeners) || {}).forEach(function (guid) {
        var L = TickHub.listeners[guid];
        if (!L) return;
        listeners.push({
          guid: String(guid).slice(0, 12),
          symbol: L.symbol,
          res: String(L.resolution),
        });
      });
    } catch (_) {}
    var panes = {};
    try {
      panes = {
        grid: currentGrid,
        sector: paneSymbols.sector,
        stock: paneSymbols.stock,
        extras: (paneSymbols.extras || []).slice(),
      };
    } catch (_) {}
    return { panes: panes, subs: subs, listeners: listeners, log: gridLiveLog.slice(-40) };
  };

  function gridLog(event, data) {
    var row = {
      t: new Date().toISOString().slice(11, 23),
      event: String(event || ""),
      data: data || {},
    };
    gridLiveLog.push(row);
    if (gridLiveLog.length > 80) gridLiveLog.shift();
    try {
      lb("grid." + row.event, (data && data.symbol) || activeSymbol || "", data || {});
    } catch (_) {}
    try {
      console.debug("[bc-grid]", row.event, data || {});
    } catch (_) {}
    if (gridLiveLogWant) paintGridLiveLog();
  }

  function ensureGridLiveLogEl() {
    if (gridLiveLogEl && gridLiveLogEl.parentNode) return gridLiveLogEl;
    var el = document.getElementById("cxGridLiveLog");
    if (!el) {
      el = document.createElement("div");
      el.id = "cxGridLiveLog";
      el.setAttribute("aria-live", "polite");
      el.style.cssText =
        "position:fixed;left:8px;bottom:48px;z-index:9999;max-width:min(420px,92vw);max-height:220px;overflow:auto;" +
        "background:rgba(10,12,16,0.92);color:#d7e0ea;border:1px solid rgba(76,175,80,0.45);border-radius:8px;" +
        "font:11px/1.35 ui-monospace,SFMono-Regular,Menlo,monospace;padding:8px 10px;pointer-events:none;";
      (document.body || document.documentElement).appendChild(el);
    }
    gridLiveLogEl = el;
    return el;
  }

  function paintGridLiveLog() {
    if (!gridLiveLogWant) return;
    var el = ensureGridLiveLogEl();
    var dump = window.__bcGridDump();
    var head =
      "<div style='color:#81c784;font-weight:700;margin-bottom:4px'>Grid live log · subs=" +
      dump.subs.length +
      " listeners=" +
      dump.listeners.length +
      "</div>";
    var subLine = dump.subs
      .map(function (s) {
        return s.symbol + "|" + s.res + " raw=" + (s.hasRaw ? "1" : "0") + " edge=" + (s.edge != null ? s.edge : "—");
      })
      .join("<br>");
    var lines = gridLiveLog
      .slice(-12)
      .map(function (r) {
        var d = r.data || {};
        return (
          "<div><span style='color:#90a4ae'>" +
          r.t +
          "</span> " +
          r.event +
          (d.symbol ? " " + d.symbol : "") +
          (d.res ? "|" + d.res : "") +
          (d.n_subs != null ? " subs=" + d.n_subs : "") +
          (d.n_ok != null ? " ok=" + d.n_ok : "") +
          (d.n_fail != null ? " fail=" + d.n_fail : "") +
          (d.close != null ? " c=" + d.close : "") +
          (d.why ? " " + d.why : "") +
          "</div>"
        );
      })
      .join("");
    el.innerHTML = head + (subLine ? "<div style='margin-bottom:6px;color:#ffe082'>" + subLine + "</div>" : "") + lines;
  }

  function copyBar(b) {
    return {
      time: Number(b.time),
      open: Number(b.open),
      high: Number(b.high),
      low: Number(b.low),
      close: Number(b.close),
      volume: Number(b.volume) || 0,
    };
  }

  function liveStatFor(key) {
    var s = liveStat[key];
    if (!s) {
      s = liveStat[key] = { in: 0, emit: 0, gates: {}, lastGate: "", pxAt: 0, px: 0, quote: null, emitAt: 0 };
    }
    return s;
  }

  function liveGate(key, gate) {
    var s = liveStatFor(key);
    s.gates[gate] = (s.gates[gate] || 0) + 1;
    s.lastGate = gate;
  }

  /**
   * Only path to TradingView's realtime callback. Copies the bar (TV may mutate it) and
   * refuses bars older than what THAT subscriber holds (multi-grid panes must not share one edge).
   */
  function tvSend(key, cb, bar, why, guid) {
    if (!bar || typeof cb !== "function" || !(Number(bar.close) > 0)) return false;
    // Absolute mute: during replay only why==="replay" may update this symbol's series.
    if (why !== "replay") {
      try {
        var parts = String(key || "").split("|");
        if (barReplayBlocksLive(parts[0], parts.slice(1).join("|") || "")) {
          liveGate(key, "replay");
          return false;
        }
      } catch (_) {}
    }
    var edgeKey = guid ? "g:" + String(guid) : key;
    var edge = tvEdge[edgeKey];
    // Replay bars are historical — never let a leftover live edge block play.
    if (why !== "replay" && edge && Number(bar.time) < Number(edge.time)) {
      liveGate(key, "older_than_tv:" + (why || "?"));
      var sb = liveStatFor(key);
      sb.lastBlock = { why: why || "?", bar_t: Number(bar.time), edge_t: Number(edge.time), at: Date.now(), guid: guid || "" };
      if (Date.now() - (sb.blockLogAt || 0) > 30000) {
        sb.blockLogAt = Date.now();
        lb("chart.block", key.split("|")[0], Object.assign({ resolution: key.split("|")[1] }, sb.lastBlock));
      }
      return false;
    }
    var out = copyBar(bar);
    tvEdge[edgeKey] = copyBar(out);
    // Aggregate tip for builders / gates — never move backwards for the shared key
    // (except replay, which rewinds the series on purpose).
    if (why === "replay" || !tvEdge[key] || Number(out.time) >= Number(tvEdge[key].time)) {
      tvEdge[key] = copyBar(out);
    }
    lastDeliveredTime[key] = out.time;
    var s = liveStatFor(key);
    s.emit++;
    s.emitAt = Date.now();
    if (why === "live" || why === "force") s.liveAt = s.emitAt;
    try {
      cb(out);
    } catch (e) {
      console.error("[tick onTick]", e);
    }
    return true;
  }

  /** Push one live bar to every multi-grid pane on this symbol|resolution. */
  function deliverLiveBarToPanes(symbol, resolution, bar, why) {
    if (!bar || !(Number(bar.close) > 0)) return 0;
    if (why !== "replay" && barReplayBlocksLive(symbol, resolution)) return 0;
    var n = 0;
    var nFail = 0;
    var seen = {};
    var okGuids = [];
    var failGuids = {};
    function deliverGuid(guid, ticker, rawCb) {
      if (!guid || seen[guid]) return;
      seen[guid] = 1;
      if (typeof rawCb !== "function") {
        nFail++;
        failGuids[String(guid).slice(0, 8)] = "no_raw";
        return;
      }
      var key = subKey(ticker, resolution);
      if (tvSend(key, rawCb, bar, why || "live", guid)) {
        n++;
        okGuids.push(String(guid).slice(0, 8));
      } else {
        nFail++;
        failGuids[String(guid).slice(0, 8)] = "tvSend";
      }
    }
    var nSubs = 0;
    for (var guid in realtimeSubscribers) {
      var sub = realtimeSubscribers[guid];
      if (!sub || String(sub.resolution) !== String(resolution)) continue;
      var tick =
        (sub.symbolInfo && (sub.symbolInfo.ticker || sub.symbolInfo.name)) ||
        (TickHub.listeners[guid] && TickHub.listeners[guid].symbol) ||
        "";
      if (!sameChartSymbol(tick, symbol)) continue;
      nSubs++;
      deliverGuid(guid, tick, sub.rawCallback || null);
    }
    for (var g2 in TickHub.listeners) {
      if (seen[g2]) continue;
      var L = TickHub.listeners[g2];
      if (!L || String(L.resolution) !== String(resolution)) continue;
      if (!sameChartSymbol(L.symbol, symbol)) continue;
      nSubs++;
      var sub2 = realtimeSubscribers[g2];
      if (sub2 && sub2.rawCallback) {
        deliverGuid(g2, L.symbol, sub2.rawCallback);
      } else if (typeof L.onTick === "function") {
        seen[g2] = 1;
        try {
          L.onTick(copyBar(bar), why || "live");
          n++;
          okGuids.push(String(g2).slice(0, 8));
        } catch (e) {
          nFail++;
          failGuids[String(g2).slice(0, 8)] = "onTick";
          console.error("[tick onTick]", e);
        }
      }
    }
    // Throttle HUD / logbot: always log mismatches; otherwise ~1/s while multi-sub.
    var multi = nSubs > 1 || n > 1;
    var now = Date.now();
    if (!deliverLiveBarToPanes._at) deliverLiveBarToPanes._at = 0;
    if (nSubs !== n || nFail || (multi && now - deliverLiveBarToPanes._at > 900)) {
      deliverLiveBarToPanes._at = now;
      gridLog("deliver", {
        symbol: symbol,
        res: String(resolution),
        why: why || "live",
        close: bar.close,
        n_subs: nSubs,
        n_ok: n,
        n_fail: nFail,
        ok: okGuids.join(","),
        fail: failGuids,
      });
    }
    return n;
  }

  /**
   * History must never hold a bar past the current bucket (server clock, 60 s slack for skew):
   * TV would keep that future bar as its last bar and every live tick would fold into it.
   */
  function dropFutureBars(key, symbol, resolution, bars) {
    if (!bars || !bars.length) return bars;
    var skew = TickHub.clockSkewMs || 0;
    var capMs = floorBarTimeMs(Date.now() - skew + 60000, resolution, symbol);
    if (!(Number(bars[bars.length - 1].time) > capMs)) return bars;
    var kept = bars.filter(function (b) {
      return Number(b.time) <= capMs;
    });
    lb("history.future_bars", symbol, {
      resolution: String(resolution),
      dropped: bars.length - kept.length,
      last_t: Number(bars[bars.length - 1].time),
      cap_t: capMs,
      clock_skew_ms: skew,
    });
    return kept;
  }

  /** getBars reply: record what TV now holds; a first request replaces the edge (reset). */
  function noteTvHistory(key, bars, firstRequest) {
    var last = bars && bars.length ? bars[bars.length - 1] : null;
    if (firstRequest) {
      delete getBarsPending[key];
      tvEdge[key] = last ? copyBar(last) : null;
      var lb0 = liveBar[key];
      if (lb0 && last) {
        if (Number(last.time) > lb0.time) delete liveBar[key];
        else if (Number(last.time) === lb0.time) {
          lb0.open = Number(last.open) || lb0.open;
          lb0.high = Math.max(lb0.high, Number(last.high) || 0);
          lb0.low = Math.min(lb0.low, Number(last.low) > 0 ? Number(last.low) : lb0.low);
        }
      }
      return;
    }
    if (last && (!tvEdge[key] || Number(last.time) > Number(tvEdge[key].time))) {
      tvEdge[key] = copyBar(last);
    }
  }

  function subKey(symbol, resolution) {
    return String(symbol) + '|' + String(resolution);
  }

  function inPaintGrace(cacheKey) {
    var t = paintCommittedAt[cacheKey];
    return t > 0 && Date.now() - t < PAINT_GRACE_MS;
  }

  /**
   * Seed / refresh liveBar from TickHub quote so first paint and WL LTP share one close.
   * Prefers daemon quote.bars[tf] OHLC when the bucket matches; otherwise opens at prior close.
   */
  function ensureLiveBarFromQuote(symbol, resolution) {
    var key = subKey(symbol, resolution);
    var openB = floorBarTimeMs(Date.now() - (TickHub.clockSkewMs || 0), resolution, symbol);
    var existing = liveBar[key];
    if (existing && Number(existing.time) === openB && Number(existing.close) > 0) {
      return existing;
    }
    var q = null;
    try {
      q = lookupQuote(symbol);
    } catch (_) {}
    if (!q || !(Number(q.ltp) > 0)) return existing || null;
    if (!inSessionNow(symbol)) return existing || null;
    var ltp = Number(q.ltp);
    var mins = resMinutes(resolution);
    var serverRaw =
      q.bars &&
      (q.bars[String(mins)] ||
        q.bars[String(resolution)] ||
        (mins === 1440 ? q.bars["1D"] : null) ||
        (mins === 240 ? q.bars["4H"] : null));
    var bar = null;
    if (serverRaw && Number(serverRaw.close) > 0) {
      var srvT = Number(serverRaw.time);
      if (srvT > 0 && srvT < 1e12) srvT *= 1000;
      if (srvT === openB) {
        var sO = Number(serverRaw.open) > 0 ? Number(serverRaw.open) : ltp;
        var sH = Number(serverRaw.high) > 0 ? Number(serverRaw.high) : ltp;
        var sL = Number(serverRaw.low) > 0 ? Number(serverRaw.low) : ltp;
        bar = {
          time: openB,
          open: sO,
          high: Math.max(sH, sO, ltp),
          low: Math.min(sL > 0 ? sL : ltp, sO, ltp),
          close: ltp,
          volume: Number(serverRaw.volume) > 0 ? Number(serverRaw.volume) : 0,
        };
      }
    }
    if (!bar) {
      var prior = lastHistoricalBar[key] || tvEdge[key] || null;
      var open0 = ltp;
      if (prior && Number(prior.close) > 0 && Number(prior.time) < openB) {
        open0 = Number(prior.close);
      } else if (Number(q.open) > 0 && mins >= 1440) {
        open0 = Number(q.open);
      }
      bar = {
        time: openB,
        open: open0,
        high: Math.max(open0, ltp),
        low: Math.min(open0, ltp),
        close: ltp,
        volume: 0,
      };
    }
    liveBar[key] = bar;
    return bar;
  }

  /**
   * Tip/history strip the open bucket so live owns it — but TradingView onTick only WIDENS
   * high/low and never shrinks a flash wick. Re-attach the authoritative liveBar so a
   * resetCache/getBars paint can replace the forming candle with the correct structure.
   */
  function appendFormingLiveBar(list, symbol, resolution) {
    var key = subKey(symbol, resolution);
    ensureLiveBarFromQuote(symbol, resolution);
    var lb = liveBar[key];
    if (!lb || !(Number(lb.close) > 0) || !(Number(lb.time) > 0)) return list || [];
    var openB = floorBarTimeMs(Date.now() - (TickHub.clockSkewMs || 0), resolution, symbol);
    if (Number(lb.time) !== openB) return list || [];
    var out = list && list.length ? list.slice() : [];
    var stepMs = resMinutes(resolution) * 60000;
    if (!(stepMs > 0)) stepMs = 5 * 60000;
    // Never append a lone forming bar across missing closed buckets — TV onTick
    // cannot insert the middle; that is exactly the "gap until refresh" symptom.
    if (out.length) {
      var prevLast = Number(out[out.length - 1].time);
      if (openB - prevLast > stepMs * 1.01) return out;
    } else if (inSessionNow(symbol)) {
      return out;
    }
    while (out.length && Number(out[out.length - 1].time) >= openB) out.pop();
    out.push(copyBar(lb));
    return out;
  }

  /**
   * Continuous candle formation under tip/MEM lag: insert flat continuity bars for
   * missing same-session closed buckets between the last known close and the open
   * bucket (exclusive). Tip heal replaces these with real OHLC ASAP. Caps at ~1h
   * (5m×12) so overnight / dead symbols do not invent a full session.
   */
  function padSessionLagBars(list, symbol, resolution) {
    if (!list || !list.length) return list || [];
    if (!symbol || resMinutes(resolution) >= 1440) return list;
    if (!inSessionNow(symbol)) return list;
    var stepMs = resMinutes(resolution) * 60000;
    if (!(stepMs > 0)) stepMs = 5 * 60000;
    var openB = floorBarTimeMs(Date.now() - (TickHub.clockSkewMs || 0), resolution, symbol);
    var out = list.slice();
    while (out.length && Number(out[out.length - 1].time) >= openB) out.pop();
    if (!out.length) return list;
    var last = out[out.length - 1];
    var lastT = Number(last.time) || 0;
    if (lastT > 0 && lastT < 1e12) lastT *= 1000;
    var px = Number(last.close) || 0;
    if (!(lastT > 0) || !(px > 0)) return out;
    if (openB - lastT <= stepMs * 1.01) return out;
    var maxSteps = isMcxSymbol(symbol) ? 18 : 12;
    var lagSteps = Math.round((openB - lastT) / stepMs);
    if (lagSteps > maxSteps) return out;
    var day0 = istDayStartMs(openB);
    var have = {};
    for (var i = 0; i < out.length; i++) {
      var ht = Number(out[i].time) || 0;
      if (ht > 0 && ht < 1e12) ht *= 1000;
      if (ht > 0) have[ht] = true;
    }
    var added = 0;
    for (var t = lastT + stepMs; t < openB; t += stepMs) {
      if (istDayStartMs(t) !== day0) continue;
      if (!inSessionAt(symbol, t)) continue;
      if (have[t]) continue;
      out.push({ time: t, open: px, high: px, low: px, close: px, volume: 1 });
      have[t] = true;
      added++;
      if (added >= maxSteps) break;
    }
    if (!added) return out;
    out.sort(function (a, b) {
      return Number(a.time) - Number(b.time);
    });
    return out;
  }

  /**
   * Commit lag pads into MEM and keep lastHistoricalBar on the last closed pad so
   * live can form on the open bucket without a series hole. Returns pads added.
   */
  function ensureSessionLagPads(symbol, resolution, reason) {
    if (!symbol || resMinutes(resolution) >= 1440) return 0;
    if (!inSessionNow(symbol)) return 0;
    var key = subKey(symbol, resolution);
    var pool = histCache[key] || [];
    if (!pool.length) return 0;
    var beforeN = pool.length;
    var beforeLast = barLastTimeMs(pool);
    var padded = padSessionLagBars(pool, symbol, resolution);
    var afterLast = barLastTimeMs(padded);
    var stepMs = resMinutes(resolution) * 60000 || 300000;
    var addedApprox = afterLast > beforeLast ? Math.round((afterLast - beforeLast) / stepMs) : 0;
    if (!(addedApprox > 0) && padded.length <= beforeN) return 0;
    var openB = floorBarTimeMs(Date.now(), resolution, symbol);
    var closedPads = [];
    for (var i = 0; i < padded.length; i++) {
      if (Number(padded[i].time) < openB) closedPads.push(padded[i]);
    }
    histCache[key] = mergeBars(histCache[key] || [], closedPads);
    var lastClosed = closedPads.length ? closedPads[closedPads.length - 1] : null;
    if (lastClosed && Number(lastClosed.close) > 0) {
      lastHistoricalBar[key] = copyBar(lastClosed);
      seedBuilder(symbol, resolution, lastClosed);
    }
    if (String(resolution) === "5") {
      try {
        commitFreshFive(symbol, closedPads);
      } catch (_) {}
    }
    lb("candle.lag.pad", symbol, {
      resolution: String(resolution),
      reason: reason || "lag.pad",
      added: addedApprox || Math.max(0, closedPads.length - beforeN),
      before_last: beforeLast || null,
      after_last: barLastTimeMs(histCache[key] || []) || null,
      open_bucket: openB,
    });
    return addedApprox || 1;
  }

  /** Last closed bar time in MEM for this TF (not liveBar / lastHistoricalBar forming edge). */
  function memClosedLastMs(symbol, resolution) {
    var pool = histCache[subKey(symbol, resolution)] || [];
    if (!pool.length) return 0;
    var openB = floorBarTimeMs(Date.now(), resolution, symbol);
    var last = 0;
    for (var i = pool.length - 1; i >= 0; i--) {
      var t = Number(pool[i].time) || 0;
      if (t > 0 && t < 1e12) t *= 1000;
      if (t > 0 && t < openB && !barIsFlatPlaceholder(pool[i])) {
        last = t;
        break;
      }
    }
    if (!last && pool.length && !barIsFlatPlaceholder(pool[pool.length - 1])) {
      last = barLastTimeMs(pool);
    }
    return last;
  }

  /**
   * Finalize bars for first paint: strip forming open bucket, sanitize vs LTP,
   * pad in-session lag, drop trailing vol=0 invents. One committed right edge.
   */
  function commitPaintBars(bars, symbol, resolution) {
    var list = bars || [];
    if (!list.length) return list;
    if (list[0].time < 1e12) list = toTvBars(list);
    var beforeStrip = list;
    list = stripOpenBucket(list, resolution, symbol);
    // WL LTP seed is often ONLY the open bucket. Stripping it left getBars empty →
    // paintFromHistory/tip wait (seconds under tip 504). Keep forming bar so TV flips now.
    if (!list.length && beforeStrip.length) {
      list = [beforeStrip[beforeStrip.length - 1]];
    }
    var hint = 0;
    var dayOpen = 0;
    var prevClose = 0;
    var dayHigh = 0;
    var dayLow = 0;
    try {
      var qq = lookupQuote(symbol);
      if (qq && Number(qq.ltp) > 0) hint = Number(qq.ltp);
      if (qq && Number(qq.open) > 0) dayOpen = Number(qq.open);
      if (qq && Number(qq.prev_close) > 0) prevClose = Number(qq.prev_close);
      if (qq && Number(qq.high) > 0) dayHigh = Number(qq.high);
      if (qq && Number(qq.low) > 0) dayLow = Number(qq.low);
    } catch (_) {}
    list = sanitizeTipBars(list, hint, symbol, dayOpen, prevClose, dayHigh, dayLow);
    // Drop Sat/Sun stamps (TV session :23456) — weekend tip seed → "No data here".
    if (!isMcxSymbol(symbol)) {
      list = list.filter(function (b) {
        var ms = Number(b && b.time) || 0;
        if (!(ms > 0)) return false;
        if (ms < 1e12) ms *= 1000;
        var ist = new Date(ms + 5.5 * 3600000);
        var dow = ist.getUTCDay(); // 0 Sun .. 6 Sat
        return dow !== 0 && dow !== 6;
      });
    }
    // Ensure Volume study has non-zero series (indices often tip volume=0).
    for (var i = 0; i < list.length; i++) {
      var b = list[i];
      if (!b) continue;
      var v = Number(b.volume) || 0;
      if (v > 0) continue;
      var o = Number(b.open) || 0;
      var h = Number(b.high) || 0;
      var l = Number(b.low) || 0;
      var c = Number(b.close) || 0;
      var range = Math.max(h - l, Math.abs(c - o), 0);
      b.volume = range > 0 ? Math.max(1, Math.round(range * 100)) : 1;
    }
    list = dedupeBarsByTime(list);
    // A one-price bar at the right edge is healed from tip. Writing flat pads into
    // the cache used to hide that hole until a full refresh.
    try {
      requestEdgeHeal(symbol, resolution, "edge.flat_pad");
    } catch (_) {}
    return appendFormingLiveBar(list, symbol, resolution);
  }

  function flushPendingLiveSeeds(cacheKey) {
    if (barReplay.active) {
      delete pendingLiveSeeds[cacheKey];
      return;
    }
    var list = pendingLiveSeeds[cacheKey];
    delete pendingLiveSeeds[cacheKey];
    if (!list || !list.length) return;
    for (var i = 0; i < list.length; i++) {
      var p = list[i];
      if (barReplayBlocksLive(p.ticker, p.resolution)) continue;
      var last = liveBar[cacheKey] || lastHistoricalBar[cacheKey] || p.bar;
      if (!last || !(Number(last.close) > 0)) continue;
      if (!barMatchesLtp(last, p.ticker)) continue;
      seedBuilder(p.ticker, p.resolution, last);
      try {
        p.callback(last);
      } catch (_) {}
      lb("tv.ltp", p.ticker, {
        src: "seed.deferred",
        o: last.open,
        h: last.high,
        l: last.low,
        c: last.close,
        age_ms: 0,
      });
    }
  }

  /** Mark first paint done; release deferred live seeds; allow reconcile. */
  function markPaintCommitted(cacheKey, symbol, resolution) {
    if (!cacheKey) return;
    paintCommittedAt[cacheKey] = Date.now();
    flushPendingLiveSeeds(cacheKey);
    lb("paint.committed", symbol || "", {
      resolution: String(resolution || ""),
      key: cacheKey,
      last_t: lastHistoricalBar[cacheKey] && lastHistoricalBar[cacheKey].time,
    });
    if (symbol && resolution && inSessionNow(symbol) && resMinutes(resolution) < 1440) {
      var pool = histCache[cacheKey] || [];
      var holes = findIntradayHoles(pool, resolution, symbol).length;
      var thin = !poolHasEnoughToday(pool, symbol, resolution);
      var memLast = memClosedLastMs(symbol, resolution);
      var openB = floorBarTimeMs(Date.now(), resolution, symbol);
      var stepMs = resMinutes(resolution) * 60000 || 5 * 60000;
      var edgeLag = memLast > 0 ? openB - memLast : 0;
      // Quiet tip heal only for real holes / thin today / lag ≥2 steps — never pad+reload.
      if (holes > 0 || thin || edgeLag > stepMs * 2) {
        setTimeout(function () {
          requestEdgeHeal(symbol, resolution, "paint.committed");
        }, 120);
      }
    }
  }

  /**
   * Base tip TF for universal edge: 1m stays native; everything else heals via 5m
   * then derives so 15/30/60 match the same timeline.
   */
  function universalBaseRes(resolution) {
    var m = resMinutes(resolution);
    if (m >= 1440) return "1D";
    if (m <= 1) return "1";
    return "5";
  }

  function getOrCreateBuilder(symbol, resolution) {
    var key = subKey(symbol, resolution);
    var b = candleBuilders[key];
    if (!b) {
      b = new CandleBuilder({
        symbol: symbol,
        resolution: String(resolution),
        volumeMode: "day-delta",
        lateTickPolicy: "foldIntoBar",
        // A new bucket opens at its first trade, like broker candles.
        openFromPrevClose: false,
        maxJumpBuckets: 1.5,
      });
      candleBuilders[key] = b;
    }
    return b;
  }

  function seedBuilder(symbol, resolution, bar, cumVol) {
    var b = getOrCreateBuilder(symbol, resolution);
    b.seed(bar, cumVol);
    return b;
  }

  /**
   * After tip/5m heal: push last closed (or ≤ open-bucket) bar into lastHistoricalBar
   * for every TF and re-seed CandleBuilders so live continues the same edge.
   */
  function syncLastBarsAllTfs(symbol, cumVol) {
    var tfs = UNIVERSAL_INTRADAY_TFS.slice();
    tfs.push("1D");
    var synced = [];
    for (var i = 0; i < tfs.length; i++) {
      var tf = tfs[i];
      var key = subKey(symbol, tf);
      var pool = barsFromFivePool(symbol, tf) || histCache[key] || [];
      if (!pool.length) continue;
      var openB = floorBarTimeMs(Date.now(), tf, symbol);
      var last = null;
      for (var j = pool.length - 1; j >= 0; j--) {
        if (Number(pool[j].time) <= openB && Number(pool[j].close) > 0) {
          last = pool[j];
          break;
        }
      }
      if (!last) continue;
      lastHistoricalBar[key] = last;
      lastDeliveredTime[key] = last.time;
      seedBuilder(symbol, tf, last, cumVol);
      synced.push(tf);
    }
    return synced;
  }

  /** Fire TradingView onResetCacheNeededCallback for matching symbol (all TFs or one). */
  function fireResetCacheNeeded(symbol, resolution) {
    var wantKey = resolution != null ? subKey(symbol, resolution) : null;
    for (var guid in resetCacheNeededCbs) {
      var cb = resetCacheNeededCbs[guid];
      var lis = TickHub.listeners[guid] || realtimeSubscribers[guid];
      if (!cb || !lis) continue;
      var sym = lis.symbol || (lis.symbolInfo && (lis.symbolInfo.ticker || lis.symbolInfo.name));
      var res = lis.resolution;
      if (String(sym).toUpperCase() !== String(symbol).toUpperCase()) continue;
      if (wantKey && subKey(sym, res) !== wantKey) continue;
      try {
        cb();
      } catch (_) {}
    }
  }

  /**
   * Commit tip bars for base TF, derive all higher TFs from the same 5m window,
   * sync lastBars + builders, optionally force TV reload when edge grew.
   */
  function applyUniversalTipEdge(symbol, baseRes, tvBars, opts) {
    opts = opts || {};
    if (!tvBars || !tvBars.length) return { synced: [], grew: false };
    var fetchRes = String(baseRes || universalBaseRes(opts.wantRes || "5"));
    if (tvBars[0].time < 1e12) tvBars = toTvBars(tvBars);
    tvBars = stripOpenBucket(tvBars, fetchRes, symbol);
    if (!tvBars.length) return { synced: [], grew: false };

    if (fetchRes === "5") {
      commitFreshFive(symbol, tvBars);
    } else if (fetchRes === "1") {
      commitFreshNative(symbol, "1", tvBars);
      // Also tip-cold 5m is preferred for derive — if we only have 1m, aggregate up.
      var as5 = aggregateTvBars(tvBars, 5, symbol);
      if (as5.length) commitFreshFive(symbol, as5);
    } else {
      commitFreshNative(symbol, fetchRes, tvBars);
      if (fetchRes !== "1D") {
        // Prefer storing exact TF then still try 5m pool if present.
        persistTipBars(subKey(symbol, fetchRes), tvBars);
      }
    }

    var beforeLast = opts.beforeLast || 0;
    var stepMs = resMinutes(opts.wantRes || fetchRes) * 60 * 1000;
    var synced = syncLastBarsAllTfs(symbol, opts.cumVol);
    var wantKey = subKey(symbol, opts.wantRes || fetchRes);
    var last = lastHistoricalBar[wantKey];
    // ≥2 closed buckets ahead (or +3 today bars) before resetData — 1-bucket edge tickle was flicker.
    var grew =
      !!(last && beforeLast && Number(last.time) > beforeLast + stepMs * 1.5) ||
      !!(opts.beforeToday != null &&
        todayBarCount(histCache[wantKey] || [], symbol) > opts.beforeToday + 3);

    if (synced.length) {
      bridgeReady[wantKey] = true;
      // Caller-owned heal (gap.resync) merges quietly — no hard reload from here.
      if (opts.skipHardReload) {
        return { synced: synced, grew: !!grew, last: last };
      }
      // During first-paint grace: never resetData / onResetCacheNeeded — that is the
      // half-second "wrong tall candle → snap" flicker. Quietly sync lastBars only.
      var grace = inPaintGrace(wantKey) || inPaintGrace(subKey(symbol, fetchRes));
      if ((grew || opts.forceReset) && !grace) {
        // Reset-cache makes TV drop the realtime subscription; only resetData brings it back,
        // so the two must always go together (forceHistoryReload does both).
        for (var guid in TickHub.listeners) {
          var L = TickHub.listeners[guid];
          if (L && String(L.symbol).toUpperCase() === String(symbol).toUpperCase()) {
            bridgeReady[subKey(L.symbol, L.resolution)] = true;
          }
        }
        forceHistoryReload(symbol, opts.wantRes || fetchRes, opts.reason || "universal.edge");
      } else if (!grace) {
        resetChartDataSoft(wantKey);
      } else if (grew || opts.forceReset) {
        lb("paint.grace.skip_reset", symbol, {
          resolution: String(opts.wantRes || fetchRes),
          reason: opts.reason || "universal.edge",
        });
        queueEdgeHealAfterGrace(
          symbol,
          opts.wantRes || fetchRes,
          opts.reason || "universal.edge"
        );
      }
    }
    return { synced: synced, grew: !!grew, last: last };
  }

  /**
   * Bars handed to TradingView on first paint.
   * Keep ~15 trading sessions in the series so left-pan is smooth before HIST arrives.
   * Full tip still lives in histCache for deeper getBars.
   */
  function firstPaintSlice(bars) {
    if (!bars || !bars.length) return bars;
    var days = tradingDayLabels(bars);
    var keepDays = 15;
    if (days.length > keepDays) {
      var fromDay = days[days.length - keepDays];
      var sliced = [];
      for (var i = 0; i < bars.length; i++) {
        var t = Number(bars[i].time);
        var ms = t < 1e12 ? t * 1000 : t;
        var d = new Date(ms + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
        if (d >= fromDay) sliced.push(bars[i]);
      }
      if (sliced.length >= 40) return sliced;
    }
    if (bars.length <= FIRST_PAINT_BARS) return bars;
    return bars.slice(-FIRST_PAINT_BARS);
  }

  /**
   * First-paint bars for THIS request. The 15-session slice is right for a
   * fresh chart, but when TV asks for an older window (visible-range jump,
   * hole-heal reload with a persisted old viewport), answering with only the
   * recent slice makes TV treat the whole [from,to] as served: it then pages
   * left from its earliest *merged* bar and never re-asks for the interior —
   * the Aug→Sep higher-TF hole. When the pool is continuous and reaches the
   * requested `from`, hand TV the full window slice in the first response.
   */
  function paintForRequest(bars, periodParams) {
    var sliced = firstPaintSlice(bars);
    if (!bars || !bars.length || !periodParams) return sliced;
    var fromMs = (periodParams.from || 0) * 1000;
    var toMs = (periodParams.to || 0) * 1000;
    if (!(fromMs > 0) || !sliced || !sliced.length) return sliced;
    var sliceStart = Number(sliced[0].time);
    if (sliceStart < 1e12) sliceStart *= 1000;
    if (fromMs >= sliceStart - 86400000) return sliced;
    var poolStart = Number(bars[0].time);
    if (poolStart < 1e12) poolStart *= 1000;
    if (poolStart > fromMs + 2 * 86400000) return sliced;
    var win = slicePool(bars, fromMs, toMs > 0 ? toMs : Date.now());
    if (!win.length || findBarHoles(win, 4 * 86400000).length) return sliced;
    if (win.length > 5000) win = win.slice(-5000);
    return win;
  }

  function barEdge(bars) {
    if (!bars || !bars.length) return { first_t: null, last_t: null };
    return { first_t: bars[0].time, last_t: bars[bars.length - 1].time };
  }

  /** Logbot: time holes, trailing vol0 invents, price cliffs, LTP vs tip close. */
  function emitCandleGapAudit(symbol, resolution, bars, cache) {
    var list = bars || [];
    var stepMs = resMinutes(resolution) * 60 * 1000;
    var holes = findIntradayHoles(list, resolution, symbol);
    var trailingVol0 = 0;
    for (var i = list.length - 1; i >= 0; i--) {
      if (Number(list[i].volume) > 0) break;
      trailingVol0++;
    }
    var lastReal = null;
    for (var j = list.length - 1; j >= 0; j--) {
      if (Number(list[j].volume) > 0) {
        lastReal = list[j];
        break;
      }
    }
    var cliff = null;
    for (var k = Math.max(1, list.length - 40); k < list.length; k++) {
      var prevC = Number(list[k - 1].close);
      var curO = Number(list[k].open);
      if (prevC > 0 && curO > 0 && Math.abs(curO - prevC) / prevC >= 0.015) {
        cliff = {
          from_t: list[k - 1].time,
          to_t: list[k].time,
          prev_c: prevC,
          open: curO,
          close: Number(list[k].close),
          low: Number(list[k].low),
          high: Number(list[k].high),
          vol: Number(list[k].volume) || 0,
        };
      }
    }
    var ltp = 0;
    try {
      var q = lookupQuote(symbol);
      if (q && Number(q.ltp) > 0) ltp = Number(q.ltp);
    } catch (_) {}
    var tipClose = lastReal ? Number(lastReal.close) : list.length ? Number(list[list.length - 1].close) : 0;
    var ltpDiv =
      ltp > 0 && tipClose > 0 ? Math.round(((ltp - tipClose) / tipClose) * 10000) / 100 : null;
    var ageSec =
      lastReal && lastReal.time ? Math.max(0, Math.round((Date.now() - Number(lastReal.time)) / 1000)) : null;
    var resMin = resMinutes(resolution);
    var sessionOpen = typeof inSessionNow === "function" ? inSessionNow(symbol) : true;
    // Tip-forming bars often carry vol=0 until the bucket closes (esp. indices).
    // Never hard-fail hist on trailing vol0 / session cliffs — warn only.
    // Daily tip close vs live LTP diverges all session — only hard-fail stale tip + big move.
    // After close / seed.deferred, tip age is always large vs frozen LTP — soft only.
    var ltpDivHard =
      sessionOpen &&
      ltpDiv != null &&
      Math.abs(ltpDiv) >= (resMin >= 1440 ? 2.5 : 1.2) &&
      ageSec != null &&
      ageSec > (resMin >= 1440 ? 900 : 180);
    var ok = holes.length === 0 && !ltpDivHard;
    var warnParts = [
      trailingVol0 >= 2 ? "trailing_vol0_soft=" + trailingVol0 : "",
      cliff ? "price_cliff_soft" : "",
      ltpDiv != null && Math.abs(ltpDiv) >= 0.6 && !ltpDivHard ? "ltp_div_soft=" + ltpDiv + "%" : "",
    ].filter(Boolean);
    lb("candle.gap", symbol, {
      ok: ok,
      soft: warnParts.length > 0,
      resolution: String(resolution),
      cache: cache || "",
      n: list.length,
      holes: holes.length,
      hole_sample: holes.slice(0, 4).map(function (h) {
        return { from: h.from, to: h.to, min: Math.round((h.to - h.from) / 60000) };
      }),
      trailing_vol0: trailingVol0,
      cliff: cliff,
      tip_close: tipClose,
      ltp: ltp || null,
      ltp_div_pct: ltpDiv,
      last_real_age_s: ageSec,
      step_ms: stepMs,
      why: !ok
        ? [
            holes.length ? "time_holes=" + holes.length : "",
            ltpDivHard ? "ltp_div=" + ltpDiv + "%" : "",
          ]
            .filter(Boolean)
            .join(",")
        : warnParts.length
          ? "dense_soft:" + warnParts.join(",")
          : "dense",
    });
    return ok;
  }

  function emitPrefetch(symbol, bars, cache, ms) {
    if (!bars || !bars.length) return;
    var e = barEdge(bars);
    lb("tv.deep.prefetch", symbol, {
      count: bars.length,
      bars: bars.length,
      first_t: e.first_t,
      last_t: e.last_t,
      cache: cache || "FILE",
      fetch_ms: ms != null ? ms : 1,
    });
    lb("tv.scroll.warm.ready", symbol, {
      count: bars.length,
      first_t: e.first_t,
      last_t: e.last_t,
      cache: cache || "FILE",
      fetch_ms: ms != null ? ms : 1,
    });
  }

  function emitScrollPage(symbol, bars, cache) {
    var e = barEdge(bars);
    var dates = tradingDayLabels(bars);
    var payload = {
      mode: "scroll",
      kind: "scroll",
      first: false,
      count: bars.length,
      n: bars.length,
      first_t: e.first_t,
      last_t: e.last_t,
      first_date: dates.length ? dates[0] : null,
      last_date: dates.length ? dates[dates.length - 1] : null,
      days: dates,
      ms: 1,
      client_ms: 1,
      cache: cache || "MEM",
      verdict: "fast",
    };
    lb("tv.getBars.ok", symbol, payload);
    lb("tv.speed", symbol, payload);
    lb("hist.scroll", symbol, {
      n: bars.length,
      cache: cache || "MEM",
      first_date: payload.first_date,
      last_date: payload.last_date,
      days: dates.slice(0, 10),
    });
  }

  function tradingDayLabels(bars) {
    var out = [];
    var seen = {};
    var off = typeof IST_OFFSET_MS === "number" ? IST_OFFSET_MS : 5.5 * 60 * 60 * 1000;
    for (var i = 0; i < (bars || []).length; i++) {
      var t = Number(bars[i].time);
      if (!(t > 0)) continue;
      if (t < 1e12) t *= 1000;
      var shifted = new Date(t + off);
      var d =
        shifted.getUTCFullYear() +
        "-" +
        String(shifted.getUTCMonth() + 1).padStart(2, "0") +
        "-" +
        String(shifted.getUTCDate()).padStart(2, "0");
      if (!seen[d]) {
        seen[d] = true;
        out.push(d);
      }
    }
    return out;
  }

  function tradingDayBarCounts(bars, resolution, symbol) {
    var m = resMinutes(resolution);
    var openMin = sessionOpenMin(symbol);
    var closeMin = sessionCloseMin(symbol);
    var sessionMins = Math.max(1, closeMin - openMin);
    // Tip AGG from 5m cannot invent denser bars — expect ~5m count for 3m.
    var srcStride = resMinutes(nativeSourceRes(resolution));
    var effective = m < srcStride ? srcStride : m;
    var expected = effective >= 1440 ? 1 : Math.max(1, Math.floor(sessionMins / effective));
    var byDay = {};
    var off = typeof IST_OFFSET_MS === "number" ? IST_OFFSET_MS : 5.5 * 60 * 60 * 1000;
    for (var i = 0; i < (bars || []).length; i++) {
      var t = Number(bars[i].time);
      if (!(t > 0)) continue;
      if (t < 1e12) t *= 1000;
      var shifted = new Date(t + off);
      var d =
        shifted.getUTCFullYear() +
        "-" +
        String(shifted.getUTCMonth() + 1).padStart(2, "0") +
        "-" +
        String(shifted.getUTCDate()).padStart(2, "0");
      byDay[d] = (byDay[d] || 0) + 1;
    }
    var days = Object.keys(byDay).sort();
    var todayShifted = new Date(Date.now() + off);
    var today =
      todayShifted.getUTCFullYear() +
      "-" +
      String(todayShifted.getUTCMonth() + 1).padStart(2, "0") +
      "-" +
      String(todayShifted.getUTCDate()).padStart(2, "0");
    var rows = days.map(function (d, idx) {
      var n = byDay[d];
      // Today (or last day in pack) may be a partial session — allow short.
      var partial = d === today || idx === days.length - 1;
      var full = effective >= 1440 ? n >= 1 : partial ? n >= 1 : n >= Math.max(1, Math.floor(expected * 0.7));
      return { date: d, n: n, expected: expected, ok: full, partial: partial };
    });
    return {
      expected_per_day: expected,
      days: rows,
      tip_days: days.slice(-3),
      tip_depth: days.length,
      tip_30d_ok: days.length >= 15,
    };
  }


  function replaceTipBars(cacheKey, bars) {
    if (!bars || !bars.length) return;
    histCache[cacheKey] = bars.slice();
    try {
      sessionStorage.setItem("bc_tip_" + cacheKey, JSON.stringify(bars.slice(-TIP_SESSION_BARS)));
    } catch (_) {}
    try {
      if (window.BcIdb && typeof window.BcIdb.put === "function") {
        window.BcIdb.put(cacheKey, bars.slice(-280));
      }
    } catch (_) {}
  }


  /**
   * Tip/hist fetch resolution. Broker-native 1/15/60 match Dhan candle OHLC;
   * other TFs still use the hot 5m pack + local AGG (framework unchanged).
   */
  function nativeSourceRes(resolution) {
    var m = resMinutes(resolution);
    if (m >= 1440) return "5";
    // Dhan native intervals — PAYTM 15m 09:30 was red via 5m AGG, green on Dhan native.
    if (m === 1 || m === 15 || m === 60) return String(m);
    return "5";
  }


  /** History pages fill missing times only; tip (commitFreshFive) owns the right edge. */
  function persistFiveAndDerive(symbol, bars5) {
    if (!bars5 || !bars5.length) return;
    // Replay owns this symbol — merging full 5m then deriving 15m was putting post-cut bars back.
    if (barReplay.active && sameChartSymbol(symbol, barReplay.symbol)) return;
    var k5 = subKey(symbol, "5");
    histCache[k5] = mergeBars(bars5, histCache[k5] || []);
    storeCachedBars(k5);
    var five = histCache[k5];
    ["10", "15", "30", "45", "60", "240"].forEach(function (tf) {
      var agg = aggregateTvBars(five, resMinutes(tf), symbol);
      if (!agg.length) return;
      var key = subKey(symbol, tf);
      var existing = histCache[key] || [];
      histCache[key] = mergeBars(existing, agg);
      try {
        sessionStorage.setItem("bc_tip_" + key, JSON.stringify(histCache[key].slice(-TIP_SESSION_BARS)));
      } catch (_) {}
    });
  }

  function barsFromFivePool(symbol, resolution) {
    var want = String(resolution);
    var m = resMinutes(want);
    // During replay never rebuild/overwrite this symbol's TF from full 5m (that restored right-side candles).
    if (barReplay.active && sameChartSymbol(symbol, barReplay.symbol)) {
      if (String(want) === String(barReplay.resolution)) {
        barReplayApplyTruncateToCache();
        return (histCache[subKey(symbol, want)] || []).slice();
      }
      // Other TFs of the same symbol: read-only, no merge write.
      return (histCache[subKey(symbol, want)] || []).slice();
    }
    // Prefer exact TF when warm (esp. broker-native 15/60 — never clobber closed OHLC).
    // Exception: TF switch mid-session — stale exact tip leaves a hole until refresh; extend
    // from the hotter 5m pool when exact lags / has thin today.
    var exact = histCache[subKey(symbol, want)] || [];
    var five = histCache[subKey(symbol, "5")] || [];
    function exactNeedsFiveHeal(ex) {
      if (!ex || !ex.length || !(five && five.length >= 8)) return false;
      if (!inSessionNow(symbol) || m >= 1440) return false;
      if (!poolHasEnoughToday(ex, symbol, want)) return true;
      if (!memEdgeFreshForPaint(ex, symbol, want)) return true;
      var aggTmp = aggregateTvBars(five, m, symbol);
      if (!aggTmp.length) return false;
      var step = m * 60000;
      return (
        barLastTimeMs(aggTmp) > barLastTimeMs(ex) + step * 0.5 ||
        todayBarCount(aggTmp, symbol) > todayBarCount(ex, symbol) + 1
      );
    }
    // A holey exact-TF cache (e.g. persisted from a session where the middle
    // page never landed) must not shadow the continuous 5m pool.
    var exactHoley =
      exact.length > 1 && findBarHoles(exact, 4 * 86400000).length > 0 && five.length >= 8;
    if (exact.length > 40 && !exactHoley && !exactNeedsFiveHeal(exact)) return exact;
    if ((m === 15 || m === 60) && exact.length >= 8 && !exactHoley && !exactNeedsFiveHeal(exact)) return exact;
    if (m === 5) return five.length ? five : exact;
    if (five.length >= 8) {
      var agg = aggregateTvBars(five, m, symbol);
      if (agg.length) {
        if (m === 15 || m === 60) {
          // Fill holes / extend edge from 5m AGG; keep broker OHLC on shared timestamps.
          var merged15 = tightEdge(exact, agg);
          if (merged15.length) {
            histCache[subKey(symbol, want)] = merged15;
            return merged15;
          }
          return exact;
        }
        histCache[subKey(symbol, want)] = mergeBars(histCache[subKey(symbol, want)] || [], agg);
        return histCache[subKey(symbol, want)];
      }
    }
    return exact;
  }


  var histPrefetching = {};
  var bridgeReady = {};
  var bridgeResetDone = {};
  var sparseFirstPaint = {};
  /** Continuous MEM/AGG first paint — never resetData (avoids TF flicker). */
  var solidFirstPaint = {};
  /** cacheKey → today's bar count already handed to TradingView. */
  var tvPaintToday = {};
  var symbolSwitchGen = 0;
  var barsRequestGen = 0;
  /** cacheKey → gen: MEM may paint only if written by the current switch tip/hist. */
  var netPaintReady = {};
  /** After first default zoom for a switch gen, never force viewport again (free scroll). */
  var viewportSettledGen = 0;
  var _optFitToken = 0;
  var _optFitBarCount = 0;
  var viewportUserFree = false;

  function persistTipBars(cacheKey, bars) {
    if (!bars || !bars.length) return;
    var parts = String(cacheKey || "").split("|");
    var sym = parts[0] || "";
    var res = parts.slice(1).join("|") || "";
    // During replay never let tip/IDB writes extend past the cut for this symbol (any TF).
    if (barReplay.active && sameChartSymbol(sym, barReplay.symbol)) {
      if (String(res) === String(barReplay.resolution)) {
        barReplayApplyTruncateToCache();
      }
      return;
    }
    var clamped = barReplayClampBars(bars, sym, res);
    var merged = mergeBars(histCache[cacheKey] || [], clamped);
    histCache[cacheKey] = merged;
    storeCachedBars(cacheKey);
  }

  function storeCachedBars(cacheKey) {
    var bars = histCache[cacheKey] || [];
    if (!bars.length) return;
    try {
      sessionStorage.setItem("bc_tip_" + cacheKey, JSON.stringify(bars.slice(-TIP_SESSION_BARS)));
    } catch (_) {}
    try {
      if (window.BcIdb && typeof window.BcIdb.put === "function") {
        window.BcIdb.put(cacheKey, bars.slice(-280));
      }
    } catch (_) {}
  }

  /**
   * Fresh tip is authoritative from its first bar to the right edge: stale MEM
   * bars inside that window (partial/flat candles) are dropped, not merged.
   * An older pack (e.g. FILE-STALE after a newer DHAN fetch) never shortens MEM.
   */
  function tightEdge(existing, fresh) {
    existing = existing || [];
    if (!fresh || !fresh.length) return existing;
    if (!existing.length) return fresh.slice();
    var freshLast = Number(fresh[fresh.length - 1].time);
    var memLast = Number(existing[existing.length - 1].time);
    var freshCut = Number(fresh[0].time);
    if (freshCut > 0 && freshCut < 1e12) freshCut *= 1000;
    if (freshLast < memLast) {
      var kept = [];
      for (var fi = 0; fi < existing.length; fi++) {
        var eb = existing[fi];
        var et = Number(eb.time) || 0;
        if (et > 0 && et < 1e12) et *= 1000;
        if (et >= freshCut && barIsFlatPlaceholder(eb)) continue;
        kept.push(eb);
      }
      return mergeBars(fresh, kept);
    }
    var cut = Number(fresh[0].time);
    var out = [];
    for (var i = 0; i < existing.length; i++) {
      if (Number(existing[i].time) < cut) out.push(existing[i]);
    }
    // Mid-week hole (e.g. MEM ends 22nd, live tip starts 25th) — drop stale left
    // instead of painting 22→25 with 23/24 missing.
    if (out.length) {
      var seam = cut - Number(out[out.length - 1].time);
      if (seam > 3 * 86400000) {
        return fresh.slice();
      }
    }
    return out.concat(fresh);
  }

  /** Replace native 5m right edge, then rebuild every derived TF from the same window. */
  function commitFreshFive(symbol, fresh5) {
    if (!fresh5 || !fresh5.length) return;
    // Replay owns this symbol — a 5m tip rebuild was overwriting truncated 15/30/… MEM past the cut.
    if (barReplay.active && sameChartSymbol(symbol, barReplay.symbol)) return;
    var k5 = subKey(symbol, "5");
    histCache[k5] = tightEdge(histCache[k5], fresh5);
    storeCachedBars(k5);
    var dayStart = istDayStartMs(fresh5[0].time);
    var five = histCache[k5];
    var tail = [];
    for (var i = 0; i < five.length; i++) {
      if (Number(five[i].time) >= dayStart) tail.push(five[i]);
    }
    ["10", "15", "30", "45", "60", "240"].forEach(function (tf) {
      var agg = aggregateTvBars(tail, resMinutes(tf), symbol);
      if (!agg.length) return;
      var key = subKey(symbol, tf);
      // 15/60: broker-native tip owns closed OHLC — only extend the right edge from 5m AGG.
      if (tf === "15" || tf === "60") {
        var ex = histCache[key] || [];
        if (ex.length >= 8) {
          var lastEx = barLastTimeMs(ex);
          var newer = [];
          for (var j = 0; j < agg.length; j++) {
            var tj = Number(agg[j].time) || 0;
            if (tj > 0 && tj < 1e12) tj *= 1000;
            if (tj > lastEx) newer.push(agg[j]);
          }
          if (newer.length) {
            histCache[key] = mergeBars(ex, newer);
            storeCachedBars(key);
          }
          return;
        }
      }
      histCache[key] = tightEdge(histCache[key], agg);
      storeCachedBars(key);
    });
  }

  function commitFreshNative(symbol, fetchRes, bars) {
    if (!bars || !bars.length) return;
    // Replay owns this symbol's MEM — ignore tip refreshes that would paint past the cut.
    if (barReplay.active && sameChartSymbol(symbol, barReplay.symbol)) {
      return;
    }
    if (fetchRes === "5") {
      commitFreshFive(symbol, bars);
      return;
    }
    var k = subKey(symbol, fetchRes);
    histCache[k] = tightEdge(histCache[k], bars);
    storeCachedBars(k);
  }

  function barKeyOhlc(b) {
    return [
      Number(b.open).toFixed(2),
      Number(b.high).toFixed(2),
      Number(b.low).toFixed(2),
      Number(b.close).toFixed(2),
    ].join("|");
  }

  /**
   * True when the painted series disagrees with the current cache on the latest
   * session day (different OHLC, missing/extra bars, or newer closed bars).
   */
  function latestDayDiffers(painted, current) {
    if (!painted || !painted.length || !current || !current.length) return false;
    var pLast = Number(painted[painted.length - 1].time);
    var cLast = Number(current[current.length - 1].time);
    if (cLast > pLast) return true;
    var from = Math.min(istDayStartMs(pLast), istDayStartMs(cLast));
    var pm = {};
    var cm = {};
    var pn = 0;
    var cn = 0;
    var i;
    for (i = 0; i < painted.length; i++) {
      if (Number(painted[i].time) >= from) {
        pm[painted[i].time] = barKeyOhlc(painted[i]);
        pn++;
      }
    }
    for (i = 0; i < current.length; i++) {
      if (Number(current[i].time) >= from && Number(current[i].time) <= pLast) {
        cm[current[i].time] = barKeyOhlc(current[i]);
        cn++;
      }
    }
    if (pn !== cn) return true;
    for (var t in pm) {
      if (pm[t] !== cm[t]) return true;
    }
    return false;
  }

  /**
   * Today's closed bars (excluding open bucket) disagree on OHLC — quiet edge
   * patch cannot fix these; TV keeps the wrong bodies until resetData/refresh.
   */
  function todayClosedOhlcDiffers(painted, current, symbol, resolution) {
    if (!painted || !painted.length || !current || !current.length) return false;
    var openB = floorBarTimeMs(Date.now(), resolution, symbol);
    var from = istDayStartMs(Date.now());
    var pm = {};
    var i;
    for (i = 0; i < painted.length; i++) {
      var pt = Number(painted[i].time);
      if (pt >= from && pt < openB) pm[pt] = barKeyOhlc(painted[i]);
    }
    for (i = 0; i < current.length; i++) {
      var ct = Number(current[i].time);
      if (ct < from || ct >= openB) continue;
      var want = pm[ct];
      if (want == null) return true;
      if (want !== barKeyOhlc(current[i])) return true;
      delete pm[ct];
    }
    for (var left in pm) {
      if (Object.prototype.hasOwnProperty.call(pm, left)) return true;
    }
    return false;
  }

  /**
   * After close on a weekday, MEM whose latest bar is today but ends before the
   * final session bucket is a mid-session snapshot — do not paint it.
   */
  function memEdgeTrusted(bars, symbol, resolution) {
    if (!bars || !bars.length) return false;
    if (resMinutes(resolution) >= 1440) return true;
    var last = Number(bars[bars.length - 1].time);
    if (last > 0 && last < 1e12) last *= 1000;
    var now = Date.now();
    var nowShift = new Date(now + IST_OFFSET_MS);
    var dow = nowShift.getUTCDay();
    if (dow === 0 || dow === 6) return true;
    var stepMs = resMinutes(resolution) * 60000;
    if (inSessionNow(symbol)) {
      return last >= now - Math.max(45 * 60000, 3 * stepMs);
    }
    var mins = nowShift.getUTCHours() * 60 + nowShift.getUTCMinutes();
    var closeMin = sessionCloseMin(symbol);
    // After close: accept any same-day bar inside the session. Requiring "near
    // official close" blanked SBIN when warehouse tip ended a few buckets early.
    if (mins > closeMin && istDayStartMs(last) === istDayStartMs(now)) {
      var openMs = istDayStartMs(now) + sessionOpenMin(symbol) * 60000;
      var closeMs = istDayStartMs(now) + closeMin * 60000;
      return last >= openMs && last <= closeMs + stepMs;
    }
    return true;
  }

  /**
   * Instant MEM paint only when the right edge is near "now".
   * Looser memEdgeTrusted (45m) still allows mid-session holes → gap flash then heal.
   * Gate first paint at ~2.5 buckets (15m floor) so click opens continuous candles.
   */
  function memEdgeFreshForPaint(bars, symbol, resolution) {
    if (!bars || !bars.length) return false;
    if (resMinutes(resolution) >= 1440) return true;
    if (!inSessionNow(symbol)) return memEdgeTrusted(bars, symbol, resolution);
    var last = barLastTimeMs(bars);
    if (!(last > 0)) return false;
    var stepMs = resMinutes(resolution) * 60000;
    if (!(stepMs > 0)) stepMs = 5 * 60000;
    return last >= Date.now() - Math.max(stepMs * 2.5, 15 * 60000);
  }

  var edgeResetAt = {};
  /** Deferred tip-edge heals queued while first-paint grace blocks resetData. */
  var edgeHealPending = {};

  function barLastTimeMs(bars) {
    if (!bars || !bars.length) return 0;
    var t = Number(bars[bars.length - 1].time) || 0;
    return t > 0 && t < 1e12 ? t * 1000 : t;
  }

  /**
   * Tip/MEM deepened past what TV painted (mid-session hole). Reload history only —
   * does not touch live WS / onTick delivery. Queues past paint grace when needed.
   */
  function reloadIfPaintedEdgeStale(symbol, resolution, paintedBars, freshBars, reason) {
    var cacheKey = subKey(symbol, resolution);
    var paintedLast = barLastTimeMs(paintedBars);
    var freshLast = barLastTimeMs(freshBars);
    if (!(freshLast > 0)) return false;
    var stepMs = resMinutes(resolution) * 60 * 1000;
    if (!(stepMs > 0)) stepMs = 5 * 60 * 1000;
    var paintedToday = todayBarCount(paintedBars || [], symbol);
    var freshToday = todayBarCount(freshBars || [], symbol);
    var edgeLag = paintedLast > 0 ? freshLast - paintedLast : 0;
    var missingToday = freshToday > paintedToday + 1;
    var edgeAdvanced = paintedLast > 0 && edgeLag > stepMs * 1.5;
    var paintedHoles = findIntradayHoles(paintedBars || [], resolution, symbol).length;
    var freshHoles = findIntradayHoles(freshBars || [], resolution, symbol).length;
    var holesHealed = paintedHoles > 0 && freshHoles < paintedHoles;
    var quoteThin = (paintedBars || []).length < 8 && (freshBars || []).length >= 15;
    var sparseToday = paintedToday < 8 && freshToday >= Math.max(paintedToday + 3, 12);
    if (!edgeAdvanced && !missingToday && !holesHealed && !quoteThin && !sparseToday) return false;

    // Allow soft/hard reload paths that were gated on solid first paint.
    delete solidFirstPaint[cacheKey];
    sparseFirstPaint[cacheKey] = true;
    delete bridgeResetDone[cacheKey];

    var gen = symbolSwitchGen;
    var paintedSnap = paintedLast;
    var paintedTodaySnap = paintedToday;
    var paintedHolesSnap = paintedHoles;
    var run = function () {
      if (gen !== symbolSwitchGen || viewportUserFree) return;
      var mem = barsFromFivePool(symbol, resolution) || histCache[cacheKey] || [];
      var memLast = barLastTimeMs(mem);
      var stillLag = paintedSnap > 0 && memLast > paintedSnap + stepMs * 1.5;
      var stillMissing = todayBarCount(mem, symbol) > paintedTodaySnap + 1;
      var stillHoles =
        paintedHolesSnap > 0 && findIntradayHoles(mem, resolution, symbol).length < paintedHolesSnap;
      // quoteThin / sparseToday already committed better MEM — always reload once.
      if (!stillLag && !stillMissing && !stillHoles && !quoteThin && !sparseToday) return;
      lb("candle.edge.heal", symbol, {
        resolution: String(resolution),
        reason: reason || "edge.stale",
        painted_last: paintedSnap,
        mem_last: memLast,
        missing_today: stillMissing,
        holes_healed: stillHoles || holesHealed,
        quote_thin: quoteThin,
      });
      forceHistoryReload(symbol, resolution, reason || "edge.stale");
    };

    if (inPaintGrace(cacheKey)) {
      if (edgeHealPending[cacheKey]) {
        try {
          clearTimeout(edgeHealPending[cacheKey]);
        } catch (_) {}
      }
      var committed = paintCommittedAt[cacheKey] || Date.now();
      var wait = Math.max(80, PAINT_GRACE_MS - (Date.now() - committed) + 80);
      edgeHealPending[cacheKey] = setTimeout(function () {
        delete edgeHealPending[cacheKey];
        run();
      }, wait);
      lb("paint.grace.queue_edge_heal", symbol, {
        resolution: String(resolution),
        reason: reason || "edge.stale",
        wait_ms: wait,
        painted_last: paintedLast,
        fresh_last: freshLast,
      });
      return true;
    }
    run();
    return true;
  }

  /** Queue a tip-edge reload after paint grace (grace skip paths). */
  function queueEdgeHealAfterGrace(symbol, resolution, reason) {
    var cacheKey = subKey(symbol, resolution);
    var gen = symbolSwitchGen;
    if (edgeHealPending[cacheKey]) {
      try {
        clearTimeout(edgeHealPending[cacheKey]);
      } catch (_) {}
    }
    var committed = paintCommittedAt[cacheKey] || Date.now();
    var wait = inPaintGrace(cacheKey)
      ? Math.max(80, PAINT_GRACE_MS - (Date.now() - committed) + 80)
      : 80;
    edgeHealPending[cacheKey] = setTimeout(function () {
      delete edgeHealPending[cacheKey];
      if (gen !== symbolSwitchGen || viewportUserFree) return;
      delete solidFirstPaint[cacheKey];
      sparseFirstPaint[cacheKey] = true;
      delete bridgeResetDone[cacheKey];
      forceHistoryReload(symbol, resolution, reason || "grace.queue");
    }, wait);
  }

  /** Forced TradingView reload when the painted edge was stale (rate-limited per symbol/TF). */
  function forceEdgeReset(cacheKey, gen, reason) {
    if (viewportUserFree) return;
    if (gen !== symbolSwitchGen) return;
    if (inPaintGrace(cacheKey)) {
      lb("paint.grace.skip_edge_reset", "", { key: cacheKey, reason: reason || "" });
      var parts = String(cacheKey || "").split("|");
      if (parts.length >= 2) {
        queueEdgeHealAfterGrace(parts[0], parts.slice(1).join("|"), reason || "edge.reset");
      }
      return;
    }
    if (edgeResetAt[cacheKey] && Date.now() - edgeResetAt[cacheKey] < 3000) return;
    edgeResetAt[cacheKey] = Date.now();
    netPaintReady[cacheKey] = gen;
    frontTrace("cache.edge.reset", { key: cacheKey, gen: gen, reason: reason || "" });
    setTimeout(function () {
      if (gen !== symbolSwitchGen || viewportUserFree) return;
      try {
        var ch = widget && typeof widget.activeChart === "function" ? widget.activeChart() : null;
        if (ch && typeof ch.resetData === "function") ch.resetData();
      } catch (e) {
        log("edge.reset.err", e && e.message ? e.message : e);
      }
    }, 0);
  }

  function sameChartSymbol(a, b) {
    var ua = String(a || "").trim().toUpperCase();
    var ub = String(b || "").trim().toUpperCase();
    return !!ua && (ua === ub || ua.split(":").pop() === ub.split(":").pop());
  }

  var historyReloadAt = {};
  var holeHealAt = {};
  /**
   * TV docs recipe for lost data (reconnect / stuck first paint): drop TV's cached bars and
   * re-request history. Only the active chart is reset; the forming bar is resent after.
   */
  function forceHistoryReload(symbol, resolution, reason) {
    var key = subKey(symbol, resolution);
    var now = Date.now();
    if (historyReloadAt[key] && now - historyReloadAt[key] < 10000) return;
    historyReloadAt[key] = now;
    var ch = null;
    try {
      ch = widget && typeof widget.activeChart === "function" ? widget.activeChart() : null;
    } catch (_) {}
    var chSym = "";
    try {
      chSym = ch && typeof ch.symbol === "function" ? String(ch.symbol()) : "";
    } catch (_) {}
    var onChart = !!chSym && sameChartSymbol(chSym, symbol);
    var chRes = "";
    try {
      chRes = ch && typeof ch.resolution === "function" ? String(ch.resolution()) : "";
    } catch (_) {}
    lb("chart.reload", symbol, { resolution: String(resolution), chart_res: chRes, reason: reason || "", on_chart: onChart });
    if (!onChart) return;
    fireResetCacheNeeded(symbol, chRes || null);
    try {
      if (typeof ch.resetData === "function") ch.resetData();
    } catch (e) {
      log("chart.reload.err", e && e.message ? e.message : e);
    }
  }

  /**
   * Solid edge heal — single flight per symbol, quiet tip→MEM merge, at most one
   * hard resetData when closed series actually grew. No pad→reload thrash.
   */
  var tipGapResyncAt = {};
  var tipGapResyncInFlight = {};
  var flatEdgeTries = {};
  var EDGE_HEAL_HARD_COOLDOWN_MS = 20000;

  /**
   * Rightmost closed-bar edge known to MEM and/or TV (not the forming open bucket).
   * Used to detect "history ended, live jumped to now" holes.
   */
  function seriesClosedEdgeMs(symbol, resolution) {
    var key = subKey(symbol, resolution);
    var openB = floorBarTimeMs(Date.now(), resolution, symbol);
    var mem = memClosedLastMs(symbol, resolution) || 0;
    var edge = tvEdge[key];
    var edgeT = edge ? Number(edge.time) : 0;
    if (edgeT > 0 && edgeT < 1e12) edgeT *= 1000;
    // A flat seed at the right edge is not a closed candle. Counting it hid the hole.
    if (edgeT >= openB || (edge && barIsFlatPlaceholder(edge))) edgeT = 0;
    var histLast = 0;
    var painted = histCache[key] || [];
    if (painted.length) {
      var tail = painted[painted.length - 1];
      histLast = barLastTimeMs(painted);
      if (histLast >= openB || barIsFlatPlaceholder(tail)) histLast = mem || 0;
    }
    return Math.max(mem || 0, edgeT || 0, histLast || 0);
  }

  /** Single entry for mid-session tip heal (live / edge watch / paint). */
  function requestEdgeHeal(symbol, resolution, reason) {
    return resyncSameSessionTipGap(symbol, resolution, reason || "edge.heal");
  }

  /**
   * TradingView onTick cannot insert missing closed bars. Hold live across a real
   * hole (>1 step) and enqueue one quiet tip heal — never pad+resetData here.
   * Returns true when the tick must be dropped.
   */
  function holdLiveAcrossSeriesHole(symbol, resolution, bucket, reason) {
    if (!symbol || resMinutes(resolution) >= 1440) return false;
    if (!inSessionNow(symbol)) return false;
    var stepMs = resMinutes(resolution) * 60000;
    if (!(stepMs > 0)) stepMs = 5 * 60000;
    var closed = seriesClosedEdgeMs(symbol, resolution);
    // Normal stripOpenBucket state: closed edge is previous bucket — not a hole.
    if (!(closed > 0) || !(bucket > closed + stepMs * 1.01)) return false;
    var maxLag = (isMcxSymbol(symbol) ? 6 : 4) * 3600 * 1000;
    if (bucket - closed > maxLag) return false;
    lb("candle.hole.real", symbol, {
      resolution: String(resolution),
      tip_last: closed,
      open_bucket: bucket,
      lag_ms: bucket - closed,
      why: reason || "live.series_hole",
    });
    try {
      var hk = subKey(symbol, resolution);
      var lbCur = liveBar[hk];
      if (lbCur && Number(lbCur.time) > closed + stepMs * 1.01) delete liveBar[hk];
    } catch (_) {}
    requestEdgeHeal(symbol, resolution, reason || "live.series_hole");
    return true;
  }

  function resyncSameSessionTipGap(symbol, resolution, reason) {
    if (!symbol || resMinutes(resolution) >= 1440) return false;
    if (!inSessionNow(symbol)) return false;
    if (barReplay.active) return false;
    var wantRes = String(resolution);
    var key = subKey(symbol, wantRes);
    var debounceKey = String(symbol).toUpperCase() + "|gap";
    var now = Date.now();
    var liveUrgent = reason && /^live\.|^edge\./.test(String(reason));
    if (tipGapResyncInFlight[debounceKey]) return true;
    var stepMs = resMinutes(wantRes) * 60000;
    if (!(stepMs > 0)) stepMs = 5 * 60000;
    var painted = histCache[key] || [];
    var openBucket = floorBarTimeMs(now, wantRes, symbol);
    var tipLast = seriesClosedEdgeMs(symbol, wantRes) || barLastTimeMs(painted);
    var lagMs = tipLast > 0 ? openBucket - tipLast : 0;
    // Debounce: never bypass. Live urgent still waits ≥1.2s; others ≥5s.
    var debounceMs = liveUrgent ? (lagMs > stepMs * 3 ? 1200 : 2500) : 5000;
    if (tipGapResyncAt[debounceKey] && now - tipGapResyncAt[debounceKey] < debounceMs) {
      return liveUrgent;
    }

    var paintedHoles = findIntradayHoles(painted, wantRes, symbol).length;
    var thinToday = !poolHasEnoughToday(painted, symbol, wantRes);
    var maxLag = (isMcxSymbol(symbol) ? 6 : 4) * 3600 * 1000;
    var lastClosed = null;
    for (var ci = painted.length - 1; ci >= 0; ci--) {
      var ct = Number(painted[ci].time) || 0;
      if (ct > 0 && ct < 1e12) ct *= 1000;
      if (ct > 0 && ct < openBucket) {
        lastClosed = painted[ci];
        break;
      }
    }
    var lastClosedFlat = !!(lastClosed && barIsFlatPlaceholder(lastClosed));
    var flatKey = lastClosedFlat ? key + "|" + Number(lastClosed.time) : "";
    var flatTries = flatKey ? flatEdgeTries[flatKey] || 0 : 0;
    // One-price right edge: a reload used to replace it. Retry tip a few times, then stop
    // so a genuine quiet candle does not refetch for the rest of the session.
    if (lastClosedFlat && flatTries >= 4) lastClosedFlat = false;
    // Heal for a flat edge, mid holes, thin today, or lag ≥2 steps (1 step = normal open bucket).
    if (!(lastClosedFlat || lagMs > stepMs * 2.01 || paintedHoles > 0 || thinToday)) return false;
    if (lagMs > maxLag && paintedHoles === 0 && !thinToday && !lastClosedFlat) return false;

    tipGapResyncAt[debounceKey] = now;
    tipGapResyncInFlight[debounceKey] = true;
    if (flatKey) flatEdgeTries[flatKey] = flatTries + 1;
    var baseRes = universalBaseRes(wantRes);
    var beforeLast = tipLast || 0;
    var beforeToday = todayBarCount(painted, symbol);
    var beforeHoles = paintedHoles;
    var paintedSnap = painted.slice();
    var gen = symbolSwitchGen;

    lb("candle.gap.resync", symbol, {
      resolution: wantRes,
      reason: reason || "gap",
      lag_ms: lagMs,
      tip_last: tipLast || null,
      open_bucket: openBucket,
      painted_holes: paintedHoles,
      thin_today: thinToday,
      today_n: beforeToday,
    });

    tipNet(symbol, baseRes, "cold")
      .then(function (pack) {
        var raw = (pack && pack.bars) || [];
        var tvBars =
          raw.length && raw[0].time < 1e12
            ? stripOpenBucket(toTvBars(raw), baseRes, symbol)
            : stripOpenBucket(raw, baseRes, symbol);
        var tipHoles = findIntradayHoles(tvBars, baseRes, symbol).length;
        if (tvBars.length && tipHoles === 0 && todayBarCount(tvBars, symbol) >= beforeToday) {
          return { bars: tvBars, cache: (pack && pack.cache) || "TIP", via: "tip" };
        }
        return histNet(symbol, baseRes, sessionOpenMsToday(symbol), Date.now() + 60000).then(function (hp) {
          var hb = (hp && hp.bars) || [];
          if (hb.length && hb[0].time < 1e12) hb = toTvBars(hb);
          hb = stripOpenBucket(hb, baseRes, symbol);
          var merged = mergeBars(tvBars, hb);
          return {
            bars: merged.length ? merged : hb.length ? hb : tvBars,
            cache: (hp && hp.cache) || (pack && pack.cache) || "HIST",
            via: hb.length ? "tip+hist" : "tip",
          };
        });
      })
      .then(function (pack) {
        if (gen !== symbolSwitchGen) return;
        var tvBars = (pack && pack.bars) || [];
        if (!tvBars.length) return;
        // Quiet MEM merge only — applyUniversalTipEdge must not also hard-reload.
        var result = applyUniversalTipEdge(symbol, baseRes, tvBars, {
          wantRes: wantRes,
          beforeLast: beforeLast,
          beforeToday: beforeToday,
          forceReset: false,
          skipHardReload: true,
          reason: "gap.resync." + (reason || "gap"),
        });
        var mem = barsFromFivePool(symbol, wantRes) || histCache[key] || [];
        var memHoles = findIntradayHoles(mem, wantRes, symbol).length;
        var snapHoles = findIntradayHoles(paintedSnap, wantRes, symbol).length;
        var todayAfter = todayBarCount(mem, symbol);
        var afterLast = seriesClosedEdgeMs(symbol, wantRes) || barLastTimeMs(mem);
        var tipHolesAfter = findIntradayHoles(tvBars, wantRes, symbol).length;
        var edgeGrew = beforeLast > 0 && afterLast > beforeLast + stepMs * 1.01;
        var holesHealed = beforeHoles > 0 && memHoles < beforeHoles;
        var tipHolesHealed = beforeHoles > 0 && tipHolesAfter < beforeHoles;
        var tipNowDense = tipHolesAfter === 0 && poolHasEnoughToday(tvBars, symbol, wantRes);
        var todayGrew = todayAfter > beforeToday + 1;
        var flatFilled = false;
        if (lastClosedFlat && lastClosed) {
          var wantT = Number(lastClosed.time) || 0;
          if (wantT > 0 && wantT < 1e12) wantT *= 1000;
          for (var mi = mem.length - 1; mi >= 0; mi--) {
            var mt = Number(mem[mi].time) || 0;
            if (mt > 0 && mt < 1e12) mt *= 1000;
            if (mt !== wantT) continue;
            flatFilled = !barIsFlatPlaceholder(mem[mi]);
            break;
          }
        }
        // Simple: if TV painted holes and tip/MEM is denser (or tip now dense) → hard reload once.
        // A flat closed bar that gained a high/low must redraw too — onTick will not shrink or reshape it.
        var needHard =
          holesHealed ||
          tipHolesHealed ||
          flatFilled ||
          (snapHoles > 0 && (memHoles < snapHoles || tipNowDense || tipHolesAfter < snapHoles)) ||
          (thinToday && todayGrew) ||
          (edgeGrew && (lagMs > stepMs * 1.5 || beforeHoles > 0 || snapHoles > 0));
        var lastHard = historyReloadAt[key] || 0;
        // Hole fills always bypass the 20s cooldown — stuck gap is worse than one redraw.
        var coolOk =
          holesHealed || tipHolesHealed || (snapHoles > 0 && tipNowDense)
            ? true
            : !lastHard || now - lastHard >= EDGE_HEAL_HARD_COOLDOWN_MS;
        lb("candle.gap.healed", symbol, {
          resolution: wantRes,
          reason: reason || "gap",
          via: (pack && pack.via) || "",
          cache: (pack && pack.cache) || "",
          holes_before: snapHoles,
          holes_after: memHoles,
          tip_holes_after: tipHolesAfter,
          today_before: beforeToday,
          today_after: todayAfter,
          edge_grew: !!edgeGrew,
          hard: !!(needHard && coolOk),
          synced: (result && result.synced) || [],
        });
        if (!(needHard && coolOk)) return;
        // One gated hard reload — never clear historyReloadAt to bypass debounce.
        var runReload = function () {
          if (gen !== symbolSwitchGen) return;
          if (inPaintGrace(key)) {
            queueEdgeHealAfterGrace(symbol, wantRes, "gap.resync");
            return;
          }
          forceHistoryReload(symbol, wantRes, "gap.resync");
        };
        setTimeout(runReload, inPaintGrace(key) ? 200 : 80);
      })
      .catch(function () {})
      .then(function () {
        tipGapResyncInFlight[debounceKey] = false;
      });
    return true;
  }

  /**
   * Option charts: the server replaces tick-built minutes with the broker's candles (bc_opt_heal)
   * and fills minutes the feed missed. Re-read today's closed candles and repaint once when what
   * the chart shows (last history load + candles built live since) has a hole or a real
   * difference; tick-vs-broker noise under max(0.5, 1%) is left alone.
   */
  var optHealAt = {};
  function optHealCheck(symbol, resolution) {
    var key = subKey(symbol, resolution);
    if (getBarsPending[key] || !paintCommittedAt[key] || inPaintGrace(key) || barReplay.active) return;
    if (optHealAt[key] && Date.now() - optHealAt[key] < 50000) return;
    optHealAt[key] = Date.now();
    var shown = {};
    (histCache[key] || []).forEach(function (b) {
      shown[b.time] = b;
    });
    var live = optLiveBars[key] || {};
    for (var lt in live) shown[lt] = live[lt];
    tipNet(symbol, resolution, "warm")
      .then(function () {
        var fresh = barsFromFivePool(symbol, resolution) || [];
        var now = Date.now();
        var dayStart = istDayStartMs(now);
        var openBucket = floorBarTimeMs(now, resolution, symbol);
        var holes = 0;
        var diffs = 0;
        var worst = null;
        for (var i = 0; i < fresh.length; i++) {
          var b = fresh[i];
          var t = Number(b.time);
          if (t < dayStart || t >= openBucket) continue;
          var p = shown[t];
          if (!p) {
            holes++;
            if (!worst) worst = { t: t, hole: true };
            continue;
          }
          var d = Math.max(
            Math.abs(Number(p.open) - Number(b.open)),
            Math.abs(Number(p.high) - Number(b.high)),
            Math.abs(Number(p.low) - Number(b.low)),
            Math.abs(Number(p.close) - Number(b.close))
          );
          if (d > Math.max(0.5, Number(b.close) * 0.01)) {
            diffs++;
            if (!worst || worst.hole || d > worst.d) worst = { t: t, d: Math.round(d * 100) / 100 };
          }
        }
        lb("opt.heal", symbol, { resolution: String(resolution), holes: holes, diffs: diffs, worst: worst, n: fresh.length });
        if (!holes && !diffs) return;
        var ch = null;
        try {
          ch = widget && typeof widget.activeChart === "function" ? widget.activeChart() : null;
        } catch (_) {}
        if (!ch || !sameChartSymbol(String(ch.symbol()), symbol) || String(ch.resolution()) !== String(resolution)) return;
        histCache[key] = fresh;
        lastHistoricalBar[key] = fresh[fresh.length - 1];
        netPaintReady[key] = symbolSwitchGen;
        optHealReset[key] = true;
        forceHistoryReload(symbol, resolution, "opt_heal");
      })
      .catch(function () {});
  }

  function slicePool(pool, fromMs, toMs) {
    return (pool || []).filter(function (b) {
      return b.time >= fromMs && (toMs ? b.time < toMs : true);
    });
  }

  /** Gaps larger than a long weekend (~4d) mean a missing history bridge. */
  function findBarHoles(pool, minGapMs) {
    minGapMs = minGapMs || 4 * 86400000;
    var holes = [];
    if (!pool || pool.length < 2) return holes;
    for (var i = 1; i < pool.length; i++) {
      var prev = pool[i - 1].time;
      var cur = pool[i].time;
      if (cur - prev > minGapMs) {
        holes.push({ from: prev, to: cur });
      }
    }
    return holes;
  }

  /**
   * Intraday holes: missing resolution steps inside the same IST session day.
   * These are what look like "candle gaps" vs TradingView (not overnight).
   */
  function findIntradayHoles(pool, resolution, symbol) {
    var holes = [];
    if (!pool || pool.length < 2) return holes;
    var stepMs = resMinutes(resolution) * 60 * 1000;
    if (!(stepMs > 0) || stepMs >= 86400000) return holes;
    var maxGapMs = (isMcxSymbol(symbol) ? 6 : 4) * 3600 * 1000;
    var threshold = stepMs * 1.5;
    var fnoAuction = hasFnoClosingAuction(symbol);
    for (var i = 1; i < pool.length; i++) {
      var prev = pool[i - 1];
      var cur = pool[i];
      var gap = cur.time - prev.time;
      if (gap <= threshold || gap > maxGapMs) continue;
      var pShifted = new Date(prev.time + IST_OFFSET_MS);
      var nShifted = new Date(cur.time + IST_OFFSET_MS);
      var sameDay =
        pShifted.getUTCFullYear() === nShifted.getUTCFullYear() &&
        pShifted.getUTCMonth() === nShifted.getUTCMonth() &&
        pShifted.getUTCDate() === nShifted.getUTCDate();
      if (!sameDay) continue;
      // F&O closing auction: continuous books end 15:15 — 15:15→15:30 is not a missing candle.
      // Pattern alone is enough (indices that trade to 15:30 never jump 15:15→15:30).
      if (!isMcxSymbol(symbol)) {
        var pMin = pShifted.getUTCHours() * 60 + pShifted.getUTCMinutes();
        var nMin = nShifted.getUTCHours() * 60 + nShifted.getUTCMinutes();
        if (
          (fnoAuction || pMin === SESSION_FNO_CLOSE_MIN) &&
          pMin === SESSION_FNO_CLOSE_MIN &&
          nMin === 15 * 60 + 30 &&
          gap <= 20 * 60 * 1000
        ) {
          continue;
        }
      }
      if (!inSessionAt(symbol, prev.time + stepMs) && !inSessionAt(symbol, cur.time - stepMs)) {
        continue;
      }
      holes.push({ from: prev.time, to: cur.time });
    }
    return holes;
  }

  function poolIsContinuous(pool) {
    if (!pool || pool.length <= 8) return false;
    // Catch mid-week holes (22→25 missing 23/24). Weekend Fri→Mon is ~2.5d — allow 3d.
    if (findBarHoles(pool, 3 * 86400000).length > 0) return false;
    return tradingDayLabels(pool).length >= 8;
  }

  /** True when tip pool looks like a full ~30d middleware (not a 1–3 day live stub). */
  function poolHasTipDepth(pool) {
    return !!(pool && tradingDayLabels(pool).length >= 15);
  }

  /**
   * Viewport settle after first paint. firstPaintSlice already limits to ~3 sessions,
   * so do not call setVisibleRange (that was the full-candle blink on refresh).
   * Options are different: weekend "today" is empty — fit to last bar like Nifty/Crude.
   */
  function zoomToRecentSessions(bars) {
    try {
      if (barReplay.active || barReplay.selecting) return;
      if (viewportUserFree || viewportSettledGen === symbolSwitchGen) return;
      if (!bars || bars.length < 2) return;
      if (/^OPT:/i.test(String(activeSymbol || ""))) {
        // Only fit when tip-depth (≥10); skip 2-bar quote flashes.
        if (bars.length >= 10) {
          fitOptViewport(bars, { gen: symbolSwitchGen, delayMs: 60, againMs: 280 });
        }
        return;
      }
      if (bars.length < 10) return;
      viewportSettledGen = symbolSwitchGen;
      setTimeout(function () {
        viewportUserFree = true;
        try {
          var w = widget || window._bcWidget;
          var ch = w && typeof w.activeChart === "function" ? w.activeChart() : null;
          if (ch && typeof ch.onVisibleRangeChanged === "function") {
            ch.onVisibleRangeChanged().subscribe(null, function () {
              viewportUserFree = true;
            });
          }
        } catch (_) {}
      }, 400);
    } catch (_) {}
  }

  /** Fit chart window to tip bars (end at last candle) — fixes blank Saturday viewport on OPT. */
  function fitOptViewport(bars, opts) {
    opts = opts || {};
    if (!bars || bars.length < 2) return;
    // Never zoom on thin quote seeds — they fight the real tip fit (2 ↔ 228 race).
    if (bars.length < 10 && !opts.force) return;
    try {
      var last = Number(bars[bars.length - 1].time);
      var first = Number(bars[0].time);
      if (!(last > 0)) return;
      if (last < 1e12) last *= 1000;
      if (first > 0 && first < 1e12) first *= 1000;
      var spanMs = last - (first > 0 ? first : last - 3 * 86400000);
      if (spanMs > 5 * 86400000) spanMs = 5 * 86400000;
      if (spanMs < 6 * 3600000) spanMs = Math.min(Math.max(last - first, 6 * 3600000), 3 * 86400000);
      var toSec = Math.floor(last / 1000) + 180;
      var fromSec = Math.floor((last - spanMs) / 1000);
      var gen = opts.gen;
      var nBars = bars.length;
      // Monotonic: a later thinner fit must not undo a deeper tip fit.
      if (nBars < _optFitBarCount && gen === symbolSwitchGen && !opts.force) return;
      _optFitBarCount = Math.max(_optFitBarCount, nBars);
      var token = ++_optFitToken;
      var apply = function () {
        if (token !== _optFitToken) return;
        if (gen != null && gen !== symbolSwitchGen) return;
        if (nBars < _optFitBarCount && !opts.force) return;
        try {
          var w = widget || window._bcWidget;
          var ch = w && typeof w.activeChart === "function" ? w.activeChart() : null;
          if (!ch || typeof ch.setVisibleRange !== "function") return;
          ch.setVisibleRange({ from: fromSec, to: toSec });
          viewportSettledGen = symbolSwitchGen;
          ocLog("viewport.fit", {
            bars: nBars,
            from: fromSec,
            to: toSec,
            last_t: last,
          });
        } catch (_) {}
      };
      setTimeout(apply, opts.delayMs != null ? opts.delayMs : 80);
      if (opts.againMs) setTimeout(apply, opts.againMs);
      setTimeout(function () {
        if (token !== _optFitToken) return;
        if (gen != null && gen !== symbolSwitchGen) return;
        viewportUserFree = true;
      }, (opts.againMs || 280) + 200);
    } catch (_) {}
  }

  /** Session-dense: no multi-day holes AND no intraday holes (TV-like clock). */
  function poolIsSessionDense(pool, resolution, symbol) {
    if (!poolIsContinuous(pool)) return false;
    return findIntradayHoles(pool, resolution, symbol).length === 0;
  }

  function loadHistoryBridge(symbol, resolution, tipBars) {
    var cacheKey = subKey(symbol, resolution);
    var tipFirst = tipBars && tipBars.length ? tipBars[0].time : Date.now();
    // tipBars may be "last 5 of a full pool" on scroll — use pool head if older.
    var existing = histCache[cacheKey] || [];
    if (existing.length && existing[0].time < tipFirst) {
      tipFirst = existing[0].time;
    }
    // Options: never storm hist.bridge — seed/tip already painted; deepen via tipNet only.
    if (/^OPT:/i.test(String(symbol || ""))) {
      bridgeReady[cacheKey] = true;
      ocLog("bridge.skip", { symbol: symbol, bars: (tipBars || existing || []).length });
      return Promise.resolve(tipBars || existing || []);
    }
    // Tip hot shelf (~45d) is enough for first paint — skip heavy HIST unless tip is thin.
    var tipSpan = tipBars && tipBars.length > 40
      ? Number(tipBars[tipBars.length - 1].time) - Number(tipBars[0].time)
      : 0;
    if (tipSpan >= 20 * 86400000 || tradingDayLabels(tipBars || []).length >= 15) {
      bridgeReady[cacheKey] = true;
      return Promise.resolve(tipBars || existing || []);
    }
    // Thin tip: only stitch a short seam (not year) so paint stays snappy.
    var bridgeFrom = Math.min(tipFirst, Date.now()) - 14 * 86400000;
    var bridgeTo = Date.now() + 86400000;
    return histNet(symbol, resolution, bridgeFrom, bridgeTo).then(function (pack) {
      var raw = pack.bars || [];
      var tvBars =
        raw.length && raw[0].time < 1e12
          ? stripOpenBucket(toTvBars(raw), resolution, symbol)
          : stripOpenBucket(raw, resolution, symbol);
      if (tvBars.length) {
        persistTipBars(cacheKey, tvBars);
        emitPrefetch(symbol, histCache[cacheKey] || tvBars, pack.cache, pack.client_ms);
        lb("tv.backfill", symbol, {
          n: tvBars.length,
          cache: pack.cache || "",
          ms: pack.client_ms,
          mode: "bridge",
          first_t: tvBars[0].time,
          last_t: tvBars[tvBars.length - 1].time,
        });
      }
      var pool = barsFromFivePool(symbol, resolution) || histCache[cacheKey] || tipBars || [];
      var holes = findBarHoles(pool, 4 * 86400000);
      bridgeReady[cacheKey] = holes.length === 0;
      var allDays = tradingDayLabels(pool);
      // Tip-near days (last 14) — Autobot continuity must not use oldest-only slice.
      var tipNear = allDays.slice(-14);
      log("hist.bridge", symbol, "ready=" + !!bridgeReady[cacheKey], "holes=" + holes.length, pack.cache);
      lb("hist.bridge", symbol, {
        ready: !!bridgeReady[cacheKey],
        holes: holes.length,
        n: pool.length,
        cache: pack.cache || "",
        ms: pack.client_ms,
        resolution: String(resolution),
        days: tipNear,
        tip_near_days: tipNear,
        oldest_days: allDays.slice(0, 8),
        first_date: allDays[0] || null,
        last_date: allDays.length ? allDays[allDays.length - 1] : null,
      });
      return pool;
    });
  }

  /**
   * Multi-day bridges only. In-session minutes with no trades stay empty (like the broker);
   * minutes the feed lost are refetched server-side by live_gap_repair.
   */
  function backfillHoles(symbol, resolution) {
    var cacheKey = subKey(symbol, resolution);
    var pool = histCache[cacheKey] || [];
    var deepHoles = findBarHoles(pool, 4 * 86400000);
    if (!deepHoles.length) {
      bridgeReady[cacheKey] = true;
      return Promise.resolve(0);
    }
    log("hist.hole", symbol, "deep=" + deepHoles.length);
    var chain = Promise.resolve();
    deepHoles.forEach(function (h) {
      chain = chain.then(function () {
        return histNet(symbol, resolution, h.from - 86400000, h.to + 86400000).then(function (pack) {
          var tvBars = stripOpenBucket(toTvBars(pack.bars), resolution, symbol);
          if (tvBars.length) persistTipBars(cacheKey, tvBars);
        });
      });
    });
    return chain.then(function () {
      var leftDeep = findBarHoles(histCache[cacheKey] || [], 4 * 86400000).length;
      bridgeReady[cacheKey] = leftDeep === 0;
      return leftDeep;
    });
  }

  function resetChartDataSoft(cacheKey) {
    // One soft reset per symbol/TF after tip-only paint gains a continuous bridge.
    // Skip when first paint was already continuous MEM/AGG5 — resetData = double flicker.
    // Skip when user is freely panning — resetData snaps viewport and feels "forceful".
    if (viewportUserFree || viewportSettledGen === symbolSwitchGen) return;
    if (cacheKey && solidFirstPaint[cacheKey]) return;
    if (cacheKey && bridgeResetDone[cacheKey]) return;
    if (cacheKey && !sparseFirstPaint[cacheKey]) return;
    if (cacheKey && inPaintGrace(cacheKey)) return;
    try {
      if (widget && typeof widget.activeChart === "function") {
        var ch = widget.activeChart();
        if (ch && typeof ch.resetData === "function") {
          if (cacheKey) {
            bridgeResetDone[cacheKey] = true;
            sparseFirstPaint[cacheKey] = false;
          }
          ch.resetData();
          log("hist.resetData", "bridge ready", cacheKey || "");
        }
      }
    } catch (e) {
      log("hist.resetData.err", e && e.message ? e.message : e);
    }
  }

  /** Merge one histNet pack into MEM/5m pool; returns bar count. */
  function ingestHistPack(symbol, resolution, pack) {
    var cacheKey = subKey(symbol, resolution);
    var raw = (pack && pack.bars) || [];
    var tvBars =
      raw.length && raw[0].time < 1e12
        ? stripOpenBucket(toTvBars(raw), resolution, symbol)
        : stripOpenBucket(raw, resolution, symbol);
    if (tvBars.length) {
      persistTipBars(cacheKey, tvBars);
      if (nativeSourceRes(resolution) === "5" && pack.bars5) {
        persistFiveAndDerive(symbol, pack.bars5);
      }
    }
    return tvBars.length;
  }

  /** Background warm → warehouse/PG. Off during live session — warm.php was melting FPM (tip 504s). */
  var warmKickAt = {};
  var warmKickTimer = {};
  /** @param {string} symbol
   *  @param {{immediate?: boolean, force?: boolean}} [opts] */
  function kickWarehouseWarm(symbol, opts) {
    if (/^OPT:/i.test(String(symbol || ""))) return;
    var sym = String(symbol || "").trim();
    if (!sym) return;
    opts = opts || {};
    // Live session: never hit warm.php from click/prefetch (12s FPM timeouts → tip 504).
    // Off-hours / explicit force only.
    try {
      if (!opts.force && inSessionNow(sym)) return;
    } catch (_) {
      if (!opts.force) return;
    }
    var delayMs = opts.immediate ? 1500 : 4000;
    if (warmKickAt[sym] && Date.now() - warmKickAt[sym] < 120000) return;
    if (warmKickTimer[sym]) {
      try {
        clearTimeout(warmKickTimer[sym]);
      } catch (_) {}
    }
    var gen = symbolSwitchGen;
    warmKickTimer[sym] = setTimeout(function () {
      delete warmKickTimer[sym];
      if (gen !== symbolSwitchGen) return;
      if (warmKickAt[sym] && Date.now() - warmKickAt[sym] < 120000) return;
      if (Date.now() < tipBackoffUntil) return;
      warmKickAt[sym] = Date.now();
      try {
        fetch("/api/warm.php?symbol=" + encodeURIComponent(sym) + "&days=30&nobars=1", {
          credentials: "include",
          cache: "no-store",
        })
          .then(function (r) {
            return r.json();
          })
          .then(function (j) {
            lb("hist.warm", sym, {
              warm: !!(j && j.warm),
              days: j && j.days,
              n: j && j.n,
              cache: (j && j.cache) || "—",
              ms: j && j.ms,
            });
          })
          .catch(function () {});
      } catch (_) {}
    }, delayMs);
  }

  /** IST session open ms for today (NSE 09:15 / MCX 09:00). */
  function sessionOpenMsToday(symbol) {
    var day0 = istDayStartMs(Date.now());
    return day0 + sessionOpenMin(symbol) * 60000;
  }

  /** Bars whose IST calendar day is today (paint/dense window). */
  function barsTodaySession(bars, _symbol) {
    var list = bars || [];
    if (!list.length) return [];
    var day0 = istDayStartMs(Date.now());
    var day1 = day0 + 86400000;
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var t = Number(list[i].time);
      if (!(t > 0)) continue;
      if (t < 1e12) t *= 1000;
      if (t >= day0 && t < day1) out.push(list[i]);
    }
    return out;
  }

  /** Count bars whose IST calendar day is today. */
  function todayBarCount(bars, symbol) {
    return barsTodaySession(bars, symbol).length;
  }

  /**
   * Mid-session holes that matter for live first paint / dense gate.
   * Ancient tip/warehouse holes must not block solid paint when today is continuous
   * (live-audit / server tip continuity already score today only).
   */
  function findLiveSessionHoles(bars, resolution, symbol) {
    return findIntradayHoles(barsTodaySession(bars, symbol), resolution, symbol);
  }

  /**
   * During a live session, warm MEM must include enough of today's closed bars.
   * Otherwise we paint "1 live candle" and solidFirstPaint blocks today-fill redraw.
   */
  function poolHasEnoughToday(bars, symbol, resolution) {
    if (!inSessionNow(symbol)) return true;
    var stepMin = resMinutes(resolution);
    if (stepMin >= 1440) return true;
    var openMs = sessionOpenMsToday(symbol);
    var elapsedMin = Math.max(0, (Date.now() - openMs) / 60000);
    // Expect ~elapsed/step closed buckets; require ≥40% or a small floor.
    var expect = Math.floor(elapsedMin / Math.max(1, stepMin));
    var need = Math.max(stepMin <= 5 ? 8 : stepMin <= 15 ? 4 : 2, Math.floor(expect * 0.4));
    // Early session: don't block first minutes.
    if (elapsedMin < stepMin * 3) need = Math.min(need, 2);
    return todayBarCount(bars, symbol) >= need;
  }

  /**
   * Classic gap flash: deep history but almost no today while the session is underway
   * (yesterday candles + one lone "now" bar). That must wait for tip.
   */
  function isThinSessionFlash(bars, symbol, resolution) {
    if (!bars || !bars.length) return true;
    if (!inSessionNow(symbol)) return false;
    if (resMinutes(resolution) >= 1440) return false;
    var stepMin = Math.max(1, resMinutes(resolution));
    var openMs = sessionOpenMsToday(symbol);
    var elapsedMin = Math.max(0, (Date.now() - openMs) / 60000);
    var expect = Math.floor(elapsedMin / stepMin);
    var today = todayBarCount(bars, symbol);
    return today <= 1 && expect >= 6;
  }

  /**
   * Click/search first paint: last sessions must already include today's candles.
   * A 1-bar LTP seed, or a shelf that stops on the opening bar, is not ready.
   */
  function coversLiveSession(bars, symbol, resolution) {
    if (!bars || bars.length < 8) return false;
    if (!inSessionNow(symbol) || resMinutes(resolution) >= 1440) return true;
    if (isThinSessionFlash(bars, symbol, resolution)) return false;
    return poolHasEnoughToday(bars, symbol, resolution);
  }

  /**
   * Dense enough for solid/instant first paint (MEM or tip): no *today* mid-session
   * holes, enough today, closed edge within 1 step of open (stripOpenBucket = normal).
   * Historical tip holes do not block (matches live-audit today-only continuity).
   */
  function poolIsDenseForPaint(bars, symbol, resolution) {
    if (!bars || !bars.length) return false;
    if (!inSessionNow(symbol)) return bars.length >= 15;
    if (resMinutes(resolution) >= 1440) return bars.length >= 2;
    if (isThinSessionFlash(bars, symbol, resolution)) return false;
    if (!poolHasEnoughToday(bars, symbol, resolution)) return false;
    if (findLiveSessionHoles(bars, resolution, symbol).length > 0) return false;
    var last = barLastTimeMs(bars);
    if (!(last > 0)) return false;
    var openB = floorBarTimeMs(Date.now(), resolution, symbol);
    var stepMs = resMinutes(resolution) * 60000 || 300000;
    // 1 step behind open = expected after stripOpenBucket; ≥2 = lagging tip/MEM.
    if (openB - last > stepMs * 1.01) return false;
    return true;
  }

  /**
   * Good enough for solid first paint (esp. LIVE-MEM). Requires dense continuous today.
   */
  function sessionPaintReady(bars, symbol, resolution) {
    if (!bars || !bars.length) return false;
    if (!inSessionNow(symbol)) return bars.length >= 15;
    if (resMinutes(resolution) >= 1440) return bars.length >= 2;
    return poolIsDenseForPaint(bars, symbol, resolution);
  }

  /**
   * Tip/network may paint when not thin-flash. Prefer dense; soft tip (holes/lag) is
   * allowed only so tip-first can stitch today — callers should not mark solidFirstPaint.
   */
  function sessionTipPaintable(bars, symbol, resolution) {
    if (!bars || bars.length < 8) return false;
    if (!inSessionNow(symbol)) return bars.length >= 15;
    if (resMinutes(resolution) >= 1440) return true;
    if (isThinSessionFlash(bars, symbol, resolution)) return false;
    return poolHasEnoughToday(bars, symbol, resolution) || todayBarCount(bars, symbol) >= 4;
  }

  /** True when tip pack is dense enough to solid-paint (no post-paint gap heal needed). */
  function sessionTipSolid(bars, symbol, resolution) {
    return poolIsDenseForPaint(bars, symbol, resolution);
  }

  /**
   * Force TV to re-ask getBars after MEM gained today's session bars.
   * Bypasses solidFirstPaint (that flag intentionally blocked soft reset).
   */
  function redrawAfterTodayFill(cacheKey, reason) {
    if (!cacheKey) return;
    // First-paint grace: updating MEM is fine; forcing resetData causes the flicker.
    if (inPaintGrace(cacheKey)) {
      lb("paint.grace.skip_today_reset", "", { key: cacheKey, reason: reason || "" });
      var partsToday = String(cacheKey || "").split("|");
      if (partsToday.length >= 2) {
        queueEdgeHealAfterGrace(partsToday[0], partsToday.slice(1).join("|"), reason || "today.fill");
      }
      return;
    }
    // Thin today / first-candle: hard reload only if cooldown allows (no debounce bypass).
    var partsFill = String(cacheKey || "").split("|");
    var sym = partsFill[0] || activeSymbol;
    var resFill = partsFill.slice(1).join("|") || activeRes;
    bridgeReady[cacheKey] = true;
    if (inSessionNow(sym)) {
      var lastHardFill = historyReloadAt[cacheKey] || 0;
      if (lastHardFill && Date.now() - lastHardFill < EDGE_HEAL_HARD_COOLDOWN_MS) {
        lb("hist.today.skip_hard", sym, { key: cacheKey, reason: reason || "", cool: true });
        return;
      }
      delete solidFirstPaint[cacheKey];
      sparseFirstPaint[cacheKey] = true;
      delete bridgeResetDone[cacheKey];
      forceHistoryReload(sym, resFill, reason || "today.fill");
      lb("hist.today.hard", sym, { key: cacheKey, reason: reason || "" });
      return;
    }
    forceEdgeReset(cacheKey, symbolSwitchGen, reason || "today.fill");
  }

  /**
   * P0: pull today's closed bars (session open → now) into MEM so tip isn't
   * a single live candle. Same path for every symbol (CRUDE + NSE).
   */
  function fillTodaySession(symbol, resolution, opts) {
    opts = opts || {};
    var silent = !!opts.silent;
    var cacheKey = subKey(symbol, resolution);
    var fromMs = sessionOpenMsToday(symbol);
    var toMs = Date.now() + 60000;
    var beforeToday = todayBarCount(histCache[cacheKey] || [], symbol);
    return histNet(symbol, resolution, fromMs, toMs)
      .then(function (pack) {
        var n = ingestHistPack(symbol, resolution, pack);
        var afterToday = todayBarCount(histCache[cacheKey] || [], symbol);
        var thinBefore = beforeToday < 8;
        var grew = afterToday > beforeToday + 1;
        if (n > 0) {
          bridgeReady[cacheKey] = true;
          // Silent = caller paints once after merge (tip-first). Never redraw mid-flight.
          if (!silent) {
            if (thinBefore && grew) {
              redrawAfterTodayFill(cacheKey, "hist.today");
            } else {
              resetChartDataSoft(cacheKey);
            }
          }
        }
        lb("hist.today", symbol, {
          resolution: String(resolution),
          n: n,
          today_before: beforeToday,
          today_after: afterToday,
          redraw: !silent && !!(thinBefore && grew),
          silent: silent,
          cache: (pack && pack.cache) || "—",
        });
        return n;
      })
      .catch(function () {
        return 0;
      });
  }

  /**
   * After tip: short seam → ~45d tip is already hot → deeper year pages on scroll / idle.
   */
  function prefetchHistory(symbol, resolution, tipBars) {
    var cacheKey = subKey(symbol, resolution);
    // Options: tip/seed paint only — year hist paging made OC clicks feel multi-second.
    if (/^OPT:/i.test(String(symbol || ""))) {
      histPrefetching[cacheKey] = false;
      return;
    }
    if (histPrefetching[cacheKey]) return;
    histPrefetching[cacheKey] = true;
    kickWarehouseWarm(symbol);
    var tipFirst = tipBars && tipBars.length ? tipBars[0].time : Date.now();
    var t0 = performance.now();
    // P1: tip shelf overlap only (tip already ~45d from Redis).
    var p1Days = 10;
    // P2: warehouse year in ~50d Postgres pages (fast each; not one huge pull).
    var pageDays = 50;
    var yearDays = 220;

    function runDeep() {
      // Solid tip already painted — only quiet MEM deepen (no bridge / resetData).
      var tipAlreadySolid = !!solidFirstPaint[cacheKey];
      var start = tipAlreadySolid
        ? fillTodaySession(symbol, resolution)
        : fillTodaySession(symbol, resolution)
            .then(function () {
              return loadHistoryBridge(symbol, resolution, tipBars);
            })
            .then(function () {
              return backfillHoles(symbol, resolution);
            });
      return start
        .then(function () {
          var p1From = tipFirst - p1Days * 86400000;
          return histNet(symbol, resolution, p1From, Date.now() + 2 * 86400000)
            .then(function (pack) {
              var n = ingestHistPack(symbol, resolution, pack);
              var lastCache = (pack && pack.cache) || "";
              if (n > 0) {
                bridgeReady[cacheKey] = true;
              }
              var pool = histCache[cacheKey] || [];
              var spanMs = pool.length
                ? Number(pool[pool.length - 1].time) - Number(pool[0].time)
                : 0;
              var p1Ready = spanMs >= 20 * 86400000 || tradingDayLabels(pool).length >= 15;
              lb("hist.prefetch", symbol, {
                resolution: String(resolution),
                chunks: 1,
                bars: n,
                ms: Math.round(performance.now() - t0),
                cache: lastCache || "—",
                pool: pool.length,
                p1_days: p1Days,
                p1_ready: !!p1Ready,
                days: tradingDayLabels(pool).slice(0, 6).concat(tradingDayLabels(pool).slice(-6)),
              });
              if (!p1Ready) {
                return backfillHoles(symbol, resolution);
              }
              // Chain ~50d HIST pages so scroll-left past tip stays fast.
              return new Promise(function (resolve) {
                setTimeout(resolve, 80);
              }).then(function () {
                var pages = Math.ceil(yearDays / pageDays);
                var chain = Promise.resolve();
                var cursor = tipFirst;
                for (var i = 0; i < pages; i++) {
                  (function (pageTo, pageFrom, idx) {
                    chain = chain.then(function () {
                      return histNet(symbol, resolution, pageFrom, pageTo).then(function (pack2) {
                        ingestHistPack(symbol, resolution, pack2);
                        lb("hist.prefetch.p2", symbol, {
                          resolution: String(resolution),
                          page: idx + 1,
                          bars: (pack2 && pack2.n) || 0,
                          cache: (pack2 && pack2.cache) || "—",
                          pool: (histCache[cacheKey] || []).length,
                        });
                      });
                    });
                  })(cursor + 2 * 86400000, cursor - pageDays * 86400000, i);
                  cursor -= pageDays * 86400000;
                }
                return chain.catch(function () {});
              });
            })
            .catch(function () {
              return backfillHoles(symbol, resolution);
            });
        })
        .then(function () {
          histPrefetching[cacheKey] = false;
          var pool = barsFromFivePool(symbol, resolution) || histCache[cacheKey] || [];
          bridgeReady[cacheKey] = findBarHoles(pool, 4 * 86400000).length === 0;
        })
        .catch(function (err) {
          log("hist.prefetch.bridge.err", err);
          histPrefetching[cacheKey] = false;
        });
    }

    // Let tip paint commit before any HIST work.
    setTimeout(function () {
      runDeep();
    }, 350);
  }

  function ensureHistoryLeft(symbol, resolution, fromMs) {
    var cacheKey = subKey(symbol, resolution);
    var pool = barsFromFivePool(symbol, resolution) || histCache[cacheKey] || [];
    // Tip shelf in MEM — pan inside that window is instant.
    if (pool.length > 80) {
      var spanMs = Number(pool[pool.length - 1].time) - Number(pool[0].time);
      if (spanMs >= 25 * 86400000) {
        bridgeReady[cacheKey] = true;
        if (pool[0].time <= fromMs - 86400000) return;
      }
    }
    if (!pool.length || !bridgeReady[cacheKey]) {
      prefetchHistory(symbol, resolution, pool.length ? pool.slice(-5) : null);
      return;
    }
    if (pool[0].time <= fromMs - 2 * 86400000) return;
    if (histPrefetching[cacheKey]) return;
    histPrefetching[cacheKey] = true;
    // ~50 calendar-day Postgres pages — small = fast; chain next page ahead of pan.
    var lookback = 50 * 86400000;
    var pageTo = pool[0].time + 86400000;
    var pageFrom = pool[0].time - lookback;
    histNet(symbol, resolution, pageFrom, pageTo)
      .then(function (pack) {
        ingestHistPack(symbol, resolution, pack);
        // Prefetch one more page ahead so left-scroll stays smooth.
        var pool2 = barsFromFivePool(symbol, resolution) || histCache[cacheKey] || [];
        if (pool2.length && pool2[0].time > fromMs - 90 * 86400000) {
          return histNet(symbol, resolution, pool2[0].time - lookback, pool2[0].time + 86400000).then(
            function (pack2) {
              ingestHistPack(symbol, resolution, pack2);
            }
          );
        }
      })
      .catch(function () {})
      .then(function () {
        histPrefetching[cacheKey] = false;
      });
  }

  function unpackIdb(pack) {
    if (!pack) return null;
    if (Array.isArray(pack)) return pack;
    if (pack.bars && pack.bars.length) return pack.bars;
    return null;
  }

  function tipBarsFresh(bars) {
    if (!bars || bars.length < 8) return false;
    var t = Number(bars[bars.length - 1].time);
    if (!(t > 0)) return false;
    if (t < 1e12) t *= 1000;
    // During market hours, tip must include today's session — otherwise paint looks lagged vs Dhan.
    try {
      if (typeof inSessionNow === "function" && inSessionNow(activeSymbol || "NIFTY 50")) {
        var a = new Date(t + IST_OFFSET_MS);
        var b = new Date(Date.now() + IST_OFFSET_MS);
        var sameDay =
          a.getUTCFullYear() === b.getUTCFullYear() &&
          a.getUTCMonth() === b.getUTCMonth() &&
          a.getUTCDate() === b.getUTCDate();
        if (!sameDay) return false;
        // Last closed tip bar should be recent (≤45m) so current day is not a stale stub.
        return t >= Date.now() - 45 * 60 * 1000;
      }
    } catch (_) {}
    return t >= Date.now() - 3.5 * 86400000;
  }

  /** Same IST session day with last bar ≤4h — good enough for TF-switch AGG paint. */
  function tipBarsSameSession(bars, symbol) {
    if (!bars || bars.length < 8) return false;
    var t = Number(bars[bars.length - 1].time);
    if (!(t > 0)) return false;
    if (t < 1e12) t *= 1000;
    try {
      var a = new Date(t + IST_OFFSET_MS);
      var b = new Date(Date.now() + IST_OFFSET_MS);
      var sameDay =
        a.getUTCFullYear() === b.getUTCFullYear() &&
        a.getUTCMonth() === b.getUTCMonth() &&
        a.getUTCDate() === b.getUTCDate();
      if (!sameDay) return false;
      return t >= Date.now() - 4 * 60 * 60 * 1000;
    } catch (_) {
      return false;
    }
  }

  function invalidateSymbolTip(symbol, resolution) {
    // Soft invalidate: keep deep hist so WL/search switch paints instantly from MEM/AGG.
    // Only clear paint gates — tip merge will refresh the right edge.
    var tfs = ["1", "3", "5", "10", "15", "30", "45", "60", "240", "1D"];
    tfs.forEach(function (tf) {
      var k = subKey(symbol, tf);
      if (edgeHealPending[k]) {
        try {
          clearTimeout(edgeHealPending[k]);
        } catch (_) {}
        delete edgeHealPending[k];
      }
      delete bridgeResetDone[k];
      delete sparseFirstPaint[k];
      delete solidFirstPaint[k];
      delete histPrefetching[k];
      delete netPaintReady[k];
      delete paintCommittedAt[k];
      delete pendingLiveSeeds[k];
      delete candleBuilders[k];
      delete liveBar[k];
      delete liveCumAtOpen[k];
      delete tvEdge[k];
      // Drop any poisoned open-bucket left in MEM (mega dayOpen→LTP candle from a prior visit).
      try {
        var mem = histCache[k];
        if (mem && mem.length) {
          histCache[k] = stripOpenBucket(mem, tf, symbol);
        }
      } catch (_) {}
    });
  }

  function barMatchesLtp(bar, symbol) {
    if (!bar || !(Number(bar.close) > 0)) return false;
    try {
      var q = TickHub.lastQuotes[symbol];
      if (!q || !(Number(q.ltp) > 0)) return true; // no LTP yet — allow tip
      var ratio = Number(bar.close) / Number(q.ltp);
      // Tight: block Nifty (~23k) onto mid-cap (~7k) and similar mixes.
      return ratio >= 0.5 && ratio <= 1.8;
    } catch (_) {
      return true;
    }
  }

  /** Reject cross-symbol LTP before it poisons forming high/low. */
  function ltpMatchesTip(ltp, tipClose, symbol) {
    var px = Number(ltp);
    var tip = Number(tipClose);
    if (!(px > 0) || !(tip > 0)) return false;
    var ratio = px / tip;
    if (ratio < 0.5 || ratio > 1.8) return false;
    // Absolute swing vs tip — index print on equity fails here too.
    var maxAbs = Math.max(tip * 0.15, isMcxSymbol(symbol) ? 200 : 500);
    if (Math.abs(px - tip) > maxAbs && ratio > 1.25) return false;
    return true;
  }

  function idbGetTip(cacheKey) {
    var p = null;
    try {
      if (window.BcIdb && typeof window.BcIdb.get === "function") {
        p = window.BcIdb.get(cacheKey);
      }
    } catch (_) {}
    if (!p) return Promise.resolve(null);
    return Promise.race([
      p.then(unpackIdb).catch(function () {
        return null;
      }),
      new Promise(function (resolve) {
        setTimeout(function () {
          resolve(null);
        }, 80);
      }),
    ]);
  }

  window._bcDeliverRealtimeBar = function(symbolTicker, resolution, bar) {
    var key = subKey(symbolTicker, resolution);
    var matched = 0;
    for (var guid in realtimeSubscribers) {
      var sub = realtimeSubscribers[guid];
      if (subKey(sub.symbolInfo.ticker, sub.resolution) !== key) continue;
      matched++;

      var prev = lastDeliveredTime[key];
      if (prev !== undefined && bar.time < prev) {
        console.error('[TV REALTIME VIOLATION] time went backwards', { key: key, previous: prev, current: bar.time });
        continue;
      }
      console.log(prev === bar.time ? '[TV BAR UPDATE]' : '[TV NEW BAR]', { key: key, guid: guid, time: bar.time, timeISO: new Date(bar.time).toISOString(), close: bar.close });
      lastDeliveredTime[key] = bar.time;

      console.log('[TV CALLBACK BEFORE]', { guid: guid, key: key, bar: bar });
      try {
        sub.callback(bar);
        console.log('[TV CALLBACK AFTER]', { guid: guid, key: key, time: bar.time });
      } catch (e) {
        console.error('[TV CALLBACK ERROR]', { guid: guid, key: key, error: e });
      }
    }
    if (matched === 0) {
      console.warn('[TV NO SUBSCRIBER MATCH]', { key: key, activeKeys: Object.keys(realtimeSubscribers).map(function(g) { return subKey(realtimeSubscribers[g].symbolInfo.ticker, realtimeSubscribers[g].resolution); }) });
    }
    return matched;
  };

  window._bcHasRealtimeSubscriber = function(symbolTicker, resolution) {
    var key = subKey(symbolTicker, resolution);
    for (var guid in realtimeSubscribers) {
      var sub = realtimeSubscribers[guid];
      if (subKey(sub.symbolInfo.ticker, sub.resolution) === key) return true;
    }
    return false;
  };


  /** Front badge: only Live | Market closed. Feed/WS detail stays in backend/diag. */
  function setLiveBadge(state, text) {
    var badge = document.getElementById("cx-live-badge");
    var label = document.getElementById("cxLiveLabel");
    var ui = state === "live" ? "live" : "closed";
    var txt = ui === "live" ? "Live" : "Market closed";
    if (text === "Live" || text === "Market closed") txt = text;
    if (badge) badge.setAttribute("data-state", ui);
    if (label) label.textContent = txt;
  }

  function applyMarketUiState(market) {
    if (!market || typeof market !== "object") return;
    if (market.state === "live" || market.open === true) setLiveBadge("live", "Live");
    else setLiveBadge("closed", "Market closed");
  }

  /** Client clock fallback when watchlist poll has not answered yet. */
  function refreshMarketBadgeFromClock() {
    try {
      var now = new Date();
      var parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kolkata",
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).formatToParts(now);
      var map = {};
      parts.forEach(function (p) {
        map[p.type] = p.value;
      });
      var wd = String(map.weekday || "");
      if (wd === "Sat" || wd === "Sun") {
        setLiveBadge("closed", "Market closed");
        return;
      }
      var mins = parseInt(map.hour || "0", 10) * 60 + parseInt(map.minute || "0", 10);
      var open = mins >= 9 * 60 + 15 && mins <= 15 * 60 + 30;
      setLiveBadge(open ? "live" : "closed", open ? "Live" : "Market closed");
    } catch (_) {
      setLiveBadge("closed", "Market closed");
    }
  }

  /** Series-suffix aliases (MTARTECH-BE ↔ MTARTECH) for quote lookup / mirror. */
  function seriesSuffixAliases(symbol) {
    var u = String(symbol || "")
      .trim()
      .toUpperCase();
    if (!u) return [];
    var out = [u];
    if (/-(BE|SM|ST)$/.test(u)) {
      out.push(u.replace(/-(BE|SM|ST)$/, ""));
    } else {
      out.push(u + "-BE");
    }
    return out;
  }

  /** Register OPT:sid ↔ pretty label ("NIFTY 23250 CE 29SEP") for chart title, URL, and live ticks. */
  function registerOptLabel(wireSym, label) {
    var wire = String(wireSym || "").trim();
    var pretty = String(label || "").trim();
    if (!wire || !pretty) return pretty || wire;
    window.__BC_OPT_LABELS = window.__BC_OPT_LABELS || {};
    window.__BC_OPT_BY_LABEL = window.__BC_OPT_BY_LABEL || {};
    window.__BC_OPT_LABELS[wire] = pretty;
    window.__BC_OPT_LABELS[pretty] = pretty;
    window.__BC_OPT_BY_LABEL[pretty.toUpperCase()] = wire;
    return pretty;
  }

  function optWireSymbol(sym) {
    var s = String(sym || "").trim();
    if (!s) return s;
    if (/^OPT:/i.test(s)) return "OPT:" + s.replace(/^OPT:/i, "");
    try {
      var map = window.__BC_OPT_BY_LABEL;
      if (map && map[s.toUpperCase()]) return map[s.toUpperCase()];
    } catch (_) {}
    return s;
  }

  function optDisplayLabel(sym) {
    var s = String(sym || "").trim();
    if (!s) return s;
    try {
      var labels = window.__BC_OPT_LABELS;
      if (labels) {
        if (labels[s]) return labels[s];
        var wire = optWireSymbol(s);
        if (labels[wire]) return labels[wire];
      }
    } catch (_) {}
    return s;
  }

  function isOptSymbol(sym) {
    var s = String(sym || "").trim();
    return /^OPT:/i.test(s) || /\b(CE|PE)\b/i.test(s);
  }

  // Must match computeAliases() in services/go-relay/main.go: the relay delivers a tick
  // published as one member to clients subscribed as any other member, unchanged.
  var RELAY_ALIAS_GROUPS = [
    ["NIFTY 50", "NIFTY", "NIFTY50"],
    ["BANK NIFTY", "BANK", "BANKNIFTY", "NIFTY BANK"],
    ["FIN NIFTY", "FINANCIAL SERVICES", "FINNIFTY", "NIFTY FIN SERVICE"],
    ["AUTO", "NIFTY AUTO"],
    ["IT", "NIFTYIT", "NIFTY IT"],
    ["PHARMA", "NIFTY PHARMA"],
    ["FMCG", "NIFTY FMCG"],
    ["METAL", "NIFTY METAL"],
    ["MEDIA", "NIFTY MEDIA"],
    ["REALTY", "NIFTY REALTY"],
    ["ENERGY", "NIFTY ENERGY"],
    ["INFRA", "NIFTY INFRA"],
    ["HEALTHCARE", "NIFTY HEALTHCARE"],
    ["PRIVATE BANK", "NIFTY PVT BANK"],
    ["PSU BANK", "NIFTY PSU BANK"],
    ["OIL & GAS", "NIFTY OIL AND GAS"],
    ["CONSUMER DURABLES", "NIFTY CONSR DURBL"],
    ["CEMENT", "NIFTY CEMENT"],
    ["CHEMICALS", "NIFTY CHEMICALS"],
    ["COMMODITIES", "NIFTY COMMODITIES"],
    ["INFRASTRUCTURE", "NIFTYINFRA"],
    ["MID SELECT", "MIDCPNIFTY", "NIFTY MID SELECT"],
    ["CONSUMPTION", "NIFTY CONSUMPTION"],
    ["DEFENCE", "NIFTY IND DEFENCE"],
    ["CRUDE OIL", "CRUDEOIL", "CRUDE", "CRUDE OIL CONT"],
  ];
  var relayAliasIndex = null;
  function relayAliasKey(symbol) {
    var u = String(symbol || "").trim().toUpperCase();
    var c = u.lastIndexOf(":");
    if (c >= 0) u = u.slice(c + 1).trim();
    return u.replace(/\s+/g, "");
  }
  function sameRelayGroup(a, b) {
    if (!relayAliasIndex) {
      relayAliasIndex = {};
      RELAY_ALIAS_GROUPS.forEach(function (g, i) {
        g.forEach(function (n) {
          relayAliasIndex[relayAliasKey(n)] = i;
        });
      });
    }
    var ka = relayAliasKey(a);
    var kb = relayAliasKey(b);
    if (!ka || !kb) return false;
    if (ka === kb) return true;
    var ga = relayAliasIndex[ka];
    return ga != null && ga === relayAliasIndex[kb];
  }

  function lookupQuote(symbol) {
    var bag = (TickHub && TickHub.lastQuotes) || {};
    var keys = seriesSuffixAliases(symbol);
    keys.unshift(String(symbol || "").trim());
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      if (k && bag[k] && Number(bag[k].ltp) > 0) return bag[k];
      var up = String(k || "").toUpperCase();
      if (up && bag[up] && Number(bag[up].ltp) > 0) return bag[up];
    }
    return null;
  }

  /**
   * Header / badge LTP always from TickHub (same source as watchlist), not tip last close.
   * TV legend still tracks via forming liveBar.close seeded from the same quote.
   */
  function syncActiveLtpHeader(symbol, ltpHint) {
    var el = document.getElementById("cxActiveLtp");
    if (!el) return;
    var sym = String(symbol || "").trim();
    if (sym && activeSymbol && !sameChartSymbol(sym, activeSymbol) && !sameRelayGroup(sym, activeSymbol)) {
      return;
    }
    var px = Number(ltpHint);
    if (!(px > 0)) {
      var q = lookupQuote(activeSymbol || sym);
      px = q && Number(q.ltp) > 0 ? Number(q.ltp) : 0;
    }
    if (!(px > 0)) {
      el.textContent = "";
      el.setAttribute("hidden", "");
      return;
    }
    el.removeAttribute("hidden");
    el.textContent = px.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    el.setAttribute("title", "LTP " + (activeSymbol || sym || ""));
  }

  function mirrorSeriesSuffixQuotes(bag, symbol, quote) {
    if (!bag || !quote) return;
    seriesSuffixAliases(symbol).forEach(function (alias) {
      if (!alias || alias === symbol) return;
      bag[alias] = Object.assign({}, quote, { symbol: alias });
    });
  }

  var TickHub = {
    listeners: {},
    lastQuotes: {},
    ws: null,
    reconnectMs: 500,
    pollTimer: null,
    lastAgeMs: null,
    lastLatency: null,
    wanted: {},
    _pendingChart: {},
    _flushTimer: null,
    _lastEmit: {},
    // ~400ms coalesce — watchlist LTP updates ~250ms; chart close must track closely.
    // New bucket still flushes immediately (see _scheduleChartFlush).
    CHART_MIN_MS: 400,
    CHART_SAME_PX_MS: 400,
    _reconciling: {},
    /**
     * Tip-first right-edge heal: when lastHistoricalBar lags the open bucket by
     * >2 steps, refresh tip (DHAN-TODAY if PG stale) before painting a lone live bar.
     */
    _reconcileAt: {},
    _edgeWatchTimer: null,
    reconcileSymbol: function (symbol, resolution) {
      var self = this;
      var wantRes = String(resolution);
      var baseRes = universalBaseRes(wantRes);
      // Debounce per symbol (all TFs share one tip heal).
      var debounceKey = String(symbol) + "|*" ;
      if (self._reconciling[debounceKey]) return;
      var now = Date.now();
      var key = subKey(symbol, wantRes);
      var prev = lastHistoricalBar[key];
      var stepMs = resMinutes(wantRes) * 60 * 1000;
      var openBucket = floorBarTimeMs(now, wantRes, symbol);
      var lagBuckets =
        prev && stepMs > 0 ? Math.floor((openBucket - Number(prev.time)) / stepMs) : 0;
      // Large lag: heal fast so continuous candles replace flat pads quickly.
      var debounceMs = lagBuckets >= 5 ? 2500 : lagBuckets >= 3 ? 5000 : 15000;
      if (self._reconcileAt[debounceKey] && now - self._reconcileAt[debounceKey] < debounceMs) {
        return;
      }
      if (Date.now() < tipBackoffUntil || !tipSymReady(symbol, "cold")) {
        return;
      }
      self._reconcileAt[debounceKey] = now;
      self._reconciling[debounceKey] = true;
      var beforeToday = todayBarCount(histCache[key] || [], symbol);
      var beforeLast = prev && prev.time ? Number(prev.time) : 0;
      var q0 = self.lastQuotes[symbol];
      var cumVol = q0 && Number(q0.volume) >= 0 ? Number(q0.volume) : undefined;

      // Universal: heal base tip (5m or 1m), then derive every TF from the same window.
      tipNet(symbol, baseRes, "cold")
        .then(function (pack) {
          var raw = (pack && pack.bars) || [];
          var tvBars =
            raw.length && raw[0].time < 1e12
              ? stripOpenBucket(toTvBars(raw), baseRes, symbol)
              : stripOpenBucket(raw, baseRes, symbol);
          if (!tvBars.length) {
            return histNet(symbol, baseRes, sessionOpenMsToday(symbol), now).then(function (hp) {
              return {
                bars: (hp && hp.bars) || [],
                cache: (hp && hp.cache) || "HIST",
                via: "hist",
              };
            });
          }
          return { bars: tvBars, cache: (pack && pack.cache) || "TIP", via: "tip" };
        })
        .then(function (pack) {
          var tvBars = (pack && pack.bars) || [];
          if (!tvBars.length) return;
          var result = applyUniversalTipEdge(symbol, baseRes, tvBars, {
            wantRes: wantRes,
            beforeLast: beforeLast,
            beforeToday: beforeToday,
            cumVol: cumVol,
            reason: "ws.reconcile.universal",
            // Only hard-reload on large lag; grew≥2 steps also triggers via applyUniversalTipEdge.
            forceReset: lagBuckets >= 5,
          });
          var last = result.last || lastHistoricalBar[key];
          var openB = floorBarTimeMs(Date.now(), wantRes, symbol);
          lb("ws.reconcile", symbol, {
            resolution: wantRes,
            base: baseRes,
            n: tvBars.length,
            cache: (pack && pack.cache) || "—",
            via: (pack && pack.via) || "tip",
            last_t: last ? last.time : null,
            lag_buckets_before: lagBuckets,
            today_before: beforeToday,
            today_after: todayBarCount(histCache[key] || [], symbol),
            synced: (result.synced || []).join(","),
            redraw: !!result.grew,
          });
          lb("candle.gap", symbol, {
            ok: lagBuckets < 3 || (last && openB - Number(last.time) <= stepMs * 2),
            resolution: wantRes,
            why: result.grew ? "edge_healed_universal" : "edge_reconcile_universal",
            lag_buckets: lagBuckets,
            last_t: last ? last.time : null,
            open_bucket: openB,
            base: baseRes,
            cache: (pack && pack.cache) || "",
          });
          var q = self.lastQuotes[symbol];
          if (q) self.applyQuote(symbol, q);
        })
        .catch(function () {})
        .then(function () {
          self._reconciling[debounceKey] = false;
        });
    },
    /** True when hist tip edge is behind the open bucket by >2 steps (1 step = normal). */
    edgeIsLagging: function (symbol, resolution) {
      var stepMs = resMinutes(resolution) * 60 * 1000;
      if (!(stepMs > 0)) return false;
      var openBucket = floorBarTimeMs(Date.now(), resolution, symbol);
      var closed = seriesClosedEdgeMs(symbol, resolution);
      if (!(closed > 0)) {
        var key = subKey(symbol, resolution);
        var prev = lastHistoricalBar[key];
        if (!prev || !(Number(prev.time) > 0)) return true;
        closed = Number(prev.time);
        if (closed > 0 && closed < 1e12) closed *= 1000;
      }
      return openBucket - closed > stepMs * 2.01;
    },
    startEdgeWatch: function () {
      var self = this;
      if (self._edgeWatchTimer) return;
      self._edgeWatchTimer = setInterval(function () {
        if (!inSessionNow(activeSymbol)) return;
        var seenSym = {};
        for (var guid in self.listeners) {
          var lis = self.listeners[guid];
          if (!lis) continue;
          // One reconcile per symbol — universal derive covers all TFs.
          var sk = String(lis.symbol).toUpperCase();
          if (seenSym[sk]) continue;
          var edgeKey = subKey(lis.symbol, lis.resolution);
          var edgeThinToday = !poolHasEnoughToday(histCache[edgeKey] || [], lis.symbol, lis.resolution);
          // Stricter than 1-step lag: edgeIsLagging is ~1.5 steps; only heal thin today
          // or lag that still fails after that (avoid pad/resync every few seconds).
          var lagging = self.edgeIsLagging(lis.symbol, lis.resolution);
          if (lagging || edgeThinToday) {
            seenSym[sk] = true;
            lb("candle.hole.real", lis.symbol, {
              ok: false,
              resolution: String(lis.resolution),
              why: edgeThinToday ? "edge_watch_thin_today" : "edge_watch_lag",
              open_bucket: floorBarTimeMs(Date.now(), lis.resolution, lis.symbol),
              last_t: lastHistoricalBar[edgeKey] && lastHistoricalBar[edgeKey].time,
              today_n: todayBarCount(histCache[edgeKey] || [], lis.symbol),
            });
            // Detect → single healer only. No pads, no parallel reconcile storm.
            requestEdgeHeal(
              lis.symbol,
              lis.resolution,
              edgeThinToday ? "edge.thin_today" : "edge.watch"
            );
          }
        }
      }, 12000);
    },
    startOptHeal: function () {
      var self = this;
      if (self._optHealTimer) return;
      self._optHealTimer = setInterval(function () {
        if (document.hidden) return;
        var seen = {};
        for (var guid in self.listeners) {
          var lis = self.listeners[guid];
          if (!lis || !/^OPT:/i.test(String(lis.symbol || ""))) continue;
          var k = subKey(lis.symbol, lis.resolution);
          if (seen[k]) continue;
          seen[k] = true;
          var ist = new Date(Date.now() + IST_OFFSET_MS);
          var mins = ist.getUTCHours() * 60 + ist.getUTCMinutes();
          var close = sessionCloseMin(lis.symbol);
          var weekday = ist.getUTCDay() >= 1 && ist.getUTCDay() <= 5;
          if (inSessionNow(lis.symbol) || (weekday && mins > close && mins <= close + 20)) {
            optHealCheck(lis.symbol, lis.resolution);
          }
        }
      }, 15000);
    },
    start: function () {
      this.openWs();
      this.startEdgeWatch();
      this.startLivePulse();
      this.startOptHeal();
    },
    /** TV drops onTick that lands before the series exists — resend the forming bar after history. */
    scheduleReseed: function (key) {
      var self = this;
      [0, 250, 700, 1400, 2500].forEach(function (ms) {
        setTimeout(function () {
          self.reemit(key, "reseed");
        }, ms);
      });
    },
    reemit: function (key, why) {
      var bar = liveBar[key];
      if (!bar || getBarsPending[key] || !paintCommittedAt[key]) return;
      var parts = String(key || "").split("|");
      var sym = parts[0] || "";
      var res = parts.slice(1).join("|") || "";
      if (!sym || !res) return;
      if (barReplayBlocksLive(sym, res)) return;
      deliverLiveBarToPanes(sym, res, bar, why || "reseed");
    },
    _livePulseTimer: null,
    _liveStatAt: 0,
    clockSkewMs: null,
    startLivePulse: function () {
      var self = this;
      if (self._livePulseTimer) return;
      if (!self._visHooked && typeof document !== "undefined") {
        self._visHooked = true;
        // Hidden tabs run timers at most 1/s; catch the chart up the moment it is visible again.
        document.addEventListener("visibilitychange", function () {
          if (document.hidden) {
            self._hiddenAt = Date.now();
            return;
          }
          var away = self._hiddenAt ? Date.now() - self._hiddenAt : 0;
          self._hiddenAt = 0;
          var keys = {};
          for (var g in self.listeners) {
            var L = self.listeners[g];
            if (L) keys[subKey(L.symbol, L.resolution)] = true;
          }
          Object.keys(keys).forEach(function (k) {
            self.reemit(k, "visible");
          });
          lb("tab.visible", activeSymbol, { away_ms: away, ws_open: !!(self.ws && self.ws.readyState === 1) });
          if (!self.ws || self.ws.readyState > 1) self.openWs();
        });
      }
      window.__bcLive.hub = self;
      window.__bcLive.test = {
        wsDrop: function (ms) {
          self._testWsBlockUntil = Date.now() + (Number(ms) || 5000);
          if (self.ws) self.ws.close();
          return "ws blocked for " + (Number(ms) || 5000) + " ms";
        },
        mute: function (ms) {
          self._testMuteUntil = Date.now() + (Number(ms) || 4000);
          return "chart updates muted for " + (Number(ms) || 4000) + " ms (watchdog should force)";
        },
        paintTimeout: function (key) {
          key = key || subKey(activeSymbol, activeRes);
          getBarsPending[key] = Date.now() - 4000;
          liveStatFor(key).paintTimeoutAt = 0;
          return "marked " + key + " as stuck loading";
        },
      };
      self._livePulseTimer = setInterval(function () {
        try {
          self._livePulse();
        } catch (e) {
          console.error("[livePulse]", e);
        }
      }, 250);
    },
    /**
     * Safety net per chart: first paint must land within 3s (else reload history); the forming
     * bar is resent once a second when quiet; if prices arrive but the chart has not taken one
     * for 2s, force the latest price through and log which gate held it.
     */
    /**
     * The chart on screen must always have a live subscription. TV drops it on reset-cache and
     * re-subscribes only after getBars; if that never happens the candle freezes until refresh.
     */
    _checkChartListener: function (now) {
      if (barReplay.active) return;
      var sym = "";
      var res = "";
      try {
        var ch = widget && typeof widget.activeChart === "function" ? widget.activeChart() : null;
        sym = ch ? String(ch.symbol() || "") : "";
        res = ch ? String(ch.resolution() || "") : "";
      } catch (_) {
        return;
      }
      if (!sym || !res || !inSessionNow(sym)) {
        this._noLisSince = 0;
        return;
      }
      var has = false;
      for (var g in this.listeners) {
        var L = this.listeners[g];
        if (L && String(L.resolution) === res && sameChartSymbol(L.symbol, sym)) {
          has = true;
          break;
        }
      }
      var key = subKey(sym, res);
      var pend = getBarsPending[key];
      if (has || (pend && now - pend < 3000)) {
        this._noLisSince = 0;
        return;
      }
      if (this._noLisKey !== key || !this._noLisSince) {
        this._noLisKey = key;
        this._noLisSince = now;
        return;
      }
      if (now - this._noLisSince < 3000) return;
      this._noLisSince = now + 7000;
      lb("chart.no_listener", sym, {
        resolution: res,
        getbars_pending_ms: pend ? now - pend : null,
        listeners: Object.keys(this.listeners).length,
      });
      forceHistoryReload(sym, res, "no_listener");
    },
    _livePulse: function () {
      var now = Date.now();
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        this._checkChartListener(now);
      } catch (e) {
        console.error("[checkChartListener]", e);
      }
      var seen = {};
      for (var guid in this.listeners) {
        var lis = this.listeners[guid];
        if (!lis) continue;
        var key = subKey(lis.symbol, lis.resolution);
        if (seen[key]) continue;
        seen[key] = true;
        if (!inSessionNow(lis.symbol)) continue;
        if (barReplayBlocksLive(lis.symbol, lis.resolution)) continue;
        var st = liveStatFor(key);
        if (getBarsPending[key] || !paintCommittedAt[key]) {
          var since = Math.max(lis.addedAt || 0, getBarsPending[key] || 0);
          if (since && now - since > 3000 && now - (st.paintTimeoutAt || 0) > 15000) {
            st.paintTimeoutAt = now;
            lb("paint.timeout", lis.symbol, {
              resolution: String(lis.resolution),
              pending_ms: now - since,
              getbars_pending: !!getBarsPending[key],
              gates: st.gates,
            });
            forceHistoryReload(lis.symbol, lis.resolution, "paint_timeout");
          }
          continue;
        }
        var bar = liveBar[key];
        var edge = tvEdge[key];
        if (bar && now - st.emitAt >= 3000 && (!edge || bar.time >= Number(edge.time))) {
          this.reemit(key, "pulse");
        }
        var shown = edge ? Number(edge.close) : 0;
        if (st.pxAt && now - st.pxAt < 2000 && st.px > 0 && shown !== st.px && now - (st.liveAt || 0) > 2000) {
          if (now - (st.stallAt || 0) > 5000) {
            st.stallAt = now;
            lb("chart.stall", lis.symbol, {
              resolution: String(lis.resolution),
              last_gate: st.lastGate,
              gates: st.gates,
              px: st.px,
              tick_ms: st.quote && st.quote.ts_ms ? Number(st.quote.ts_ms) : null,
              chart_close: shown,
              chart_t: edge ? edge.time : null,
              live_t: bar ? bar.time : null,
              last_fold: st.lastFold || null,
              last_block: st.lastBlock || null,
            });
          }
          this._emitChartBar(lis, lis.symbol, st.px, st.quote, true);
        } else if (
          st.wlAt && now - st.wlAt < 3000 && st.wlPx > 0 && shown !== st.wlPx &&
          !(st.pxAt && now - st.pxAt < 2000) && now - (st.liveAt || 0) > 2000
        ) {
          // Watchlist has a newer price but no tick reached this chart (unmatched feed name).
          if (now - (st.stallAt || 0) > 5000) {
            st.stallAt = now;
            lb("chart.stall", lis.symbol, {
              resolution: String(lis.resolution),
              reason: "watchlist_ahead",
              last_gate: st.lastGate,
              gates: st.gates,
              wl_px: st.wlPx,
              tick_age_ms: st.pxAt ? now - st.pxAt : null,
              chart_close: shown,
              chart_t: edge ? edge.time : null,
            });
          }
          this._emitChartBar(lis, lis.symbol, st.wlPx, { symbol: lis.symbol, ltp: st.wlPx, ts_ms: now }, true);
        }
      }
      if (now - this._liveStatAt > 30000) {
        this._liveStatAt = now;
        var k = subKey(activeSymbol, activeRes);
        var s = liveStat[k];
        if (s) {
          lb("chart.live.stat", activeSymbol, {
            resolution: String(activeRes),
            ticks_in: s.in,
            emits: s.emit,
            gates: s.gates,
            clock_skew_ms: this.clockSkewMs,
            pipe_ms: s.pipeMs,
            chart_t: tvEdge[k] ? tvEdge[k].time : null,
            live_t: liveBar[k] ? liveBar[k].time : null,
            tick_ms: s.quote && s.quote.ts_ms ? Number(s.quote.ts_ms) : null,
            last_fold: s.lastFold || null,
            last_block: s.lastBlock || null,
          });
        }
      }
    },
    noteWatchlistQuotes: function (q) {
      var now = Date.now();
      var syms = Object.keys(q || {});
      for (var guid in this.listeners) {
        var lis = this.listeners[guid];
        if (!lis) continue;
        for (var i = 0; i < syms.length; i++) {
          var ltp = Number(q[syms[i]] && q[syms[i]][0]);
          if (!(ltp > 0)) continue;
          var su = String(syms[i]).toUpperCase();
          var lu = String(lis.symbol).toUpperCase();
          if (su !== lu && !sameRelayGroup(su, lu) && seriesSuffixAliases(su).indexOf(lu) < 0) continue;
          var st = liveStatFor(subKey(lis.symbol, lis.resolution));
          st.wlPx = this._smoothLtp(ltp, lis.symbol);
          st.wlAt = now;
          break;
        }
      }
    },
    stop: function () {
      if (this.ws) {
        try {
          this.ws.close();
        } catch (_) {}
        this.ws = null;
      }
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      if (this._flushTimer) {
        clearTimeout(this._flushTimer);
        this._flushTimer = null;
      }
      if (this._edgeWatchTimer) {
        clearInterval(this._edgeWatchTimer);
        this._edgeWatchTimer = null;
      }
      if (this._livePulseTimer) {
        clearInterval(this._livePulseTimer);
        this._livePulseTimer = null;
      }
    },
    add: function (guid, symbol, resolution, onTick, onResetCacheNeeded) {
      var self = this;
      this.listeners[guid] = {
        symbol: symbol,
        resolution: resolution,
        onTick: onTick,
        onResetCacheNeeded: onResetCacheNeeded || null,
        addedAt: Date.now(),
      };
      if (typeof onResetCacheNeeded === "function") {
        resetCacheNeededCbs[guid] = onResetCacheNeeded;
      }
      this.start();
      this.ensureChartSubscribed(symbol);
      // Hist seam — defer until first getBars commits tip edge (anti-flicker).
      var key = subKey(symbol, resolution);
      if (paintCommittedAt[key]) {
        this.reconcileSymbol(symbol, resolution);
      } else {
        setTimeout(function () {
          if (!self.listeners[guid]) return;
          self.reconcileSymbol(symbol, resolution);
        }, PAINT_GRACE_MS);
      }
      try {
        var hist = lastHistoricalBar[key];
        var cached = this.lastQuotes[symbol];
        if (hist) seedBuilder(symbol, resolution, hist, cached && cached.volume);
        if (
          paintCommittedAt[key] &&
          hist &&
          barMatchesLtp(hist, symbol) &&
          cached &&
          Number(cached.ltp) > 0
        ) {
          this.applyQuote(symbol, cached);
        }
      } catch (_) {}
    },
    remove: function (guid) {
      delete this.listeners[guid];
      delete resetCacheNeededCbs[guid];
      if (!Object.keys(this.listeners).length && !Object.keys(this.quoteSubs).length) this.stop();
    },
    /** UPPER → symbol for lightweight watchlist/tape quotes (relay batches them every 250ms). */
    quoteSubs: {},
    setQuoteSymbols: function (list) {
      var next = {};
      (list || []).forEach(function (s) {
        var v = String(s || "").trim();
        if (v) next[v.toUpperCase()] = v;
      });
      var add = [];
      var drop = [];
      var k;
      for (k in next) if (!this.quoteSubs[k]) add.push(next[k]);
      for (k in this.quoteSubs) if (!next[k]) drop.push(this.quoteSubs[k]);
      this.quoteSubs = next;
      if (!add.length && !drop.length) return;
      if (add.length && !(this.ws && (this.ws.readyState === 0 || this.ws.readyState === 1))) {
        this.openWs();
        return;
      }
      if (!this.ws || this.ws.readyState !== 1) return;
      try {
        if (drop.length) this.ws.send(JSON.stringify({ action: "unsubscribe_quotes", symbols: drop }));
        if (add.length) this.ws.send(JSON.stringify({ action: "subscribe_quotes", symbols: add }));
      } catch (_) {}
    },
    sendQuoteSubs: function () {
      var syms = Object.keys(this.quoteSubs).map(function (k) {
        return TickHub.quoteSubs[k];
      });
      if (!syms.length || !this.ws || this.ws.readyState !== 1) return;
      try {
        this.ws.send(JSON.stringify({ action: "subscribe_quotes", symbols: syms }));
      } catch (_) {}
    },
    sendSub: function (symbol) {
      if (this.ws && this.ws.readyState === 1) {
        try {
          // PHP daemon protocol
          this.ws.send(JSON.stringify({ op: "sub", symbol: symbol }));
          // Go relay protocol
          this.ws.send(JSON.stringify({ action: "subscribe", symbols: [symbol] }));
        } catch (_) {}
      }
    },
    /**
     * Subscribe every alias the relay / daemon might publish under, so chart LTP
     * never stalls behind watchlist (watchlist_ahead).
     */
    ensureChartSubscribed: function (symbol) {
      var self = this;
      var sym = String(symbol || "").trim();
      if (!sym) return;
      var names = {};
      function add(n) {
        n = String(n || "").trim();
        if (!n) return;
        names[n] = true;
        names[n.toUpperCase()] = true;
      }
      add(sym);
      add(optWireSymbol(sym));
      add(optDisplayLabel(sym));
      seriesSuffixAliases(sym).forEach(add);
      try {
        var g = null;
        var key = relayAliasKey(sym);
        if (!relayAliasIndex) sameRelayGroup(sym, sym);
        if (relayAliasIndex && relayAliasIndex[key] != null) {
          g = RELAY_ALIAS_GROUPS[relayAliasIndex[key]];
        }
        if (g) g.forEach(add);
      } catch (_) {}
      Object.keys(names).forEach(function (n) {
        self.wanted[n] = true;
        self.sendSub(n);
      });
      // Equities are already on the feed. One ticks.php only for a contract the
      // daemon does not hold yet, so the option stays on the live socket.
      var opt = String(sym).toUpperCase();
      if (opt.indexOf("OPT:") === 0 || /\b(CE|PE)\b/.test(opt)) {
        fetch(TICKS_URL + "?symbol=" + encodeURIComponent(sym), {
          credentials: "include",
          cache: "no-store",
        }).catch(function () {});
      }
    },
    openWs: function () {
      var self = this;
      if (this.ws && (this.ws.readyState === 0 || this.ws.readyState === 1)) return;
      var blockMs = (this._testWsBlockUntil || 0) - Date.now();
      if (blockMs > 0) {
        if (!this._wsDownAt) this._wsDownAt = Date.now();
        setTimeout(function () {
          self.openWs();
        }, blockMs + 50);
        return;
      }
      var ws;
      try {
        ws = new WebSocket(LIVE_WS_URL);
      } catch (e) {
        log("ws.open.err", e);
        this.fallbackPoll();
        return;
      }
      this.ws = ws;
      ws.onopen = function () {
        self.reconnectMs = 500;
        window.__kiWsConnected = true;
        // Front badge is market-hours only — do not flip on WS connect.
        lb("ws.open", activeSymbol, { url: LIVE_WS_URL });
        Object.keys(self.wanted).forEach(function (sym) {
          self.sendSub(sym);
          lb("ws.subscribe", sym, { url: LIVE_WS_URL });
        });
        self.sendQuoteSubs();
        // Gap kill on (re)connect — hist/tip backfill then resume ticks for EVERY
        // active listener (indices, equities, F&O, MCX — same path).
        for (var guid in self.listeners) {
          var lis = self.listeners[guid];
          if (lis && lis.symbol) {
            self.reconcileSymbol(lis.symbol, lis.resolution);
            lb("ws.reconnect.reconcile", lis.symbol, { resolution: lis.resolution });
          }
        }
        // Down long enough to miss ticks: TV docs — reset cache + resetData after the tip heal
        // so missed minutes are redrawn; the forming bar is resent by getBars reseed.
        var downMs = self._wsDownAt ? Date.now() - self._wsDownAt : 0;
        self._wsDownAt = 0;
        if (downMs > 3000) {
          lb("ws.reconnect.reset", activeSymbol, { down_ms: downMs });
          setTimeout(function () {
            var done = {};
            for (var g0 in self.listeners) {
              var L0 = self.listeners[g0];
              if (!L0) continue;
              var k0 = subKey(L0.symbol, L0.resolution);
              if (done[k0]) continue;
              done[k0] = true;
              forceHistoryReload(L0.symbol, L0.resolution, "ws_reconnect");
            }
            try {
              var ch0 = widget && typeof widget.activeChart === "function" ? widget.activeChart() : null;
              if (ch0) forceHistoryReload(String(ch0.symbol()), String(ch0.resolution()), "ws_reconnect");
            } catch (_) {}
          }, 2500);
        }
        // Also force-sub + reconcile wanted symbols that may not have a TV listener yet.
        Object.keys(self.wanted || {}).forEach(function (sym) {
          self.sendSub(sym);
          var anyRes = null;
          for (var g in self.listeners) {
            var L = self.listeners[g];
            if (L && String(L.symbol).toUpperCase() === String(sym).toUpperCase()) {
              anyRes = L.resolution;
              break;
            }
          }
          if (anyRes) self.reconcileSymbol(sym, anyRes);
        });
        self.startEdgeWatch();
      };
      ws.onmessage = function (ev) {
        var tRecv = performance.now();
        var msg;
        try {
          msg = JSON.parse(ev.data);
        } catch (_) {
          return;
        }
        if (msg.t === "tick" && msg.quote) {
          if (Number(msg.server_ms) > 0) {
            // Browser clock minus server clock (+ network latency). Session/bucket gates use the browser clock.
            var skew = Date.now() - Number(msg.server_ms);
            self.clockSkewMs = self.clockSkewMs == null ? skew : Math.round(self.clockSkewMs * 0.9 + skew * 0.1);
            if (Math.abs(self.clockSkewMs) > 30000 && !self._skewWarned) {
              self._skewWarned = true;
              lb("clock.skew", activeSymbol, { skew_ms: self.clockSkewMs });
            }
          }
          self.applyQuote(msg.quote.symbol, msg.quote, tRecv, msg.server_ms);
        } else if (msg.t === "qs" && msg.q) {
          stampWatchlistQuotesBatch(msg.q);
          self.noteWatchlistQuotes(msg.q);
        } else if (msg.t === "snapshot" && msg.quotes) {
          Object.keys(msg.quotes).forEach(function (sym) {
            self.applyQuote(sym, msg.quotes[sym], tRecv, null);
          });
        } else if (msg.t === "hello") {
          // technical feed state — ignore for front badge
        } else if (msg.type === "snapshot" && msg.symbol) {
          // Go relay snapshot shape
          var gq = {
            symbol: msg.symbol,
            ltp: msg.ltp,
            volume: msg.volume,
            ts_ms: msg.ts_ms,
            bars: msg.bars || null,
            bar: msg.bar || null,
          };
          self.applyQuote(msg.symbol, gq, tRecv, null);
        } else if (msg.type === "subscribed" || msg.type === "pong") {
          // technical — ignore for front badge
        }
      };
      ws.onerror = function () {
        lb("ws.error", activeSymbol, {});
      };
      ws.onclose = function () {
        self.ws = null;
        if (!self._wsDownAt) self._wsDownAt = Date.now();
        var wait = self.reconnectMs;
        self.reconnectMs = Math.min(8000, self.reconnectMs * 2);
        setTimeout(function () {
          if (Object.keys(self.listeners).length || Object.keys(self.quoteSubs).length) self.openWs();
        }, wait);
        self.fallbackPoll();
      };
    },
    fallbackPoll: function () {
      var self = this;
      if (this.pollTimer) return;
      // VC Stocks floor ~2.5s — do not hammer at 250ms.
      this.pollTimer = setInterval(function () {
        if (self.ws && self.ws.readyState === 1) {
          clearInterval(self.pollTimer);
          self.pollTimer = null;
          return;
        }
        self.poll();
      }, 2500);
      this.poll();
    },
    _smoothLtp: function (ltp, symbol) {
      var n = Number(ltp);
      if (!(n > 0)) return 0;
      // Exact exchange price (indices move in 0.01) — the chart must show what the broker shows.
      return Math.round(n * 100) / 100;
    },
    _scheduleChartFlush: function (immediate) {
      var self = this;
      if (immediate) {
        if (this._flushTimer) {
          try {
            clearTimeout(this._flushTimer);
          } catch (_) {}
          this._flushTimer = null;
        }
        this._flushPendingCharts();
        return;
      }
      if (this._flushTimer) return;
      this._flushTimer = setTimeout(function () {
        self._flushTimer = null;
        self._flushPendingCharts();
      }, this.CHART_MIN_MS);
    },
    _flushPendingCharts: function () {
      var self = this;
      // Coalesce by symbol|resolution so multi-grid panes on the same chart share one bar build.
      var byKey = {};
      var keys = Object.keys(this._pendingChart);
      for (var i = 0; i < keys.length; i++) {
        var pk = keys[i];
        var job = self._pendingChart[pk];
        delete self._pendingChart[pk];
        if (!job || !job.lis) continue;
        if (barReplayBlocksLive(job.lis.symbol, job.lis.resolution)) continue;
        var k = subKey(job.lis.symbol, job.lis.resolution);
        var cur = byKey[k];
        if (!cur) {
          byKey[k] = job;
          continue;
        }
        cur.hi = Math.max(cur.hi || cur.ltp || 0, job.hi || job.ltp || 0, job.ltp || 0);
        var curLo = cur.lo > 0 ? cur.lo : cur.ltp;
        var jobLo = job.lo > 0 ? job.lo : job.ltp;
        cur.lo = Math.min(curLo > 0 ? curLo : jobLo, jobLo > 0 ? jobLo : curLo);
        cur.ltp = job.ltp;
        cur.quote = job.quote;
        cur.symbol = job.symbol || cur.symbol;
      }
      Object.keys(byKey).forEach(function (k) {
        var job = byKey[k];
        try {
          self._emitChartBar(job.lis, job.symbol, job.ltp, job.quote, false, job.hi, job.lo);
        } catch (e) {
          console.error("[emitChartBar]", e);
        }
      });
      if (Object.keys(this._pendingChart).length) self._scheduleChartFlush();
    },
    /**
     * Live candle from ticks (TV streaming-tutorial contract): one forming bar per chart,
     * updated by every price; a newer bucket opens a new bar. Only three checks can drop a
     * tick — market hours, TV has no history yet, another symbol's price. The bar time never
     * goes behind what TV holds (late ticks fold into the current bar). Server quote.bars
     * only seed/widen the same bucket (true open/high/low before this chart opened).
     */
    _emitChartBar: function (lis, symbol, ltp, quote, force, hi, lo) {
      var self = this;
      var resolution = lis.resolution;
      var key = subKey(lis.symbol, resolution);
      var st = liveStatFor(key);
      var nowMs = Date.now();
      st.in++;
      st.px = Number(ltp);
      st.pxAt = nowMs;
      st.quote = quote;
      // Replay owns this series — do not mutate liveBar / edge / TV (was jumping zoom on each LTP).
      if (barReplayBlocksLive(lis.symbol, resolution)) return liveGate(key, "replay");
      if (!inSessionNow(symbol)) return liveGate(key, "session");
      if (!(Number(ltp) > 0)) return liveGate(key, "price");
      if (!force && nowMs < (self._testMuteUntil || 0)) return liveGate(key, "test_mute");
      if (getBarsPending[key] || !paintCommittedAt[key]) return liveGate(key, "await_paint");
      var mins = resMinutes(resolution);
      var stepMs = mins * 60 * 1000;
      var tickMs = quote && Number(quote.ts_ms) > 0 ? Number(quote.ts_ms) : nowMs;
      if (mins < 1440 && isPreOpenAt(lis.symbol, tickMs)) return liveGate(key, "preopen");
      var edge = tvEdge[key] || null;
      var cur = liveBar[key] || null;
      var ref = cur || edge || lastHistoricalBar[key];
      var isOpt = /^OPT:/i.test(String(lis.symbol || ""));
      if (!isOpt && ref && Number(ref.close) > 0 && !ltpMatchesTip(ltp, ref.close, symbol)) {
        if (!force) return liveGate(key, "xsym");
        lb("chart.force", symbol, { resolution: String(resolution), bypassed: "xsym", ltp: ltp, ref: ref.close });
      }

      var bucket = floorBarTimeMs(tickMs, resolution, lis.symbol);
      // TV onTick cannot insert missing closed bars — never paint a new bucket across a hole.
      // Still fold LTP into the painted edge so chart close tracks watchlist while tip heals.
      if (
        mins < 1440 &&
        !(cur && Number(cur.time) === bucket) &&
        holdLiveAcrossSeriesHole(lis.symbol, resolution, bucket, "live.mem_gap")
      ) {
        if (edge && Number(edge.time) > 0) {
          bucket = Number(edge.time);
          liveGate(key, "gap_heal_fold");
        } else {
          return liveGate(key, "await_gap_heal");
        }
      }
      if (edge && bucket < Number(edge.time)) {
        st.lastFold = { tick_ms: tickMs, tick_bucket: bucket, edge_t: Number(edge.time), at: nowMs };
        bucket = Number(edge.time);
        liveGate(key, "fold_behind_tv");
        if (nowMs - (st.foldLogAt || 0) > 30000) {
          st.foldLogAt = nowMs;
          lb("chart.fold", lis.symbol, Object.assign({ resolution: String(resolution) }, st.lastFold));
        }
      }
      var cumVol = quote && Number(quote.volume) >= 0 ? Number(quote.volume) : undefined;
      var serverRaw =
        quote &&
        quote.bars &&
        (quote.bars[String(mins)] ||
          quote.bars[resolution] ||
          (mins === 1440 ? quote.bars["1D"] : null) ||
          (mins === 240 ? quote.bars["4H"] : null));
      var server = null;
      if (serverRaw && Number(serverRaw.close) > 0) {
        var srvT = Number(serverRaw.time);
        if (srvT > 0 && srvT < 1e12) srvT *= 1000;
        if (srvT === bucket) server = serverRaw;
      }

      var dayOpen = quote && Number(quote.open) > 0 ? Number(quote.open) : 0;
      var prevClose = quote && Number(quote.prev_close) > 0 ? Number(quote.prev_close) : 0;
      // First bucket of today's session — open must be exchange day open, never prev_close glue.
      var sessOpenBucket =
        mins < 1440 && bucket === floorBarTimeMs(sessionOpenMsToday(lis.symbol), resolution, lis.symbol);
      var bar;
      var base = cur && cur.time === bucket ? cur : edge && Number(edge.time) === bucket ? edge : null;

      // Prefer daemon forming OHLC (1m-window rebuild). Do not copy a poisoned liveBar and widen it
      // first — that left flash wicks on the client until the candle closed.
      if (server) {
        var sO = Number(server.open) > 0 ? Number(server.open) : ltp;
        var sH = Number(server.high) > 0 ? Number(server.high) : ltp;
        var sL = Number(server.low) > 0 ? Number(server.low) : ltp;
        if (sessOpenBucket && dayOpen > 0) sO = dayOpen;
        bar = {
          time: bucket,
          open: sO,
          high: Math.max(sH, sO, ltp),
          low: Math.min(sL > 0 ? sL : ltp, sO, ltp),
          close: ltp,
          volume: Number(server.volume) > 0 ? Number(server.volume) : 0,
        };
      } else if (base) {
        bar = copyBar(base);
        bar.high = Math.max(bar.high, ltp, Number(hi) > 0 ? Number(hi) : 0);
        bar.low = Math.min(bar.low > 0 ? bar.low : ltp, ltp, Number(lo) > 0 ? Number(lo) : ltp);
        bar.close = ltp;
      } else {
        // Continuous series: open at prior close so a new bucket never floats a gap off tip edge.
        // Session-open bucket still uses exchange day open.
        var open0 = ltp;
        if (sessOpenBucket && dayOpen > 0) {
          open0 = dayOpen;
        } else if (edge && Number(edge.close) > 0 && mins < 1440) {
          // Mid-session tip lag: NEVER open at dayOpen — that paints a mega candle
          // (day open → LTP) until refresh. Open flat at LTP; reconcile fills closed bars.
          if (bucket - Number(edge.time) > stepMs * 1.5) {
            open0 = ltp;
          } else {
            open0 = Number(edge.close);
          }
        } else if (prevClose > 0 && dayOpen > 0 && Math.abs(ltp - prevClose) / prevClose < 0.0008) {
          open0 = dayOpen;
        }
        bar = { time: bucket, open: open0, high: Math.max(open0, ltp), low: Math.min(open0, ltp), close: ltp, volume: 0 };
        liveCumAtOpen[key] = cumVol;
        // New bucket more than one step past TV edge → hold + heal (never append across gap).
        if (
          edge &&
          mins < 1440 &&
          bucket - Number(edge.time) > stepMs * 1.01 &&
          holdLiveAcrossSeriesHole(symbol, resolution, bucket, "live.jump")
        ) {
          return liveGate(key, "await_gap_heal");
        }
      }
      if (mins >= 1440 && quote) {
        if (Number(quote.open) > 0) bar.open = Number(quote.open);
        if (Number(quote.high) > 0) bar.high = Math.max(bar.high, Number(quote.high));
        if (Number(quote.low) > 0) bar.low = Math.min(bar.low, Number(quote.low));
        if (cumVol != null) bar.volume = Math.max(bar.volume, cumVol);
      } else if (!server && cumVol != null) {
        if (liveCumAtOpen[key] == null || cumVol < liveCumAtOpen[key]) liveCumAtOpen[key] = cumVol - (bar.volume || 0);
        bar.volume = Math.max(bar.volume, cumVol - liveCumAtOpen[key]);
      }

      // TradingView onTick only widens H/L — it will not shrink a flash wick on the same bar.
      // Reset only for meaningful open/structure errors (not tiny wick noise) and at most once
      // per forming bucket — frequent resetCache was the mid-candle flicker users refreshed away.
      var painted = tvEdge[key];
      var openDiff =
        painted &&
        Number(bar.open) > 0 &&
        Number(painted.open) > 0 &&
        Math.abs(Number(bar.open) - Number(painted.open)) > Math.max(0.05, Number(bar.close) * 0.0005);
      var wickTooWide =
        painted &&
        Number(painted.time) === Number(bar.time) &&
        ((Number(painted.high) - Number(bar.high) > Math.max(0.5, Number(bar.close) * 0.002)) ||
          (Number(bar.low) - Number(painted.low) > Math.max(0.5, Number(bar.close) * 0.002)));
      // Mega body from a prior dayOpen→LTP mistake: force one reset even if open was never corrected.
      var bodyAbsurd =
        painted &&
        Number(painted.time) === Number(bar.time) &&
        Number(painted.close) > 0 &&
        Math.abs(Number(painted.close) - Number(painted.open)) > Math.max(80, Number(painted.close) * 0.012) &&
        Math.abs(Number(bar.close) - Number(bar.open)) < Math.abs(Number(painted.close) - Number(painted.open)) * 0.35;
      var needsShrinkReset =
        painted &&
        Number(painted.time) === Number(bar.time) &&
        (openDiff || wickTooWide || bodyAbsurd) &&
        st.lastShrinkBucket !== Number(bar.time);

      liveBar[key] = bar;

      var lastEm = self._lastEmit[key] || { px: null, t: 0, at: 0, h: 0, l: 0 };
      // Same-bucket paint coalesce ~3s (MCX a bit faster). New bucket always emits.
      var samePxMs = isMcxSymbol(symbol) ? 1000 : self.CHART_SAME_PX_MS;
      if (
        !force &&
        !needsShrinkReset &&
        lastEm.t === bar.time &&
        lastEm.px === bar.close &&
        lastEm.h === bar.high &&
        lastEm.l === bar.low &&
        nowMs - lastEm.at < samePxMs
      ) {
        return;
      }
      lastHistoricalBar[key] = copyBar(bar);
      self._lastEmit[key] = { px: bar.close, t: bar.time, at: nowMs, h: bar.high, l: bar.low };

      if (needsShrinkReset) {
        st.lastShrinkBucket = Number(bar.time);
        st.lastShrinkResetAt = nowMs;
        lb("candle.shrink_reset", lis.symbol, {
          resolution: String(resolution),
          o: bar.open,
          h: bar.high,
          l: bar.low,
          c: bar.close,
          was_h: painted.high,
          was_l: painted.low,
        });
        try {
          fireResetCacheNeeded(lis.symbol, resolution);
        } catch (_) {}
        // Still deliver — after reset, getBars uses liveBar; until then close keeps moving.
      }

      deliverLiveBarToPanes(lis.symbol, resolution, bar, force ? "force" : "live");
      if (isOpt) (optLiveBars[key] || (optLiveBars[key] = {}))[bar.time] = copyBar(bar);
      if (quote && quote._tRecv > 0) {
        var pipe = performance.now() - quote._tRecv;
        st.pipeMs = st.pipeMs == null ? Math.round(pipe) : Math.round(st.pipeMs * 0.9 + pipe * 0.1);
      }
      if (!self._ltpOnce) self._ltpOnce = {};
      var ltpKey = symbol + "|" + String(resolution);
      if (nowMs - (self._ltpOnce[ltpKey] || 0) > 1500) {
        self._ltpOnce[ltpKey] = nowMs;
        lb("tv.ltp", symbol, {
          src: server ? "server" : "live",
          o: bar.open,
          h: bar.high,
          l: bar.low,
          c: bar.close,
          age_ms: quote && quote.age_ms != null ? quote.age_ms : 0,
        });
      }
    },
    applyQuote: function (symbol, quote, tRecv, serverMs) {
      var self = this;
      if (!quote || !(Number(quote.ltp) > 0)) return;
      var ltp = this._smoothLtp(quote.ltp, symbol);
      if (!(ltp > 0)) return;
      // Ignore stale packets when we already hold a newer exchange print for this name.
      try {
        var inTs = Number(quote.exchange_ts_ms || quote.ts_ms || 0);
        var prevQ = lookupQuote(symbol) || this.lastQuotes[symbol];
        var prevTs = prevQ ? Number(prevQ.exchange_ts_ms || prevQ.ts_ms || 0) : 0;
        if (inTs > 0 && prevTs > 0 && inTs + 250 < prevTs) {
          return;
        }
        // Pack ghost: exchange print >2m old while session is live — skip chart poison.
        if (inTs > 0 && inSessionNow(symbol) && Date.now() - inTs > 120000) {
          if (!(prevTs > 0 && Date.now() - prevTs < 30000)) {
            // keep for WL tip fallback only if nothing fresher — still stamp WL below if no prev
            if (prevTs > 0 && Date.now() - prevTs < 120000) return;
          } else {
            return;
          }
        }
      } catch (_) {}
      quote = Object.assign({}, quote, { ltp: ltp });
      this.lastQuotes[symbol] = quote;
      // Mirror under every alias label we care about (BANK ↔ BANK NIFTY, MTARTECH-BE ↔ MTARTECH).
      var sid = quote.security_id != null ? String(quote.security_id) : "";
      try {
        var optWire = optWireSymbol(symbol);
        var optDisp = optDisplayLabel(symbol);
        if (optWire && optWire !== symbol) {
          this.lastQuotes[optWire] = Object.assign({}, quote, { symbol: optWire });
        }
        if (optDisp && optDisp !== symbol) {
          this.lastQuotes[optDisp] = Object.assign({}, quote, { symbol: optDisp });
        }
        if (sid && /^OPT:/i.test(optWire || "")) {
          var fromSid = "OPT:" + sid;
          var sidLabel = optDisplayLabel(fromSid);
          if (sidLabel && sidLabel !== fromSid) {
            this.lastQuotes[fromSid] = Object.assign({}, quote, { symbol: fromSid });
            this.lastQuotes[sidLabel] = Object.assign({}, quote, { symbol: sidLabel });
          }
        }
      } catch (_) {}
      if (sid === "25") {
        this.lastQuotes["BANK NIFTY"] = Object.assign({}, quote, { symbol: "BANK NIFTY" });
        this.lastQuotes["BANK"] = Object.assign({}, quote, { symbol: "BANK" });
      }
      if (sid === "27") {
        this.lastQuotes["FIN NIFTY"] = Object.assign({}, quote, { symbol: "FIN NIFTY" });
        this.lastQuotes["FINANCIAL SERVICES"] = Object.assign({}, quote, { symbol: "FINANCIAL SERVICES" });
      }
      mirrorSeriesSuffixQuotes(this.lastQuotes, symbol, quote);
      stampWatchlistQuote(symbol, quote);
      try {
        syncActiveLtpHeader(symbol, ltp);
      } catch (_) {}
      try {
        if (alertCtl && typeof alertCtl.onQuote === "function") {
          alertCtl.onQuote(symbol, ltp);
          if (quote.symbol && String(quote.symbol) !== String(symbol)) {
            alertCtl.onQuote(quote.symbol, ltp);
          }
        }
      } catch (_) {}
      if (!inSessionNow(symbol)) {
        return;
      }
      // Replay: watchlist/alerts above still update; chart series for this symbol stays frozen.
      if (barReplay.active && sameChartSymbol(symbol, barReplay.symbol)) {
        return;
      }
      if (tRecv != null) {
        this.lastLatency = {
          exchange_ts_ms: quote.exchange_ts_ms || null,
          server_ms: serverMs || quote.ts_ms || null,
          browser_recv: Math.round(tRecv),
        };
        this.lastAgeMs = quote.ts_ms ? Date.now() - Number(quote.ts_ms) : null;
      }
      var qSid = sid;
      var matched = false;
      for (var guid in this.listeners) {
        var lis = this.listeners[guid];
        var sameName = String(lis.symbol).toUpperCase() === String(symbol).toUpperCase();
        var sameSid = false;
        if (!sameName && qSid) {
          // Exact aliases only — never /BANK/ (that matched PSU BANK / PRIVATE BANK).
          var lisU = String(lis.symbol).toUpperCase().trim();
          if (qSid === "25" && (lisU === "BANK" || lisU === "BANK NIFTY" || lisU === "BANKNIFTY")) {
            sameSid = true;
          }
          if (
            qSid === "27" &&
            (lisU === "FIN NIFTY" || lisU === "FINNIFTY" || lisU === "FINANCIAL SERVICES")
          ) {
            sameSid = true;
          }
        }
        if (!sameName && !sameSid) {
          // MTARTECH-BE listener receives MTARTECH quote (and reverse).
          var quoteU = String(symbol || "")
            .toUpperCase()
            .trim();
          var lisU2 = String(lis.symbol || "")
            .toUpperCase()
            .trim();
          if (
            quoteU &&
            lisU2 &&
            (seriesSuffixAliases(quoteU).indexOf(lisU2) >= 0 ||
              seriesSuffixAliases(lisU2).indexOf(quoteU) >= 0)
          ) {
            sameSid = true;
          }
        }
        if (!sameName && !sameSid && sameRelayGroup(symbol, lis.symbol)) sameSid = true;
        if (!sameName && !sameSid) {
          try {
            var wQ = optWireSymbol(symbol);
            var wL = optWireSymbol(lis.symbol);
            if (wQ && wL && /^OPT:/i.test(wQ) && wQ.toUpperCase() === wL.toUpperCase()) sameSid = true;
            if (!sameSid && qSid) {
              var lisWire = String(wL || "").replace(/^OPT:/i, "");
              if (lisWire && lisWire === qSid) sameSid = true;
            }
          } catch (_) {}
        }
        if (!sameName && !sameSid) continue;
        // Replay owns this symbol's chart — do not queue live LTP jobs (watchlist still updates above).
        if (barReplayBlocksLive(lis.symbol, lis.resolution)) continue;
        matched = true;
        var prevJob = self._pendingChart[guid];
        var bucketNow = floorBarTimeMs(
          quote && Number(quote.ts_ms) > 0 ? Number(quote.ts_ms) : Date.now(),
          lis.resolution,
          lis.symbol
        );
        var edge0 = tvEdge[subKey(lis.symbol, lis.resolution)];
        var newBucket = !edge0 || Number(edge0.time) !== bucketNow;
        self._pendingChart[guid] = {
          lis: lis,
          symbol: lis.symbol,
          ltp: ltp,
          quote: Object.assign({}, quote, { symbol: lis.symbol, _tRecv: tRecv }),
          hi: prevJob ? Math.max(prevJob.hi || prevJob.ltp, prevJob.ltp) : 0,
          lo: prevJob ? Math.min(prevJob.lo || prevJob.ltp, prevJob.ltp) : 0,
          _newBucket: !!(prevJob && prevJob._newBucket) || newBucket,
        };
      }
      if (!matched && tRecv != null) {
        var symU = String(symbol || "").toUpperCase();
        var known = Object.keys(this.wanted || {}).some(function (w) {
          return String(w).toUpperCase() === symU;
        });
        if (!known) {
          if (!this._unmatchedLogged) this._unmatchedLogged = {};
          if (!this._unmatchedLogged[symU]) {
            this._unmatchedLogged[symU] = Date.now();
            lb("tick.unmatched", symbol, { wanted: Object.keys(this.wanted || {}).slice(0, 20) });
          }
        }
      }
      var flushNow = false;
      for (var pg in this._pendingChart) {
        if (this._pendingChart[pg] && this._pendingChart[pg]._newBucket) {
          flushNow = true;
          break;
        }
      }
      // Active chart: paint LTP without waiting on the coalesce timer so WL and candle match.
      if (!flushNow && matched && activeSymbol && sameChartSymbol(symbol, activeSymbol)) {
        flushNow = true;
      }
      this._scheduleChartFlush(flushNow);
    },
    poll: function () {
      var self = this;
      if (Date.now() < (this._testWsBlockUntil || 0)) return;
      var want = {};
      for (var guid in this.listeners) {
        want[this.listeners[guid].symbol] = true;
      }
      var symbols = Object.keys(want);
      if (!symbols.length) return;
      var symbol = symbols[0];
      fetch(TICKS_URL + "?symbol=" + encodeURIComponent(symbol), { credentials: "include", cache: "no-store" })
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          self.lastAgeMs = data && data.age_ms;
          if (data && data.quote) self.applyQuote(symbol, data.quote);
          if (data && data.quotes) {
            Object.keys(data.quotes).forEach(function (sym) {
              self.applyQuote(sym, data.quotes[sym]);
            });
          }
        })
        .catch(function () {});
    },
  };

  function prewarmTips() {
    var jobs = [];
    ["NIFTY 50", "BANK NIFTY", "CRUDE OIL", "SENSEX", "FIN NIFTY"].forEach(function (s) {
      ["5", "15"].forEach(function (r) {
        var k = subKey(s, r);
        if (!histCache[k] || !bridgeReady[k]) jobs.push([s, r]);
      });
    });
    var i = 0;
    function next() {
      if (i >= jobs.length) return;
      var j = jobs[i++];
      tipNet(j[0], j[1], "warm")
        .then(function (pack) {
          var bars = pack.bars || [];
          if (bars.length && bars[0].time < 1e12) {
            bars = stripOpenBucket(toTvBars(bars), j[1], j[0]);
          }
          if (bars.length) {
            persistTipBars(subKey(j[0], j[1]), bars);
            prefetchHistory(j[0], j[1], bars);
          }
        })
        .catch(function () {})
        .then(next);
    }
    next();
  }

  var datafeedSeq = 0;
  function createDatafeed() {
    var supported = ["5", "10", "15", "30", "45", "60", "240", "1D"];
    // TV reuses the same listenerGUID for the same symbol across widgets (e.g. PAYTM_#_regu).
    // Namespace by datafeed instance so multi-grid panes each keep their own callback.
    var dfId = "df" + ++datafeedSeq;
    log("datafeed", "tip + history + MarketFeed WS", dfId);
    function storeGuid(listenerGUID) {
      return dfId + "::" + String(listenerGUID || "");
    }
    return {
      onReady: function (cb) {
        setTimeout(function () {
          cb({
            supported_resolutions: supported,
            supports_marks: false,
            supports_timescale_marks: false,
            supports_time: true,
            exchanges: [
              { value: "", name: "All", desc: "All" },
              { value: "NSE", name: "NSE", desc: "NSE" },
              { value: "BSE", name: "BSE", desc: "BSE" },
              { value: "MCX", name: "MCX", desc: "MCX" },
            ],
            symbols_types: [
              { name: "All", value: "" },
              { name: "Index", value: "index" },
              { name: "Stock", value: "stock" },
              { name: "Futures", value: "futures" },
            ],
          });
        }, 0);
      },
      searchSymbols: function (userInput, exchange, symbolType, onResult) {
        var q = String(userInput || "").trim();
        if (!q) {
          onResult([]);
          return;
        }
        fetch("/api/search.php?q=" + encodeURIComponent(q), {
          credentials: "include",
          cache: "no-store",
        })
          .then(function (r) {
            return r.json();
          })
          .then(function (data) {
            var rows = (data && data.results) || [];
            var wantEx = String(exchange || "").toUpperCase();
            var wantType = String(symbolType || "").toLowerCase();
            var out = [];
            for (var i = 0; i < rows.length; i++) {
              var row = rows[i];
              var kind = String(row.kind || "stock").toLowerCase();
              var tvType =
                kind === "index" || kind === "sector"
                  ? "index"
                  : kind === "fno"
                    ? "futures"
                    : "stock";
              if (wantType && wantType !== tvType && wantType !== kind) continue;
              var sym = String(row.symbol || row.label || "").trim();
              if (!sym) continue;
              var ex =
                kind === "fno" && /CRUDE|MCX/i.test(sym)
                  ? "MCX"
                  : kind === "index" || kind === "sector"
                    ? "NSE"
                    : "NSE";
              if (wantEx && wantEx !== ex) continue;
              var item = {
                symbol: sym,
                full_name: ex + ":" + sym,
                description: String(row.label || sym),
                exchange: ex,
                ticker: sym,
                type: tvType,
              };
              if (row.logo_url) {
                rememberLogo(sym, row.logo_url);
                rememberLogo(row.label, row.logo_url);
              }
              var logos = logoUrlsForTv(sym) || logoUrlsForTv(row.label);
              if (logos) item.logo_urls = logos;
              out.push(item);
            }
            onResult(out);
          })
          .catch(function () {
            onResult([]);
          });
      },
      getTimescaleMarks: function (_symbolInfo, _from, _to, onDataCallback) {
        // Custom day circles stacked into the pane on 60m/zoomed ranges.
        // Rely on TV time axis + session_breaks for clean dates.
        onDataCallback([]);
      },
      resolveSymbol: function (symbolName, onResolve) {
        var name = String(symbolName || "").trim() || "NIFTY 50";
        var mcx = isMcxSymbol(name);
        var gift = /GIFT\s*NIFTY/i.test(name);
        var displayName = optDisplayLabel(name);
        var optLabel =
          (window.__BC_OPT_LABELS && (window.__BC_OPT_LABELS[name] || window.__BC_OPT_LABELS[displayName])) ||
          (isOptSymbol(name) ? displayName : null);
        if (optLabel && optLabel !== name && /^OPT:/i.test(name)) {
          // Chart legend + URL use the pretty strike name, not OPT:73889.
          name = optLabel;
        }
        var isCrudeAlias = /^CRUDE\s*OIL$/i.test(name);
        var desc = mcx
          ? (isCrudeAlias
              ? (window.__BC && window.__BC.crudeContract) || "CRUDEOIL OCT FUT"
              : name) + " · MCX"
          : gift
            ? name + " · NSE IX"
            : optLabel || name;
        // MCX energy: 09:00–23:30 IST weekdays. Use a real session (not 24x7) so
        // Charting Library collapses overnight — 23:30 abuts next 09:00 like TV.com.
        // Keep exchange id BC_MCX (not built-in MCX) so the library does not inject a
        // wrong cash/day calendar that remapped evening bars into the next "day".
        var sessionStr = mcx ? "0900-2330:23456" : gift ? "0630-2355:23456" : "0915-1530:23456";
        var sessionDisp = mcx ? "0900-2330" : gift ? "0630-2355" : "0915-1530";
        setTimeout(function () {
          var info = {
            name: name,
            ticker: name,
            description: desc,
            type: mcx ? "futures" : gift && /FUT/i.test(name) ? "futures" : optLabel ? "option" : "index",
            session: sessionStr,
            session_display: sessionDisp,
            timezone: "Asia/Kolkata",
            exchange: mcx
              ? "BC_MCX"
              : gift
                ? "NSE_IX"
              : optLabel
                ? /SENSEX/i.test(String(desc))
                  ? "BSE_FNO"
                  : "NSE_FNO"
                : "NSE",
            listed_exchange: mcx
              ? "MCX"
              : gift
                ? "NSE IX"
              : optLabel
                ? /SENSEX/i.test(String(desc))
                  ? "BSE_FNO"
                  : "NSE_FNO"
                : "NSE",
            minmov: 1,
            pricescale: 100,
            has_intraday: true,
            has_daily: true,
            // No-trade minutes stay empty (broker behaviour); library must not invent them.
            has_empty_bars: false,
            supported_resolutions: supported,
            intraday_multipliers: ["5", "10", "15", "30", "45", "60", "240"],
            daily_multipliers: ["1"],
            volume_precision: 0,
            // Required for Volume study histogram — without ohlcv, TV treats series as price-only.
            visible_plots_set: "ohlcv",
            has_volume: true,
            data_status: "streaming",
            format: "price",
          };
          var logoUrls = logoUrlsForTv(name);
          if (logoUrls) info.logo_urls = logoUrls;
          if (mcx) {
            info.subsession_id = "regular";
            info.subsessions = [
              {
                id: "regular",
                description: "MCX Energy (09:00-23:30 IST)",
                session: sessionStr,
                "session-display": sessionDisp,
              },
            ];
          } else {
            info.subsession_id = "regular";
            info.subsessions = [
              {
                id: "regular",
                description: "Regular Trading Hours",
                session: sessionStr,
                "session-display": sessionDisp,
              },
            ];
          }
          lb("tv.resolve", name, {
            type: info.type,
            session: info.session,
            exchange: info.exchange,
            mcx: !!mcx,
          });
          onResolve(info);
        }, 0);
      },
      getBars: function (symbolInfo, resolution, periodParams, onResult, onError) {
        var symbol = (symbolInfo && (symbolInfo.ticker || symbolInfo.name)) || activeSymbol;
        var isFirstRequest = !!(periodParams && periodParams.firstDataRequest);
        var cacheKey = subKey(symbol, resolution);
        var tvFirstReq = isFirstRequest;
        if (tvFirstReq) getBarsPending[cacheKey] = Date.now();
        var onResultTv = onResult;
        var onErrorTv = onError;
        onResult = function (bars, meta) {
          bars = dropFutureBars(cacheKey, symbol, resolution, bars);
          noteTvHistory(cacheKey, bars, tvFirstReq);
          onResultTv(bars, meta);
          if (!tvFirstReq) return;
          delete optLiveBars[cacheKey];
          if (!paintCommittedAt[cacheKey]) markPaintCommitted(cacheKey, symbol, resolution);
          // Never reseed live LTP onto a replay cut — that jumped the viewport.
          if (!barReplay.active) TickHub.scheduleReseed(cacheKey);
        };
        onError = function (reason) {
          if (tvFirstReq) delete getBarsPending[cacheKey];
          if (typeof onErrorTv === "function") onErrorTv(reason);
          else onResult([], { noData: true });
        };
        var cached = histCache[cacheKey];
        if (!isFirstRequest && !(cached && cached.length)) {
          isFirstRequest = true;
        }
        // Bar replay: serve the truncated series from MEM only — never tipNet/hist.
        // Tip refresh was wiping the cut, leaving 1–2 fat candles and Play stuck at N/N.
        if (
          barReplay.active &&
          sameChartSymbol(symbol, barReplay.symbol) &&
          String(resolution) === String(barReplay.resolution)
        ) {
          // Always paint from barReplay.bars prefix — histCache may have been polluted by tip/5m rebuild.
          barReplayApplyTruncateToCache();
          var replayView = (barReplay.bars || []).slice(0, Math.max(1, barReplay.index + 1));
          if (!replayView.length) {
            onResult([], { noData: true });
            return;
          }
          if (isFirstRequest) {
            var paintN = Math.min(replayView.length, 3000);
            var paint = replayView.slice(-paintN);
            onResult(paint, { noData: false });
            try {
              barReplaySyncEdges();
              barReplayClearPendingLive();
              barReplayApplyTruncateToCache();
              barReplayScheduleRestoreView();
            } catch (_) {}
          } else {
            var fromMsR = (periodParams.from || 0) * 1000;
            var toMsR = (periodParams.to || 0) * 1000;
            if (!(toMsR > 0)) toMsR = Date.now();
            if (!(fromMsR > 0)) fromMsR = toMsR - 10 * 86400000;
            var page = replayView.filter(function (b) {
              var t = Number(b.time);
              if (!(t > 0)) return false;
              if (t < 1e12) t *= 1000;
              return t >= fromMsR && t <= toMsR;
            });
            onResult(page, { noData: !page.length });
          }
          return;
        }
        if (isFirstRequest) {
          barsRequestGen = symbolSwitchGen;
          frontTrace("bars.request", {
            requested_symbol: symbol,
            requested_resolution: String(resolution),
            cached_bars: cached ? cached.length : 0,
            from: periodParams && periodParams.from,
            to: periodParams && periodParams.to,
          });
        }

        /** Tip miss / DEMO / error → history API only (never synthetic / stagnant paint). */
        function paintFromHistory(reason) {
          var toMs = Date.now();
          var fromMs = toMs - 21 * 86400000;
          lb("hist.fallback", symbol, {
            reason: String(reason || "tip_empty"),
            resolution: String(resolution),
          });
          histNet(symbol, resolution, fromMs, toMs)
            .then(function (pack) {
              var bars = (pack && pack.bars) || [];
              if (bars.length) {
                finish(pack, "hist");
                return;
              }
              // Options with no hist: never show TradingView "No data here" — seed from LTP.
              if (/^OPT:/i.test(String(symbol || ""))) {
                var hint = 0;
                try {
                  var qq = TickHub.lastQuotes[symbol];
                  if (qq && Number(qq.ltp) > 0) hint = Number(qq.ltp);
                } catch (_) {}
                var seed = null;
                try {
                  var ol = hint > 0 ? hint : 0.05;
                  var bkt = lastSessionBuckets(resolution, symbol);
                  seed = [
                    {
                      time: bkt.openBucket,
                      open: ol,
                      high: ol,
                      low: ol,
                      close: ol,
                      volume: 1,
                    },
                  ];
                  if (bkt.curBucket !== bkt.openBucket) {
                    seed.push({
                      time: bkt.curBucket,
                      open: ol,
                      high: ol,
                      low: ol,
                      close: ol,
                      volume: 1,
                    });
                  }
                } catch (_) {}
                if (seed && seed.length) {
                  ocLog("paint.opt-seed", {
                    symbol: symbol,
                    bars: seed.length,
                    openBucket: seed[0].time,
                    curBucket: seed[seed.length - 1].time,
                    reason: String(reason || "hist_empty"),
                  });
                  finish({ bars: seed, cache: "OPT-SEED", client_ms: 0 }, "opt-seed");
                  return;
                }
              }
              ocLog("noData", { symbol: symbol, reason: String(reason || "hist_empty"), stage: "fallback" });
              onResult([], { noData: true });
            })
            .catch(function (err) {
              log("hist.fallback.err", err);
              lb("hist.err", String(err && err.message ? err.message : err), { mode: "fallback" });
              if (/^OPT:/i.test(String(symbol || ""))) {
                try {
                  var bktE = lastSessionBuckets(resolution, symbol);
                  ocLog("paint.opt-seed", {
                    symbol: symbol,
                    bars: 1,
                    openBucket: bktE.openBucket,
                    reason: "hist_err",
                  });
                  finish(
                    {
                      bars: [
                        {
                          time: bktE.curBucket,
                          open: 0.05,
                          high: 0.05,
                          low: 0.05,
                          close: 0.05,
                          volume: 1,
                        },
                      ],
                      cache: "OPT-SEED",
                      client_ms: 0,
                    },
                    "opt-seed"
                  );
                } catch (_) {
                  finish(
                    {
                      bars: [
                        {
                          time: Date.now(),
                          open: 0.05,
                          high: 0.05,
                          low: 0.05,
                          close: 0.05,
                          volume: 1,
                        },
                      ],
                      cache: "OPT-SEED",
                      client_ms: 0,
                    },
                    "opt-seed"
                  );
                }
                return;
              }
              ocLog("noData", {
                symbol: symbol,
                reason: String(err && err.message ? err.message : err),
                stage: "fallback_err",
              });
              onResult([], { noData: true });
            });
        }

        function finish(pack, mode) {
          // tipNet/histNet already return TV bars (ms) aggregated to the request TF.
          var tvBars = pack.bars || [];
          var cacheLabel = String((pack && pack.cache) || "");
          // Current API uses FILE-STALE / REDIS-STALE for valid cached Dhan OHLC.
          // Preserve the v0.5.95 flow, but reject only invented fallback candles.
          if (/\bDEMO\b/i.test(cacheLabel) || /\bSYNTH/i.test(cacheLabel)) {
            tvBars = [];
          }
          if (tvBars.length) {
            tvBars = commitPaintBars(tvBars, symbol, resolution);
            tvBars = barReplayApply(symbol, resolution, tvBars);
          }
          if (tvBars.length) {
            if (barReplay.active && sameChartSymbol(symbol, barReplay.symbol)) {
              // Never replace MEM with tip during replay — keep the cut prefix only.
              if (String(resolution) === String(barReplay.resolution)) {
                barReplayApplyTruncateToCache();
              }
              netPaintReady[cacheKey] = symbolSwitchGen;
            } else if (isFirstRequest) {
              histCache[cacheKey] = tightEdge(histCache[cacheKey], tvBars);
              storeCachedBars(cacheKey);
              netPaintReady[cacheKey] = symbolSwitchGen;
            } else {
              persistTipBars(cacheKey, tvBars);
            }
          }
          if (isFirstRequest) {
            if (!tvBars.length) {
              if (mode !== "hist") {
                paintFromHistory(cacheLabel || "tip_empty");
                return;
              }
              ocLog("noData", {
                symbol: symbol,
                reason: cacheLabel || "empty_finish",
                stage: "finish",
                mode: mode || "",
              });
              onResult([], { noData: true });
              return;
            }
            // Never first-paint a holey live series — TV cements mid-session gaps.
            // Stitch tip-cold + today, then one dense paint (or best-effort once).
            var stitchDone = mode === "hist-today" || mode === "dense-force";
            if (
              inSessionNow(symbol) &&
              resMinutes(resolution) < 1440 &&
              !sessionTipSolid(tvBars, symbol, resolution) &&
              !stitchDone
            ) {
              lb("paint.hold.dense", symbol, {
                cache: pack.cache || "",
                today: todayBarCount(tvBars, symbol),
                holes: findIntradayHoles(tvBars, resolution, symbol).length,
                mode: mode || "",
                n: tvBars.length,
              });
              histCache[cacheKey] = mergeBars(histCache[cacheKey] || [], tvBars);
              storeCachedBars(cacheKey);
              var stitchPainted = false;
              var afterStitch = function () {
                if (stitchPainted) return;
                stitchPainted = true;
                var filled =
                  barsFromFivePool(symbol, resolution) || histCache[cacheKey] || tvBars;
                if (sessionTipSolid(filled, symbol, resolution)) {
                  finish(
                    {
                      bars: filled,
                      cache: (pack.cache || "TIP") + "+TODAY",
                      client_ms: pack.client_ms,
                    },
                    "hist-today"
                  );
                  return;
                }
                solidFirstPaint[cacheKey] = false;
                sparseFirstPaint[cacheKey] = true;
                finish(
                  {
                    bars: filled && filled.length ? filled : tvBars,
                    cache: (pack.cache || "TIP") + "+BEST",
                    client_ms: pack.client_ms,
                  },
                  "dense-force"
                );
                resyncSameSessionTipGap(symbol, resolution, "paint.dense_force");
              };
              var stitchP =
                mode === "tip-cold"
                  ? fillTodaySession(symbol, resolution, { silent: true })
                  : tipNet(symbol, resolution, "cold")
                      .then(function (coldPack) {
                        var coldRaw = (coldPack && coldPack.bars) || [];
                        if (coldRaw.length) {
                          var coldTv =
                            coldRaw[0].time < 1e12
                              ? stripOpenBucket(toTvBars(coldRaw), resolution, symbol)
                              : stripOpenBucket(coldRaw, resolution, symbol);
                          if (coldTv.length) {
                            histCache[cacheKey] = mergeBars(histCache[cacheKey] || [], coldTv);
                            if (sessionTipSolid(coldTv, symbol, resolution)) {
                              stitchPainted = true;
                              finish(
                                {
                                  bars: coldTv,
                                  cache: (coldPack && coldPack.cache) || "TIP-COLD",
                                  client_ms: pack.client_ms,
                                },
                                "tip-cold"
                              );
                              return null;
                            }
                          }
                        }
                        return fillTodaySession(symbol, resolution, { silent: true });
                      })
                      .catch(function () {
                        return fillTodaySession(symbol, resolution, { silent: true });
                      });
              // Cap stitch wait. If tip API is backing off, don't hold the chart.
              var stitchMs = Date.now() < tipBackoffUntil ? 200 : 1200;
              var stitchCap = setTimeout(function () {
                afterStitch();
              }, stitchMs);
              Promise.resolve(stitchP)
                .then(function () {
                  clearTimeout(stitchCap);
                  afterStitch();
                })
                .catch(function () {
                  clearTimeout(stitchCap);
                  afterStitch();
                });
              return;
            }
            setMetric("mCache", pack.cache || "—");
            markCandle({ cache: pack.cache });
            hideEmpty();
            lastHistoricalBar[cacheKey] = tvBars[tvBars.length - 1];
            seedBuilder(symbol, resolution, lastHistoricalBar[cacheKey]);
            // Full tip stays in MEM; paint up to FIRST_PAINT_BARS (~45d of 5m).
            // Only mark solid when live session tip is dense (no mid holes / edge ≤1 step).
            if (tvBars.length > 80) {
              var tipSpan = Number(tvBars[tvBars.length - 1].time) - Number(tvBars[0].time);
              if (tipSpan >= 20 * 86400000) {
                bridgeReady[cacheKey] = true;
                if (!inSessionNow(symbol) || sessionTipSolid(tvBars, symbol, resolution)) {
                  solidFirstPaint[cacheKey] = true;
                  sparseFirstPaint[cacheKey] = false;
                } else {
                  solidFirstPaint[cacheKey] = false;
                  sparseFirstPaint[cacheKey] = true;
                }
              }
            }
            var painted =
              barReplay.active && String(symbol || "").toUpperCase() === String(barReplay.symbol || "").toUpperCase()
                ? tvBars
                : paintForRequest(tvBars, periodParams);
            var edge = barEdge(painted);
            var days = tradingDayLabels(tvBars);
            var dayCounts = tradingDayBarCounts(tvBars, resolution, symbol);
            frontTrace("bars.paint", {
              requested_symbol: symbol,
              requested_resolution: String(resolution),
              bars: painted.length,
              cache: pack.cache || "",
              source: mode || "cold",
              first_t: edge.first_t,
              last_t: edge.last_t,
            });
            if (/^OPT:/i.test(String(symbol || ""))) {
              ocLog("paint", {
                symbol: symbol,
                bars: painted.length,
                cache: pack.cache || "",
                source: mode || "cold",
                first_t: edge.first_t,
                last_t: edge.last_t,
                elapsed_ms: Math.round(performance.now() - switchT0),
              });
            }
            lb("tv.getBars.ok", symbol, {
              ms: pack.client_ms || Math.round(performance.now() - switchT0),
              n: painted.length,
              count: painted.length,
              first_t: edge.first_t,
              last_t: edge.last_t,
              first_date: days[0] || null,
              last_date: days.length ? days[days.length - 1] : null,
              days: days.slice(0, 14),
              cache: pack.cache || "",
              resolution: String(resolution),
              mode: mode || "cold",
            });
            lb("candle.audit", symbol, {
              n: tvBars.length,
              painted: painted.length,
              cache: pack.cache || "",
              resolution: String(resolution),
              days: days.slice(-30),
              tip_days: days.slice(-3),
              tip_depth: days.length,
              tip_ok: days.length >= 3 || (resMinutes(resolution) >= 1440 && days.length >= 1),
              tip_30d_ok: days.length >= 15,
              day_counts: dayCounts,
              mode: mode || "cold",
            });
            emitCandleGapAudit(symbol, resolution, tvBars, pack.cache);
            emitPrefetch(symbol, tvBars, pack.cache, pack.client_ms);
            onResult(painted, { noData: false });
            markPaintCommitted(cacheKey, symbol, resolution);
            zoomToRecentSessions(tvBars);
            if (barReplay.active) {
              try {
                barReplaySyncEdges();
                barReplayClearPendingLive();
              } catch (_) {}
              return;
            }
            // Tip hot shelf (~45d): solid only when dense. Holey tip stays sparse so
            // edge healer can hard-reload once MEM fills the painted gap.
            var tipDaysN = days.length;
            var tipDeep =
              tipDaysN >= 15 ||
              (tvBars.length > 80 &&
                Number(tvBars[tvBars.length - 1].time) - Number(tvBars[0].time) >= 20 * 86400000);
            if (
              tipDeep ||
              (poolIsContinuous(histCache[cacheKey] || tvBars) &&
                poolHasEnoughToday(histCache[cacheKey] || tvBars, symbol, resolution))
            ) {
              var deepPool = histCache[cacheKey] || tvBars;
              var deepDense =
                !inSessionNow(symbol) ||
                resMinutes(resolution) >= 1440 ||
                sessionTipSolid(deepPool, symbol, resolution);
              bridgeReady[cacheKey] = true;
              if (deepDense) {
                solidFirstPaint[cacheKey] = true;
                sparseFirstPaint[cacheKey] = false;
              } else {
                solidFirstPaint[cacheKey] = false;
                sparseFirstPaint[cacheKey] = true;
              }
              prefetchHistory(symbol, resolution, tvBars);
              if (!deepDense && inSessionNow(symbol) && resMinutes(resolution) < 1440) {
                resyncSameSessionTipGap(symbol, resolution, "paint.tip.holes");
              }
              return;
            }
            sparseFirstPaint[cacheKey] = true;
            solidFirstPaint[cacheKey] = false;
            // Options: never hist.bridge storm after sparse tip — already painted.
            if (/^OPT:/i.test(String(symbol || ""))) {
              bridgeReady[cacheKey] = true;
              solidFirstPaint[cacheKey] = true;
              sparseFirstPaint[cacheKey] = false;
              prefetchHistory(symbol, resolution, tvBars);
              return;
            }
            loadHistoryBridge(symbol, resolution, tvBars)
              .then(function () {
                return backfillHoles(symbol, resolution);
              })
              .then(function (left) {
                if (left === 0) {
                  resetChartDataSoft(cacheKey);
                }
                prefetchHistory(symbol, resolution, tvBars);
              })
              .catch(function () {
                prefetchHistory(symbol, resolution, tvBars);
              });
            return;
          }
          if (!tvBars.length) {
            var spanDays = ((periodParams.to || 0) - (periodParams.from || 0)) / 86400;
            onResult([], { noData: spanDays > 12 });
            return;
          }
          onResult(tvBars, { noData: false });
        }

        function refreshAfterPaint(painted) {
          var gen = symbolSwitchGen;
          tipNet(symbol, resolution, "warm")
            .then(function (pack) {
              if (gen !== symbolSwitchGen) return;
              // Replay owns MEM — never tip-extend past the cut after paint.
              if (barReplay.active && sameChartSymbol(symbol, barReplay.symbol)) {
                barReplayApplyTruncateToCache();
                return;
              }
              var fresh = pack.bars || [];
              if (fresh.length && fresh[0].time < 1e12) fresh = toTvBars(fresh);
              fresh = commitPaintBars(fresh, symbol, resolution);
              if (!fresh.length) return;
              histCache[cacheKey] = tightEdge(histCache[cacheKey], fresh);
              storeCachedBars(cacheKey);
              emitPrefetch(symbol, fresh, pack.cache, pack.client_ms);
              var current = barsFromFivePool(symbol, resolution);
              if (!current || !current.length) current = histCache[cacheKey];
              var differs = latestDayDiffers(painted, current);
              var closedDiffers = todayClosedOhlcDiffers(painted, current, symbol, resolution);
              frontTrace("cache.edge.verify", {
                key: cacheKey,
                cache: pack.cache || "",
                differs: differs,
                closed_differs: closedDiffers,
                painted_last: painted && painted.length ? painted[painted.length - 1].time : null,
                fresh_last: current && current.length ? current[current.length - 1].time : null,
              });
              if (!differs || !current.length) return;
              lastHistoricalBar[cacheKey] = current[current.length - 1];
              seedBuilder(symbol, resolution, lastHistoricalBar[cacheKey]);
              var edge = current[current.length - 1];
              var paintedLast = painted && painted.length ? painted[painted.length - 1] : null;
              var stepMsEdge = resMinutes(resolution) * 60 * 1000;
              var edgeAdvance =
                paintedLast && edge
                  ? Number(edge.time) - Number(paintedLast.time)
                  : 0;
              var paintedToday = todayBarCount(painted, symbol);
              var currentToday = todayBarCount(current, symbol);
              var missingToday = currentToday > paintedToday + 1;
              // Closed-bar OHLC heal must reset — onTick only widens the forming bar.
              if (closedDiffers || missingToday) {
                forceEdgeReset(
                  cacheKey,
                  gen,
                  closedDiffers ? "tip.today_ohlc_heal" : "tip.today_backfill"
                );
                return;
              }
              // Quiet patch only for small right-edge moves (newer forming bucket).
              var quietPatch =
                edgeAdvance > 0 &&
                edgeAdvance <= stepMsEdge * 2.5 &&
                (inPaintGrace(cacheKey) || inSessionNow(symbol));
              if (quietPatch) {
                for (var guid in realtimeSubscribers) {
                  var sub = realtimeSubscribers[guid];
                  if (subKey(sub.symbolInfo.ticker, sub.resolution) !== cacheKey) continue;
                  try {
                    sub.callback(edge);
                  } catch (_) {}
                }
                lb("paint.grace.quiet_edge", symbol, {
                  resolution: String(resolution),
                  last_t: edge && edge.time,
                  live_patch: !inPaintGrace(cacheKey),
                });
                return;
              }
              forceEdgeReset(cacheKey, gen, "tip.refresh");
            })
            .catch(function () {});
        }

        function paintLocal(bars, src) {
          var rawLocal = bars || [];
          bars = commitPaintBars(rawLocal, symbol, resolution);
          if (!bars.length) {
            // Prefer LTP forming seed over waiting on tip (often 504 under load).
            try {
              var seedLocal = quoteSeedBars(symbol, resolution, 0);
              if (seedLocal && seedLocal.length) {
                bars = commitPaintBars(seedLocal, symbol, resolution);
              }
            } catch (_) {}
            if (!bars.length) {
              paintFromHistory("paintLocal_empty");
              return;
            }
            src = (src || "MEM") + "+SEED";
          }
          var continuous = poolIsContinuous(bars);
          var dense = poolIsSessionDense(bars, resolution, symbol);
          var tipDaysLocal = tradingDayLabels(bars).length;
          var tipDeepLocal =
            tipDaysLocal >= 15 ||
            (bars.length > 80 &&
              Number(bars[bars.length - 1].time) - Number(bars[0].time) >= 20 * 86400000);
          if (!continuous && bars.length >= 40) {
            sparseFirstPaint[cacheKey] = true;
            solidFirstPaint[cacheKey] = false;
            log(
              "paintLocal.sparse",
              src,
              "holes=" + findBarHoles(bars, 4 * 86400000).length,
              "intra=" + findIntradayHoles(bars, resolution, symbol).length
            );
          } else if (tipDeepLocal || (continuous && poolHasEnoughToday(bars, symbol, resolution))) {
            // Deep tip: solid only when live session is dense (no mid holes / lag ≤1).
            bridgeReady[cacheKey] = true;
            if (
              !inSessionNow(symbol) ||
              resMinutes(resolution) >= 1440 ||
              sessionTipSolid(bars, symbol, resolution)
            ) {
              solidFirstPaint[cacheKey] = true;
              sparseFirstPaint[cacheKey] = false;
            } else {
              solidFirstPaint[cacheKey] = false;
              sparseFirstPaint[cacheKey] = true;
            }
          } else if (continuous) {
            // Continuous history but thin today → allow today-fill soft reset.
            sparseFirstPaint[cacheKey] = true;
            solidFirstPaint[cacheKey] = false;
            bridgeReady[cacheKey] = true;
          }
          replaceTipBars(cacheKey, bars);
          netPaintReady[cacheKey] = symbolSwitchGen;
          lastHistoricalBar[cacheKey] = bars[bars.length - 1];
          seedBuilder(symbol, resolution, lastHistoricalBar[cacheKey]);
          setMetric("mCache", src);
          markCandle({ cache: src, local: true });
          hideEmpty();
          var painted = paintForRequest(bars, periodParams);
          var edge = barEdge(painted);
          var days = tradingDayLabels(bars);
          lb("boot.paint", src, { n: painted.length, symbol: String(symbol), resolution: String(resolution) });
          lb("tv.getBars.ok", symbol, {
            ms: Math.round(performance.now() - switchT0),
            n: painted.length,
            count: painted.length,
            first_t: edge.first_t,
            last_t: edge.last_t,
            first_date: days[0] || null,
            last_date: days.length ? days[days.length - 1] : null,
            days: days.slice(0, 14),
            cache: src,
            resolution: String(resolution),
            mode: "warm",
          });
          lb("candle.audit", symbol, {
            n: bars.length,
            painted: painted.length,
            cache: src,
            resolution: String(resolution),
            days: days.slice(-30),
            tip_days: days.slice(-3),
            tip_depth: days.length,
            tip_ok: days.length >= 3 || (resMinutes(resolution) >= 1440 && days.length >= 1),
            tip_30d_ok: days.length >= 15,
            day_counts: tradingDayBarCounts(bars, resolution, symbol),
            mode: "warm",
          });
          var paintedDense = emitCandleGapAudit(symbol, resolution, painted, src);
          emitCandleGapAudit(symbol, resolution, bars, src);
          emitPrefetch(symbol, bars, src, 1);
          onResult(painted, { noData: false });
          tvPaintToday[cacheKey] = todayBarCount(bars, symbol);
          markPaintCommitted(cacheKey, symbol, resolution);
          zoomToRecentSessions(bars);
          if (
            inSessionNow(symbol) &&
            resMinutes(resolution) < 1440 &&
            !paintedDense
          ) {
            resyncSameSessionTipGap(symbol, resolution, "paint.local.holes");
          }
          var tipSlice = painted;
          // Deep tip: one paint only — skip bridge/reset flicker.
          // Do not refreshAfterPaint→forceEdgeReset on solid tip (weekend blink).
          // Mid-session exception: stale MEM / intraday holes / tip lag behind "now".
          if (solidFirstPaint[cacheKey]) {
            prefetchHistory(symbol, resolution, tipSlice);
            if (inSessionNow(symbol) && resMinutes(resolution) < 1440) {
              var solidHoles = findIntradayHoles(bars, resolution, symbol).length;
              var solidLag =
                (function () {
                  var last = barLastTimeMs(bars);
                  var step = resMinutes(resolution) * 60 * 1000;
                  return last > 0 && Date.now() - last > Math.max(step * 3, 15 * 60 * 1000);
                })();
              if (
                solidHoles > 0 ||
                solidLag ||
                !memEdgeTrusted(bars, symbol, resolution) ||
                !poolHasEnoughToday(bars, symbol, resolution)
              ) {
                if (solidHoles > 0 || solidLag) {
                  resyncSameSessionTipGap(symbol, resolution, "paint.solid.holes");
                } else {
                  refreshAfterPaint(painted);
                }
              }
            }
            return;
          }
          if (!continuous) sparseFirstPaint[cacheKey] = true;
          // Bridge deepens scroll in background; resetData only if tip-sparse first paint.
          loadHistoryBridge(symbol, resolution, tipSlice)
            .then(function () {
              return backfillHoles(symbol, resolution);
            })
            .then(function (left) {
              if (left === 0) {
                resetChartDataSoft(cacheKey);
              }
              prefetchHistory(symbol, resolution, tipSlice);
            })
            .catch(function () {
              prefetchHistory(symbol, resolution, tipSlice);
            });
          refreshAfterPaint(painted);
        }

        if (isFirstRequest) {
          // TV contract: first paint prefers continuous MEM / 5m→AGG pool (no tip→Sep11 holes).
          // Tip network only when the shared pool is cold.
          function paintFromNetwork() {
            var hand = switchHandoff;
            switchHandoff = null;
            // Symbol switch staged bars — only when they already cover today's session.
            var handReady =
              hand &&
              hand.bars.length &&
              (!inSessionNow(symbol) ||
                resMinutes(resolution) >= 1440 ||
                coversLiveSession(hand.bars, symbol, resolution));
            if (
              hand &&
              hand.key === cacheKey &&
              hand.gen === symbolSwitchGen &&
              Date.now() - hand.at < 15000 &&
              handReady
            ) {
              lb("tv.getBars.handoff", symbol, {
                n: hand.bars.length,
                cache: hand.cache,
                dense: sessionTipSolid(hand.bars, symbol, resolution),
                resolution: String(resolution),
              });
              paintTipPack({ bars: hand.bars.slice(), cache: hand.cache, client_ms: 0 });
              return;
            }
            // Instant MEM only when it includes today's candles. A 1-bar seed or
            // an opening-bar shelf stays off the chart until tip/history arrives.
            var memInstant =
              histCache[cacheKey] || barsFromFivePool(symbol, resolution) || [];
            var memReady =
              !inSessionNow(symbol) || resMinutes(resolution) >= 1440
                ? memInstant.length >= 1
                : coversLiveSession(memInstant, symbol, resolution);
            if (memReady) {
              lb("tv.getBars.mem_instant", symbol, {
                n: memInstant.length,
                resolution: String(resolution),
              });
              paintTipPack({
                bars: memInstant.slice(),
                cache: "MEM-INSTANT",
                client_ms: 0,
              });
              return;
            }
            // Cold click: race tip vs history. Keep a thin pack only as fallback
            // so the opening candle is not what TradingView commits.
            var raced = false;
            var pendingThin = null;
            function packBars(pack) {
              var bars = (pack && pack.bars) || [];
              if (bars.length && bars[0].time < 1e12) bars = toTvBars(bars);
              return bars;
            }
            function acceptFirst(pack, src) {
              if (raced) return;
              var bars = packBars(pack);
              if (!bars.length) return;
              if (
                inSessionNow(symbol) &&
                resMinutes(resolution) < 1440 &&
                !coversLiveSession(bars, symbol, resolution)
              ) {
                var thinToday = todayBarCount(bars, symbol);
                var heldToday = pendingThin ? todayBarCount(pendingThin.bars, symbol) : -1;
                if (!pendingThin || thinToday > heldToday || bars.length > pendingThin.bars.length) {
                  pendingThin = {
                    bars: bars,
                    cache: (pack && pack.cache) || src,
                    client_ms: pack && pack.client_ms,
                  };
                }
                return;
              }
              raced = true;
              paintTipPack({
                bars: bars,
                cache: (pack && pack.cache) || src,
                client_ms: pack && pack.client_ms,
              });
            }
            var clickTipP = null;
            try {
              if (
                switchOpenTip &&
                switchOpenTip.key === cacheKey &&
                switchOpenTip.p &&
                Date.now() - switchOpenTip.at < 20000
              ) {
                clickTipP = switchOpenTip.p;
              }
            } catch (_) {}
            if (!clickTipP) {
              try {
                var pfClick = tipPrefetch[cacheKey];
                if (pfClick && Date.now() - pfClick.at < 20000) clickTipP = pfClick.p;
              } catch (_) {}
            }
            if (!clickTipP) clickTipP = tipNet(symbol, resolution, "warm");
            clickTipP
              .then(function (pack) {
                acceptFirst(pack, "TIP");
              })
              .catch(function () {});
            histNet(symbol, resolution, Date.now() - 21 * 86400000, Date.now(), 2500)
              .then(function (pack) {
                acceptFirst(pack, (pack && pack.cache) || "HIST");
              })
              .catch(function () {});
            setTimeout(function () {
              if (raced) return;
              if (pendingThin && pendingThin.bars.length) {
                raced = true;
                lb("tv.getBars.thin_fallback", symbol, {
                  n: pendingThin.bars.length,
                  today: todayBarCount(pendingThin.bars, symbol),
                });
                paintTipPack(pendingThin);
                return;
              }
              raced = true;
              log("tip.err", "race_timeout");
              lb("tip.err", "race_timeout", {});
              paintFromHistory("race_timeout");
            }, 1600);
          }
          function deepenBarsAfterPaint(symbol, resolution, cacheKey) {
            var paintedBefore = (histCache[cacheKey] || []).slice();
            // Tip load opt: deepen via history.php only when MEM already has a usable shelf.
            // Tip only for truly thin first paints (avoids tip storms after every switch).
            var tipP =
              paintedBefore.length >= 15
                ? Promise.resolve(null)
                : tipNet(symbol, resolution, "deepen").catch(function () {
                    return null;
                  });
            var histP = histNet(symbol, resolution, Date.now() - 21 * 86400000, Date.now(), 3000).catch(function () {
              return null;
            });
            Promise.all([tipP, histP]).then(function (both) {
              var tipPack = both[0];
              var histPack = both[1];
              var bars = [];
              var cache = "";
              if (tipPack && tipPack.bars && tipPack.bars.length >= 8) {
                bars = tipPack.bars;
                cache = tipPack.cache || "TIP";
              } else if (histPack && histPack.bars && histPack.bars.length) {
                bars = histPack.bars;
                cache = histPack.cache || "HIST";
              }
              if (!bars.length) return;
              if (bars[0].time < 1e12) bars = toTvBars(bars);
              bars = commitPaintBars(bars, symbol, resolution);
              if (bars.length < 8) return;
              var before = paintedBefore.length;
              var beforeHoles = findIntradayHoles(paintedBefore, resolution, symbol).length;
              var afterHoles = findIntradayHoles(bars, resolution, symbol).length;
              // Only hard-reload when closed-bar structure actually healed — not for open-bucket OHLC.
              var heals =
                before < 15 ||
                (beforeHoles > 0 && afterHoles < beforeHoles) ||
                todayBarCount(bars, symbol) > todayBarCount(paintedBefore, symbol) + 5;
              histCache[cacheKey] = bars;
              storeCachedBars(cacheKey);
              lastHistoricalBar[cacheKey] = bars[bars.length - 1];
              setMetric("mCache", cache || "TIP");
              if (heals) {
                delete solidFirstPaint[cacheKey];
                sparseFirstPaint[cacheKey] = true;
                delete bridgeResetDone[cacheKey];
                forceHistoryReload(symbol, resolution, "deepen.after.paint");
              } else {
                reloadIfPaintedEdgeStale(symbol, resolution, paintedBefore, bars, "deepen.after.paint");
              }
            });
          }
          function paintTipPack(pack) {
                var bars = pack.bars || [];
                if (bars.length) {
                  // Merge tip onto warm pool only when same price regime (no SEP+OCT stitch).
                  var pooled = barsFromFivePool(symbol, resolution) || [];
                  if (
                    pooled.length > bars.length &&
                    samePriceRegime(pooled, bars) &&
                    poolIsContinuous(tightEdge(pooled, bars))
                  ) {
                    finish(
                      {
                        bars: tightEdge(pooled, bars),
                        cache: (pack.cache || "TIP") + "+POOL",
                        client_ms: pack.client_ms,
                      },
                      "tip+pool"
                    );
                    return;
                  }
                  // Stale IDB/MEM from prior contract → tip alone wins.
                  finish(pack, "cold");
                  return;
                }
                paintFromHistory("tip_empty");
          }

          var mem = histCache[cacheKey];
          // Options: paint any pre-seeded MEM immediately (bypass equity continuity gates).
          // Strict memOk was rejecting 1–2 LTP seed bars → network tip → "No data here" on OTM.
          if (
            /^OPT:/i.test(String(symbol || "")) &&
            mem &&
            mem.length &&
            netPaintReady[cacheKey] === symbolSwitchGen
          ) {
            bridgeReady[cacheKey] = true;
            solidFirstPaint[cacheKey] = true;
            paintLocal(mem, "OPT-MEM");
            ocLog("paint.opt-mem", {
              symbol: symbol,
              bars: mem.length,
              first_t: mem[0] && mem[0].time,
              last_t: mem[mem.length - 1] && mem[mem.length - 1].time,
              elapsed_ms: Math.round(performance.now() - switchT0),
            });
            if (optHealReset[cacheKey]) {
              delete optHealReset[cacheKey];
              return;
            }
            // Deepen quietly behind the seed paint.
            tipNet(symbol, resolution, "warm")
              .then(function (pack) {
                var bars = (pack && pack.bars) || [];
                if (!bars.length) {
                  return histNet(symbol, resolution, Date.now() - 60 * 86400000, Date.now());
                }
                return pack;
              })
              .then(function (pack) {
                if (!pack) return;
                var bars = pack.bars || [];
                if (!bars.length) return;
                if (bars[0].time < 1e12) bars = toTvBars(bars);
                bars = commitPaintBars(bars, symbol, resolution);
                if (bars.length < (mem.length || 0)) return;
                histCache[cacheKey] = bars;
                storeCachedBars(cacheKey);
                lastHistoricalBar[cacheKey] = bars[bars.length - 1];
                try {
                  if (widget && widget.activeChart && typeof widget.activeChart().resetData === "function") {
                    widget.activeChart().resetData();
                  }
                } catch (_) {}
              })
              .catch(function () {});
            return;
          }
          // Staged MEM: dense session only. A 1–2 bar LTP seed is not history —
          // getBars must wait for tip/history (Stock Hunter first paint).
          if (mem && mem.length && netPaintReady[cacheKey] === symbolSwitchGen) {
            var stagedDense = !inSessionNow(symbol)
              ? mem.length >= 1
              : sessionTipSolid(mem, symbol, resolution) &&
                coversLiveSession(mem, symbol, resolution);
            if (stagedDense) {
              bridgeReady[cacheKey] = true;
              solidFirstPaint[cacheKey] = mem.length >= 40;
              sparseFirstPaint[cacheKey] = !solidFirstPaint[cacheKey];
              paintLocal(mem, mem.length >= 40 ? "MEM" : "MEM-SOFT");
              return;
            }
            lb("paint.skip.staged", symbol, {
              reason: "not_dense",
              today: todayBarCount(mem, symbol),
              holes: findIntradayHoles(mem, resolution, symbol).length,
              bars: mem.length,
            });
            delete netPaintReady[cacheKey];
            delete solidFirstPaint[cacheKey];
          }
          var memOk =
            mem &&
            mem.length &&
            tipBarsFresh(mem) &&
            memEdgeTrusted(mem, symbol, resolution) &&
            poolIsContinuous(mem) &&
            sessionTipSolid(mem, symbol, resolution) &&
            barMatchesLtp(mem[mem.length - 1], symbol);
          if (memOk) {
            bridgeReady[cacheKey] = true;
            netPaintReady[cacheKey] = symbolSwitchGen;
            solidFirstPaint[cacheKey] = true;
            paintLocal(mem, "MEM");
            return;
          }

          // TF switch: reuse warm native pool only when dense in session.
          var fromPool = barsFromFivePool(symbol, resolution) || [];
          if (fromPool.length >= 40 && poolIsContinuous(fromPool)) {
            var poolFresh = tipBarsFresh(fromPool) || tipBarsSameSession(fromPool, symbol);
            var poolOk =
              !inSessionNow(symbol) || sessionTipSolid(fromPool, symbol, resolution);
            if (poolOk && (poolFresh || fromPool.length >= 120 || !inSessionNow(symbol))) {
              bridgeReady[cacheKey] = true;
              netPaintReady[cacheKey] = symbolSwitchGen;
              solidFirstPaint[cacheKey] = true;
              paintLocal(fromPool, "AGG5");
              prefetchHistory(symbol, resolution, fromPool.slice(-5));
              return;
            }
            if (inSessionNow(symbol) && !poolOk) {
              lb("paint.skip.pool", symbol, {
                reason: "not_dense",
                today: todayBarCount(fromPool, symbol),
                holes: findIntradayHoles(fromPool, resolution, symbol).length,
                resolution: String(resolution),
              });
            }
          }

          // SESS/IDB: mid-session only when dense (holey shelves caused gap flash).
          var sessBars = sessionGetTip(cacheKey);
          var sessOk =
            sessBars &&
            sessBars.length >= 15 &&
            (!inSessionNow(symbol) || sessionTipSolid(sessBars, symbol, resolution));
          if (sessOk || localTipPaintable(sessBars, symbol, resolution)) {
            if (!inSessionNow(symbol) || sessionTipSolid(sessBars, symbol, resolution)) {
              bridgeReady[cacheKey] = true;
              solidFirstPaint[cacheKey] = sessBars.length >= 40;
              sparseFirstPaint[cacheKey] = false;
              paintLocal(sessBars, sessBars.length >= 40 ? "SESS" : "SESS-SOFT");
              refreshAfterPaint(firstPaintSlice(histCache[cacheKey] || sessBars));
              return;
            }
          }
          idbGetTip(cacheKey).then(function (idbBars) {
            if (
              idbBars &&
              idbBars.length >= 15 &&
              (!inSessionNow(symbol) || sessionTipSolid(idbBars, symbol, resolution))
            ) {
              bridgeReady[cacheKey] = true;
              solidFirstPaint[cacheKey] = idbBars.length >= 40;
              sparseFirstPaint[cacheKey] = false;
              paintLocal(idbBars, idbBars.length >= 40 ? "IDB" : "IDB-SOFT");
              refreshAfterPaint(firstPaintSlice(histCache[cacheKey] || idbBars));
              return;
            }
            paintFromNetwork();
          });
          return;
        }

        // Scroll-left — serve continuous pool immediately (never wait on bridgeReady flag).
        var fromMs = (periodParams.from || 0) * 1000;
        var toMs = (periodParams.to || 0) * 1000;
        var pool = barsFromFivePool(symbol, resolution);
        if (!pool.length) pool = histCache[cacheKey] || cached || [];

        // Options: MEM first; older than the 7-session cache → broker history API via /api/history.php.
        // Never re-serve OPT-MEM-EDGE (TV left-scroll loop storm).
        if (/^OPT:/i.test(String(symbol || ""))) {
          var optSlice = slicePool(pool, fromMs, toMs);
          if (optSlice.length) {
            emitScrollPage(symbol, optSlice, "OPT-MEM");
            onResult(optSlice, { noData: false });
            return;
          }
          var optPage = function (pack) {
            var b = pack.bars || [];
            if (b.length && b[0].time < 1e12) b = stripOpenBucket(toTvBars(b), resolution, symbol);
            return b;
          };
          var optRetryable = function (pack) {
            return pack.cache === "HIST-TIMEOUT" || pack.cache === "OPT-API-ERR";
          };
          histNet(symbol, resolution, fromMs, toMs || Date.now(), 8000)
            .then(function (pack) {
              var bars = optPage(pack);
              if (bars.length || optRetryable(pack)) return { bars: bars, pack: pack };
              // Empty window (weekend / holiday) — look 10 days further back before calling it the start.
              return histNet(symbol, resolution, fromMs - 10 * 86400000, toMs || Date.now(), 8000).then(function (p2) {
                return { bars: optPage(p2), pack: p2 };
              });
            })
            .then(function (res) {
              if (res.bars.length) {
                persistTipBars(cacheKey, res.bars);
                emitScrollPage(symbol, res.bars, res.pack.cache || "OPT-HIST");
                onResult(res.bars, { noData: false });
                return;
              }
              onResult([], { noData: !optRetryable(res.pack) });
            })
            .catch(function () {
              onResult([], { noData: true });
            });
          return;
        }

        function respondScroll(bars, cacheLabel) {
          emitScrollPage(symbol, bars, cacheLabel);
          setMetric("mCache", cacheLabel || "MEM");
          onResult(bars, { noData: false });
          ensureHistoryLeft(symbol, resolution, fromMs);
        }

        function fetchScrollPage() {
          histNet(symbol, resolution, fromMs, toMs || Date.now())
            .then(function (pack) {
              var tvBars = pack.bars || [];
              if (tvBars.length && tvBars[0].time < 1e12) {
                tvBars = stripOpenBucket(toTvBars(tvBars), resolution, symbol);
              }
              if (tvBars.length) {
                persistTipBars(cacheKey, tvBars);
                var filled = slicePool(
                  barsFromFivePool(symbol, resolution) || histCache[cacheKey] || tvBars,
                  fromMs,
                  toMs
                );
                // Server bars are the truth for this window: a holey/partial MEM
                // slice must never override them (that cemented interior holes
                // into TV's series). MEM may answer alone only when it is
                // internally continuous AND reaches the window's right edge —
                // an internally-clean slice that stops mid-window (pool still
                // paging) is exactly the Aug 28 → Sep 30 RELIANCE hole.
                var stepMsPage = Math.max(60000, resMinutes(resolution) * 60000);
                var filledCovers =
                  filled.length > 0 &&
                  !findBarHoles(filled, 4 * 86400000).length &&
                  Number(filled[filled.length - 1].time) >=
                    (toMs || Date.now()) - stepMsPage * 2;
                var outBars = filledCovers
                  ? filled
                  : slicePool(mergeBars(filled, tvBars), fromMs, toMs);
                respondScroll(outBars.length ? outBars : tvBars, pack.cache || "HIST");
                // Interior hole (ask starts well inside the pool span): TV cannot
                // patch the middle of its series from a scroll response alone, so
                // rebuild once from the healed pool (throttled per symbol/TF).
                if (
                  pool.length &&
                  fromMs > Number(pool[0].time) + 86400000 &&
                  (!holeHealAt[cacheKey] || Date.now() - holeHealAt[cacheKey] > 300000)
                ) {
                  holeHealAt[cacheKey] = Date.now();
                  // Delay: resetting 60ms after the response raced TV's merge
                  // and discarded the just-healed page. Let TV settle first.
                  setTimeout(function () {
                    forceHistoryReload(symbol, resolution, "scroll.hole.heal");
                  }, 1500);
                }
                // Hole fill in background — do not block the pan response.
                backfillHoles(symbol, resolution).catch(function () {});
                return;
              }
              var spanDays = ((periodParams.to || 0) - (periodParams.from || 0)) / 86400;
              onResult([], { noData: spanDays > 5 });
            })
            .catch(function (err) {
              log("hist.err", err);
              lb("hist.err", String(err && err.message ? err.message : err), { mode: "scroll" });
              if (typeof onError === "function") onError(String(err && err.message ? err.message : err));
              else onResult([], { noData: true });
            });
        }

        var sliced = slicePool(pool, fromMs, toMs);
        // Fast path only when MEM fully covers the requested window. TV treats a
        // getBars response as the complete window: answering with a partial slice
        // (pool still paging in) makes TV mark the middle as loaded and it never
        // re-asks — the Aug 7 → Sep 30 RELIANCE hole. Partial coverage goes to
        // the server for the exact window instead.
        var stepMsScroll = Math.max(60000, resMinutes(resolution) * 60000);
        var sliceCovers =
          sliced.length > 0 &&
          !findBarHoles(sliced, 4 * 86400000).length &&
          Number(sliced[sliced.length - 1].time) >= (toMs || Date.now()) - stepMsScroll * 2;
        if (sliceCovers) {
          bridgeReady[cacheKey] = true;
          // MEM = already in browser after tip/PG prefetch (instant). Network shows WAREHOUSE-PG.
          respondScroll(sliced, "MEM");
          if (findBarHoles(pool, 4 * 86400000).length) {
            loadHistoryBridge(symbol, resolution, pool.slice(-5))
              .then(function () {
                return backfillHoles(symbol, resolution);
              })
              .catch(function () {});
          }
          return;
        }
        // Window not fully in MEM — fetch the exact page from the server (never
        // answer with older edge bars: TV would cement an interior hole).
        // Empty/partial slice — fetch page immediately (do not block on bridge).
        fetchScrollPage();
        loadHistoryBridge(symbol, resolution, pool.length ? pool.slice(-5) : null)
          .then(function () {
            return backfillHoles(symbol, resolution);
          })
          .catch(function () {});
      },
      subscribeBars: function (symbolInfo, resolution, onRealtimeCallback, listenerGUID, onResetCacheNeededCallback) {
        var ticker = (symbolInfo && (symbolInfo.ticker || symbolInfo.name)) || activeSymbol;
        frontTrace("live.subscribe", {
          requested_symbol: ticker,
          requested_resolution: String(resolution),
          listener: String(listenerGUID || ""),
        });
        var key = subKey(ticker, resolution);
        var sid = storeGuid(listenerGUID);
        var tvCb = function (bar, why) {
          // Hard mute live/seed while this symbol is in replay (even if why omitted).
          if (barReplayBlocksLive(ticker, resolution) && why !== "replay") return false;
          return tvSend(key, onRealtimeCallback, bar, why || "sub", sid);
        };
        realtimeSubscribers[sid] = {
          symbolInfo: symbolInfo,
          resolution: resolution,
          rawCallback: onRealtimeCallback,
          callback: tvCb,
          onResetCacheNeeded: onResetCacheNeededCallback || null,
          dfId: dfId,
          tvGuid: String(listenerGUID || ""),
        };
        if (typeof onResetCacheNeededCallback === "function") {
          resetCacheNeededCbs[sid] = onResetCacheNeededCallback;
        }
        TickHub.add(sid, ticker, resolution, tvCb, onResetCacheNeededCallback);
        gridLog("subscribe", {
          symbol: ticker,
          res: String(resolution),
          guid: sid.slice(0, 24),
          tv_guid: String(listenerGUID || "").slice(0, 16),
          df: dfId,
          n_subs: Object.keys(realtimeSubscribers).length,
          n_listen: Object.keys(TickHub.listeners || {}).length,
        });
        var last = lastHistoricalBar[key];
        // Never seed from liveBar during replay — today's LTP bar jumps the viewport.
        if (!barReplayBlocksLive(ticker, resolution)) {
          last = liveBar[key] || last;
        }
        // TV: never seed realtime before hist. Defer until first paint commits tip edge
        // so we don't flash a wrong tall candle then snap (seed → LTP correct).
        if (last && last.close != null && barMatchesLtp(last, ticker)) {
          seedBuilder(ticker, resolution, last);
          if (paintCommittedAt[key] && !getBarsPending[key]) {
            if (!barReplayBlocksLive(ticker, resolution)) {
              tvCb(last, "seed");
              lb("tv.ltp", ticker, {
                src: "seed",
                o: last.open,
                h: last.high,
                l: last.low,
                c: last.close,
                age_ms: 0,
              });
            }
          } else if (!barReplayBlocksLive(ticker, resolution)) {
            if (!pendingLiveSeeds[key]) pendingLiveSeeds[key] = [];
            pendingLiveSeeds[key].push({
              guid: sid,
              callback: tvCb,
              ticker: ticker,
              resolution: resolution,
              bar: last,
            });
          }
        }
        // If another pane already has live quotes for this symbol, push the tip now.
        try {
          if (!barReplayBlocksLive(ticker, resolution)) {
            var qNow = TickHub.lastQuotes[ticker] || TickHub.lastQuotes[String(ticker).toUpperCase()];
            if (qNow && Number(qNow.ltp) > 0 && paintCommittedAt[key]) {
              setTimeout(function () {
                if (!realtimeSubscribers[sid] || barReplayBlocksLive(ticker, resolution)) return;
                TickHub.applyQuote(ticker, qNow);
              }, 0);
            }
          }
        } catch (_) {}
        log("tv.subscribeBars", ticker, resolution, dfId);
        lb("tv.subscribe", ticker, {
          resolution: String(resolution || ""),
          live: "ticks",
          df: dfId,
          tip: last && barMatchesLtp(last, ticker) ? { src: paintCommittedAt[key] ? "seed" : "seed.pending", c: last.close } : null,
        });
      },
      unsubscribeBars: function (listenerGUID) {
        var sid = storeGuid(listenerGUID);
        var gone = realtimeSubscribers[sid];
        var goneSym =
          (gone && gone.symbolInfo && (gone.symbolInfo.ticker || gone.symbolInfo.name)) || "";
        delete realtimeSubscribers[sid];
        delete resetCacheNeededCbs[sid];
        try {
          delete tvEdge["g:" + sid];
        } catch (_) {}
        TickHub.remove(sid);
        gridLog("unsubscribe", {
          symbol: goneSym,
          res: gone ? String(gone.resolution) : "",
          guid: sid.slice(0, 24),
          df: dfId,
          n_subs: Object.keys(realtimeSubscribers).length,
        });
      },
    };
  }

  function getSharedDatafeed() {
    // Prefer a fresh datafeed object per widget. A shared instance can leave only the
    // last chart's realtime series accepting updates (multi-grid same symbol).
    return createDatafeed();
  }

  function paneContainerId(paneId) {
    if (paneId === "sector") return "tv_sector_chart";
    if (paneId === "stock") return "tv_stock_chart";
    if (String(paneId || "").indexOf("extra") === 0) {
      var ei = parseInt(String(paneId).replace("extra", ""), 10);
      if (!isNaN(ei)) return "tv_extra" + ei + "_chart";
    }
    return null;
  }

  function paneTitleEl(paneId) {
    if (paneId === "sector") return document.getElementById("cx-left-title");
    if (paneId === "stock") return document.getElementById("cx-right-title");
    if (String(paneId || "").indexOf("extra") === 0) {
      var ei = parseInt(String(paneId).replace("extra", ""), 10);
      if (!isNaN(ei)) return document.getElementById("cx-extra" + ei + "-title");
    }
    return null;
  }

  function setPaneSymbol(paneId, sym) {
    var q = String(sym || "").trim();
    if (paneId === "sector") paneSymbols.sector = q || null;
    else if (paneId === "stock") paneSymbols.stock = q || null;
    else if (String(paneId || "").indexOf("extra") === 0) {
      var ei = parseInt(String(paneId).replace("extra", ""), 10);
      if (!isNaN(ei) && ei >= 0 && ei < 6) paneSymbols.extras[ei] = q || null;
    }
    var title = paneTitleEl(paneId);
    if (title && q) title.textContent = optDisplayLabel(q);
  }

  function getPaneSymbol(paneId) {
    if (paneId === "sector") return paneSymbols.sector;
    if (paneId === "stock") return paneSymbols.stock;
    if (String(paneId || "").indexOf("extra") === 0) {
      var ei = parseInt(String(paneId).replace("extra", ""), 10);
      if (!isNaN(ei) && ei >= 0 && ei < 6) return paneSymbols.extras[ei];
    }
    return null;
  }

  /** Keep TickHub subscribed to every visible pane symbol (same or different). */
  function ensureVisiblePaneWsSubs() {
    var panes = panesForGrid(currentGrid);
    for (var i = 0; i < panes.length; i++) {
      var sym = String(getPaneSymbol(panes[i]) || "").trim();
      if (!sym) continue;
      try {
        if (TickHub) {
          TickHub.wanted[sym] = true;
          var wire = optWireSymbol(sym);
          if (wire && wire !== sym) TickHub.wanted[wire] = true;
          if (typeof TickHub.sendSub === "function") {
            TickHub.sendSub(sym);
            if (wire && wire !== sym) TickHub.sendSub(wire);
          }
        }
      } catch (_) {}
    }
  }

  function ensureEmptyVisiblePanes(seed) {
    var q = String(seed || "").trim();
    if (!q) return;
    var panes = panesForGrid(currentGrid);
    var keep = activePane;
    for (var i = 0; i < panes.length; i++) {
      var p = panes[i];
      if (!String(getPaneSymbol(p) || "").trim()) {
        openSymbolOnPane(q, p, { skipAutosave: true, _syncing: true });
      }
    }
    setActivePane(panes.indexOf(keep) >= 0 ? keep : panes[0]);
    ensureVisiblePaneWsSubs();
  }

  function widgetForPane(paneId) {
    return paneWidgets[paneId] || null;
  }

  function allPaneWidgets() {
    var out = [];
    Object.keys(paneWidgets).forEach(function (k) {
      if (paneWidgets[k]) out.push(paneWidgets[k]);
    });
    return out;
  }

  function setActivePane(paneId) {
    var visible = panesForGrid(currentGrid);
    var next = paneId || "sector";
    if (visible.indexOf(next) < 0) next = visible[0] || "sector";
    activePane = next;
    document.querySelectorAll(".cx-pane").forEach(function (el) {
      el.classList.toggle("is-active-pane", el.getAttribute("data-pane") === activePane);
    });
    var w = widgetForPane(activePane);
    if (w) {
      widget = w;
      window._bcWidget = w;
    }
    var sym =
      activePane === "sector"
        ? paneSymbols.sector
        : activePane === "stock"
          ? paneSymbols.stock
          : paneSymbols.extras[parseInt(String(activePane).replace("extra", ""), 10)];
            if (sym) {
      activeSymbol = sym;
      var activeBtn = document.getElementById("cxActiveSym");
      if (activeBtn) activeBtn.textContent = optDisplayLabel(sym);
    }
  }

  function applyGridLayout(gridId, opts) {
    opts = opts || {};
    var id = VALID_GRIDS[gridId] ? gridId : "1";
    currentGrid = id;
    var splitEl = document.getElementById("cxSplit");
    if (splitEl) splitEl.setAttribute("data-layout", id);
    setActivePane(activePane);
    var seed =
      (activePane === "sector" && paneSymbols.sector) ||
      paneSymbols.sector ||
      paneSymbols.stock ||
      activeSymbol;
    if (opts.fillAll) {
      if (seed) fillVisiblePanesWithSymbol(seed);
    } else if (!opts.silent) {
      // Keep existing pane symbols; only seed empty panes.
      if (seed) ensureEmptyVisiblePanes(seed);
    }
    ensureVisiblePaneWsSubs();
    bumpAllWidgetSizes();
    if (!opts.silent && layoutCtl) layoutCtl.scheduleAutosave();
  }

  function bumpAllWidgetSizes() {
    allPaneWidgets().forEach(function (w) {
      try {
        if (w && typeof w.resize === "function") w.resize();
      } catch (_) {}
    });
  }

  function saveWidgetState(w) {
    return new Promise(function (resolve) {
      if (!w || typeof w.save !== "function") {
        resolve(null);
        return;
      }
      var done = false;
      var finish = function (state) {
        if (done) return;
        done = true;
        resolve(state || null);
      };
      try {
        w.save(function (state) {
          finish(state);
        });
      } catch (_) {
        finish(null);
        return;
      }
      // TV save() can stall; don't block autosave forever.
      setTimeout(function () {
        finish(null);
      }, 3500);
    });
  }

  function isVolumeStudy(s) {
    if (!s) return false;
    var name = String(s.name || s.description || "").trim();
    var id = String(s.id || "");
    // Exact Volume pane only — not Volume Profile / VWAP / etc.
    if (/^volume$/i.test(name)) return true;
    if (/Volume@tv-basicstudies/i.test(id)) return true;
    if (/^volume\b/i.test(name) && !/profile|oscillator|weighted|vwap/i.test(name)) return true;
    return false;
  }

  /** Exactly one Volume study per chart. Removes extras from saved layouts / refresh stacks. */
  function ensureVolumeStudy() {
    try {
      var targets = [];
      if (typeof widget !== "undefined" && widget) targets.push(widget);
      Object.keys(paneWidgets || {}).forEach(function (k) {
        if (paneWidgets[k] && targets.indexOf(paneWidgets[k]) < 0) targets.push(paneWidgets[k]);
      });
      targets.forEach(function (w) {
        if (!w || typeof w.activeChart !== "function") return;
        var chart = w.activeChart();
        if (!chart) return;
        var studies = chart.getAllStudies ? chart.getAllStudies() : [];
        var vols = Array.isArray(studies) ? studies.filter(isVolumeStudy) : [];
        // Keep first, remove the rest (refresh + create_volume_default used to stack panes).
        for (var i = 1; i < vols.length; i++) {
          try {
            var rid = vols[i] && vols[i].id;
            if (!rid) continue;
            if (typeof chart.removeEntity === "function") chart.removeEntity(rid);
            else if (typeof chart.removeStudy === "function") chart.removeStudy(rid);
          } catch (_) {}
        }
        if (vols.length === 0 && typeof chart.createStudy === "function") {
          try {
            chart.createStudy("Volume", false, false);
          } catch (_) {}
        }
        try {
          if (typeof chart.applyOverrides === "function") {
            chart.applyOverrides({ volumePaneSize: "medium" });
          }
        } catch (_) {}
      });
    } catch (_) {}
  }

  function loadWidgetState(w, state) {
    if (!w || !state) return;
    function afterLoad() {
      // Saved templates embed the theme from when they were saved — force current theme.
      applyTvTheme(w);
      setTimeout(ensureVolumeStudy, 80);
    }
    try {
      if (typeof w.load === "function") {
        var p = w.load(state);
        if (p && typeof p.then === "function") {
          p.then(afterLoad, afterLoad);
        } else {
          afterLoad();
        }
        return;
      }
      var chart = w.activeChart && w.activeChart();
      if (chart && typeof chart.load === "function") {
        chart.load(state);
        afterLoad();
      }
    } catch (_) {
      afterLoad();
    }
  }

  /** Dotted last-price (LTP) line + axis label on every chart; saved user settings must not hide it. */
  var LTP_LINE_OVERRIDES = {
    "mainSeriesProperties.showPriceLine": true,
    "mainSeriesProperties.priceLineWidth": 1,
    "scalesProperties.showSeriesLastValue": true,
  };

  function applyLtpLine(w) {
    try {
      if (w && typeof w.applyOverrides === "function") w.applyOverrides(LTP_LINE_OVERRIDES);
    } catch (_) {}
  }

  function tvChartOverrides(light) {
    return Object.assign({}, tvThemeOverrides(light), LTP_LINE_OVERRIDES);
  }

  function tvThemeOverrides(light) {
    if (light) {
      return {
        "paneProperties.background": "#ffffff",
        "paneProperties.backgroundType": "solid",
        "paneProperties.vertGridProperties.color": "rgba(53, 56, 57, 0.08)",
        "paneProperties.horzGridProperties.color": "rgba(53, 56, 57, 0.08)",
        "scalesProperties.textColor": "#7f8283",
        "scalesProperties.lineColor": "#e9e9eb",
        "scalesProperties.backgroundColor": "#ffffff",
        "chartProperties.background": "#ffffff",
        "chartProperties.backgroundType": "solid",
        "sessions.vertlines.sessBreaks.color": "#b0b2ba",
        "mainSeriesProperties.candleStyle.upColor": "#4caf50",
        "mainSeriesProperties.candleStyle.downColor": "#ef5350",
        "mainSeriesProperties.candleStyle.borderUpColor": "#4caf50",
        "mainSeriesProperties.candleStyle.borderDownColor": "#ef5350",
        "mainSeriesProperties.candleStyle.wickUpColor": "#4caf50",
        "mainSeriesProperties.candleStyle.wickDownColor": "#ef5350",
      };
    }
    return {
      "paneProperties.background": "#121212",
      "paneProperties.backgroundType": "solid",
      "paneProperties.vertGridProperties.color": "rgba(255,255,255,0.04)",
      "paneProperties.horzGridProperties.color": "rgba(255,255,255,0.04)",
      "scalesProperties.textColor": "#989ea0",
      "scalesProperties.lineColor": "#2e2e2e",
      "sessions.vertlines.sessBreaks.color": "#696e70",
      "mainSeriesProperties.candleStyle.upColor": "#4caf50",
      "mainSeriesProperties.candleStyle.downColor": "#e53935",
      "mainSeriesProperties.candleStyle.borderUpColor": "#4caf50",
      "mainSeriesProperties.candleStyle.borderDownColor": "#e53935",
      "mainSeriesProperties.candleStyle.wickUpColor": "#4caf50",
      "mainSeriesProperties.candleStyle.wickDownColor": "#e53935",
    };
  }

  function tvCssVars(light) {
    if (light) {
      return {
        "--tv-color-platform-background": "#ffffff",
        "--tv-color-pane-background": "#ffffff",
        "--tv-color-toolbar-button-background-hover": "#f0f0f2",
        "--tv-color-toolbar-button-background-expanded": "rgba(76, 175, 80, 0.1)",
        "--tv-color-toolbar-button-text": "#353839",
        "--tv-color-toolbar-button-text-hover": "#353839",
        "--tv-color-toolbar-button-text-active": "#4caf50",
        "--tv-color-toolbar-button-text-active-hover": "#43a047",
        "--tv-color-item-active-text": "#4caf50",
        "--tv-color-toolbar-divider-background": "#e9e9eb",
        "--tv-color-popup-background": "#ffffff",
        "--tv-color-popup-element-text": "#353839",
        "--tv-color-popup-element-background-hover": "#f0f0f2",
      };
    }
    return {
      "--tv-color-platform-background": "#121212",
      "--tv-color-pane-background": "#121212",
      "--tv-color-toolbar-button-background-hover": "#252a2c",
      "--tv-color-toolbar-button-background-expanded": "#1e2224",
      "--tv-color-toolbar-button-text": "#b5bbbd",
      "--tv-color-toolbar-button-text-hover": "#f2f5f7",
      "--tv-color-toolbar-button-text-active": "#4caf50",
      "--tv-color-toolbar-button-text-active-hover": "#66bb6a",
      "--tv-color-item-active-text": "#4caf50",
      "--tv-color-toolbar-divider-background": "#2e2e2e",
      "--tv-color-popup-background": "#1b1b1b",
      "--tv-color-popup-element-text": "#f2f5f7",
      "--tv-color-popup-element-background-hover": "#252a2c",
    };
  }

  function applyTvTheme(w) {
    if (!w) return;
    var light = chartTheme === "light";
    // Prefer overrides/CSS only — changeTheme mid-life can blank the chart.
    try {
      if (typeof w.applyOverrides === "function") w.applyOverrides(tvChartOverrides(light));
    } catch (_) {}
    try {
      if (typeof w.setCSSCustomProperty === "function") {
        var vars = tvCssVars(light);
        Object.keys(vars).forEach(function (k) {
          w.setCSSCustomProperty(k, vars[k]);
        });
      }
    } catch (_) {}
  }

  function updateThemeIcon() {
    var btn = document.getElementById("cx-theme-toggle");
    if (!btn) return;
    var light = chartTheme === "light";
    btn.classList.toggle("is-light", light);
    btn.classList.toggle("is-dark", !light);
    btn.setAttribute("aria-label", light ? "Switch to dark theme" : "Switch to light theme");
    btn.setAttribute("title", light ? "Switch to dark" : "Switch to light");
  }

  function applyPageTheme() {
    var light = chartTheme === "light";
    var shell = document.getElementById("cxShell");
    if (shell) {
      shell.classList.toggle("theme-light", light);
      shell.classList.toggle("theme-dark", !light);
    }
    document.documentElement.classList.toggle("theme-light", light);
    document.documentElement.classList.toggle("theme-dark", !light);
    document.body.classList.toggle("theme-light", light);
    document.body.classList.toggle("theme-dark", !light);
    try {
      var bg = light ? "#ffffff" : "#121212";
      document.documentElement.style.colorScheme = light ? "light" : "dark";
      document.documentElement.style.backgroundColor = light ? "#f7f7f7" : "#121212";
      document.body.style.backgroundColor = light ? "#f7f7f7" : "#121212";
      document.body.style.color = light ? "#353839" : "#f2f5f7";
      var splitEl = document.getElementById("cxSplit");
      if (splitEl) splitEl.style.setProperty("--cx-chart-bg", bg);
    } catch (_) {}
    updateThemeIcon();
  }

  function setBootMask(on) {
    var splitEl = document.getElementById("cxSplit");
    if (!splitEl) return;
    splitEl.classList.toggle("is-booting", !!on);
    // Always drop theme-switching with boot reveal so chart cannot stay blank.
    if (!on) splitEl.classList.remove("is-theme-switching");
  }

  function revealCharts() {
    setBootMask(false);
    var splitEl = document.getElementById("cxSplit");
    if (splitEl) splitEl.classList.remove("is-theme-switching");
  }

  function destroyTvWidget(w) {
    if (!w) return;
    try {
      if (typeof w.remove === "function") w.remove();
    } catch (_) {}
  }

  function resetChartContainer(containerId, placeholder) {
    var el = document.getElementById(containerId);
    if (!el) return;
    el.innerHTML = "";
    var empty = document.createElement("div");
    empty.className = "cx-empty";
    empty.textContent = placeholder || "Loading…";
    if (containerId === "tv_sector_chart") empty.id = "cx-left-empty";
    else if (containerId === "tv_stock_chart") empty.id = "cx-right-empty";
    el.appendChild(empty);
  }

  function rebuildChartsForTheme() {
    if (themeSwitchBusy) return;
    themeSwitchBusy = true;
    if (layoutCtl) layoutCtl.suppress(true);
    applyPageTheme();
    var splitEl = document.getElementById("cxSplit");
    // Full theme-colored cover — never dim/fade through white while TV rebuilds.
    if (splitEl) {
      splitEl.classList.remove("is-booting");
      splitEl.classList.add("is-theme-switching");
    }

    var sectorSym = paneSymbols.sector || activeSymbol;
    var stockSym = paneSymbols.stock;
    var extrasSnap = paneSymbols.extras.slice();

    Promise.all([
      saveWidgetState(paneWidgets.sector),
      saveWidgetState(paneWidgets.stock),
      Promise.all(
        [0, 1, 2, 3, 4, 5].map(function (i) {
          return saveWidgetState(paneWidgets["extra" + i]);
        })
      ),
    ])
      .then(function (parts) {
        pendingChartStates.sector = parts[0] || null;
        pendingChartStates.stock = parts[1] || null;
        pendingChartStates.extras = (parts[2] || []).slice();
        while (pendingChartStates.extras.length < 6) pendingChartStates.extras.push(null);

        destroyTvWidget(paneWidgets.sector);
        destroyTvWidget(paneWidgets.stock);
        paneWidgets.sector = null;
        paneWidgets.stock = null;
        widget = null;
        for (var i = 0; i < 6; i++) {
          destroyTvWidget(paneWidgets["extra" + i]);
          paneWidgets["extra" + i] = null;
        }

        resetChartContainer("tv_sector_chart", "Applying theme…");
        resetChartContainer("tv_stock_chart", "Applying theme…");
        for (var e = 0; e < 6; e++) {
          resetChartContainer("tv_extra" + e + "_chart", "Applying theme…");
        }

        if (typeof recreatePrimaryWidget === "function") {
          if (sectorSym) activeSymbol = sectorSym;
          recreatePrimaryWidget();
        }
        var visible = panesForGrid(currentGrid);
        if (stockSym && visible.indexOf("stock") >= 0) {
          ensurePaneWidget("stock", stockSym, pendingChartStates.stock);
        }
        for (var j = 0; j < extrasSnap.length; j++) {
          if (extrasSnap[j] && visible.indexOf("extra" + j) >= 0) {
            ensurePaneWidget("extra" + j, extrasSnap[j], pendingChartStates.extras[j]);
          }
        }

        // Hold cover until widgets paint with the target theme (avoids white flash).
        setTimeout(function () {
          allPaneWidgets().forEach(function (w) {
            applyTvTheme(w);
          });
          setTimeout(function () {
            revealCharts();
            bumpAllWidgetSizes();
            if (layoutCtl) {
              layoutCtl.suppress(false);
              layoutCtl.saveActiveLayout(true);
            }
            themeSwitchBusy = false;
          }, 180);
        }, 520);
      })
      .catch(function () {
        revealCharts();
        if (layoutCtl) layoutCtl.suppress(false);
        themeSwitchBusy = false;
      });
  }

  function toggleChartTheme() {
    if (themeSwitchBusy) return;
    chartTheme = chartTheme === "light" ? "dark" : "light";
    try {
      localStorage.setItem("ki_charts_theme", chartTheme);
    } catch (_) {}
    applyPageTheme();
    pageSaved("Theme saved");
    if (allPaneWidgets().length) rebuildChartsForTheme();
  }

  function hookPaneAutosave(w) {
    if (!w || w.__bcAutosaveHooked) return;
    w.__bcAutosaveHooked = true;
    var kick = function (reason) {
      kickAutosave(reason || "chart");
    };
    try {
      if (typeof w.subscribe === "function") {
        w.subscribe("onAutoSaveNeeded", function () {
          kick("chart");
        });
        try {
          w.subscribe("drawing_event", function () {
            kick("drawing");
          });
        } catch (_) {}
        try {
          w.subscribe("study_event", function (_id, ev) {
            var kind = String(ev || "").toLowerCase();
            if (kind === "create" || kind === "remove" || kind.indexOf("creat") >= 0 || kind.indexOf("remov") >= 0) {
              kick("indicator");
            } else {
              kick("indicator");
            }
          });
        } catch (_) {}
        try {
          w.subscribe("layout_changed", function () {
            kick("layout");
          });
        } catch (_) {}
        try {
          w.subscribe("series_event", function () {
            kick("series");
          });
        } catch (_) {}
      }
    } catch (_) {}
    try {
      var chart = w.activeChart && w.activeChart();
      if (chart && chart.onStudyEvent && typeof chart.onStudyEvent().subscribe === "function") {
        chart.onStudyEvent().subscribe(null, function () {
          kick("indicator");
        }, false);
      }
      if (chart && chart.onIntervalChanged && typeof chart.onIntervalChanged().subscribe === "function") {
        chart.onIntervalChanged().subscribe(null, function (interval) {
          if (interval) activeRes = String(interval);
          kick("interval");
          // 5m↔15m (etc.) often paints AGG/MEM with a lagging tip → live leaves a hole.
          // Same-session tip resync fills closed bars without waiting for a refresh.
          try {
            var symTf = activeSymbol;
            var resTf = activeRes;
            if (symTf && resTf && inSessionNow(symTf) && resMinutes(resTf) < 1440) {
              setTimeout(function () {
                if (String(activeSymbol) !== String(symTf)) return;
                if (String(activeRes) !== String(resTf)) return;
                resyncSameSessionTipGap(symTf, resTf, "tf.interval");
              }, 120);
            }
          } catch (_) {}
        });
      }
      if (chart && chart.onSymbolChanged && typeof chart.onSymbolChanged().subscribe === "function") {
        chart.onSymbolChanged().subscribe(null, function () {
          kick("symbol");
        });
      }
    } catch (_) {}

    // Backup: poll study list — catches add/remove when TV events are quiet.
    var lastStudySig = null;
    var studyPollReady = false;
    setTimeout(function () {
      studyPollReady = true;
    }, 2500);
    setInterval(function () {
      if (!studyPollReady) return;
      try {
        var ch = w.activeChart && w.activeChart();
        if (!ch || typeof ch.getAllStudies !== "function") return;
        var studies = ch.getAllStudies() || [];
        var sig = studies
          .map(function (s) {
            return String((s && s.id) || "") + ":" + String((s && s.name) || "");
          })
          .sort()
          .join("|");
        if (lastStudySig == null) {
          lastStudySig = sig;
          return;
        }
        if (sig !== lastStudySig) {
          lastStudySig = sig;
          kick("indicator");
        }
      } catch (_) {}
    }, 1600);
  }

  function buildTvWidgetOpts(container, symbol) {
    var isLightTheme = chartTheme === "light";
    var chartBg = isLightTheme ? "#ffffff" : "#121212";
    var localAnalysis = false;
    try {
      if (window.__BC && window.__BC.localAnalysis) localAnalysis = true;
      else {
        var h = location.hostname || "";
        localAnalysis = h === "localhost" || h === "127.0.0.1";
      }
    } catch (_) {}
    return {
      container: container,
      library_path: LIB_PATH,
      locale: "en",
      symbol: symbol,
      interval: activeRes,
      fullscreen: false,
      autosize: true,
      theme: isLightTheme ? "Light" : "Dark",
      toolbar_bg: isLightTheme ? "#ffffff" : "#121212",
      timezone: "Asia/Kolkata",
      datafeed: getSharedDatafeed(),
      // ?tvdebug=1 → library FEED logs (time violations, getBars ranges) in the console.
      debug: /[?&]tvdebug=1\b/.test(String(location.search || "")),
      disabled_features: [
        "header_compare",
        "display_market_status",
        "use_localstorage_for_settings",
        "popup_hints",
      ],
      // Volume is added once via ensureVolumeStudy — do not use create_volume_indicator_by_default
      // (it stacks with createStudy and with restored layout studies on every refresh).
      // chart_crosshair_menu → price-axis "+" (Advanced Charts emits onPlusClick; we fill the menu).
      enabled_features: [
        "session_breaks_visibility",
        "items_favoriting",
        "show_symbol_logos",
        "show_symbol_logo_in_legend",
        "chart_crosshair_menu",
      ],
      favorites: {
        intervals: ["5", "15", "60", "1D"],
        indicators: favIndicators.map(function (f) {
          return f.name;
        }),
      },
      settings_adapter: tvSettingsAdapter(),
      overrides: {
        "paneProperties.background": chartBg,
        "paneProperties.backgroundType": "solid",
        "paneProperties.vertGridProperties.color": isLightTheme
          ? "rgba(42, 46, 57, 0.08)"
          : "rgba(255,255,255,0.04)",
        "paneProperties.horzGridProperties.color": isLightTheme
          ? "rgba(42, 46, 57, 0.08)"
          : "rgba(255,255,255,0.04)",
        "sessions.vertlines.sessBreaks.visible": true,
        "sessions.vertlines.sessBreaks.style": 2,
        "sessions.vertlines.sessBreaks.color": isLightTheme ? "#989ea0" : "#475569",
        "sessions.vertlines.sessBreaks.width": 1,
        "mainSeriesProperties.candleStyle.upColor": "#4caf50",
        "mainSeriesProperties.candleStyle.downColor": isLightTheme ? "#ef5350" : "#ff5e3b",
        "mainSeriesProperties.candleStyle.borderUpColor": "#4caf50",
        "mainSeriesProperties.candleStyle.borderDownColor": isLightTheme ? "#ef5350" : "#ff5e3b",
        "mainSeriesProperties.candleStyle.wickUpColor": "#4caf50",
        "mainSeriesProperties.candleStyle.wickDownColor": isLightTheme ? "#ef5350" : "#ff5e3b",
        "mainSeriesProperties.showPriceLine": true,
        "mainSeriesProperties.priceLineWidth": 1,
        "scalesProperties.showSeriesLastValue": true,
        volumePaneSize: "medium",
      },
      studies_overrides: {
        "volume.volume.color.0": isLightTheme ? "#ef5350" : "#ff5e3b",
        "volume.volume.color.1": "#4caf50",
        "volume.volume.transparency": 55,
      },
      custom_indicators_getter: localAnalysis
        ? function (PineJS) {
            return Promise.resolve([buildSmartOiIndicator(PineJS)]);
          }
        : undefined,
    };
  }

  /** Format a price for the price-axis "+" menu. */
  function fmtPlusPrice(price) {
    var n = Number(price);
    if (!(n > 0) && n !== 0) return "—";
    if (n >= 1000) return n.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (n >= 100) return n.toFixed(2);
    if (n >= 1) return n.toFixed(2);
    return String(Math.round(n * 10000) / 10000);
  }

  function drawHorizontalAtPrice(w, price) {
    try {
      var chart = w && w.activeChart && w.activeChart();
      if (!chart || !(Number(price) > 0) || typeof chart.createShape !== "function") return;
      var isLight = document.documentElement.classList.contains("theme-light");
      var color = isLight ? "#2962ff" : "#60a5fa";
      var p = chart.createShape(
        { price: Number(price) },
        {
          shape: "horizontal_line",
          lock: false,
          disableSelection: false,
          disableSave: false,
          disableUndo: false,
          overrides: {
            linecolor: color,
            linestyle: 0,
            linewidth: 1,
            showLabel: true,
            textcolor: color,
            fontsize: 11,
            horzLabelsAlign: "right",
            vertLabelsAlign: "middle",
            showPrice: true,
          },
        }
      );
      return p;
    } catch (_) {
      return null;
    }
  }

  var plusMenuEl = null;
  var plusMenuCloser = null;

  function hidePlusMenu() {
    if (plusMenuCloser) {
      try {
        document.removeEventListener("mousedown", plusMenuCloser, true);
        document.removeEventListener("keydown", plusMenuCloser, true);
      } catch (_) {}
      plusMenuCloser = null;
    }
    if (plusMenuEl && plusMenuEl.parentNode) {
      try {
        plusMenuEl.parentNode.removeChild(plusMenuEl);
      } catch (_) {}
    }
    plusMenuEl = null;
  }

  function showPlusMenu(params, w) {
    hidePlusMenu();
    var price = Number(params && params.price);
    if (!(price > 0)) return;
    var sym =
      (params && params.symbol) ||
      (function () {
        try {
          var c = w && w.activeChart && w.activeChart();
          return (c && c.symbol && c.symbol()) || "";
        } catch (_) {
          return "";
        }
      })() ||
      activeSymbol ||
      "Symbol";
    sym = String(sym).trim() || "Symbol";
    var pxLabel = fmtPlusPrice(price);

    var menu = document.createElement("div");
    menu.className = "cx-plus-menu";
    menu.setAttribute("role", "menu");
    menu.innerHTML =
      '<button type="button" class="cx-plus-menu-item" data-act="hline" role="menuitem">' +
      '<span class="cx-plus-menu-ico" aria-hidden="true">' +
      '<svg viewBox="0 0 18 18" width="16" height="16"><path d="M2 9h14" stroke="currentColor" stroke-width="1.6" fill="none"/><path d="M2 6.5h3M2 11.5h3M13 6.5h3M13 11.5h3" stroke="currentColor" stroke-width="1.2" fill="none"/></svg>' +
      "</span>" +
      "<span>Draw Horizontal Line at " +
      pxLabel +
      "</span></button>" +
      '<button type="button" class="cx-plus-menu-item" data-act="alert" role="menuitem">' +
      '<span class="cx-plus-menu-ico" aria-hidden="true">' +
      '<svg viewBox="0 0 18 18" width="16" height="16"><path d="M9 2.5a5 5 0 0 1 5 5v2.2l1.2 1.8H2.8L4 9.7V7.5a5 5 0 0 1 5-5z" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M7.2 14.2a1.8 1.8 0 0 0 3.6 0" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>' +
      "</span>" +
      "<span>Add Alert on " +
      String(sym).replace(/</g, "") +
      " @ " +
      pxLabel +
      "</span></button>";

    var x = Number(params.clientX) || 0;
    var y = Number(params.clientY) || 0;
    menu.style.left = Math.max(8, Math.min(window.innerWidth - 320, x + 6)) + "px";
    menu.style.top = Math.max(8, Math.min(window.innerHeight - 120, y + 6)) + "px";
    document.body.appendChild(menu);
    plusMenuEl = menu;

    menu.addEventListener("mousedown", function (ev) {
      ev.stopPropagation();
    });
    menu.addEventListener("click", function (ev) {
      var btn = ev.target && ev.target.closest ? ev.target.closest("[data-act]") : null;
      if (!btn) return;
      var act = btn.getAttribute("data-act");
      hidePlusMenu();
      if (act === "hline") {
        drawHorizontalAtPrice(w, price);
      } else if (act === "alert") {
        try {
          if (alertCtl && typeof alertCtl.openCreate === "function") {
            alertCtl.openCreate({ symbol: sym, price: price });
          }
        } catch (_) {}
      }
    });

    plusMenuCloser = function (ev) {
      if (ev.type === "keydown" && ev.key !== "Escape") return;
      if (ev.type === "mousedown" && plusMenuEl && plusMenuEl.contains(ev.target)) return;
      hidePlusMenu();
    };
    setTimeout(function () {
      document.addEventListener("mousedown", plusMenuCloser, true);
      document.addEventListener("keydown", plusMenuCloser, true);
    }, 0);
  }

  /** Price-axis "+" → Horizontal line + Alert (no trading). */
  function wirePlusButtonMenu(w) {
    if (!w || w.__bcPlusMenuWired) return;
    w.__bcPlusMenuWired = true;
    // chart_crosshair_menu is already in enabled_features — do not call setFeatureEnabled
    // (it toggles and would turn the plus button back off).
    try {
      if (typeof w.subscribe === "function") {
        w.subscribe("onPlusClick", function (params) {
          showPlusMenu(params || {}, w);
        });
      }
    } catch (_) {}
    try {
      if (typeof w.onContextMenu === "function") {
        w.onContextMenu(function (_unix, price) {
          var px = Number(price);
          if (!(px > 0)) return [];
          var sym = activeSymbol || "Symbol";
          try {
            var c = w.activeChart && w.activeChart();
            if (c && c.symbol) sym = c.symbol() || sym;
          } catch (_) {}
          var label = fmtPlusPrice(px);
          return [
            {
              position: "top",
              text: "Draw Horizontal Line at " + label,
              click: function () {
                drawHorizontalAtPrice(w, px);
              },
            },
            {
              position: "top",
              text: "Add Alert on " + sym + " @ " + label + "…",
              click: function () {
                try {
                  if (alertCtl && typeof alertCtl.openCreate === "function") {
                    alertCtl.openCreate({ symbol: sym, price: px });
                  }
                } catch (_) {}
              },
            },
          ];
        });
      }
    } catch (_) {}
  }

  function ensurePaneWidget(paneId, symbol, savedState) {
    var q = String(symbol || "").trim();
    if (!q) return;
    setPaneSymbol(paneId, q);
    var existing = widgetForPane(paneId);
    if (savedState) {
      if (paneId === "sector") pendingChartStates.sector = savedState;
      else if (paneId === "stock") pendingChartStates.stock = savedState;
      else if (String(paneId).indexOf("extra") === 0) {
        var ei0 = parseInt(String(paneId).replace("extra", ""), 10);
        if (!isNaN(ei0)) pendingChartStates.extras[ei0] = savedState;
      }
    }
    if (existing) {
      try {
        var curSym = "";
        try {
          curSym = String((existing.activeChart && existing.activeChart().symbol()) || "").trim();
        } catch (_) {}
        if (!curSym) {
          curSym = String(
            paneId === "stock"
              ? paneSymbols.stock || ""
              : paneSymbols.extras[parseInt(String(paneId).replace("extra", ""), 10)] || ""
          ).trim();
        }
        if (curSym.toUpperCase() === q.toUpperCase()) {
          if (savedState) {
            loadWidgetState(existing, savedState);
            if (paneId === "sector") pendingChartStates.sector = null;
            else if (paneId === "stock") pendingChartStates.stock = null;
            else pendingChartStates.extras[parseInt(String(paneId).replace("extra", ""), 10)] = null;
          }
          return existing;
        }
        if (typeof existing.setSymbol === "function") {
          existing.setSymbol(q, activeRes, function () {
            var pend =
              paneId === "sector"
                ? pendingChartStates.sector
                : paneId === "stock"
                  ? pendingChartStates.stock
                  : pendingChartStates.extras[parseInt(String(paneId).replace("extra", ""), 10)];
            if (pend) {
              loadWidgetState(existing, pend);
              setTimeout(ensureVolumeStudy, 300);
              if (paneId === "sector") pendingChartStates.sector = null;
              else if (paneId === "stock") pendingChartStates.stock = null;
              else pendingChartStates.extras[parseInt(String(paneId).replace("extra", ""), 10)] = null;
            }
            bumpAllWidgetSizes();
            if (layoutCtl) layoutCtl.scheduleAutosave();
          });
        }
      } catch (_) {}
      return existing;
    }
    if (paneId === "sector") {
      // Primary widget is created by bootWidget/createWidget.
      return paneWidgets.sector;
    }
    var cid = paneContainerId(paneId);
    var el = cid ? document.getElementById(cid) : null;
    if (!el || typeof TradingView === "undefined" || !TradingView.widget) return null;
    var empty = el.querySelector(".cx-empty");
    if (empty) empty.style.display = "none";
    var opts = buildTvWidgetOpts(el, q);
    var w = new TradingView.widget(opts);
    paneWidgets[paneId] = w;
    w.onChartReady(function () {
      try {
        wirePlusButtonMenu(w);
      } catch (_) {}
      hookPaneAutosave(w);
      applyTvTheme(w);
      var pend =
        paneId === "sector"
          ? pendingChartStates.sector
          : paneId === "stock"
            ? pendingChartStates.stock
            : pendingChartStates.extras[parseInt(String(paneId).replace("extra", ""), 10)];
      if (pend) {
        loadWidgetState(w, pend);
        if (paneId === "sector") pendingChartStates.sector = null;
        else if (paneId === "stock") pendingChartStates.stock = null;
        else pendingChartStates.extras[parseInt(String(paneId).replace("extra", ""), 10)] = null;
      }
      bumpAllWidgetSizes();
      if (layoutCtl) layoutCtl.scheduleAutosave();
    });
    return w;
  }

  function openSymbolOnPane(sym, paneId, opts) {
    opts = opts || {};
    var q = String(sym || "").trim();
    if (!q) return;
    q = optDisplayLabel(q) || q;
    var visible = panesForGrid(currentGrid);
    var target = paneId || activePane || "sector";
    if (visible.indexOf(target) < 0) target = visible[0] || "sector";
    setActivePane(target);
    try {
      if (opts.ltp != null && Number(opts.ltp) > 0 && TickHub && typeof TickHub.applyQuote === "function") {
        var seedLtp = Number(opts.ltp);
        var seedQ = {
          ltp: seedLtp,
          open: opts.open != null && Number(opts.open) > 0 ? Number(opts.open) : seedLtp,
          high: opts.high != null && Number(opts.high) > 0 ? Number(opts.high) : seedLtp,
          low: opts.low != null && Number(opts.low) > 0 ? Number(opts.low) : seedLtp,
          ts: Date.now(),
        };
        TickHub.applyQuote(q, seedQ);
        var wire = optWireSymbol(q);
        if (wire && wire !== q) TickHub.applyQuote(wire, seedQ);
      }
    } catch (_) {}
    if (target === "sector") {
      var curSec = String(paneSymbols.sector || activeSymbol || "")
        .trim()
        .toUpperCase();
      // Layout restore on boot: same symbol already loading — do not tip/setSymbol again.
      if (opts.skipAutosave && curSec && curSec === q.toUpperCase()) {
        if (opts.savedState) {
          pendingChartStates.sector = opts.savedState;
          if (widget) {
            try {
              loadWidgetState(widget, opts.savedState);
              pendingChartStates.sector = null;
            } catch (_) {}
          }
        }
        if (!opts._syncing) ensureVisiblePaneWsSubs();
        return;
      }
      setSymbol(q, {
        pane: "sector",
        skipAutosave: opts.skipAutosave,
        savedState: opts.savedState,
        force: opts.force,
        _syncing: opts._syncing,
        ltp: opts.ltp,
        open: opts.open,
        high: opts.high,
        low: opts.low,
        _sectorOnly: true,
      });
      try {
        if (alertCtl && typeof alertCtl.redrawLines === "function") {
          setTimeout(function () {
            alertCtl.redrawLines();
          }, 900);
        }
      } catch (_) {}
    } else {
      var curPane = String(
        target === "stock" ? paneSymbols.stock || "" : paneSymbols.extras[parseInt(String(target).replace("extra", ""), 10)] || ""
      )
        .trim()
        .toUpperCase();
      if (opts.skipAutosave && curPane && curPane === q.toUpperCase() && widgetForPane(target)) {
        if (opts.savedState) {
          try {
            loadWidgetState(widgetForPane(target), opts.savedState);
          } catch (_) {}
        }
        if (!opts._syncing) ensureVisiblePaneWsSubs();
        return;
      }
      ensurePaneWidget(target, q, opts.savedState);
      activeSymbol = q;
      var activeBtn = document.getElementById("cxActiveSym");
      if (activeBtn) activeBtn.textContent = optDisplayLabel(q);
      var titleEl = document.getElementById("cx-left-title");
      if (titleEl) titleEl.textContent = optDisplayLabel(q);
      try {
        history.replaceState(null, "", "/charts?symbol=" + encodeURIComponent(optDisplayLabel(q)));
      } catch (_) {}
      if (!opts.skipAutosave && layoutCtl) layoutCtl.scheduleAutosave();
    }
    // Keep TickHub subscribed for every visible pane (same or different symbols).
    ensureVisiblePaneWsSubs();
    try {
      var wireSub = optWireSymbol(q);
      if (wireSub && TickHub && typeof TickHub.sendSub === "function") {
        TickHub.sendSub(wireSub);
        if (wireSub !== q) TickHub.sendSub(q);
      }
    } catch (_) {}
  }

  function fillVisiblePanesWithSymbol(sym) {
    var q = String(sym || "").trim();
    if (!q) return;
    var panes = panesForGrid(currentGrid);
    var keep = activePane;
    for (var i = 0; i < panes.length; i++) {
      openSymbolOnPane(q, panes[i], { skipAutosave: true, _syncing: true });
    }
    setActivePane(panes.indexOf(keep) >= 0 ? keep : panes[0]);
    ensureVisiblePaneWsSubs();
    if (layoutCtl) layoutCtl.scheduleAutosave();
  }

  function initLayoutManager() {
    if (layoutCtl) return layoutCtl;
    layoutCtl = initLayouts({
      getGrid: function () {
        return currentGrid;
      },
      setGrid: function (gridId, opts) {
        applyGridLayout(gridId, opts);
      },
      getActivePane: function () {
        return activePane;
      },
      setActivePane: setActivePane,
      getSymbols: function () {
        return {
          sector: paneSymbols.sector,
          stock: paneSymbols.stock,
          extras: paneSymbols.extras.slice(),
        };
      },
      openOnPane: openSymbolOnPane,
      fillVisiblePanes: fillVisiblePanesWithSymbol,
      snapshotCharts: function () {
        return Promise.all([
          saveWidgetState(paneWidgets.sector),
          saveWidgetState(paneWidgets.stock),
          Promise.all(
            [0, 1, 2, 3, 4, 5].map(function (i) {
              return saveWidgetState(paneWidgets["extra" + i]);
            })
          ),
        ]).then(function (parts) {
          return {
            sectorChart: parts[0],
            stockChart: parts[1],
            extraCharts: parts[2],
          };
        });
      },
      applyChartStates: function (states, delayMs) {
        setTimeout(function () {
          if (states.sectorChart && paneWidgets.sector) loadWidgetState(paneWidgets.sector, states.sectorChart);
          if (states.stockChart && paneWidgets.stock) loadWidgetState(paneWidgets.stock, states.stockChart);
          if (Array.isArray(states.extraCharts)) {
            for (var i = 0; i < states.extraCharts.length; i++) {
              if (states.extraCharts[i] && paneWidgets["extra" + i]) {
                loadWidgetState(paneWidgets["extra" + i], states.extraCharts[i]);
              }
            }
          }
          allPaneWidgets().forEach(function (w) {
            applyTvTheme(w);
          });
          bumpAllWidgetSizes();
          // End boot mask after one restore settle.
          setTimeout(function () {
            revealCharts();
          }, 120);
        }, delayMs == null ? 400 : delayMs);
      },
      getInterval: function () {
        return activeRes;
      },
      setInterval: function (res) {
        if (res) activeRes = String(res);
      },
      bumpSize: bumpAllWidgetSizes,
      status: function (msg) {
        log("layout", msg);
      },
    });
    document.querySelectorAll(".cx-pane").forEach(function (pane) {
      function pickPane(e) {
        var id = pane.getAttribute("data-pane");
        if (!id) return;
        if (pane.classList.contains("is-active-pane")) return;
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        setActivePane(id);
        if (layoutCtl) layoutCtl.scheduleAutosave();
      }
      pane.addEventListener("click", pickPane);
    });
    return layoutCtl;
  }

  function bootWidget() {
    if (typeof TradingView === "undefined" || !TradingView.widget) {
      log("TV missing");
      return;
    }
    try {
      performance.mark("bc-libs");
      performance.mark("ki-tv-libs");
    } catch (_) {}
    setMetric("mLibs", Math.round(performance.now() - navT0) + "ms");

    var host = document.getElementById("tv_sector_chart");
    if (!host) {
      log("missing #tv_sector_chart");
      return;
    }

    function loadTVLibrary() {
      return new Promise(function(resolve) {
        if (typeof TradingView !== "undefined" && TradingView.widget) {
          resolve();
          return;
        }
        var tvScript = document.createElement("script");
        tvScript.src = LIB_PATH + "charting_library.standalone.js";
        tvScript.async = true;
        tvScript.onload = function() {
          log("TV library loaded in", Math.round(performance.now() - navT0), "ms");
          resolve();
        };
        tvScript.onerror = function() {
          log("ERROR loading TradingView library");
          resolve();
        };
        document.head.appendChild(tvScript);
      });
    }

    loadTVLibrary().then(function() {
      createWidget();
    });

    function isLocalAnalysisEnabled() {
      try {
        if (window.__BC && window.__BC.localAnalysis) return true;
        var h = location.hostname || "";
        return h === "localhost" || h === "127.0.0.1";
      } catch (_) {
        return false;
      }
    }

    function buildSmartOiIndicator(PineJS) {
      return {
        name: "Smart OI",
        metainfo: {
          _metainfoVersion: 51,
          id: "BCSmartOI@tv-basicstudies-1",
          description: "Smart OI",
          shortDescription: "Smart OI",
          isCustomIndicator: true,
          is_hidden_study: false,
          is_price_study: false,
          linkedToSeries: true,
          format: { type: "volume" },
          plots: [
            { id: "plot_0", type: "line" },
            {
              id: "plot_1",
              type: "colorer",
              target: "plot_0",
              palette: "palette_0",
            },
          ],
          palettes: {
            palette_0: {
              colors: {
                0: { name: "Up / Put writing" },
                1: { name: "Down / Call writing" },
              },
            },
          },
            defaults: {
            styles: {
              plot_0: {
                linestyle: 0,
                linewidth: 4,
                plottype: 1, // Histogram (5=Columns)
                trackPrice: false,
                transparency: 0,
                visible: true,
                color: "#4caf50",
              },
            },
            palettes: {
              palette_0: {
                colors: {
                  0: { color: "#4caf50", width: 1, style: 0 },
                  1: { color: "#ff5e3b", width: 1, style: 0 },
                },
              },
            },
            precision: 0,
            inputs: {},
          },
          styles: {
            plot_0: { title: "Smart OI", histogramBase: 0 },
          },
          inputs: [],
        },
        constructor: function () {
          this.init = function (context) {
            this._context = context;
          };
          this.main = function (context) {
            this._context = context;
            var t = PineJS.Std.time(this._context);
            // Std.time may be ms or seconds depending on CL build — normalize to seconds.
            var sec = Number(t);
            if (sec > 1e12) sec = Math.floor(sec / 1000);
            else sec = Math.floor(sec);
            var map = window.__BC_SMART_OI_MAP || {};
            var v = map[sec];
            if (v == null) {
              var resSec = 60;
              try {
                var r = String(activeRes || "5");
                if (/^\d+$/.test(r)) resSec = parseInt(r, 10) * 60;
              } catch (_) {}
              var bucket = Math.floor(sec / resSec) * resSec;
              v = map[bucket];
              // Look back a few buckets (sparse local samples).
              if (v == null) {
                for (var i = 1; i <= 6 && v == null; i++) {
                  v = map[bucket - i * resSec];
                }
              }
            }
            if (v == null || isNaN(Number(v))) v = 0;
            v = Number(v);
            return [v, v >= 0 ? 0 : 1];
          };
        },
      };
    }

    function createWidget() {
    // Always use chartTheme (localStorage-backed), not a late DOM class read.
    var isLightTheme = chartTheme === "light";
    var chartBg = isLightTheme ? "#ffffff" : "#121212";
    var chartText = isLightTheme ? "#131722" : "#eef3fb";
    var localAnalysis = isLocalAnalysisEnabled();
    // Do NOT re-enable boot mask here — theme toggle recreates the widget and
    // a stuck is-booting left the chart permanently empty.
    applyPageTheme();

    widget = new TradingView.widget({
      container: host,
      library_path: LIB_PATH,
      locale: "en",
      symbol: activeSymbol,
      interval: activeRes,
      fullscreen: false,
      autosize: true,
      theme: isLightTheme ? "Light" : "Dark",
      toolbar_bg: isLightTheme ? "#ffffff" : "#121212",
      timezone: "Asia/Kolkata",
      datafeed: getSharedDatafeed(),
      // ?tvdebug=1 → library FEED logs (time violations, getBars ranges) in the console.
      debug: /[?&]tvdebug=1\b/.test(String(location.search || "")),
      disabled_features: [
        "header_compare",
        "display_market_status",
        "use_localstorage_for_settings",
        "popup_hints",
      ],
      enabled_features: [
        "session_breaks_visibility",
        "items_favoriting",
        "show_symbol_logos",
        "show_symbol_logo_in_legend",
        "chart_crosshair_menu",
      ],
      favorites: {
        intervals: ["5", "15", "60", "1D"],
        indicators: favIndicators.map(function (f) {
          return f.name;
        }),
      },
      settings_adapter: tvSettingsAdapter(),
      overrides: {
        "paneProperties.background": chartBg,
        "paneProperties.backgroundType": "solid",
        "paneProperties.vertGridProperties.color": isLightTheme ? "rgba(42, 46, 57, 0.08)" : "rgba(255,255,255,0.04)",
        "paneProperties.horzGridProperties.color": isLightTheme ? "rgba(42, 46, 57, 0.08)" : "rgba(255,255,255,0.04)",
        // Day / session breaks — same overrides as VC Stocks charts.php
        "sessions.vertlines.sessBreaks.visible": true,
        "sessions.vertlines.sessBreaks.style": 2,
        "sessions.vertlines.sessBreaks.color": isLightTheme ? "#989ea0" : "#475569",
        "sessions.vertlines.sessBreaks.width": 1,
        "mainSeriesProperties.candleStyle.upColor": "#4caf50",
        "mainSeriesProperties.candleStyle.downColor": isLightTheme ? "#ef5350" : "#ff5e3b",
        "mainSeriesProperties.candleStyle.borderUpColor": "#4caf50",
        "mainSeriesProperties.candleStyle.borderDownColor": isLightTheme ? "#ef5350" : "#ff5e3b",
        "mainSeriesProperties.candleStyle.wickUpColor": "#4caf50",
        "mainSeriesProperties.candleStyle.wickDownColor": isLightTheme ? "#ef5350" : "#ff5e3b",
        "mainSeriesProperties.showPriceLine": true,
        "mainSeriesProperties.priceLineWidth": 1,
        "scalesProperties.showSeriesLastValue": true,
        volumePaneSize: "medium",
      },
      studies_overrides: {
        "volume.volume.color.0": isLightTheme ? "#ef5350" : "#ff5e3b",
        "volume.volume.color.1": "#4caf50",
        "volume.volume.transparency": 55,
      },
      custom_indicators_getter: localAnalysis
        ? function (PineJS) {
            return Promise.resolve([buildSmartOiIndicator(PineJS)]);
          }
        : undefined,
    });

    // Exposed for console-based diagnostics (e.g. widget.activeChart().exportData())
    // to query TradingView's own internal series state directly, independent of
    // our own delivery logs — the ground truth for "did the chart actually update".
    paneWidgets.sector = widget;
    setPaneSymbol("sector", activeSymbol);
    window._bcWidget = widget;
    recreatePrimaryWidget = createWidget;

    if (widget && widget.onChartReady) {
      widget.onChartReady(function () {
        try {
          wirePlusButtonMenu(widget);
        } catch (_) {}
        try {
          performance.mark("ki-tv-ready");
        } catch (_) {}
        hideEmpty();
        applyLtpLine(widget);
        var readyTime = Math.round(performance.now());
        log("tv.ready", "chart initialized in " + readyTime + "ms");
        hookPaneAutosave(widget);
        // Theme already set in widget constructor — skip applyTvTheme here (redraw blink).
        if (pendingChartStates.sector) {
          loadWidgetState(widget, pendingChartStates.sector);
          pendingChartStates.sector = null;
        }
        try {
          // One Volume only — delayed once so layout restore can finish first, then dedupe.
          ensureVolumeStudy();
          setTimeout(ensureVolumeStudy, 600);
        } catch (_) {}
        try {
          initLayoutManager();
        } catch (eLay) {
          log("layout.init.err", eLay && eLay.message ? eLay.message : eLay);
        }
        // No saved layout restore path — reveal chart after first paint.
        setTimeout(function () {
          var lay = layoutCtl && layoutCtl.activeLayout && layoutCtl.activeLayout();
          var hasSaved =
            lay &&
            (lay.sectorChart ||
              lay.stockChart ||
              (Array.isArray(lay.extraCharts) && lay.extraCharts.some(Boolean)));
          if (!hasSaved) revealCharts();
          // Safety: never leave chart blank even if restore path stalls.
          setTimeout(revealCharts, 1600);
        }, 300);
        try {
          var sessOv = {
            "sessions.vertlines.sessBreaks.visible": true,
            "sessions.vertlines.sessBreaks.style": 2,
            "sessions.vertlines.sessBreaks.color": document.documentElement.classList.contains("theme-light")
              ? "#b0b2ba"
              : "#475569",
            "sessions.vertlines.sessBreaks.width": 1,
          };
          if (typeof widget.applyOverrides === "function") widget.applyOverrides(sessOv);
          var chart = widget.activeChart && widget.activeChart();
          if (chart && typeof chart.applyOverrides === "function") chart.applyOverrides(sessOv);
          // Prefer enable over toggle — toggle can turn breaks OFF if already on.
          if (chart && typeof chart.executeActionById === "function") {
            try {
              chart.executeActionById("Chart.SessionBreaks.enable");
            } catch (_e1) {
              try {
                chart.executeActionById("chartSessionBreaks");
              } catch (_e2) {}
            }
          }
          log("session_breaks", "enabled");
        } catch (e) {
          log("session_breaks.err", e && e.message ? e.message : e);
        }
        var cl = "";
        try {
          if (typeof TradingView !== "undefined" && TradingView.version) cl = String(TradingView.version() || "");
        } catch (_) {}
        lb("page.tv_ready", activeSymbol, {
          widget_ms: readyTime,
          libs_ms: Math.round(performance.now() - navT0),
          cl_version: cl,
          tv_ready_ms: readyTime,
        });
        lb("tv.ready", activeSymbol, {
          ms: readyTime,
          total_ms: readyTime,
          status: "candles_rendering",
        });
        try {
          localStorage.setItem("bc_chart_ready_" + activeSymbol, JSON.stringify({
            ready_at: new Date().toISOString(),
            ready_ms: readyTime,
            resolution: activeRes,
          }));
        } catch (_) {}
        try {
          window.dispatchEvent(new Event("resize"));
        } catch (_) {}

        // Phase 3 live stream will be initialized on first subscribeBars() call
        // (when TradingView is ready to receive real-time updates), not here.
        // This avoids a race condition where stream starts before subscription is ready.
        log("live.init", "Ready to stream (will start on first subscribeBars)");
        lb("live.stream", "ready_for_subscription", {
          symbol: activeSymbol,
          resolution: activeRes,
        });

        try {
          ensureVolumeStudy();
          if (isLocalAnalysisEnabled()) {
            ensureVolumeAndSmartOi(false);
          }
        } catch (e) {
          log("volume.init.err", e && e.message ? e.message : e);
        }
      });
    }
    } // end createWidget

    var smartOiStudyId = null;
    var smartOiPollTimer = null;
    var smartOiWanted = false;

    function applySmartOiMap(bars) {
      var map = {};
      var resSec = 60;
      try {
        var r = String(activeRes || "5");
        if (/^\d+$/.test(r)) resSec = parseInt(r, 10) * 60;
      } catch (_) {}
      (bars || []).forEach(function (b) {
        if (!b || b.time == null) return;
        var t = Number(b.time);
        var v = Number(b.value) || 0;
        map[t] = v;
        // Align to TV bar opens for common offsets
        map[Math.floor(t / resSec) * resSec] = v;
      });
      // If only the latest bucket has data, smear it across the last few buckets
      // so the histogram is visible on a full-day chart zoom.
      if (bars && bars.length) {
        var last = bars[bars.length - 1];
        var lv = Number(last.value) || 0;
        if (Math.abs(lv) > 0) {
          var t0 = Math.floor(Number(last.time) / resSec) * resSec;
          for (var k = 0; k < 8; k++) {
            var tk = t0 - k * resSec;
            if (map[tk] == null || map[tk] === 0) map[tk] = lv;
          }
        }
      }
      window.__BC_SMART_OI_MAP = map;
      window.__BC_SMART_OI_BARS = bars || [];
      window.__BC_SMART_OI_LAST = bars && bars.length ? bars[bars.length - 1] : null;

      if (!smartOiWanted) return;
      // Custom studies do not auto-refresh from window map — recreate to redraw.
      try {
        var chart = widget && widget.activeChart && widget.activeChart();
        if (chart && smartOiStudyId) {
          try {
            chart.removeEntity(smartOiStudyId);
          } catch (_) {}
          smartOiStudyId = null;
        }
      } catch (_) {}
      ensureSmartOiStudy();
    }

    function fetchSmartOi(sample) {
      var res = String(activeRes || "5");
      if (!/^\d+$/.test(res)) res = "5";
      var url =
        "/api/smart-oi.php?symbol=" +
        encodeURIComponent("NIFTY 50") +
        "&resolution=" +
        encodeURIComponent(res) +
        "&sample=" +
        (sample ? "1" : "0") +
        "&_=" +
        Date.now();
      return fetch(url, { credentials: "include", cache: "no-store" })
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          if (data && data.ok) applySmartOiMap(data.bars || []);
          try {
            log("smart_oi", {
              n: data && data.n,
              samples: data && data.n_samples,
              last: data && data.last,
            });
          } catch (_) {}
          return data;
        })
        .catch(function () {
          return null;
        });
    }

    function ensureSmartOiStudy() {
      var chart = widget && widget.activeChart && widget.activeChart();
      if (!chart || typeof chart.createStudy !== "function") return Promise.resolve(null);
      if (smartOiStudyId) return Promise.resolve(smartOiStudyId);
      try {
        var p = chart.createStudy("Smart OI", false, false);
        if (p && typeof p.then === "function") {
          return p.then(function (id) {
            smartOiStudyId = id;
            return id;
          });
        }
        smartOiStudyId = p;
        return Promise.resolve(p);
      } catch (e) {
        log("smart_oi.study.err", e && e.message ? e.message : e);
        return Promise.resolve(null);
      }
    }

    function ensureVolumeAndSmartOi(startSmart) {
      ensureVolumeStudy();
      if (!startSmart) return;
      smartOiWanted = true;
      fetchSmartOi(true).then(function () {
        return ensureSmartOiStudy();
      });
      if (smartOiPollTimer) clearInterval(smartOiPollTimer);
      smartOiPollTimer = setInterval(function () {
        if (!smartOiWanted) return;
        fetchSmartOi(true);
      }, 15000);
    }

    function clearSmartOiStudy() {
      smartOiWanted = false;
      if (smartOiPollTimer) {
        clearInterval(smartOiPollTimer);
        smartOiPollTimer = null;
      }
      try {
        var chart = widget && widget.activeChart && widget.activeChart();
        if (chart && smartOiStudyId) {
          chart.removeEntity(smartOiStudyId);
        }
      } catch (_) {}
      smartOiStudyId = null;
    }

    // Expose for Analysis panel (defined outside bootWidget).
    window.__BC_smartOi = {
      ensure: ensureVolumeAndSmartOi,
      clear: clearSmartOiStudy,
      fetch: fetchSmartOi,
      active: function () {
        return !!smartOiWanted;
      },
    };

  } // end bootWidget

  function fmtLtp(v) {
    if (v == null || v === "" || !isFinite(Number(v))) return "—";
    // Two decimals everywhere: the chart shows 22,832.95, so the watchlist must not say 22833.
    return Number(v).toFixed(2);
  }

  function fmtChg(v) {
    if (v == null || v === "" || !isFinite(Number(v))) return "—";
    var n = Number(v);
    var s = (n >= 0 ? "+" : "") + n.toFixed(2) + "%";
    return s;
  }

  function fmtChgAbs(v) {
    if (v == null || v === "" || !isFinite(Number(v))) return "—";
    var n = Number(v);
    var abs = Math.abs(n);
    var body = abs >= 100 ? abs.toFixed(1) : abs.toFixed(2);
    return (n >= 0 ? "+" : "-") + body;
  }

  function chgClass(v) {
    if (v == null || !isFinite(Number(v))) return "flat";
    var n = Number(v);
    if (n > 0) return "up";
    if (n < 0) return "down";
    return "flat";
  }

  function escAttr(s) {
    return String(s || "").replace(/"/g, "&quot;");
  }
  function escHtml(s) {
    return String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
  function wlSvg(paths) {
    return (
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      paths +
      "</svg>"
    );
  }
  function wlIconHtml(kind) {
    var k = String(kind || "default");
    var paths = '<path d="M3 3v18h18"/><path d="M7 14l4-4 3 3 5-6"/>';
    if (k === "nifty") paths = '<circle cx="12" cy="12" r="8"/><path d="M8 12h8M12 8v8"/>';
    else if (k === "bank") paths = '<path d="M3 10l9-6 9 6"/><path d="M5 10v8h14v-8"/><path d="M9 18v-4h6v4"/>';
    else if (k === "sensex") paths = '<path d="M12 3l2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5z"/>';
    else if (k === "fin") paths = '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 15V9M12 15v-3M17 15V7"/>';
    else if (k === "auto") paths = '<path d="M5 16l1.5-6h11L19 16"/><circle cx="8" cy="17" r="2"/><circle cx="16" cy="17" r="2"/><path d="M7 10l1-3h8l1 3"/>';
    else if (k === "it") paths = '<rect x="3" y="4" width="18" height="12" rx="2"/><path d="M8 20h8M12 16v4"/>';
    else if (k === "pharma" || k === "health") paths = '<path d="M9 3h6v6h6v6h-6v6H9v-6H3V9h6z"/>';
    else if (k === "metal") paths = '<path d="M4 18h16L12 4 4 18z"/>';
    else if (k === "oil") paths = '<path d="M12 3c3 4 6 7 6 11a6 6 0 1 1-12 0c0-4 3-7 6-11z"/>';
    else if (k === "fmcg") paths = '<path d="M6 8h12l-1 13H7L6 8z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/>';
    else if (k === "realty") paths = '<path d="M3 21h18"/><path d="M5 21V9l7-5 7 5v12"/><path d="M10 21v-6h4v6"/>';
    else if (k === "defence") paths = '<path d="M12 3l8 4v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V7l8-4z"/>';
    else if (k === "media") paths = '<rect x="3" y="6" width="18" height="12" rx="2"/><path d="M10 10l5 2-5 2v-4z"/>';
    else if (k === "cement") paths = '<path d="M4 20h16"/><path d="M6 20V10l6-5 6 5v10"/><path d="M10 20v-5h4v5"/>';
    else if (k === "chemicals") paths = '<path d="M9 3h6"/><path d="M10 3v6l-5 9h14l-5-9V3"/><path d="M8 18h8"/>';
    else if (k === "psu") paths = '<path d="M4 20h16"/><path d="M6 20V8h12v12"/><path d="M9 8V5h6v3"/>';
    else if (k === "fno") paths = '<path d="M3 17l6-6 4 4 7-8"/><path d="M14 7h6v6"/>';
    else if (k === "custom") paths = '<path d="M19 21l-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>';
    else if (k === "sector") paths = '<path d="M4 10h6V4H4v6zm10 0h6V4h-6v6zM4 20h6v-6H4v6zm10 0h6v-6h-6v6z"/>';
    return '<span class="wl-row-ico ico-' + escAttr(k) + '">' + wlSvg(paths) + "</span>";
  }
  function indexIconKind(label) {
    var u = String(label || "").toUpperCase();
    if (u.indexOf("CRUDE") >= 0 || u.indexOf("NATURALGAS") >= 0 || u.indexOf("GOLD") >= 0) return "oil";
    if (u.indexOf("BANK") >= 0) return "bank";
    if (u.indexOf("SENSEX") >= 0) return "sensex";
    if (u.indexOf("FIN") >= 0) return "fin";
    if (u.indexOf("GIFT") >= 0) return "nifty";
    if (u.indexOf("MID") >= 0 || u.indexOf("NIFTY") >= 0) return "nifty";
    return "nifty";
  }
  function wlRowIconHtml(r, logo, eager) {
    if (logo) {
      return (
        '<img class="wl-logo" src="' +
        escAttr(logo) +
        '" alt="" width="18" height="18" loading="' +
        (eager ? "eager" : "lazy") +
        '" decoding="async" fetchpriority="' +
        (eager ? "high" : "auto") +
        '" onerror="this.onerror=null;this.style.visibility=\'hidden\'" />'
      );
    }
    var kind = r && r.kind;
    if (kind === "index" || kind === "fno" || kind === "commodity") {
      return wlIconHtml(indexIconKind(r.label || r.symbol));
    }
    return "";
  }
  function sectorIconKind(label) {
    var u = String(label || "").toUpperCase();
    if (u.indexOf("AUTO") >= 0) return "auto";
    if (u.indexOf("IT") >= 0) return "it";
    if (u.indexOf("PHARMA") >= 0) return "pharma";
    if (u.indexOf("HEALTH") >= 0) return "health";
    if (u.indexOf("METAL") >= 0) return "metal";
    if (u.indexOf("OIL") >= 0 || u.indexOf("GAS") >= 0 || u.indexOf("ENERGY") >= 0) return "oil";
    if (u.indexOf("FMCG") >= 0 || u.indexOf("CONSUM") >= 0) return "fmcg";
    if (u.indexOf("REALTY") >= 0) return "realty";
    if (u.indexOf("DEFENCE") >= 0 || u.indexOf("DEFENSE") >= 0) return "defence";
    if (u.indexOf("MEDIA") >= 0) return "media";
    if (u.indexOf("CEMENT") >= 0 || u.indexOf("DURBL") >= 0) return "cement";
    if (u.indexOf("CHEM") >= 0 || u.indexOf("COMMOD") >= 0) return "chemicals";
    if (u.indexOf("PSU") >= 0) return "psu";
    if (u.indexOf("BANK") >= 0 || u.indexOf("FINANCIAL") >= 0 || u.indexOf("FIN") >= 0) return "bank";
    return "sector";
  }
  function persistCustomWl() {
    persistWlLists();
  }
  function lookupRowQuote(sym) {
    var want = String(sym || "").toUpperCase();
    if (!want) return { ltp: null, chg: null, chg_abs: null };
    var found = null;
    function scan(rows) {
      for (var i = 0; i < (rows || []).length; i++) {
        var r = rows[i];
        if (
          String(r.label || "").toUpperCase() === want ||
          String(r.symbol || "").toUpperCase() === want ||
          String(r.index_symbol || "").toUpperCase() === want
        ) {
          found = r;
          return true;
        }
        var stocks = r.stocks || [];
        for (var j = 0; j < stocks.length; j++) {
          if (
            String(stocks[j].label || "").toUpperCase() === want ||
            String(stocks[j].symbol || "").toUpperCase() === want
          ) {
            found = stocks[j];
            return true;
          }
        }
      }
      return false;
    }
    if (
      scan(wlData.indices) ||
      scan(wlData.sectors) ||
      scan(wlData.fno) ||
      scan(wlData.nifty50) ||
      scan(wlData.nifty500) ||
      scan(wlData.custom)
    ) {
      return {
        ltp: found.ltp,
        chg: found.chg != null ? found.chg : found.chg_pct,
        chg_abs: found.chg_abs,
        prev_close: found.prev_close,
        label: found.label || found.symbol,
      };
    }
    try {
      var q = lookupQuote(want);
      if (q && Number(q.ltp) > 0) {
        var chg = q.chg != null ? Number(q.chg) : null;
        var prev = Number(q.prev_close);
        if (chg == null && prev > 0) chg = Math.round(((Number(q.ltp) - prev) / prev) * 10000) / 100;
        var abs = q.chg_abs != null ? Number(q.chg_abs) : prev > 0 ? Math.round((Number(q.ltp) - prev) * 100) / 100 : null;
        return { ltp: Number(q.ltp), chg: chg, chg_abs: abs, prev_close: prev || null, label: want };
      }
    } catch (_) {}
    return { ltp: null, chg: null, chg_abs: null };
  }
  function rowChgAbs(r) {
    if (!r) return null;
    var ltp = Number(r.ltp);
    var prev = Number(r.prev_close);
    // Prefer live LTP vs yesterday's prev_close so abs always matches the printed LTP.
    if (ltp > 0 && prev > 0 && Math.abs(prev - ltp) / Math.max(ltp, 1) >= 1e-5) {
      return Math.round((ltp - prev) * 100) / 100;
    }
    if (r.chg_abs != null && isFinite(Number(r.chg_abs))) return Number(r.chg_abs);
    return null;
  }
  function rowChgPct(r) {
    if (!r) return null;
    var ltp = Number(r.ltp);
    var prev = Number(r.prev_close);
    // Always recompute from LTP + previous close when both exist (feed chg can lag / mismatch).
    if (ltp > 0 && prev > 0 && Math.abs(prev - ltp) / Math.max(ltp, 1) >= 1e-5) {
      return Math.round(((ltp - prev) / prev) * 10000) / 100;
    }
    if (r.chg != null && r.chg !== "" && isFinite(Number(r.chg))) return Number(r.chg);
    if (r.chg_pct != null && isFinite(Number(r.chg_pct))) return Number(r.chg_pct);
    return null;
  }
  function buildCustomRows() {
    var L = wlSrc === "custom" ? findCustomList(wlActiveListId) : activeCustomList();
    var syms = L ? L.symbols : [];
    return syms.map(function (sym) {
      var q = lookupRowQuote(sym);
      return {
        label: q.label || sym,
        symbol: sym,
        ltp: q.ltp,
        chg: q.chg,
        chg_abs: q.chg_abs,
        prev_close: q.prev_close,
        kind: "custom",
      };
    });
  }
  /** Default page rows reuse the live index rows so quote stamping keeps them fresh. */
  function defaultWlRows() {
    var pool = (wlData.indices || []).concat(wlData.fno || []);
    return DEFAULT_WL_LABELS.map(function (lab) {
      var found = null;
      for (var i = 0; i < pool.length; i++) {
        var r = pool[i];
        if (
          String(r.label || "").toUpperCase() === lab ||
          String(r.symbol || "").toUpperCase() === lab
        ) {
          found = r;
          break;
        }
      }
      var row = found
        ? Object.assign({}, found)
        : { label: lab, symbol: lab, kind: "index" };
      if (!row.logo_url) {
        row.logo_url =
          logoUrlFor(row.symbol) || logoUrlFor(row.label) || logoUrlFor(lab) || "";
      }
      return row;
    });
  }
  /** Indices page = broad market + sector indices (Default stays the short home list). */
  function indicesWlRows() {
    var out = [];
    var seen = {};
    function push(r) {
      if (!r) return;
      var sym = String(r.symbol || r.label || "").trim();
      if (!sym) return;
      var key = sym.toUpperCase();
      if (seen[key]) return;
      seen[key] = 1;
      var row = Object.assign({}, r);
      if (!row.logo_url) {
        row.logo_url =
          logoUrlFor(row.symbol) || logoUrlFor(row.label) || logoUrlFor(row.index_symbol) || "";
      }
      out.push(row);
    }
    (wlData.indices || []).forEach(push);
    (wlData.sectors || []).forEach(function (r) {
      push({
        label: r.label,
        symbol: r.index_symbol || r.symbol || r.label,
        ltp: r.ltp,
        chg: r.chg,
        chg_abs: r.chg_abs,
        prev_close: r.prev_close,
        logo_url: r.logo_url,
        kind: "index",
      });
    });
    return out;
  }
  function systemListCount(id) {
    if (id === DEFAULT_WL_ID) return DEFAULT_WL_LABELS.length;
    if (id === "indices") return indicesWlRows().length;
    if (id === "fno") return (wlData.fno || []).length;
    if (id === "sectors") return (wlData.sectors || []).length;
    if (id === "nifty50") return (wlData.nifty50 || []).length;
    if (id === "nifty500") return (wlData.nifty500 || []).length || wlNifty500N || 0;
    return 0;
  }
  function wlListIconSvg(id) {
    if (id === DEFAULT_WL_ID) {
      return '<path d="M4 11l8-6 8 6"/><path d="M6 10v9h12v-9"/>';
    }
    if (id === "indices") {
      return '<path d="M4 18V10M10 18V6M16 18v-8M20 18H3"/>';
    }
    if (id === "sectors") {
      return '<path d="M4 10h6V4H4v6zm10 0h6V4h-6v6zM4 20h6v-6H4v6zm10 0h6v-6h-6v6z"/>';
    }
    if (id === "fno") {
      return '<path d="M3 17l6-6 4 4 7-8"/><path d="M14 7h6v6"/>';
    }
    if (id === "nifty50") {
      return '<path d="M12 3l2.2 4.5 5 .7-3.6 3.5.9 5L12 14.8 7.5 16.7l.9-5L4.8 8.2l5-.7L12 3z"/>';
    }
    if (id === "nifty500") {
      return '<circle cx="12" cy="12" r="8"/><path d="M8 12h8M12 8v8"/>';
    }
    return '<path d="M4 7h16M4 12h16M4 17h16"/>';
  }
  function syncWlListBtn() {
    var nameEl = document.getElementById("wlListBtnName");
    if (!nameEl) return;
    var name = "Indices";
    var sys = SYSTEM_WL.find(function (S) {
      return S.id === wlActiveListId;
    });
    if (sys) name = sys.name;
    else {
      var idx = -1;
      for (var i = 0; i < wlLists.length; i++) {
        if (wlLists[i].id === wlActiveListId) {
          idx = i;
          break;
        }
      }
      name = idx >= 0 ? (wlLists[idx].name || "Custom " + (idx + 1)) : "Custom";
    }
    nameEl.textContent = name;
    renderCustomTabs();
  }
  function setWlListMenuOpen(open) {
    var menu = document.getElementById("wlListMenu");
    var btn = document.getElementById("wlListBtn");
    if (!menu || !btn) return;
    menu.hidden = !open;
    btn.setAttribute("aria-expanded", open ? "true" : "false");
  }
  function nextCustomWatchlistName() {
    var n = wlLists.length + 1;
    return n <= 1 ? "Custom watchlist" : "Custom watchlist " + n;
  }
  function renderWlListMenu() {
    var menu = document.getElementById("wlListMenu");
    if (!menu) return;
    var html = "";
    function itemHtml(S) {
      var on = wlActiveListId === S.id ? " is-active" : "";
      return (
        '<button type="button" class="wl-list-menu-item' +
        on +
        '" data-wl-select="' +
        escAttr(S.id) +
        '" role="option" aria-selected="' +
        (on ? "true" : "false") +
        '">' +
        '<span class="wl-list-menu-left">' +
        '<span class="wl-list-menu-ico" aria-hidden="true">' +
        wlSvg(wlListIconSvg(S.id)) +
        "</span>" +
        '<span class="wl-list-menu-name">' +
        escHtml(S.name) +
        "</span></span>" +
        '<span class="wl-list-menu-count">' +
        systemListCount(S.id) +
        "</span></button>"
      );
    }
    function customItemHtml(L, i) {
      var on = wlSrc === "custom" && wlActiveListId === L.id ? " is-active" : "";
      var label = L.name || nextCustomWatchlistName();
      return (
        '<button type="button" class="wl-list-menu-item' +
        on +
        '" data-wl-select="' +
        escAttr(L.id) +
        '" role="option" aria-selected="' +
        (on ? "true" : "false") +
        '">' +
        '<span class="wl-list-menu-left">' +
        '<span class="wl-list-menu-ico" aria-hidden="true">' +
        wlSvg('<path d="M8 4h9a2 2 0 0 1 2 2v14l-4.5-2.5L10 20V6a2 2 0 0 1 2-2"/><path d="M8 4a2 2 0 0 0-2 2v14l4-2.2"/>') +
        "</span>" +
        '<span class="wl-list-menu-name">' +
        escHtml(label) +
        "</span></span>" +
        '<span class="wl-list-menu-count">' +
        (L.symbols ? L.symbols.length : 0) +
        "</span></button>"
      );
    }
    html += '<div class="wl-list-menu-section">Watchlists</div>';
    SYSTEM_WL.filter(function (S) {
      return S.group === "market";
    }).forEach(function (S) {
      html += itemHtml(S);
    });
    SYSTEM_WL.filter(function (S) {
      return S.group === "universe";
    }).forEach(function (S) {
      html += itemHtml(S);
    });
    if (wlLists.length) {
      html += '<div class="wl-list-menu-divider"></div>';
      html += '<div class="wl-list-menu-section">Custom</div>';
      wlLists.forEach(function (L, i) {
        html += customItemHtml(L, i);
      });
    }
    if (wlSrc === "custom" && findCustomList(wlActiveListId)) {
      html += '<div class="wl-list-menu-divider"></div>';
      html +=
        '<button type="button" class="wl-list-menu-action" data-wl-action="add">Add symbol…</button>';
      html +=
        '<button type="button" class="wl-list-menu-action" data-wl-action="rename">Rename…</button>';
      html +=
        '<button type="button" class="wl-list-menu-action is-danger" data-wl-action="delete">Delete</button>';
    }
    menu.innerHTML = html;
  }
  function isDefaultCustomName(name, index) {
    var n = String(name || "").trim();
    var slot = index + 1;
    return (
      !n ||
      /^list\s*\d+$/i.test(n) ||
      /^watchlist\s*\d+$/i.test(n) ||
      /^custom\s*watchlist(\s*\d+)?$/i.test(n) ||
      n === String(slot) ||
      n.toLowerCase() === "my list"
    );
  }
  function customListLabel(L, index) {
    if (!L) return "Custom";
    if (isDefaultCustomName(L.name, index)) {
      return index === 0 ? "Custom" : "Custom " + (index + 1);
    }
    return String(L.name).slice(0, 14);
  }
  function renameActiveCustomList() {
    var cur = findCustomList(wlActiveListId);
    if (!cur || wlSrc !== "custom") {
      cur = findCustomList(wlPinCustomId) || wlLists[0];
      if (!cur) return;
      setWlSource(cur.id);
    }
    cur = findCustomList(wlActiveListId);
    if (!cur) return;
    openRenameWatchlistModal(cur);
  }
  var wlAddSymSearchTimer = null;
  function setWlModalOpen(id, open) {
    var modal = document.getElementById(id);
    if (!modal) return;
    modal.hidden = !open;
    modal.classList.toggle("is-open", !!open);
  }
  function openNewWatchlistModal() {
    if (wlLists.length >= WL_MAX_LISTS) {
      alert("You already have " + WL_MAX_LISTS + " custom watchlists. Delete one to make room.");
      return;
    }
    setWlListMenuOpen(false);
    setWlModalOpen("wlAddSymModal", false);
    setWlModalOpen("wlRenameListModal", false);
    var input = document.getElementById("wlNewListName");
    if (input) input.value = nextCustomWatchlistName();
    setWlModalOpen("wlNewListModal", true);
    try {
      if (input) setTimeout(function () { input.focus(); input.select(); }, 40);
    } catch (_) {}
  }
  function confirmNewWatchlist() {
    var input = document.getElementById("wlNewListName");
    var name = input ? String(input.value || "").trim().slice(0, 40) : "";
    var created = createCustomList(name || nextCustomWatchlistName());
    setWlModalOpen("wlNewListModal", false);
    if (!created) return;
    setWlSource(created.id);
    openAddSymbolModal();
  }
  function openRenameWatchlistModal(list) {
    var cur = list || findCustomList(wlActiveListId);
    if (!cur) return;
    setWlListMenuOpen(false);
    setWlModalOpen("wlNewListModal", false);
    setWlModalOpen("wlAddSymModal", false);
    var input = document.getElementById("wlRenameListName");
    if (input) input.value = cur.name || "Custom watchlist";
    setWlModalOpen("wlRenameListModal", true);
    try {
      if (input) setTimeout(function () { input.focus(); input.select(); }, 40);
    } catch (_) {}
  }
  function confirmRenameWatchlist() {
    var cur = findCustomList(wlActiveListId);
    if (!cur) {
      setWlModalOpen("wlRenameListModal", false);
      return;
    }
    var input = document.getElementById("wlRenameListName");
    var name = input ? String(input.value || "").trim().slice(0, 40) : "";
    if (name) cur.name = name;
    setWlModalOpen("wlRenameListModal", false);
    persistWlLists();
    syncWlListBtn();
    renderWl();
  }
  function resolveCatalogSymbol(raw) {
    var q = String(raw || "").trim();
    if (!q) return Promise.resolve(null);
    var url =
      "/api/search.php?q=" + encodeURIComponent(q) + "&exact=1&_=" + Date.now();
    return fetch(url, { credentials: "include", cache: "no-store" })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        var rows = (data && data.results) || [];
        if (!rows.length) return null;
        var row = rows[0];
        return String(row.symbol || row.label || "").toUpperCase() || null;
      })
      .catch(function () {
        return null;
      });
  }
  function tryAddToCustom(raw) {
    return resolveCatalogSymbol(raw).then(function (sym) {
      if (!sym) {
        alert(
          "That symbol is not in Bharat Charts. Search and pick a stock, index, or F&O name from the list."
        );
        return false;
      }
      addToCustom(sym);
      return true;
    });
  }
  function activeWatchlistTitle() {
    var L = wlSrc === "custom" ? findCustomList(wlActiveListId) : findCustomList(wlPinCustomId) || wlLists[0];
    if (!L) return "Custom watchlist";
    var idx = wlLists.indexOf(L);
    if (idx < 0) idx = 0;
    return isDefaultCustomName(L.name, idx)
      ? idx === 0
        ? "Custom watchlist"
        : "Custom watchlist " + (idx + 1)
      : L.name;
  }
  function renderAddSymSuggestions(q) {
    var box = document.getElementById("wlAddSymResults");
    if (!box) return;
    var query = String(q || "").trim();
    if (query.length < 1) {
      box.innerHTML =
        '<p class="wl-add-sym-empty">Type a stock, index, or F&amp;O symbol</p>';
      return;
    }
    box.innerHTML = '<p class="wl-add-sym-empty">Searching…</p>';
    var url = "/api/search.php?q=" + encodeURIComponent(query);
    fetch(url, { credentials: "include", cache: "no-store" })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        var curQ = ((document.getElementById("wlAddSymInput") || {}).value || "").trim();
        if (curQ.toLowerCase() !== query.toLowerCase()) return;
        var rows = (data && data.results) || [];
        if (!rows.length) {
          box.innerHTML =
            '<p class="wl-add-sym-empty">No match in Bharat Charts. Try another name.</p>';
          return;
        }
        var html = "";
        rows.slice(0, 10).forEach(function (r) {
          var sym = String(r.symbol || r.label || "").toUpperCase();
          var lab = String(r.label || sym);
          var inList = isInCustom(sym);
          if (r.logo_url) {
            rememberLogo(sym, r.logo_url);
            rememberLogo(lab, r.logo_url);
          }
          var logo = r.logo_url || logoUrlFor(sym) || logoUrlFor(lab);
          var ico = logo
            ? '<img class="wl-logo" src="' +
              escAttr(logo) +
              '" alt="" width="18" height="18" loading="lazy" decoding="async" />'
            : "";
          html +=
            '<button type="button" class="wl-add-suggest' +
            (inList ? " is-in" : "") +
            '" data-add-custom="' +
            escAttr(sym) +
            '">' +
            ico +
            '<span class="wl-add-suggest-sym">' +
            escHtml(lab) +
            "</span>" +
            '<span class="wl-add-suggest-act">' +
            (inList ? "✓" : "+") +
            "</span></button>";
        });
        box.innerHTML = html;
      })
      .catch(function () {
        box.innerHTML =
          '<p class="wl-add-sym-empty">Search failed. Try again.</p>';
      });
  }
  function openAddSymbolModal() {
    setWlListMenuOpen(false);
    setWlModalOpen("wlNewListModal", false);
    setWlModalOpen("wlRenameListModal", false);
    if (wlSrc !== "custom") {
      var pin = findCustomList(wlPinCustomId) || wlLists[0];
      if (!pin) {
        openNewWatchlistModal();
        return;
      }
      setWlSource(pin.id);
    }
    var hint = document.getElementById("wlAddSymHint");
    var listName = activeWatchlistTitle();
    if (hint) {
      hint.innerHTML =
        "Adds to <b>" +
        escHtml(listName) +
        "</b> — saves automatically.";
    }
    var input = document.getElementById("wlAddSymInput");
    if (input) input.value = "";
    renderAddSymSuggestions("");
    setWlModalOpen("wlAddSymModal", true);
    try {
      if (input) setTimeout(function () { input.focus(); }, 40);
    } catch (_) {}
  }
  function beginAddToCustomList() {
    openAddSymbolModal();
  }
  function renderCustomTabs() {
    var tabs = document.getElementById("wlCustomTabs");
    if (!tabs) return;
    if (!wlLists.length) {
      tabs.innerHTML = "";
      tabs.hidden = true;
      return;
    }
    tabs.hidden = false;
    var html = "";
    wlLists.forEach(function (L, i) {
      var on = wlSrc === "custom" && wlActiveListId === L.id;
      var label = customListLabel(L, i);
      var renamed = !isDefaultCustomName(L.name, i);
      html +=
        '<button type="button" class="wl-custom-tab' +
        (on ? " is-active" : "") +
        (renamed ? " is-named" : "") +
        '" data-wl-tab="' +
        escAttr(L.id) +
        '" role="tab" aria-selected="' +
        (on ? "true" : "false") +
        '" title="' +
        escAttr((L.name || "Custom watchlist") + " — double-click to rename") +
        '">' +
        escHtml(label) +
        "</button>";
    });
    tabs.innerHTML = html;
  }
  function renderCustomAddSymbolBtn() {
    return (
      '<button type="button" class="wl-add-symbol-btn" data-wl-empty-add>' +
      "<span aria-hidden=\"true\">＋</span> Add symbol</button>"
    );
  }
  function renderCustomEmptyHtml(list) {
    var idx = 0;
    for (var i = 0; i < wlLists.length; i++) {
      if (list && wlLists[i].id === list.id) {
        idx = i;
        break;
      }
    }
    var title =
      list && !isDefaultCustomName(list.name, idx)
        ? list.name
        : idx === 0
          ? "Custom watchlist"
          : "Custom watchlist " + (idx + 1);
    return (
      '<div class="wl-empty-custom">' +
      "<strong>" +
      escHtml(title) +
      " is empty</strong>" +
      "<p>Use <b>＋ Add symbol</b> in the header above.</p>" +
      '<button type="button" class="wl-empty-rename" data-wl-empty-rename>Rename</button>' +
      "</div>"
    );
  }
  var wlAddSearchTimer = null;
  function renderCustomAddSuggestions(q) {
    var body = document.getElementById("wlBody");
    if (!body || wlSrc !== "custom") return;
    var query = String(q || "").trim();
    if (query.length < 1) {
      renderWl();
      return;
    }
    body.innerHTML =
      '<div class="wl-empty-custom"><p class="wl-empty-hint">Searching…</p></div>';
    var url = "/api/search.php?q=" + encodeURIComponent(query);
    fetch(url, { credentials: "include", cache: "no-store" })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        if (wlSrc !== "custom") return;
        var curQ = ((document.getElementById("wlSearch") || {}).value || "").trim();
        if (curQ.toLowerCase() !== query.toLowerCase()) return;
        var rows = (data && data.results) || [];
        if (!rows.length) {
          body.innerHTML =
            '<div class="wl-empty-custom"><strong>No match</strong>' +
            '<p class="wl-empty-hint">Pick a symbol from Bharat Charts search results only.</p></div>';
          return;
        }
        var html = '<div class="wl-add-suggest-head">Tap to add</div>';
        rows.slice(0, 12).forEach(function (r) {
          var sym = String(r.symbol || r.label || "").toUpperCase();
          var lab = String(r.label || sym);
          var inList = isInCustom(sym);
          if (r.logo_url) {
            rememberLogo(sym, r.logo_url);
            rememberLogo(lab, r.logo_url);
          }
          var logo = r.logo_url || logoUrlFor(sym) || logoUrlFor(lab);
          var ico = logo
            ? '<img class="wl-logo" src="' +
              escAttr(logo) +
              '" alt="" width="18" height="18" loading="lazy" decoding="async" />'
            : "";
          html +=
            '<button type="button" class="wl-add-suggest' +
            (inList ? " is-in" : "") +
            '" data-add-custom="' +
            escAttr(sym) +
            '">' +
            ico +
            '<span class="wl-add-suggest-sym">' +
            escHtml(lab) +
            "</span>" +
            '<span class="wl-add-suggest-act">' +
            (inList ? "✓" : "+") +
            "</span></button>";
        });
        body.innerHTML = html;
      })
      .catch(function () {
        if (wlSrc !== "custom") return;
        body.innerHTML =
          '<div class="wl-empty-custom"><p class="wl-empty-hint">Search failed. Try again.</p></div>';
      });
  }
  function ensureNifty500Loaded() {
    if ((wlData.nifty500 || []).length || wlNifty500Loading) return;
    wlNifty500Loading = true;
    if (wlSrc === "nifty500") {
      var bodyLoad = document.getElementById("wlBody");
      if (bodyLoad) {
        bodyLoad.innerHTML =
          '<div class="cx-empty" style="position:static;padding:1rem;opacity:.7">Loading Nifty 500…</div>';
      }
    }
    var url = WL_URL + (WL_URL.indexOf("?") >= 0 ? "&" : "?") + "bag=nifty500";
    fetch(url, { credentials: "include", cache: "no-store" })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        wlNifty500Loading = false;
        if (!data || !data.ok) return;
        if (data.nifty500_n != null) wlNifty500N = Number(data.nifty500_n) || 0;
        wlData.nifty500 = data.nifty500 || [];
        ingestLogosFromRows(wlData.nifty500);
        if (wlSrc === "nifty500") renderWl();
      })
      .catch(function () {
        wlNifty500Loading = false;
        if (wlSrc === "nifty500") renderWl();
      });
  }

  /** Pull LTP/OHLC from any watchlist bag so click can seed TickHub before tip returns. */
  function findWatchlistRow(sym) {
    var want = String(sym || "").toUpperCase();
    if (!want) return null;
    var bags = ["indices", "sectors", "fno", "nifty50", "nifty500", "custom"];
    for (var b = 0; b < bags.length; b++) {
      var rows = wlData[bags[b]] || [];
      for (var i = 0; i < rows.length; i++) {
        var r = rows[i];
        if (!r) continue;
        var keys = [r.symbol, r.label, r.index_symbol];
        for (var k = 0; k < keys.length; k++) {
          if (String(keys[k] || "").toUpperCase() === want) return r;
        }
        var stocks = r.stocks || [];
        for (var s = 0; s < stocks.length; s++) {
          var st = stocks[s];
          if (!st) continue;
          if (
            String(st.symbol || "").toUpperCase() === want ||
            String(st.label || "").toUpperCase() === want
          ) {
            return st;
          }
        }
      }
    }
    for (var j = 0; j < (wlSectorStockRows || []).length; j++) {
      var sr = wlSectorStockRows[j];
      if (
        sr &&
        (String(sr.symbol || "").toUpperCase() === want ||
          String(sr.label || "").toUpperCase() === want)
      ) {
        return sr;
      }
    }
    return null;
  }

  function setSymbolFromWatchlist(sym) {
    sym = String(sym || "").trim();
    if (!sym) return;
    var row = findWatchlistRow(sym);
    var opts = { force: true };
    // Seed TickHub only from a sane WL quote. Sid collisions (ABB↔NIFTY both id 13)
    // used to stamp +200% LTPs and paint a giant spike candle on click.
    if (row && Number(row.ltp) > 0) {
      var seedLtp = Number(row.ltp);
      var seedPrev = Number(row.prev_close) > 0 ? Number(row.prev_close) : 0;
      var seedChg = Math.abs(Number(row.chg) || 0);
      var poison =
        seedChg >= 50 ||
        (seedPrev > 0 && Math.abs(seedLtp - seedPrev) / seedPrev >= 0.5);
      if (!poison) {
        opts.ltp = seedLtp;
        if (Number(row.open) > 0) opts.open = Number(row.open);
        if (Number(row.high) > 0) opts.high = Number(row.high);
        if (Number(row.low) > 0) opts.low = Number(row.low);
        try {
          if (TickHub && typeof TickHub.applyQuote === "function") {
            TickHub.applyQuote(sym, {
              ltp: opts.ltp,
              open: opts.open || opts.ltp,
              high: opts.high || opts.ltp,
              low: opts.low || opts.ltp,
              prev_close: seedPrev > 0 ? seedPrev : undefined,
              segment: row.segment || undefined,
              security_id: row.security_id || row._sid || undefined,
              ts: Date.now(),
            });
          }
        } catch (_) {}
      }
    }
    setSymbol(sym, opts);
  }
  function setWlSource(src) {
    var id = src || DEFAULT_WL_ID;
    var L = isSystemWlId(id) ? null : findCustomList(id) || wlLists[0] || null;
    if (!L && !isSystemWlId(id)) id = DEFAULT_WL_ID;
    if (!L) {
      wlActiveListId = id;
      wlSrc = id;
    } else {
      wlActiveListId = L.id;
      wlPinCustomId = L.id;
      wlSrc = "custom";
    }
    if (wlSrc !== "sectors") {
      wlSectorView = "list";
      wlActiveSector = null;
      wlSectorStockRows = [];
    }
    if (wlSrc === "nifty500") ensureNifty500Loaded();
    persistWlLists();
    syncWlListBtn();
    renderCustomTabs();
    setWlListMenuOpen(false);
    syncSectorNav();
    renderWl();
    refreshCustomQuotes();
  }
  function createCustomList(name) {
    if (wlLists.length >= WL_MAX_LISTS) {
      alert("You already have " + WL_MAX_LISTS + " custom watchlists. Delete one to make room.");
      return null;
    }
    var L = {
      id: newListId(),
      name: String(name || "").trim().slice(0, 40) || nextCustomWatchlistName(),
      symbols: [],
    };
    wlLists.push(L);
    if (!findCustomList(wlPinCustomId)) wlPinCustomId = L.id;
    persistWlLists();
    renderCustomTabs();
    return L;
  }
  function deleteActiveCustomList() {
    var cur = wlSrc === "custom" ? findCustomList(wlActiveListId) : null;
    if (!cur) return;
    var label = cur.name || "Custom watchlist";
    if (!window.confirm("Delete “" + label + "”? This cannot be undone.")) return;
    wlLists = wlLists.filter(function (L) {
      return L.id !== cur.id;
    });
    if (wlPinCustomId === cur.id) wlPinCustomId = wlLists.length ? wlLists[0].id : "";
    wlData.custom = [];
    persistWlLists();
    setWlSource(DEFAULT_WL_ID);
  }
  function isInCustom(sym) {
    var ticker = String(sym || "").trim().toUpperCase();
    var L = activeCustomList();
    return !!ticker && !!L && L.symbols.indexOf(ticker) >= 0;
  }
  function addToCustom(sym) {
    var ticker = String(sym || "").trim().toUpperCase();
    if (!ticker) return;
    var L = activeCustomList();
    if (!L) {
      L = createCustomList(nextCustomWatchlistName());
      if (!L) return;
      wlPinCustomId = L.id;
    }
    if (L.symbols.indexOf(ticker) < 0) {
      if (L.symbols.length >= WL_MAX_SYMS) {
        alert("This watchlist is full (" + WL_MAX_SYMS + " symbols).");
        return;
      }
      L.symbols.push(ticker);
      persistWlLists();
    }
    wlData.custom = buildCustomRows();
    renderWl();
    refreshCustomQuotes();
  }
  function removeFromCustom(sym) {
    var ticker = String(sym || "").trim().toUpperCase();
    var L = wlSrc === "custom" ? findCustomList(wlActiveListId) : activeCustomList();
    if (!L) return;
    L.symbols = L.symbols.filter(function (s) {
      return s !== ticker;
    });
    persistWlLists();
    wlData.custom = buildCustomRows();
    renderWl();
  }
  function toggleCustom(sym) {
    if (isInCustom(sym)) removeFromCustom(sym);
    else addToCustom(sym);
  }
  function addCustomHtml(sym) {
    var pin = findCustomList(wlPinCustomId);
    var pinName = pin ? pin.name : "Custom watchlist";
    if (isInCustom(sym)) {
      return (
        '<button type="button" class="wl-add-custom is-in" data-toggle-custom="' +
        escAttr(sym) +
        '" title="Remove from ' +
        escAttr(pinName) +
        '">✓</button>'
      );
    }
    return (
      '<button type="button" class="wl-add-custom" data-toggle-custom="' +
      escAttr(sym) +
      '" title="Add to ' +
      escAttr(pinName) +
      '">+</button>'
    );
  }
  function refreshCustomQuotes() {
    var L = wlSrc === "custom" ? findCustomList(wlActiveListId) : null;
    var syms = L ? L.symbols : [];
    if (!syms.length) {
      wlData.custom = [];
      return Promise.resolve();
    }
    var url = WL_URL + (WL_URL.indexOf("?") >= 0 ? "&" : "?") + "symbols=" + encodeURIComponent(syms.join(","));
    return fetch(url, { credentials: "include", cache: "no-store" })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        if (!data || !data.ok) return;
        applyMarketUiState(data.market);
        var by = {};
        (data.quotes || []).forEach(function (q) {
          var k = String(q.symbol || q.label || "").toUpperCase();
          if (k) by[k] = q;
        });
        var old = {};
        (wlData.custom || []).forEach(function (r) {
          old[String(r.symbol || "").toUpperCase()] = r;
        });
        var nowMs = Date.now();
        wlData.custom = syms.map(function (sym) {
          var q = by[sym] || lookupRowQuote(sym);
          var su = String(sym).toUpperCase();
          var o = old[su];
          if (o && Number(o.ltp) > 0 && wlWsFreshAt[su] && nowMs - wlWsFreshAt[su] < 15000) {
            q = Object.assign({}, q || {}, { ltp: o.ltp, chg: o.chg, chg_pct: null, chg_abs: o.chg_abs });
          }
          return {
            label: (q && (q.label || q.symbol)) || sym,
            symbol: sym,
            ltp: q && q.ltp,
            chg: q && (q.chg != null ? q.chg : q.chg_pct),
            chg_abs: q && q.chg_abs,
            prev_close: q && q.prev_close,
            kind: "custom",
          };
        });
        if (wlSrc === "custom") {
          renderWl();
        } else {
          patchWlQuotes();
        }
      })
      .catch(function () {});
  }
  function syncSectorNav() {
    var nav = document.getElementById("wlSectorNav");
    var labelEl = document.getElementById("wlSectorNavLabel");
    var chgEl = document.getElementById("wlSectorNavChg");
    var show = wlSrc === "sectors" && wlSectorView === "stocks" && !!wlActiveSector;
    if (nav) nav.hidden = !show;
    if (!show || !labelEl) return;
    var found = null;
    for (var i = 0; i < (wlData.sectors || []).length; i++) {
      var s = wlData.sectors[i];
      if (s.key === wlActiveSector || s.label === wlActiveSector) {
        found = s;
        break;
      }
    }
    var count = found
      ? Number(found.stock_count) || (found.stocks || []).length || 0
      : wlSectorStockRows.length
        ? Math.max(0, wlSectorStockRows.length - 1)
        : 0;
    labelEl.textContent = found
      ? (found.label || found.key) + (count ? " · " + count : "")
      : String(wlActiveSector || "Sector");
    if (chgEl) {
      chgEl.textContent = found ? fmtChg(found.chg) : "—";
      chgEl.className = "wl-sector-nav-chg " + (found ? chgClass(found.chg) : "flat");
    }
  }
  function findSectorByKey(key) {
    var want = String(key || "").toUpperCase();
    for (var i = 0; i < (wlData.sectors || []).length; i++) {
      var s = wlData.sectors[i];
      var keys = [s.key, s.label, s.symbol, s.index_symbol];
      for (var j = 0; j < keys.length; j++) {
        if (String(keys[j] || "").toUpperCase() === want) return s;
      }
    }
    return null;
  }
  function openSectorStocks(sectorKey) {
    var sec = findSectorByKey(sectorKey);
    if (!sec) return;
    wlActiveListId = "sectors";
    wlSrc = "sectors";
    wlSectorView = "stocks";
    wlActiveSector = sec.key || sec.label;
    syncWlListBtn();
    var indexSym = sec.index_symbol || sec.symbol || sec.label;
    function applySectorRows(stocks) {
      var rows = [];
      rows.push({
        label: sec.label || indexSym,
        symbol: indexSym,
        ltp: sec.ltp,
        chg: sec.chg,
        prev_close: sec.prev_close,
        kind: "index",
        security_id: sec.security_id,
      });
      (stocks || []).forEach(function (s) {
        if (!s || !(s.symbol || s.label)) return;
        var sym = s.symbol || s.label;
        if (String(sym).toUpperCase() === String(indexSym).toUpperCase()) return;
        rows.push({
          label: s.label || sym,
          symbol: sym,
          ltp: s.ltp,
          chg: s.chg,
          prev_close: s.prev_close,
          kind: "stock",
          security_id: s.security_id,
        });
      });
      wlSectorStockRows = rows;
      syncSectorNav();
      renderWl();
    }
    var inline = sec.stocks || [];
    if (inline.length) {
      applySectorRows(inline);
      return;
    }
    // Lite watchlist payload omits inline stocks — fetch for this sector only.
    applySectorRows([]);
    var sk = encodeURIComponent(sec.key || sec.label || sectorKey);
    fetch(WL_URL + (WL_URL.indexOf("?") >= 0 ? "&" : "?") + "sector=" + sk, {
      credentials: "include",
      cache: "no-store",
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        if (!data || !data.ok) return;
        sec.stocks = data.stocks || [];
        if (data.index) {
          sec.ltp = data.index.ltp != null ? data.index.ltp : sec.ltp;
          sec.chg = data.index.chg != null ? data.index.chg : sec.chg;
          sec.prev_close = data.index.prev_close != null ? data.index.prev_close : sec.prev_close;
        }
        applySectorRows(sec.stocks);
      })
      .catch(function () {});
  }
  function showSectorList() {
    wlActiveListId = "sectors";
    wlSrc = "sectors";
    wlSectorView = "list";
    wlActiveSector = null;
    wlSectorStockRows = [];
    syncWlListBtn();
    syncSectorNav();
    renderWl();
  }
  function wlChgValue(row) {
    return rowChgPct(row);
  }
  function sortRowsByChg(rows, opts) {
    opts = opts || {};
    var pinIndex = !!opts.pinIndex;
    var dir = wlChgSort === "asc" ? 1 : -1;
    var indexRows = [];
    var rest = [];
    (rows || []).forEach(function (row) {
      if (!row) return;
      if (pinIndex && row.kind === "index") indexRows.push(row);
      else rest.push(row);
    });
    rest.sort(function (a, b) {
      var av = wlChgValue(a);
      var bv = wlChgValue(b);
      if (av == null && bv == null) {
        return String((a && (a.label || a.symbol)) || "").localeCompare(
          String((b && (b.label || b.symbol)) || "")
        );
      }
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av === bv) {
        return String((a && (a.label || a.symbol)) || "").localeCompare(
          String((b && (b.label || b.symbol)) || "")
        );
      }
      return (av - bv) * dir;
    });
    return pinIndex ? indexRows.concat(rest) : rest;
  }
  function syncWlSortUi() {
    var el = document.getElementById("wlColChg");
    if (!el) return;
    el.classList.toggle("is-sort", true);
    el.classList.toggle("is-asc", wlChgSort === "asc");
    el.classList.toggle("is-desc", wlChgSort === "desc");
    var arrow = wlChgSort === "asc" ? " ↑" : " ↓";
    el.textContent = "Chg%" + arrow;
    var label =
      wlChgSort === "asc" ? "Sort change %: low to high" : "Sort change %: high to low";
    el.title = label;
    el.setAttribute("aria-label", label);
    var wlPanel = document.getElementById("wlPanel");
    if (wlPanel) wlPanel.classList.toggle("is-custom-wl", wlSrc === "custom");
    var colAddBtn = document.getElementById("wlColAddSym");
    var colAddMark = document.getElementById("wlColAddMark");
    if (colAddBtn) colAddBtn.hidden = wlSrc !== "custom";
    if (colAddMark) colAddMark.hidden = wlSrc === "custom";
  }
  function setWlChgSort(dir) {
    wlChgSort = dir === "asc" ? "asc" : "desc";
    try {
      localStorage.setItem("ki_charts_wl_chg_sort", wlChgSort);
      pageSaved("Saved");
    } catch (_) {}
    syncWlSortUi();
    renderWl();
  }
  function toggleWlChgSort() {
    setWlChgSort(wlChgSort === "desc" ? "asc" : "desc");
  }
  function renderSectorListHtml(q) {
    var rows = sortRowsByChg(wlData.sectors || []);
    var chevron =
      '<span class="wl-sector-row-chevron" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M9 6l6 6-6 6"/></svg></span>';
    var html = "";
    for (var i = 0; i < rows.length; i++) {
      var s = rows[i];
      var label = s.label || s.key || "";
      if (q && String(label).toLowerCase().indexOf(q) < 0) continue;
      var count = Number(s.stock_count) || (s.stocks || []).length || 0;
      var tone = chgClass(s.chg);
      var toneCls = tone === "up" ? " is-up" : tone === "down" ? " is-down" : "";
      var key = s.key || s.label || "";
      html +=
        '<button type="button" class="wl-sector-row' +
        toneCls +
        '" data-sector-key="' +
        escAttr(key) +
        '">' +
        '<span class="wl-sector-row-main">' +
        '<span class="wl-sector-ico" aria-hidden="true">' +
        wlSvg(
          (function () {
            var k = sectorIconKind(label);
            if (k === "auto") return '<path d="M5 16l1.5-6h11L19 16"/><circle cx="8" cy="17" r="2"/><circle cx="16" cy="17" r="2"/><path d="M7 10l1-3h8l1 3"/>';
            if (k === "it") return '<rect x="3" y="4" width="18" height="12" rx="2"/><path d="M8 20h8M12 16v4"/>';
            if (k === "pharma" || k === "health") return '<path d="M9 3h6v6h6v6h-6v6H9v-6H3V9h6z"/>';
            if (k === "metal") return '<path d="M4 18h16L12 4 4 18z"/>';
            if (k === "oil") return '<path d="M12 3c3 4 6 7 6 11a6 6 0 1 1-12 0c0-4 3-7 6-11z"/>';
            if (k === "fmcg") return '<path d="M6 8h12l-1 13H7L6 8z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/>';
            if (k === "realty") return '<path d="M3 21h18"/><path d="M5 21V9l7-5 7 5v12"/><path d="M10 21v-6h4v6"/>';
            if (k === "defence") return '<path d="M12 3l8 4v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V7l8-4z"/>';
            if (k === "bank" || k === "fin") return '<path d="M3 10l9-6 9 6"/><path d="M5 10v8h14v-8"/><path d="M9 18v-4h6v4"/>';
            if (k === "psu") return '<path d="M4 20h16"/><path d="M6 20V8h12v12"/><path d="M9 8V5h6v3"/>';
            return '<path d="M4 10h6V4H4v6zm10 0h6V4h-6v6zM4 20h6v-6H4v6zm10 0h6v-6h-6v6z"/>';
          })()
        ) +
        "</span>" +
        '<span class="wl-sector-row-name">' +
        escHtml(label) +
        "</span>" +
        (count
          ? '<span class="wl-sector-row-count">' + escHtml(String(count)) + "</span>"
          : "") +
        "</span>" +
        '<span class="wl-sector-row-chg ' +
        tone +
        '">' +
        escHtml(fmtChg(s.chg)) +
        "</span>" +
        chevron +
        "</button>";
    }
    return html;
  }
  function renderWlRowsHtml(rows, q) {
    var html = "";
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      var label = r.label || r.symbol || "";
      var chartSym = r.symbol || label;
      if (q && String(label).toLowerCase().indexOf(q) < 0 && String(chartSym).toLowerCase().indexOf(q) < 0) {
        continue;
      }
      var on = chartSym === activeSymbol || label === activeSymbol ? " active" : "";
      var ltp = r.ltp;
      var chg = rowChgPct(r);
      var chgAbs = rowChgAbs(r);
      if ((ltp == null || !isFinite(Number(ltp))) && chartSym) {
        var qq = lookupRowQuote(chartSym);
        if (qq.ltp != null) {
          ltp = qq.ltp;
          if (chg == null) chg = qq.chg;
          if (chgAbs == null) chgAbs = qq.chg_abs;
        }
      }
      var logo = logoSrcForRow(chartSym, label, r.logo_url);
      var ico = wlRowIconHtml(r, logo, i < 12);
      var addOrRemove =
        wlSrc === "custom"
          ? '<button type="button" class="wl-row-remove" data-remove="' +
            escAttr(chartSym) +
            '" title="Remove">×</button>'
          : addCustomHtml(chartSym);
      var tone = chgClass(chg != null ? chg : chgAbs);
      html +=
        '<div class="wl-row' +
        on +
        '" data-symbol="' +
        escAttr(chartSym) +
        '" role="button" tabindex="0">' +
        '<span class="wl-sym">' +
        ico +
        '<span class="wl-sym-text">' +
        escHtml(label) +
        "</span></span>" +
        '<span class="wl-ltp">' +
        fmtLtp(ltp) +
        "</span>" +
        '<span class="wl-chg-abs ' +
        tone +
        '">' +
        fmtChgAbs(chgAbs) +
        "</span>" +
        '<span class="wl-chg ' +
        tone +
        '">' +
        fmtChg(chg) +
        "</span>" +
        addOrRemove +
        "</div>";
    }
    return html;
  }
  function renderWl() {
    var body = document.getElementById("wlBody");
    if (!body) return;
    var q = ((document.getElementById("wlSearch") || {}).value || "").trim().toLowerCase();
    var colStock = document.getElementById("wlColStock");
    var html = "";
    syncWlListBtn();
    var adSlot = document.getElementById("wlAdSlot");
    if (adSlot) adSlot.hidden = false;
    var wlPanel = document.getElementById("wlPanel");
    if (wlPanel) wlPanel.classList.toggle("is-default-wl", wlSrc === DEFAULT_WL_ID);
    if (wlSrc === DEFAULT_WL_ID) {
      if (colStock) colStock.textContent = "Symbol";
      html = renderWlRowsHtml(defaultWlRows(), q);
      if (!html) {
        html =
          '<div class="cx-empty" style="position:static;padding:1rem;opacity:.6">No symbols</div>';
      }
    } else if (wlSrc === "sectors" && wlSectorView === "list") {
      if (colStock) colStock.textContent = "Sector";
      html = renderSectorListHtml(q);
      if (!html) {
        html =
          '<div class="cx-empty" style="position:static;padding:1rem;opacity:.6">No sectors</div>';
      }
    } else if (wlSrc === "sectors" && wlSectorView === "stocks") {
      if (colStock) colStock.textContent = "Stock";
      html = renderWlRowsHtml(sortRowsByChg(wlSectorStockRows, { pinIndex: true }), q);
      if (!html) {
        html =
          '<div class="cx-empty" style="position:static;padding:1rem;opacity:.6">No stocks</div>';
      }
    } else if (wlSrc === "custom") {
      var L = findCustomList(wlActiveListId);
      if (colStock) colStock.textContent = L ? customListLabel(L, wlLists.indexOf(L)) : "Custom";
      var searchQ = ((document.getElementById("wlSearch") || {}).value || "").trim();
      if (searchQ.length >= 1) {
        syncSectorNav();
        syncWlSortUi();
        return;
      }
      html = renderWlRowsHtml(sortRowsByChg(wlData.custom.length ? wlData.custom : buildCustomRows()), "");
      if (!html) {
        html = renderCustomEmptyHtml(L);
      }
    } else {
      if (colStock) {
        colStock.textContent =
          wlSrc === "nifty50" || wlSrc === "nifty500" || wlSrc === "fno" ? "Stock" : "Index";
      }
      var rows =
        wlSrc === "indices" ? indicesWlRows() : wlData[wlSrc] || [];
      html = renderWlRowsHtml(sortRowsByChg(rows), q);
      if (!html) {
        html =
          '<div class="cx-empty" style="position:static;padding:1rem;opacity:.6">No symbols</div>';
      }
    }
    body.innerHTML = html;
    syncSectorNav();
    syncWlSortUi();
    wlRowIndex = null;
    syncWlQuoteSubs();
    // Idle-warm tips for visible rows → next click opens from MEM/session/IDB.
    try {
      scheduleWarmVisibleWatchlistTips();
    } catch (_) {}
  }

  /** Update LTP/chg in place — never rebuild DOM (rebuild mid-click eats watchlist taps). */
  function patchWlQuotes() {
    var body = document.getElementById("wlBody");
    if (!body) return;
    var nodes = body.querySelectorAll("[data-symbol]");
    var byLab = {};
    ["nifty500", "nifty50", "indices", "sectors", "fno", "custom"].forEach(function (src) {
      (wlData[src] || []).forEach(function (r) {
        var lab = String(r.label || r.symbol || "");
        if (lab) byLab[lab.toUpperCase()] = r;
        var sym = String(r.symbol || "");
        if (sym) byLab[sym.toUpperCase()] = r;
        var idx = String(r.index_symbol || "");
        if (idx) byLab[idx.toUpperCase()] = r;
        (r.stocks || []).forEach(function (s) {
          var sl = String(s.label || s.symbol || "");
          if (sl) byLab[sl.toUpperCase()] = s;
        });
      });
    });
    (wlSectorStockRows || []).forEach(function (r) {
      var lab = String(r.label || r.symbol || "");
      if (lab) byLab[lab.toUpperCase()] = r;
      var sym = String(r.symbol || "");
      if (sym) byLab[sym.toUpperCase()] = r;
    });
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var sym = el.getAttribute("data-symbol") || "";
      var r = byLab[sym.toUpperCase()];
      if (!r) {
        var qq = lookupRowQuote(sym);
        if (qq.ltp == null && qq.chg == null) continue;
        r = { ltp: qq.ltp, chg: qq.chg, chg_abs: qq.chg_abs, prev_close: qq.prev_close };
      }
      var pct = rowChgPct(r);
      var abs = rowChgAbs(r);
      var tone = chgClass(pct != null ? pct : abs);
      var ltpEl = el.querySelector(".wl-ltp");
      var absEl = el.querySelector(".wl-chg-abs");
      var chgEl = el.querySelector(".wl-chg");
      if (ltpEl) ltpEl.textContent = fmtLtp(r.ltp);
      if (absEl) {
        absEl.textContent = fmtChgAbs(abs);
        absEl.className = "wl-chg-abs " + tone;
      }
      if (chgEl) {
        chgEl.textContent = fmtChg(pct);
        chgEl.className = "wl-chg " + tone;
      }
      el.classList.toggle("active", sym === activeSymbol);
    }
    // Sector list rows: patch chg badges.
    var sectorNodes = body.querySelectorAll("[data-sector-key]");
    for (var j = 0; j < sectorNodes.length; j++) {
      var secEl = sectorNodes[j];
      var key = secEl.getAttribute("data-sector-key") || "";
      var sec = findSectorByKey(key);
      if (!sec) continue;
      var secChg = secEl.querySelector(".wl-sector-row-chg");
      if (secChg) {
        secChg.textContent = fmtChg(sec.chg);
        secChg.className = "wl-sector-row-chg " + chgClass(sec.chg);
      }
      secEl.classList.toggle("is-up", chgClass(sec.chg) === "up");
      secEl.classList.toggle("is-down", chgClass(sec.chg) === "down");
    }
    syncSectorNav();
  }

  function markWlActive(sym) {
    var body = document.getElementById("wlBody");
    if (!body) return;
    var nodes = body.querySelectorAll(".wl-row");
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].classList.toggle("active", nodes[i].getAttribute("data-symbol") === sym);
    }
  }

  var wlPaintTimer = null;
  function stampWatchlistQuote(symbol, quote) {
    var ltp = Number(quote && quote.ltp);
    if (!(ltp > 0)) return;
    var feedPrev = Number(quote && quote.prev_close);
    var sid = quote && quote.security_id != null ? String(quote.security_id) : "";
    // Dhan reuses security_ids across segments (13 = NIFTY IDX + ABB EQ). Require segment.
    var qSeg = quote && quote.segment != null ? String(quote.segment).toUpperCase().trim() : "";
    var hit = false;
    function stampRow(row) {
      var sameName =
        String(row.label || "").toUpperCase() === String(symbol).toUpperCase() ||
        String(row.symbol || "").toUpperCase() === String(symbol).toUpperCase() ||
        String(row.index_symbol || "").toUpperCase() === String(symbol).toUpperCase();
      var sameSid = false;
      if (sid && qSeg) {
        var rSid = row.security_id != null ? String(row.security_id) : row._sid != null ? String(row._sid) : "";
        var rSeg = String(row.segment || row._segment || "").toUpperCase().trim();
        if (rSid === sid && rSeg === qSeg) sameSid = true;
      }
      if (sameName || sameSid) {
        row.ltp = ltp;
        var base = acceptFeedPrevClose(row.prev_close, feedPrev, ltp);
        if (base > 0) {
          row.prev_close = base;
          row.chg = Math.round(((ltp - base) / base) * 10000) / 100;
          row.chg_abs = Math.round((ltp - base) * 100) / 100;
        }
        hit = true;
        return true;
      }
      return false;
    }
    ["indices", "sectors", "fno", "custom"].forEach(function (src) {
      var rows = wlData[src] || [];
      for (var i = 0; i < rows.length; i++) {
        stampRow(rows[i]);
        (rows[i].stocks || []).forEach(stampRow);
      }
    });
    (wlSectorStockRows || []).forEach(stampRow);
    if (!hit) return;
    if (wlPaintTimer) return;
    wlPaintTimer = setTimeout(function () {
      wlPaintTimer = null;
      patchWlQuotes();
    }, 250);
  }

  /** name/sid → watchlist row objects; rebuilt lazily after the lists change (renderWl). */
  var wlRowIndex = null;
  /** UPPER symbol → last time a live WS quote stamped it (the 12s poll must not roll it back). */
  var wlWsFreshAt = {};
  var mtPatchTimer = null;
  function buildWlRowIndex() {
    var byName = {};
    var bySid = {};
    function add(row) {
      [row.label, row.symbol, row.index_symbol].forEach(function (n) {
        var k = String(n || "").toUpperCase();
        if (!k) return;
        (byName[k] = byName[k] || []).push(row);
      });
      var sid = row.security_id != null ? String(row.security_id) : row._sid != null ? String(row._sid) : "";
      if (sid) (bySid[sid] = bySid[sid] || []).push(row);
    }
    ["indices", "sectors", "fno", "nifty50", "nifty500", "custom"].forEach(function (src) {
      (wlData[src] || []).forEach(function (r) {
        add(r);
        (r.stocks || []).forEach(add);
      });
    });
    (wlSectorStockRows || []).forEach(add);
    return { byName: byName, bySid: bySid };
  }

  /**
   * Live ticks only update LTP. Previous close stays sticky from tip/API (yesterday's close).
   * Kite/Dhan sometimes stream a stale prev (TRENT 2580 vs real ~2906) which must not wipe %.
   */
  function acceptFeedPrevClose(stickyPrev, feedPrev, ltp) {
    var feed = Number(feedPrev) > 0 ? Number(feedPrev) : 0;
    var sticky = Number(stickyPrev) > 0 ? Number(stickyPrev) : 0;
    if (feed > 0 && ltp > 0 && Math.abs(feed - ltp) / Math.max(ltp, 1) < 1e-5) feed = 0;
    if (!(feed > 0)) return sticky > 0 ? sticky : 0;
    if (!(sticky > 0)) return feed;
    var div = Math.abs(feed - sticky) / Math.max(sticky, 1);
    if (div < 0.02) return sticky;
    // Feed disagrees a lot — keep sticky tip/API previous close.
    return sticky;
  }

  /** Batched relay quotes {SYM: [ltp, prev_close, chg]} → rows, repaint at most every 250ms. */
  function stampWatchlistQuotesBatch(q) {
    if (!wlRowIndex) wlRowIndex = buildWlRowIndex();
    var now = Date.now();
    var hit = false;
    Object.keys(q || {}).forEach(function (sym) {
      var v = q[sym];
      var ltp = Number(v && v[0]);
      if (!(ltp > 0)) return;
      var feedPrev = Number(v[1]) > 0 ? Number(v[1]) : 0;
      var su = String(sym).toUpperCase();
      var rows = wlRowIndex.byName[su];
      wlWsFreshAt[su] = now;
      try {
        if (alertCtl && typeof alertCtl.onQuote === "function") alertCtl.onQuote(sym, ltp);
      } catch (_) {}
      if (!rows) return;
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        row.ltp = ltp;
        var base = acceptFeedPrevClose(row.prev_close, feedPrev, ltp);
        if (base > 0) {
          row.prev_close = base;
          row.chg = Math.round(((ltp - base) / base) * 10000) / 100;
          row.chg_abs = Math.round((ltp - base) * 100) / 100;
        }
      }
      hit = true;
    });
    if (!hit) return;
    if (!wlPaintTimer) {
      wlPaintTimer = setTimeout(function () {
        wlPaintTimer = null;
        patchWlQuotes();
      }, 250);
    }
    if (!mtPatchTimer) {
      mtPatchTimer = setTimeout(function () {
        mtPatchTimer = null;
        patchMarketTickerQuotes(marketTickerItems(false));
      }, 1000);
    }
  }

  /** Every row on screen (watchlist + market tape) streams over the relay's batched quotes. */
  function syncWlQuoteSubs() {
    var want = {};
    function take(sym) {
      var s = String(sym || "").trim();
      if (s) want[s.toUpperCase()] = s;
    }
    var body = document.getElementById("wlBody");
    if (body) {
      var nodes = body.querySelectorAll("[data-symbol]");
      for (var i = 0; i < nodes.length; i++) take(nodes[i].getAttribute("data-symbol"));
      var secs = body.querySelectorAll("[data-sector-key]");
      for (var j = 0; j < secs.length; j++) {
        var sec = findSectorByKey(secs[j].getAttribute("data-sector-key"));
        if (sec) take(sec.label || sec.index_symbol || sec.symbol);
      }
    }
    var track = document.getElementById("cxMarketTickerTrack");
    if (track) {
      var mt = track.querySelectorAll(".cx-mticker-item[data-symbol]");
      for (var k = 0; k < mt.length; k++) take(mt[k].getAttribute("data-symbol"));
    }
    TickHub.setQuoteSymbols(Object.keys(want).map(function (u) {
      return want[u];
    }));
  }

  /**
   * Continuous market tape under header — indices, then F&O + Nifty 50 top gainers / losers
   * interleaved (gainer, loser, gainer…). VC/915-style CSS scroll.
   */
  var marketTickerBuilt = false;
  var MARKET_TICKER_MOVERS_EACH = 20;
  var MARKET_TICKER_FALLBACK_CAP = 40;
  var MARKET_TICKER_RESELECT_MS = 5 * 60 * 1000;
  var MARKET_TICKER_SECS = 110;
  /** Scroll speed is fixed in px/s so a width change never alters the pace or jumps the tape. */
  var MARKET_TICKER_PX_PER_SEC = 80;
  /** Mover set is re-picked every few minutes, not every quote poll — a new set restarts the scroll. */
  var marketTickerPick = null;
  var marketTickerPending = null;
  var marketTickerAnim = null;
  var marketTickerHalf = 0;
  var marketTickerSwapTimer = null;

  function tickerItemFromRow(r, kind) {
    var lab = String((r && (r.label || r.symbol)) || "").trim();
    if (!lab) return null;
    var chg = r.chg != null ? Number(r.chg) : r.chg_pct != null ? Number(r.chg_pct) : null;
    return {
      label: lab,
      kind: kind,
      ltp: r.ltp != null ? Number(r.ltp) : null,
      chg: chg != null && isFinite(chg) ? chg : null,
      logo: r.logo_url || logoUrlFor(r.symbol) || logoUrlFor(lab) || "",
    };
  }

  function marketTickerStockPool() {
    var bySym = {};
    var list = [];
    function add(r) {
      var it = tickerItemFromRow(r, "stock");
      if (!it) return;
      var k = it.label.toUpperCase();
      var ex = bySym[k];
      if (ex) {
        if (ex.chg == null && it.chg != null) {
          ex.chg = it.chg;
          ex.ltp = it.ltp;
        }
        if (!ex.logo && it.logo) ex.logo = it.logo;
        return;
      }
      bySym[k] = it;
      list.push(it);
    }
    (wlData.fno || []).forEach(add);
    (wlData.nifty50 || []).forEach(add);
    return { bySym: bySym, list: list };
  }

  function pickTickerMovers(list) {
    var ranked = list
      .filter(function (it) {
        return it.chg != null && it.ltp != null && it.ltp > 0;
      })
      .sort(function (a, b) {
        return b.chg - a.chg;
      });
    var gainers = ranked
      .filter(function (it) {
        return it.chg > 0;
      })
      .slice(0, MARKET_TICKER_MOVERS_EACH);
    var losers = ranked
      .filter(function (it) {
        return it.chg < 0;
      })
      .reverse()
      .slice(0, MARKET_TICKER_MOVERS_EACH);
    var out = [];
    for (var i = 0; i < Math.max(gainers.length, losers.length); i++) {
      if (gainers[i]) out.push(gainers[i].label);
      if (losers[i]) out.push(losers[i].label);
    }
    return out;
  }

  function marketTickerItems(reselect) {
    var out = [];
    var seen = {};
    function push(it) {
      if (!it) return;
      var k = it.label.toUpperCase();
      if (seen[k]) return;
      seen[k] = true;
      out.push(it);
    }
    (wlData.indices || []).forEach(function (r) {
      push(tickerItemFromRow(r, "index"));
    });
    var pool = marketTickerStockPool();
    var now = Date.now();
    var pick = marketTickerPick;
    if (reselect || !pick || pick.fallback || now - pick.at > MARKET_TICKER_RESELECT_MS) {
      var movers = pickTickerMovers(pool.list);
      pick = movers.length
        ? { at: now, labels: movers, fallback: false }
        : {
            at: now,
            fallback: true,
            labels: pool.list.slice(0, MARKET_TICKER_FALLBACK_CAP).map(function (it) {
              return it.label;
            }),
          };
      marketTickerPick = pick;
    }
    pick.labels.forEach(function (lab) {
      push(pool.bySym[String(lab).toUpperCase()]);
    });
    return out;
  }

  function fmtTickerPx(v) {
    if (v == null || !(Number(v) > 0)) return "—";
    var n = Number(v);
    if (n >= 1000) return n.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    return n.toFixed(2);
  }

  function fmtTickerChg(pct) {
    if (pct == null || !isFinite(Number(pct))) return "—";
    var n = Number(pct);
    var sign = n > 0 ? "+" : "";
    return sign + n.toFixed(2) + "%";
  }

  function tickerChgTone(pct) {
    if (pct == null || !isFinite(Number(pct))) return "flat";
    if (Number(pct) > 0.05) return "up";
    if (Number(pct) < -0.05) return "down";
    return "flat";
  }

  /**
   * Quote text widths only ever grow (min-width in ch), so a 12s patch like 999.95 → 1,000.10
   * or +0.99% → +1.00% does not reflow every item after it.
   */
  function tickerMinWidth(len, padRem) {
    return padRem ? "calc(" + len + "ch + " + padRem + "rem)" : len + "ch";
  }

  function setTickerText(el, text, padRem) {
    if (el.textContent === text) return;
    el.textContent = text;
    var len = text.length;
    if (len > (Number(el.getAttribute("data-mt-len")) || 0)) {
      el.setAttribute("data-mt-len", String(len));
      el.style.minWidth = tickerMinWidth(len, padRem);
    }
  }

  function buildTickerItemHtml(item, ghost) {
    var tone = tickerChgTone(item.chg);
    var arrow = tone === "up" ? "▲" : tone === "down" ? "▼" : "";
    var kindCls = item.kind === "index" ? " is-index" : "";
    var pxText = fmtTickerPx(item.ltp);
    var chgText = arrow + fmtTickerChg(item.chg);
    return (
      '<button type="button" class="cx-mticker-item' +
      kindCls +
      '" data-symbol="' +
      escapeHtml(item.label) +
      '"' +
      (ghost ? ' tabindex="-1" aria-hidden="true"' : "") +
      ">" +
      (item.logo
        ? '<img class="cx-mticker-logo" src="' +
          escapeHtml(item.logo) +
          '" alt="" width="18" height="18" decoding="async" />'
        : "") +
      '<span class="cx-mticker-sym">' +
      escapeHtml(item.label) +
      "</span>" +
      '<span class="cx-mticker-px" data-mt-px data-mt-len="' +
      pxText.length +
      '" style="min-width:' +
      tickerMinWidth(pxText.length, 0) +
      '">' +
      pxText +
      "</span>" +
      '<span class="cx-mticker-chg ' +
      tone +
      '" data-mt-chg data-mt-len="' +
      chgText.length +
      '" style="min-width:' +
      tickerMinWidth(chgText.length, 0.8) +
      '">' +
      chgText +
      "</span>" +
      "</button>"
    );
  }

  function marketTickerReducedMotion() {
    try {
      return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    } catch (_) {
      return false;
    }
  }

  /** Width of one pass (the track holds the item list twice for a seamless loop). */
  function measureMarketTickerHalf(track) {
    var first = track.querySelector(".cx-mticker-item");
    var ghost = track.querySelector('.cx-mticker-item[aria-hidden="true"]');
    if (!first || !ghost) return 0;
    return ghost.offsetLeft - first.offsetLeft;
  }

  /** Current scroll offset in px within one pass. */
  function marketTickerOffset() {
    var a = marketTickerAnim;
    if (!a || !(marketTickerHalf > 0)) return 0;
    var dur = (marketTickerHalf / MARKET_TICKER_PX_PER_SEC) * 1000;
    var t = Number(a.currentTime) || 0;
    return ((t % dur) / dur) * marketTickerHalf;
  }

  /**
   * Compositor-driven scroll (Web Animations, transform only) keyed to pixels. Re-keying on a
   * width change keeps the same px offset, so the tape never jumps.
   */
  function syncMarketTickerAnim(track, keepOffset) {
    if (marketTickerReducedMotion()) return;
    var half = measureMarketTickerHalf(track);
    if (!(half > 0)) return;
    if (typeof track.animate !== "function") {
      track.classList.add("is-css");
      track.style.animationDuration = Math.max(20, Math.round(half / MARKET_TICKER_PX_PER_SEC)) + "s";
      return;
    }
    if (marketTickerAnim && keepOffset == null && Math.abs(half - marketTickerHalf) < 0.5) return;
    var offset = keepOffset != null ? keepOffset : marketTickerOffset();
    offset = ((offset % half) + half) % half;
    var paused = !!(marketTickerAnim && marketTickerAnim.playState === "paused");
    if (marketTickerAnim) marketTickerAnim.cancel();
    var dur = (half / MARKET_TICKER_PX_PER_SEC) * 1000;
    marketTickerAnim = track.animate(
      [{ transform: "translate3d(0,0,0)" }, { transform: "translate3d(" + -half + "px,0,0)" }],
      { duration: dur, iterations: Infinity, easing: "linear" }
    );
    marketTickerAnim.currentTime = (offset / half) * dur;
    marketTickerHalf = half;
    if (paused) marketTickerAnim.pause();
  }

  /** px width of the leading items shared by the current track and the next item list. */
  function marketTickerSharedPrefixPx(track, nextItems) {
    var nodes = track.querySelectorAll('.cx-mticker-item:not([aria-hidden="true"])');
    if (!nodes.length) return 0;
    var base = nodes[0].offsetLeft;
    var n = Math.min(nodes.length, nextItems.length);
    for (var i = 0; i < n; i++) {
      var a = String(nodes[i].getAttribute("data-symbol") || "").toUpperCase();
      if (a !== String(nextItems[i].label).toUpperCase()) return nodes[i].offsetLeft - base;
    }
    return n < nodes.length ? nodes[n].offsetLeft - base : 0;
  }

  /**
   * Swap in a new mover set only while the visible window sits inside the unchanged leading
   * items (indices) — then nothing on screen changes. Fade-swap if that never happens in time.
   */
  function tryMarketTickerSwap() {
    var track = document.getElementById("cxMarketTickerTrack");
    var p = marketTickerPending;
    if (!track || !p || !p.items) {
      stopMarketTickerSwapWatch();
      return;
    }
    var nextSig = p.items
      .map(function (i) {
        return i.label;
      })
      .join("|");
    var wrap = track.parentElement;
    var viewW = wrap ? wrap.clientWidth : 0;
    var offset = marketTickerOffset();
    var shared = marketTickerSharedPrefixPx(track, p.items);
    if (offset + viewW <= shared) {
      stopMarketTickerSwapWatch();
      buildMarketTickerTrack(track, p.items, nextSig, offset);
      return;
    }
    if (Date.now() - p.at < (MARKET_TICKER_SECS + 30) * 1000) return;
    stopMarketTickerSwapWatch();
    track.classList.add("is-swapping");
    setTimeout(function () {
      var cur = marketTickerPending;
      if (cur && cur.items) {
        buildMarketTickerTrack(
          track,
          cur.items,
          cur.items
            .map(function (i) {
              return i.label;
            })
            .join("|"),
          0
        );
      }
      track.classList.remove("is-swapping");
    }, 220);
  }

  function startMarketTickerSwapWatch() {
    if (marketTickerSwapTimer) return;
    marketTickerSwapTimer = setInterval(tryMarketTickerSwap, 250);
  }

  function stopMarketTickerSwapWatch() {
    if (!marketTickerSwapTimer) return;
    clearInterval(marketTickerSwapTimer);
    marketTickerSwapTimer = null;
  }

  function escapeHtml(s) {
    return String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function renderMarketTicker(forceRebuild) {
    var track = document.getElementById("cxMarketTickerTrack");
    if (!track) return;
    var wasFallback = !marketTickerPick || marketTickerPick.fallback;
    var items = marketTickerItems(forceRebuild);
    if (!items.length) {
      if (marketTickerBuilt) return;
      track.innerHTML = '<span class="cx-mticker-empty">Waiting for quotes…</span>';
      return;
    }
    // Only rebuild DOM when symbol set changes — keeps the scroll continuous.
    var sig = items
      .map(function (i) {
        return i.label;
      })
      .join("|");
    if (marketTickerBuilt && track.getAttribute("data-sig") === sig) {
      marketTickerPending = null;
      stopMarketTickerSwapWatch();
      patchMarketTickerQuotes(items);
      return;
    }
    // New mover set mid-scroll: swap once the visible window is over the unchanged indices.
    if (marketTickerBuilt && !wasFallback) {
      if (!marketTickerPending) marketTickerPending = { at: Date.now() };
      marketTickerPending.items = items;
      patchMarketTickerQuotes(items);
      startMarketTickerSwapWatch();
      return;
    }
    buildMarketTickerTrack(track, items, sig, marketTickerBuilt ? marketTickerOffset() : 0);
  }

  function buildMarketTickerTrack(track, items, sig, keepOffset) {
    marketTickerPending = null;
    var html = "";
    var pass;
    for (pass = 0; pass < 2; pass++) {
      items.forEach(function (item) {
        html += buildTickerItemHtml(item, pass === 1);
      });
    }
    track.innerHTML = html;
    track.setAttribute("data-sig", sig);
    syncWlQuoteSubs();
    if (!marketTickerBuilt) {
      track.classList.add("is-entering");
      setTimeout(function () {
        track.classList.remove("is-entering");
      }, 450);
    }
    marketTickerBuilt = true;
    syncMarketTickerAnim(track, keepOffset || 0);
    if (!track._mtWired) {
      track._mtWired = true;
      track.addEventListener("click", function (e) {
        var btn = e.target && e.target.closest ? e.target.closest(".cx-mticker-item") : null;
        if (!btn || btn.getAttribute("aria-hidden") === "true") return;
        var sym = btn.getAttribute("data-symbol");
        if (sym) setSymbolFromWatchlist(sym);
      });
      track.addEventListener(
        "error",
        function (e) {
          var img = e.target;
          // Hide, don't remove: removing shrinks the item and shifts the tape.
          if (img && img.tagName === "IMG" && img.classList.contains("cx-mticker-logo")) {
            img.style.visibility = "hidden";
          }
        },
        true
      );
      var wrap = track.parentElement;
      if (wrap) {
        wrap.addEventListener("mouseenter", function () {
          if (marketTickerAnim) marketTickerAnim.pause();
        });
        wrap.addEventListener("mouseleave", function () {
          if (marketTickerAnim) marketTickerAnim.play();
        });
      }
      if (typeof ResizeObserver === "function") {
        var roFrame = 0;
        new ResizeObserver(function () {
          if (roFrame) return;
          roFrame = requestAnimationFrame(function () {
            roFrame = 0;
            syncMarketTickerAnim(track);
          });
        }).observe(track);
      }
    }
  }

  function patchMarketTickerQuotes(items) {
    var track = document.getElementById("cxMarketTickerTrack");
    if (!track) return;
    var by = {};
    (items || marketTickerItems()).forEach(function (it) {
      by[String(it.label).toUpperCase()] = it;
    });
    var nodes = track.querySelectorAll(".cx-mticker-item[data-symbol]");
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var it = by[String(el.getAttribute("data-symbol") || "").toUpperCase()];
      if (!it) continue;
      var px = el.querySelector("[data-mt-px]");
      var chg = el.querySelector("[data-mt-chg]");
      var tone = tickerChgTone(it.chg);
      var arrow = tone === "up" ? "▲" : tone === "down" ? "▼" : "";
      if (px) setTickerText(px, fmtTickerPx(it.ltp), 0);
      if (chg) {
        setTickerText(chg, arrow + fmtTickerChg(it.chg), 0.8);
        var cls = "cx-mticker-chg " + tone;
        if (chg.className !== cls) chg.className = cls;
      }
    }
  }

  function loadWatchlist() {
    // Lite payload (no nifty500 / sector stocks) — page + ticker paint fast.
    loadLogosIndex();
    return fetch(WL_URL, { credentials: "include", cache: "no-store" })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        function attach(rows) {
          return (rows || []).map(function (r) {
            return r;
          });
        }
        wlData.indices = attach(data && data.indices);
        wlData.sectors = attach(data && data.sectors);
        wlData.fno = attach(data && data.fno);
        wlData.nifty50 = attach(data && data.nifty50);
        if (data && data.nifty500_n != null) wlNifty500N = Number(data.nifty500_n) || 0;
        if (data && Array.isArray(data.nifty500) && data.nifty500.length) {
          wlData.nifty500 = attach(data.nifty500);
        }
        try {
          setFnoClosingAuctionSymbols(
            (wlData.fno || []).map(function (r) {
              return (r && (r.label || r.symbol)) || "";
            })
          );
        } catch (_) {}
        ingestLogosFromRows(wlData.indices);
        ingestLogosFromRows(wlData.sectors);
        ingestLogosFromRows(wlData.fno);
        ingestLogosFromRows(wlData.nifty50);
        ingestLogosFromRows(wlData.nifty500);
        applyMarketUiState(data && data.market);
        renderWl();
        renderMarketTicker(true);
        refreshCustomQuotes();
        if (wlSrc === "nifty500") ensureNifty500Loaded();
      })
      .catch(function (e) {
        log("wl.err", e);
      });
  }

  /**
   * Prefetch tip into MEM/session/IDB. mode "warm" = press/click (prio 0);
   * "hover" = background warm (prio 2, never blocks click).
   */
  function prefetchTip(sym, mode) {
    sym = String(sym || "").trim();
    if (!sym) return;
    if (Date.now() < tipBackoffUntil) return;
    mode = mode || "warm";
    // Hover and watchlist warm must not call ticks.php. That path is one Redis
    // read for the open chart; firing it per visible row filled the PHP pool.
    var k = subKey(sym, activeRes);
    var cur = tipPrefetch[k];
    if (cur && Date.now() - cur.at < 20000) return;
    if ((histCache[k] || []).length >= 80 && mode === "hover") return;
    if (!tipSymReady(sym, mode === "hover" ? "hover" : "warm")) return;
    var p = tipNet(sym, activeRes, mode === "hover" ? "hover" : "warm");
    tipPrefetch[k] = { p: p, at: Date.now() };
    p.then(function (pack) {
      try {
        var bars = (pack && pack.bars) || [];
        if (bars.length) {
          if (bars[0].time < 1e12) bars = toTvBars(bars);
          bars = stripOpenBucket(bars, activeRes, sym);
          // Only cache dense tips — holey warm shelves caused gap-on-click.
          if (
            bars.length >= 40 &&
            (!inSessionNow(sym) || sessionTipSolid(bars, sym, activeRes))
          ) {
            persistTipBars(k, bars);
            lastHistoricalBar[k] = bars[bars.length - 1];
          }
        }
      } catch (_) {}
    }).catch(function () {
      if (tipPrefetch[k] && tipPrefetch[k].p === p) delete tipPrefetch[k];
    });
  }

  function prewarmSymbol(sym) {
    if (!TIP_HOVER_ENABLED) return;
    prefetchTip(sym, "hover");
  }

  var wlTipWarmTimer = null;
  var wlTipWarmQueue = [];
  var wlTipWarmAt = {};
  var wlTipWarmSched = null;

  function scheduleWarmVisibleWatchlistTips() {
    if (wlTipWarmSched) clearTimeout(wlTipWarmSched);
    wlTipWarmSched = setTimeout(function () {
      wlTipWarmSched = null;
      warmVisibleWatchlistTips();
    }, 600);
  }

  /** Background-fill tip for symbols currently listed — click then paints from MEM. */
  function warmVisibleWatchlistTips() {
    if (!TIP_HOVER_ENABLED) return;
    if (Date.now() < tipBackoffUntil) return;
    var body = document.getElementById("wlBody");
    if (!body) return;
    var nodes = body.querySelectorAll("[data-symbol]");
    var next = [];
    var seen = {};
    for (var i = 0; i < nodes.length && next.length < 30; i++) {
      var sym = String(nodes[i].getAttribute("data-symbol") || "").trim();
      if (!sym || seen[sym]) continue;
      seen[sym] = true;
      var k = subKey(sym, activeRes);
      var memN = (histCache[k] || []).length;
      if (memN >= 40) continue;
      try {
        var sess = sessionGetTip(k);
        if (sess && sess.length >= 40) {
          histCache[k] = sess;
          continue;
        }
      } catch (_) {}
      var last = wlTipWarmAt[String(sym).toUpperCase()] || 0;
      if (Date.now() - last < 45000) continue;
      next.push(sym);
    }
    if (!next.length) return;
    wlTipWarmQueue = next;
    pumpWlTipWarm();
  }

  function pumpWlTipWarm() {
    if (wlTipWarmTimer) return;
    if (!wlTipWarmQueue.length) return;
    if (Date.now() < tipBackoffUntil) {
      wlTipWarmQueue = [];
      return;
    }
    // Don't pile on while click/getBars tips are queued.
    if (tipFly >= tipMaxFly || tipWait.length > 0) {
      wlTipWarmTimer = setTimeout(function () {
        wlTipWarmTimer = null;
        pumpWlTipWarm();
      }, 700);
      return;
    }
    var sym = wlTipWarmQueue.shift();
    if (!sym) return;
    wlTipWarmAt[String(sym).toUpperCase()] = Date.now();
    prefetchTip(sym, "hover");
    wlTipWarmTimer = setTimeout(function () {
      wlTipWarmTimer = null;
      pumpWlTipWarm();
    }, 450);
  }

  function startHoverTipPrefetch() {
    if (!TIP_HOVER_ENABLED) return;
    if (!window.matchMedia || !window.matchMedia("(hover: hover)").matches) return;
    var timer = null;
    var armed = "";
    document.addEventListener("pointerover", function (e) {
      var t = e.target;
      var row =
        t && t.closest
          ? t.closest(
              "#wlBody [data-symbol], #cxMarketTickerTrack .cx-mticker-item[data-symbol], #cxSearchResults [data-search-pick]"
            )
          : null;
      var sym = row
        ? row.getAttribute("data-symbol") || row.getAttribute("data-search-pick") || ""
        : "";
      if (sym === armed) return;
      armed = sym;
      if (timer) clearTimeout(timer);
      timer = null;
      if (!sym) return;
      // ~400ms hover → tip in MEM before click (was 3s = never ready).
      timer = setTimeout(function () {
        timer = null;
        prefetchTip(sym, "hover");
      }, 400);
    });
  }

  function startWatchlistQuotePoll() {
    // Refresh LTP/chg from tip/ticks API — independent of live WebSocket.
    setInterval(function () {
      // Stay lite on poll; only pull nifty500 when that list is open.
      var pollUrl = WL_URL;
      if (wlSrc === "nifty500" && (wlData.nifty500 || []).length) {
        pollUrl = WL_URL + (WL_URL.indexOf("?") >= 0 ? "&" : "?") + "bag=nifty500";
      }
      fetch(pollUrl, { credentials: "include", cache: "no-store" })
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          if (!data || !data.ok) return;
          applyMarketUiState(data.market);
          var pollAt = Date.now();
          function wsFresh(row) {
            var a = [row.label, row.symbol, row.index_symbol];
            for (var j = 0; j < a.length; j++) {
              var t = wlWsFreshAt[String(a[j] || "").toUpperCase()];
              if (t && pollAt - t < 15000) return true;
            }
            return false;
          }
          function mergeQuotes(dst, src) {
            var byKey = {};
            (dst || []).forEach(function (r, i) {
              byKey[String(r.symbol || r.label || "").toUpperCase()] = i;
            });
            (src || []).forEach(function (r) {
              var k = String(r.symbol || r.label || "").toUpperCase();
              var i = byKey[k];
              if (i == null) return;
              var row = dst[i];
              if (r.prev_close != null && Number(r.prev_close) > 0) {
                var pc = Number(r.prev_close);
                var curLtp = Number(row.ltp);
                // Ignore glued session-close pretending to be prev_close.
                if (!(curLtp > 0) || Math.abs(pc - curLtp) / Math.max(curLtp, 1) >= 1e-5) {
                  row.prev_close = pc;
                }
              }
              if (!wsFresh(row)) {
                if (r.ltp != null) row.ltp = r.ltp;
              }
              // Keep LTP/% locked to the same previous close (WS LTP + tip/API prev).
              var ltpNow = Number(row.ltp);
              var prevNow = Number(row.prev_close);
              if (ltpNow > 0 && prevNow > 0 && Math.abs(prevNow - ltpNow) / Math.max(ltpNow, 1) >= 1e-5) {
                row.chg = Math.round(((ltpNow - prevNow) / prevNow) * 10000) / 100;
                row.chg_abs = Math.round((ltpNow - prevNow) * 100) / 100;
              } else if (!wsFresh(row)) {
                if (r.chg != null) row.chg = r.chg;
                if (r.chg_abs != null) row.chg_abs = r.chg_abs;
              }
              if (Array.isArray(r.stocks) && Array.isArray(row.stocks) && r.stocks.length) {
                mergeQuotes(row.stocks, r.stocks);
              }
            });
          }
          mergeQuotes(wlData.indices, data.indices);
          mergeQuotes(wlData.sectors, data.sectors);
          mergeQuotes(wlData.fno, data.fno);
          mergeQuotes(wlData.nifty50, data.nifty50);
          if (data.nifty500 && data.nifty500.length) mergeQuotes(wlData.nifty500, data.nifty500);
          // Refresh sector drill-in rows from latest sector payload.
          if (wlSrc === "sectors" && wlSectorView === "stocks" && wlActiveSector) {
            var sec = findSectorByKey(wlActiveSector);
            if (sec) {
              (wlSectorStockRows || []).forEach(function (row) {
                if (wsFresh(row)) {
                  // Still refresh prev_close + recompute % against live LTP.
                  var pcKeep = row.kind === "index" ? sec.prev_close : null;
                  if (row.kind !== "index") {
                    (sec.stocks || []).forEach(function (s) {
                      if (String(s.symbol || "").toUpperCase() === String(row.symbol || "").toUpperCase()) {
                        pcKeep = s.prev_close;
                      }
                    });
                  }
                  if (pcKeep != null && Number(pcKeep) > 0) row.prev_close = Number(pcKeep);
                } else if (row.kind === "index") {
                  if (sec.ltp != null) row.ltp = sec.ltp;
                  if (sec.prev_close != null) row.prev_close = sec.prev_close;
                } else {
                  (sec.stocks || []).forEach(function (s) {
                    if (String(s.symbol || "").toUpperCase() === String(row.symbol || "").toUpperCase()) {
                      if (s.ltp != null) row.ltp = s.ltp;
                      if (s.prev_close != null) row.prev_close = s.prev_close;
                    }
                  });
                }
                var ltpS = Number(row.ltp);
                var prevS = Number(row.prev_close);
                if (ltpS > 0 && prevS > 0 && Math.abs(prevS - ltpS) / Math.max(ltpS, 1) >= 1e-5) {
                  row.chg = Math.round(((ltpS - prevS) / prevS) * 10000) / 100;
                  row.chg_abs = Math.round((ltpS - prevS) * 100) / 100;
                }
              });
            }
          }
          patchWlQuotes();
          renderMarketTicker(false);
          if (wlSrc === "custom") refreshCustomQuotes();
        })
        .catch(function () {});
    }, 12000);
  }

  function setSymbol(sym, opts) {
    if (barReplay.active || barReplay.selecting) {
      try {
        barReplayExit(true);
      } catch (_) {}
    }
    sym = String(sym || "").trim();
    if (!sym) return;
    try {
      window.dispatchEvent(new CustomEvent("bc:analytics-symbol", { detail: { symbol: sym } }));
      if (window.__BC) window.__BC.symbol = sym;
    } catch (_) {}
    opts = opts || {};
    var targetPane = opts.pane || activePane || "sector";
    // Multi-grid: watchlist / clicks open on the active pane (not always sector).
    if (targetPane !== "sector" && !opts._sectorOnly) {
      openSymbolOnPane(sym, targetPane, {
        skipAutosave: opts.skipAutosave,
        savedState: opts.savedState,
        force: opts.force,
        ltp: opts.ltp,
        open: opts.open,
        high: opts.high,
        low: opts.low,
      });
      return;
    }
    setActivePane("sector");
    setPaneSymbol("sector", sym);
    if (!opts.skipAutosave && layoutCtl) layoutCtl.scheduleAutosave();
    ensureVisiblePaneWsSubs();
    // Option-chain click: seed TickHub from OC LTP so quote-paint path is instant.
    try {
      if (opts.ltp != null && Number(opts.ltp) > 0 && TickHub && typeof TickHub.applyQuote === "function") {
        var seedLtp = Number(opts.ltp);
        TickHub.applyQuote(sym, {
          ltp: seedLtp,
          open: opts.open != null && Number(opts.open) > 0 ? Number(opts.open) : seedLtp,
          high: opts.high != null && Number(opts.high) > 0 ? Number(opts.high) : seedLtp,
          low: opts.low != null && Number(opts.low) > 0 ? Number(opts.low) : seedLtp,
          ts: Date.now(),
        });
      }
    } catch (_) {}
  // Watchlist / harness: force fresh tip unless layout restore says force:false.
    var forceRefresh = opts.force !== false;
    if (opts.force === false) forceRefresh = false;
    if (!forceRefresh && sym === activeSymbol && firstCandle) {
      return;
    }
    if (
      switchPending &&
      switchPending.symbol === sym &&
      performance.now() - switchPending.started < 600
    ) {
      frontTrace("watchlist.duplicate", {
        target: sym,
        pending_gen: switchPending.gen,
        elapsed_ms: Math.round(performance.now() - switchPending.started),
      });
      return;
    }
    var previousSymbol = activeSymbol;
    var gen = ++symbolSwitchGen;
    activeSymbol = sym;
    firstCandle = false;
    switchT0 = performance.now();
    switchPending = { symbol: sym, gen: gen, started: switchT0 };
    // Drop queued hover tips so the click tip is next out of the concurrency gate.
    tipWait = tipWait.filter(function (j) {
      return j.prio < 2;
    });
    viewportUserFree = false;
    viewportSettledGen = 0;
    setMetric("mCache", "LOADING", "warn");
    frontTrace("switch.start", {
      from: previousSymbol,
      to: sym,
      gen: gen,
      force: forceRefresh,
    });
    TickHub._ltpOnce = {};
    try {
      TickHub.ensureChartSubscribed(sym);
    } catch (_) {}
    try {
      syncActiveLtpHeader(sym);
    } catch (_) {}
    var cacheKey = subKey(sym, activeRes);
    invalidateSymbolTip(sym, activeRes);
    // Drop any poisoned forming bar from a prior visit (cross-symbol High spike).
    delete lastHistoricalBar[cacheKey];
    delete lastDeliveredTime[cacheKey];
    delete paintCommittedAt[cacheKey];
    delete pendingLiveSeeds[cacheKey];
    delete candleBuilders[cacheKey];
    try {
      if (TickHub && TickHub._lastEmit) delete TickHub._lastEmit[cacheKey];
    } catch (_) {}
    var el = document.getElementById("cxActiveSym");
    if (el) el.textContent = optDisplayLabel(sym);
    var title = document.getElementById("cx-left-title");
    if (title) title.textContent = optDisplayLabel(sym);
    markWlActive(sym);
    try {
      history.replaceState(null, "", "/charts?symbol=" + encodeURIComponent(optDisplayLabel(sym)));
    } catch (_) {}

    function applyChartSymbol() {
      if (gen !== symbolSwitchGen) {
        frontTrace("switch.drop", { gen: gen, current_gen: symbolSwitchGen, stage: "tv.apply" });
        return;
      }
      frontTrace("switch.tv.apply", {
        gen: gen,
        elapsed_ms: Math.round(performance.now() - switchT0),
      });
      // Do NOT schedule a timed resetData here. A 150ms reset while tip/getBars is
      // still in flight cancelled the first paint and made WL/search switches feel stuck
      // (URL already changed, chart still on the previous symbol).
      try {
        if (widget && typeof widget.setSymbol === "function") {
          widget.setSymbol(sym, activeRes);
          return;
        }
      } catch (_) {}
      try {
        if (widget && widget.activeChart) {
          widget.activeChart().setSymbol(sym);
        }
      } catch (e2) {
        log("setSymbol", e2);
      }
    }

    // Tip → history → then setSymbol (TV getBars must see real bars, never empty+LTP).
    // Options / thin tips: always try HIST too (don't stop at LTP seed — that caused "no data").
    function isOptSym(s) {
      var u = String(s || "");
      return /^OPT:/i.test(u) || /\b(CE|PE)\b/i.test(u);
    }
    function scoreBars(bars) {
      if (!bars || !bars.length) return 0;
      var days = 0;
      try {
        days = tradingDayLabels(bars).length;
      } catch (_) {
        days = 0;
      }
      var withVol = 0;
      for (var i = 0; i < bars.length; i++) {
        if (Number(bars[i].volume) > 0) withVol++;
      }
      // Prefer multi-day real series over gap-filled LTP seed flats.
      return days * 2000 + withVol * 3 + Math.min(bars.length, 500);
    }
    function normalizeSwitchBars(raw, ltpHint) {
      var bars = raw || [];
      if (bars.length && bars[0].time < 1e12) {
        bars = stripOpenBucket(toTvBars(bars), activeRes, sym);
      } else if (bars.length) {
        bars = stripOpenBucket(bars, activeRes, sym);
      }
      return sanitizeTipBars(bars, ltpHint, sym);
    }
    function loadTipOrHist(mode) {
      var pfKey = subKey(sym, activeRes);
      var pf = tipPrefetch[pfKey];
      delete tipPrefetch[pfKey];
      var usePf = !!(pf && Date.now() - pf.at < 20000);
      frontTrace("tip.request", { gen: gen, mode: mode || "switch", opt: isOptSym(sym), prefetched: usePf });
      var tipP = usePf
        ? pf.p.catch(function () {
            return tipNet(sym, activeRes, "switch");
          })
        : tipNet(sym, activeRes, mode || "switch");
      return tipP.then(function (pack) {
        if (gen !== symbolSwitchGen) {
          frontTrace("switch.drop", { gen: gen, current_gen: symbolSwitchGen, stage: "tip.response" });
          return null;
        }
        var ltpHint = 0;
        try {
          var q = TickHub.lastQuotes[sym];
          if (q && Number(q.ltp) > 0) ltpHint = Number(q.ltp);
        } catch (_) {}
        var tipBars = normalizeSwitchBars(pack.bars || [], ltpHint);
        frontTrace("tip.response", {
          gen: gen,
          mode: mode || "switch",
          cache: pack.cache || "",
          bars: tipBars.length,
          score: scoreBars(tipBars),
          elapsed_ms: Math.round(performance.now() - switchT0),
        });
        var seedBars = null;
        try {
          seedBars = quoteSeedBars(sym, activeRes, ltpHint);
        } catch (_) {}
        var opt = isOptSym(sym);
        var tipScore = scoreBars(tipBars);

        function pickBest(histBars, hpack) {
          var cands = [
            { bars: tipBars, cache: pack.cache || "TIP", pack: pack, five: pack.bars5 },
            {
              bars: histBars || [],
              cache: (hpack && hpack.cache) || "HIST",
              pack: hpack || pack,
              five: hpack && hpack.bars5,
            },
            { bars: seedBars || [], cache: "QUOTE", pack: pack || { cache: "QUOTE" }, five: null },
          ];
          var best = null;
          var bestSc = -1;
          for (var i = 0; i < cands.length; i++) {
            var sc = scoreBars(cands[i].bars);
            if (sc <= 0) continue;
            // Prefer real tip/hist over QUOTE when scores are close.
            var boost = cands[i].cache === "QUOTE" ? 0 : 50;
            if (sc + boost > bestSc) {
              bestSc = sc + boost;
              best = cands[i];
            }
          }
          if (!best) return null;
          return {
            bars: best.bars,
            pack: best.pack,
            five: best.five,
            cache: best.cache,
          };
        }

        // Equities/indices with any tip: keep existing fast path (no hist wait).
        if (!opt && tipBars.length) {
          return pickBest(null, null);
        }

        // Options with any tip: paint tip only — never background histNet (was 11–19s + resetData).
        if (opt && tipBars.length) {
          return pickBest(null, null);
        }

        // Options (empty tip) or empty tip equity: try HIST (OPT capped at 1.5s via histNet race).
        var toMs = Date.now();
        var fromMs = toMs - (opt ? 14 : 21) * 86400000;
        return histNet(sym, activeRes, fromMs, toMs)
          .then(function (hpack) {
            if (gen !== symbolSwitchGen) return null;
            var hb = normalizeSwitchBars((hpack && hpack.bars) || [], ltpHint);
            frontTrace("hist.response", {
              gen: gen,
              bars: hb.length,
              score: scoreBars(hb),
              cache: (hpack && hpack.cache) || "",
              elapsed_ms: Math.round(performance.now() - switchT0),
            });
            // OPT timeout / empty → keep seed, do not wait forever.
            if (opt && (!hb.length || String((hpack && hpack.cache) || "") === "HIST-TIMEOUT")) {
              return pickBest(null, null);
            }
            return pickBest(hb, hpack);
          })
          .catch(function () {
            return pickBest(null, null);
          });
      });
    }

    function commitLoaded(loaded) {
      if (gen !== symbolSwitchGen || !loaded || !loaded.bars || !loaded.bars.length) return;
      if (barReplay.active && sameChartSymbol(sym, barReplay.symbol)) return;
      if (loaded.five && loaded.five.length) {
        var five = sanitizeTipBars(
          loaded.five[0].time < 1e12 ? toTvBars(loaded.five) : loaded.five,
          Number(loaded.bars[loaded.bars.length - 1].close),
          sym
        );
        if (five.length) {
          commitFreshFive(sym, five);
          netPaintReady[subKey(sym, "5")] = gen;
        }
      }
      var prevMem = histCache[cacheKey] || [];
      histCache[cacheKey] = samePriceRegime(prevMem, loaded.bars)
        ? tightEdge(prevMem, loaded.bars)
        : loaded.bars.slice();
      storeCachedBars(cacheKey);
      netPaintReady[cacheKey] = gen;
      bridgeReady[cacheKey] = poolIsContinuous(loaded.bars);
      // Dense (incl. F&O auction-aware holes) → solid. Only keep sparse when truly holey/lagging.
      if (sessionTipSolid(loaded.bars, sym, activeRes)) {
        solidFirstPaint[cacheKey] = true;
        sparseFirstPaint[cacheKey] = false;
        bridgeReady[cacheKey] = true;
      } else if (inSessionNow(sym) && resMinutes(activeRes) < 1440) {
        solidFirstPaint[cacheKey] = false;
        sparseFirstPaint[cacheKey] = true;
      } else if (bridgeReady[cacheKey]) {
        solidFirstPaint[cacheKey] = true;
        sparseFirstPaint[cacheKey] = false;
      }
      lastHistoricalBar[cacheKey] = loaded.bars[loaded.bars.length - 1];
      switchHandoff = { key: cacheKey, gen: gen, bars: loaded.bars, cache: loaded.cache || "TIP", at: Date.now() };
      setMetric("mCache", loaded.cache || "TIP");
      frontTrace("switch.commit", {
        gen: gen,
        cache: loaded.cache || "TIP",
        bars: loaded.bars.length,
        elapsed_ms: Math.round(performance.now() - switchT0),
      });
      lb("tip.refresh", sym, {
        n: loaded.bars.length,
        cache: loaded.cache || "",
        resolution: String(activeRes),
        ms: loaded.pack && loaded.pack.client_ms,
      });
      prefetchHistory(sym, activeRes, loaded.bars);
    }

    // Instant path: warm continuous pool already in MEM — switch chart now, tip refresh behind.
    var warmPool = barsFromFivePool(sym, activeRes) || histCache[cacheKey] || [];
    // Revisit in same tab: session tip is sync and paints instantly (cash warehouse shelf).
    if (!warmPool.length) {
      try {
        var sessWarm = sessionGetTip(cacheKey);
        if (sessWarm && sessWarm.length) {
          warmPool = sessWarm;
          histCache[cacheKey] = sessWarm;
        }
      } catch (_) {}
    }
    // Prior-visit IDB — only deepen when dense (avoid gap flash from stale holey IDB).
    if (warmPool.length < 40) {
      var idbWarmGen = gen;
      idbGetTip(cacheKey).then(function (idbBars) {
        if (idbWarmGen !== symbolSwitchGen) return;
        if (!idbBars || idbBars.length < 40) return;
        if (inSessionNow(sym) && !sessionTipSolid(idbBars, sym, activeRes)) return;
        var cur = histCache[cacheKey] || [];
        if (cur.length >= idbBars.length && sessionTipSolid(cur, sym, activeRes)) return;
        histCache[cacheKey] = samePriceRegime(cur, idbBars)
          ? tightEdge(cur, idbBars)
          : idbBars.slice();
        storeCachedBars(cacheKey);
        lastHistoricalBar[cacheKey] = histCache[cacheKey][histCache[cacheKey].length - 1];
        if (cur.length < 15) {
          switchHandoff = {
            key: cacheKey,
            gen: gen,
            bars: histCache[cacheKey].slice(),
            cache: "IDB",
            at: Date.now(),
          };
          try {
            if (widget && widget.activeChart && typeof widget.activeChart().resetData === "function") {
              widget.activeChart().resetData();
            }
          } catch (_) {}
        }
      });
    }
    var warmLtp = 0;
    try {
      var wq =
        TickHub.lastQuotes[sym] ||
        TickHub.lastQuotes[String(sym).toUpperCase()] ||
        null;
      if (wq && Number(wq.ltp) > 0) warmLtp = Number(wq.ltp);
    } catch (_) {}
    if (warmPool.length) {
      warmPool = sanitizeTipBars(
        warmPool,
        warmLtp || (warmPool[warmPool.length - 1] && warmPool[warmPool.length - 1].close),
        sym
      );
      // Drop weekend stamps before OPT-MEM accept (Sat tip → TV "No data here").
      if (!/^CRUDE|GOLD|SILVER|NATGAS/i.test(String(sym || ""))) {
        warmPool = warmPool.filter(function (b) {
          var ms = Number(b && b.time) || 0;
          if (!(ms > 0)) return false;
          if (ms < 1e12) ms *= 1000;
          var ist = new Date(ms + 5.5 * 3600000);
          var dow = ist.getUTCDay();
          return dow !== 0 && dow !== 6;
        });
      }
      histCache[cacheKey] = warmPool;
    }
    var memTrusted = memEdgeTrusted(warmPool, sym, activeRes);
    if (warmPool.length && !memTrusted) {
      frontTrace("cache.mem.reject", {
        gen: gen,
        bars: warmPool.length,
        last_t: warmPool[warmPool.length - 1].time,
        reason: "stale_latest_session",
      });
    }
    // Prefetched option tips: paint full MEM immediately (ignore weekend edge trust).
    // Threshold 40: real 3-session tip/batch; avoid 2-bar quote flash.
    if (isOptSym(sym) && warmPool.length >= 40 && scoreBars(warmPool) > 50) {
      _optFitBarCount = 0;
      frontTrace("switch.path", {
        gen: gen,
        path: "OPT-MEM",
        bars: warmPool.length,
        tip_depth: poolHasTipDepth(warmPool),
        elapsed_ms: Math.round(performance.now() - switchT0),
      });
      ocLog("path.opt-mem", {
        symbol: sym,
        bars: warmPool.length,
        elapsed_ms: Math.round(performance.now() - switchT0),
      });
      netPaintReady[cacheKey] = gen;
      solidFirstPaint[cacheKey] = true;
      bridgeReady[cacheKey] = true;
      lastHistoricalBar[cacheKey] = warmPool[warmPool.length - 1];
      setMetric("mCache", "OPT-MEM");
      applyChartSymbol();
      // Tip-only deepen for OPT — never wait on hist.
      var memBarCount = warmPool.length;
      // Fit like Nifty/Crude — show last tip session (not empty weekend "today").
      fitOptViewport(warmPool, { gen: gen, delayMs: 100, againMs: 350 });
      tipNet(sym, activeRes, "warm")
        .then(function (pack) {
          if (gen !== symbolSwitchGen) return;
          var bars = (pack && pack.bars) || [];
          if (!bars.length) return;
          if (bars[0].time < 1e12) bars = toTvBars(bars);
          bars = sanitizeTipBars(
            bars,
            warmLtp || (bars[bars.length - 1] && bars[bars.length - 1].close),
            sym
          );
          if (scoreBars(bars) <= scoreBars(warmPool)) return;
          // Never replace richer MEM with thinner LIVE-SEED.
          if (bars.length < memBarCount && bars.length < 15) return;
          histCache[cacheKey] = bars;
          netPaintReady[cacheKey] = gen;
          lastHistoricalBar[cacheKey] = bars[bars.length - 1];
          setMetric("mCache", (pack && pack.cache) || "TIP");
          ocLog("deepen.tip", {
            symbol: sym,
            bars: bars.length,
            cache: (pack && pack.cache) || "",
            elapsed_ms: Math.round(performance.now() - switchT0),
          });
          if (memBarCount < 120 && bars.length >= 150) {
            try {
              if (widget && widget.activeChart && typeof widget.activeChart().resetData === "function") {
                widget.activeChart().resetData();
              }
              fitOptViewport(bars, { gen: gen, delayMs: 120, againMs: 400 });
            } catch (_) {}
          } else if (bars.length > memBarCount + 20) {
            fitOptViewport(bars, { gen: gen, delayMs: 80, againMs: 300 });
          }
        })
        .catch(function () {});
      return;
    }
    // Cash equities/indices during live session: tip-first (no LIVE-QUOTE placeholder flicker).
    // Instant only when trusted continuous MEM already has a solid shelf; otherwise wait for tip
    // (~Redis) then paint once with proper candles.
    if (!isOptSym(sym) && inSessionNow(sym)) {
      function paintLiveOnce(bars, cacheLabel, solid) {
        if (!bars || !bars.length) return false;
        histCache[cacheKey] = bars;
        storeCachedBars(cacheKey);
        netPaintReady[cacheKey] = gen;
        solidFirstPaint[cacheKey] = !!solid;
        bridgeReady[cacheKey] = poolIsContinuous(bars);
        lastHistoricalBar[cacheKey] = bars[bars.length - 1];
        seedBuilder(sym, activeRes, bars[bars.length - 1]);
        switchHandoff = {
          key: cacheKey,
          gen: gen,
          bars: bars.slice(),
          cache: cacheLabel,
          at: Date.now(),
        };
        setMetric("mCache", cacheLabel);
        applyChartSymbol();
        return true;
      }

      var memSolid =
        warmPool.length >= 40 &&
        !!memTrusted &&
        poolIsContinuous(warmPool) &&
        scoreBars(warmPool) > 100 &&
        sessionPaintReady(warmPool, sym, activeRes);

      if (memSolid) {
        frontTrace("switch.path", {
          gen: gen,
          path: "LIVE-MEM",
          bars: warmPool.length,
          tip_depth: poolHasTipDepth(warmPool),
          trusted: true,
          last_t: warmPool[warmPool.length - 1].time,
          elapsed_ms: Math.round(performance.now() - switchT0),
        });
        paintLiveOnce(warmPool, "LIVE-MEM", true);
        prefetchHistory(sym, activeRes, warmPool.slice(-5));
        // Soft tip deepen behind — merge only; no resetData for small edge tickles.
        tipNet(sym, activeRes, "warm")
          .then(function (pack) {
            if (gen !== symbolSwitchGen) return;
            var tipBars = normalizeSwitchBars((pack && pack.bars) || [], warmLtp);
            if (!tipBars.length) return;
            var beforeHoles = findIntradayHoles(warmPool, activeRes, sym).length;
            var afterHoles = findIntradayHoles(tipBars, activeRes, sym).length;
            var todayGrew =
              todayBarCount(tipBars, sym) > todayBarCount(warmPool, sym) + 1 ||
              (!poolHasEnoughToday(warmPool, sym, activeRes) &&
                poolHasEnoughToday(tipBars, sym, activeRes));
            // Always accept tip when it adds today's session; otherwise need a real score bump.
            if (!todayGrew && scoreBars(tipBars) <= scoreBars(warmPool) + 50) return;
            commitLoaded({
              bars: tipBars,
              cache: (pack && pack.cache) || "TIP",
              pack: pack,
              five: pack && pack.bars5,
            });
            if ((beforeHoles > 0 && afterHoles < beforeHoles) || todayGrew) {
              reloadIfPaintedEdgeStale(sym, activeRes, warmPool, tipBars, "switch.live.mem.deepen");
            }
          })
          .catch(function () {});
        return;
      }

      // Instant open: flip TV now with best local shelf (or LTP seed), tip densifies behind.
      // Waiting on tip before setSymbol left the previous symbol on screen for 0.7–3s.
      frontTrace("switch.path", {
        gen: gen,
        path: "TIP-FIRST",
        bars: warmPool.length,
        trusted: !!memTrusted,
        elapsed_ms: Math.round(performance.now() - switchT0),
      });

      var tipFirstPainted = false;
      var tipFirstN = 0;
      var liveTipP = null;
      try {
        var pfLive = tipPrefetch[cacheKey];
        if (pfLive && Date.now() - pfLive.at < 20000) {
          liveTipP = pfLive.p;
          delete tipPrefetch[cacheKey];
        }
      } catch (_) {}
      if (!liveTipP) liveTipP = tipNet(sym, activeRes, "warm");
      switchOpenTip = { key: cacheKey, p: liveTipP, at: Date.now() };

      // Only paint dense MEM instantly — holey warm shelves were cementing candle gaps.
      var warmSolid =
        warmPool.length >= 40 &&
        !!memTrusted &&
        sessionTipSolid(warmPool, sym, activeRes);
      if (warmSolid) {
        tipFirstPainted = paintLiveOnce(warmPool, "TIP-MEM", true);
        tipFirstN = warmPool.length;
        solidFirstPaint[cacheKey] = true;
        sparseFirstPaint[cacheKey] = false;
      } else {
        // Flip the symbol now. Do not commit a 1-bar LTP seed — that was the
        // chart until a full refresh. getBars paints the last sessions, and
        // the live socket updates only the forming candle.
        applyChartSymbol();
        tipFirstPainted = true;
        tipFirstN = 0;
      }
      if (!tipFirstPainted) applyChartSymbol();
      setMetric("mCache", warmSolid ? "TIP-MEM" : "TIP…", warmSolid ? "" : "warn");

      var tipFirstDone = tipFirstPainted;
      function finishTipFirst(bars, cacheLabel, pack, force) {
        if (gen !== symbolSwitchGen) return;
        var use = bars && bars.length ? bars : null;
        if (!use) {
          if (!tipFirstDone) {
            tipFirstDone = true;
            applyChartSymbol();
          }
          return;
        }
        // Usable tip (≥15): always paint. Hold only for tiny stubs when we already have MEM.
        var usable = use.length >= 15 || sessionTipSolid(use, sym, activeRes) || !!force;
        if (!usable && tipFirstDone && tipFirstN >= 8) {
          if (scoreBars(use) > scoreBars(histCache[cacheKey] || [])) {
            histCache[cacheKey] = samePriceRegime(histCache[cacheKey] || [], use)
              ? tightEdge(histCache[cacheKey] || [], use)
              : use.slice();
            storeCachedBars(cacheKey);
          }
          return;
        }
        var prevBars = (histCache[cacheKey] || warmPool || []).slice();
        var prevN = tipFirstN || prevBars.length;
        var prevSc = scoreBars(prevBars);
        var beforeCover = coversLiveSession(prevBars, sym, activeRes);
        var afterCover = coversLiveSession(use, sym, activeRes);
        // A thin tip must not replace a series that already has today's candles.
        if (beforeCover && !afterCover) return;
        tipFirstDone = true;
        tipFirstN = use.length;
        commitLoaded({
          bars: use,
          cache: cacheLabel || "TIP",
          pack: pack || null,
          five: pack && pack.bars5,
        });
        seedBuilder(sym, activeRes, use[use.length - 1]);
        var tvAlready = tvPaintToday[cacheKey] != null;
        if (!tipFirstPainted) {
          applyChartSymbol();
          tipFirstPainted = true;
        } else if (
          tvAlready &&
          ((!beforeCover && afterCover) ||
            (prevN < 15 && use.length >= 40) ||
            (prevN <= 2 && use.length >= 15) ||
            (scoreBars(use) > prevSc + 200 && use.length >= Math.max(40, prevN + 20)))
        ) {
          // Seed/thin → real tip: one reset. Dense deepen stays soft (no flicker).
          try {
            if (widget && widget.activeChart && typeof widget.activeChart().resetData === "function") {
              widget.activeChart().resetData();
            }
          } catch (_) {}
        }
        prefetchHistory(sym, activeRes, use.slice(-5));
        frontTrace("switch.tip_first.paint", {
          gen: gen,
          bars: use.length,
          cache: cacheLabel || "TIP",
          dense: sessionPaintReady(use, sym, activeRes),
          forced: !!force,
          elapsed_ms: Math.round(performance.now() - switchT0),
        });
        if (!sessionPaintReady(use, sym, activeRes)) {
          fillTodaySession(sym, activeRes, { silent: true })
            .then(function () {
              if (gen !== symbolSwitchGen) return;
              var mem = histCache[cacheKey] || [];
              if (
                sessionPaintReady(mem, sym, activeRes) ||
                findIntradayHoles(mem, activeRes, sym).length <
                  findIntradayHoles(use, activeRes, sym).length
              ) {
                reloadIfPaintedEdgeStale(sym, activeRes, use, mem, "tip_first.soft_heal");
              }
              if (!sessionPaintReady(mem, sym, activeRes)) {
                resyncSameSessionTipGap(sym, activeRes, "tip_first.holey");
              }
            })
            .catch(function () {});
        } else {
          fillTodaySession(sym, activeRes, { silent: true }).catch(function () {});
        }
      }

      liveTipP
        .then(function (pack) {
          if (gen !== symbolSwitchGen) return;
          var tipBars = normalizeSwitchBars((pack && pack.bars) || [], warmLtp);
          if (tipBars.length >= 8) {
            finishTipFirst(tipBars, (pack && pack.cache) || "TIP", pack, tipBars.length >= 15);
          }
        })
        .catch(function () {});

      histNet(sym, activeRes, Date.now() - 21 * 86400000, Date.now(), 2500)
        .then(function (pack) {
          if (gen !== symbolSwitchGen) return;
          var hb = normalizeSwitchBars((pack && pack.bars) || [], warmLtp);
          if (hb.length >= 15) {
            finishTipFirst(hb, (pack && pack.cache) || "HIST", pack, true);
          }
        })
        .catch(function () {});

      // Escalate only if tip still thin — skip while tip API is backing off (504 storms).
      setTimeout(function () {
        if (gen !== symbolSwitchGen) return;
        if (Date.now() < tipBackoffUntil) return;
        var filled = barsFromFivePool(sym, activeRes) || histCache[cacheKey] || [];
        if (sessionTipSolid(filled, sym, activeRes) && filled.length >= 40) return;
        tipNet(sym, activeRes, "cold")
          .then(function (pack) {
            if (gen !== symbolSwitchGen) return null;
            var coldBars = normalizeSwitchBars((pack && pack.bars) || [], warmLtp);
            if (coldBars.length >= 8) {
              finishTipFirst(coldBars, (pack && pack.cache) || "TIP-COLD", pack, true);
              if (sessionTipSolid(coldBars, sym, activeRes)) return null;
            }
            return fillTodaySession(sym, activeRes, { silent: true });
          })
          .catch(function () {
            return fillTodaySession(sym, activeRes, { silent: true });
          })
          .then(function () {
            if (gen !== symbolSwitchGen) return;
            var after = barsFromFivePool(sym, activeRes) || histCache[cacheKey] || filled;
            if (after && after.length >= 15) {
              finishTipFirst(after, "COLD+TODAY", null, true);
            }
          })
          .catch(function () {});
      }, 1800);
      return;
    }

    // After hours / weekend: paint warm MEM immediately (edge not moving).
    if (warmPool.length >= 15) {
      var memContinuous = poolIsContinuous(warmPool);
      var softOnly = !(warmPool.length >= 40 && memTrusted);
      frontTrace("switch.path", {
        gen: gen,
        path: softOnly ? "MEM-SOFT" : memContinuous ? "MEM" : "MEM-SOFT",
        bars: warmPool.length,
        tip_depth: poolHasTipDepth(warmPool),
        trusted: !!memTrusted,
        edge_fresh: memEdgeFreshForPaint(warmPool, sym, activeRes),
      });
      netPaintReady[cacheKey] = gen;
      solidFirstPaint[cacheKey] = !softOnly;
      bridgeReady[cacheKey] = memContinuous;
      lastHistoricalBar[cacheKey] = warmPool[warmPool.length - 1];
      setMetric("mCache", softOnly ? "MEM-SOFT" : memContinuous ? "MEM" : "MEM-SOFT");
      switchHandoff = {
        key: cacheKey,
        gen: gen,
        bars: warmPool.slice(),
        cache: softOnly ? "MEM-SOFT" : "MEM",
        at: Date.now(),
      };
      applyChartSymbol();
      loadTipOrHist("warm")
        .then(function (loaded) {
          if (gen !== symbolSwitchGen) return;
          if (loaded && loaded.bars && loaded.bars.length) {
            var beforeN = warmPool.length;
            commitLoaded(loaded);
            // Only hard-reset when MEM was truly thin; soft deepen avoids tip-snap flicker.
            if (
              beforeN < 15 &&
              loaded.bars.length >= 80 &&
              scoreBars(loaded.bars) > scoreBars(warmPool) + 200
            ) {
              try {
                if (widget && widget.activeChart && typeof widget.activeChart().resetData === "function") {
                  widget.activeChart().resetData();
                }
              } catch (_) {}
            }
          }
        })
        .catch(function () {});
      prefetchHistory(sym, activeRes, warmPool.slice(-5));
      return;
    }

    // Options cold: tip-first (3-session shelf). Never paint 2-bar OPT-QUOTE then redraw —
    // that flashes empty/partial then fights viewport.fit with the real tip.
    if (isOptSym(sym) && !(warmPool.length >= 40 && scoreBars(warmPool) > 50)) {
      _optFitBarCount = 0;
      frontTrace("switch.path", {
        gen: gen,
        path: "OPT-TIP",
        warm_bars: warmPool.length,
        elapsed_ms: Math.round(performance.now() - switchT0),
      });
      ocLog("path.opt-tip", {
        symbol: sym,
        warm: warmPool.length,
        elapsed_ms: Math.round(performance.now() - switchT0),
      });
      setMetric("mCache", "OPT-TIP…");
      loadTipOrHist("warm")
        .then(function (loaded) {
          if (gen !== symbolSwitchGen) return;
          var bars = (loaded && loaded.bars) || [];
          if (bars.length >= 10) {
            commitLoaded(loaded);
            netPaintReady[cacheKey] = gen;
            solidFirstPaint[cacheKey] = true;
            bridgeReady[cacheKey] = true;
            applyChartSymbol();
            fitOptViewport(bars, { gen: gen, delayMs: 80, againMs: 320 });
            ocLog("paint.opt-tip", {
              symbol: sym,
              bars: bars.length,
              cache: (loaded && loaded.cache) || "",
              elapsed_ms: Math.round(performance.now() - switchT0),
            });
            return;
          }
          // Tip thin/empty — last resort quote seed so TV isn't blank.
          var qSeed = quoteSeedBars(sym, activeRes, warmLtp);
          if (qSeed && qSeed.length) {
            histCache[cacheKey] = qSeed;
            netPaintReady[cacheKey] = gen;
            bridgeReady[cacheKey] = true;
            solidFirstPaint[cacheKey] = true;
            lastHistoricalBar[cacheKey] = qSeed[qSeed.length - 1];
            setMetric("mCache", "OPT-QUOTE");
            applyChartSymbol();
            ocLog("paint.opt-quote-fallback", { symbol: sym, bars: qSeed.length });
          } else {
            applyChartSymbol();
          }
        })
        .catch(function () {
          if (gen !== symbolSwitchGen) return;
          var qSeed2 = quoteSeedBars(sym, activeRes, warmLtp);
          if (qSeed2 && qSeed2.length) {
            histCache[cacheKey] = qSeed2;
            netPaintReady[cacheKey] = gen;
            lastHistoricalBar[cacheKey] = qSeed2[qSeed2.length - 1];
          }
          applyChartSymbol();
        });
      return;
    }

    // Off-hours only: 1-bar LTP seed is fine (no live tip race). During session the
    // QUOTE→tip→resetData path flashed one candle then snapped — use NETWORK below.
    if (!inSessionNow(sym)) {
      try {
        var qSeed = quoteSeedBars(sym, activeRes, warmLtp);
        if (qSeed && qSeed.length && !(warmPool.length >= 40 && memTrusted)) {
          frontTrace("switch.path", {
            gen: gen,
            path: "QUOTE",
            bars: qSeed.length,
            elapsed_ms: Math.round(performance.now() - switchT0),
          });
          histCache[cacheKey] =
            warmPool.length > qSeed.length ? warmPool.slice() : qSeed.slice();
          storeCachedBars(cacheKey);
          netPaintReady[cacheKey] = gen;
          bridgeReady[cacheKey] = true;
          solidFirstPaint[cacheKey] = true;
          lastHistoricalBar[cacheKey] = histCache[cacheKey][histCache[cacheKey].length - 1];
          setMetric("mCache", "QUOTE");
          applyChartSymbol();
          loadTipOrHist("warm")
            .then(function (loaded) {
              if (gen !== symbolSwitchGen) return;
              if (!loaded || !loaded.bars || !loaded.bars.length) return;
              var seedSc = scoreBars(qSeed);
              var loadSc = scoreBars(loaded.bars);
              var isQuoteOnly = String(loaded.cache || "") === "QUOTE";
              if (!isQuoteOnly || loadSc > seedSc) {
                commitLoaded(loaded);
                // Prefer soft edge update; resetData only when series grew a lot.
                if (loadSc > seedSc + 200 && loaded.bars.length >= 40) {
                  try {
                    if (widget && widget.activeChart && typeof widget.activeChart().resetData === "function") {
                      widget.activeChart().resetData();
                    }
                  } catch (_) {}
                }
              }
            })
            .catch(function () {});
          return;
        }
      } catch (_) {}
    }

    // Cash cold path: flip TV to the new symbol NOW (warehouse tip fills behind).
    // Waiting on tip before setSymbol froze the previous chart while the URL already changed.
    frontTrace("switch.path", {
      gen: gen,
      path: "NETWORK",
      warm_bars: warmPool.length,
      mode: "warm",
    });
    if (warmPool.length) {
      netPaintReady[cacheKey] = gen;
      lastHistoricalBar[cacheKey] = warmPool[warmPool.length - 1];
      switchHandoff = {
        key: cacheKey,
        gen: gen,
        bars: warmPool.slice(),
        cache: "SESS",
        at: Date.now(),
      };
    }
    applyChartSymbol();
    loadTipOrHist("warm")
      .then(function (loaded) {
        if (gen !== symbolSwitchGen) return;
        if (loaded && loaded.bars && loaded.bars.length) {
          var beforeN = warmPool.length;
          commitLoaded(loaded);
          // Soft deepen when MEM already had a shelf; resetData only cold→dense.
          if (beforeN < 15 && loaded.bars.length >= 40) {
            try {
              if (widget && widget.activeChart && typeof widget.activeChart().resetData === "function") {
                widget.activeChart().resetData();
              }
            } catch (_) {}
          }
        } else {
          lb("tip.refresh", sym, { n: 0, cache: "EMPTY", resolution: String(activeRes) });
        }
      })
      .catch(function (err) {
        log("setSymbol.tip.err", err);
      });
  }

  // widget.activeChart().setSymbol() re-subscribes TradingView's datafeed
  // internally (old listenerGUID unsubscribed, new one subscribed — see
  // createDatafeed()'s realtimeSubscribers map), but it has no idea our
  // separate SSE connection (window.liveStream) exists, so it never touches
  // it. Without this, the EventSource opened for the previous symbol just
  // keeps streaming that symbol's ticks into a subscriber map that no
  // longer has a matching entry for it — every tick logs [TV NO SUBSCRIBER
  // MATCH] and the new symbol never gets any live data at all.
  // Streaming now handled by TradingView UDF streaming datafeed (streaming-datafeed.js)
  // No need for separate LiveStreamManager - subscribeBars() triggers streaming automatically

  var ocApi = {
    open: function () {},
    close: function () {},
    isOpen: function () {
      return false;
    },
  };
  var mobileNavTab = "chart";
  var mobileNavSyncing = false;
  var searchViewTimer = null;

  function isPhoneNav() {
    try {
      return !!(window.matchMedia && window.matchMedia("(max-width: 640px)").matches);
    } catch (_) {
      return false;
    }
  }

  function setMobileMenuOpen(open) {
    var sheet = document.getElementById("cxMobileMenu");
    if (!sheet) return;
    sheet.hidden = !open;
  }

  function setSearchViewOpen(open, opts) {
    opts = opts || {};
    var view = document.getElementById("cxSearchView");
    var input = document.getElementById("cxSearchInput");
    if (!view) return;
    view.hidden = !open;
    view.classList.toggle("is-open", !!open);
    if (open) {
      if (input && !opts.keepQuery) {
        input.value = "";
      }
      renderSearchViewResults("");
      if (input) {
        setTimeout(function () {
          try {
            input.focus();
            if (opts.keepQuery) input.select();
          } catch (_) {}
        }, 60);
      }
    }
  }

  function renderSearchViewResults(q) {
    var box = document.getElementById("cxSearchResults");
    if (!box) return;
    var query = String(q || "").trim();
    if (!query) {
      box.innerHTML =
        '<div class="cx-search-view-empty"><p>Type a stock, index, or F&amp;O symbol</p></div>';
      return;
    }
    box.innerHTML = '<div class="cx-search-view-empty"><p>Searching…</p></div>';
    fetch("/api/search.php?q=" + encodeURIComponent(query), {
      credentials: "include",
      cache: "no-store",
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        var cur = ((document.getElementById("cxSearchInput") || {}).value || "").trim();
        if (cur.toLowerCase() !== query.toLowerCase()) return;
        var rows = (data && data.results) || [];
        if (!rows.length) {
          box.innerHTML =
            '<div class="cx-search-view-empty">' +
            "<strong>Symbol not available</strong>" +
            "<p>It will be soon available.</p></div>";
          return;
        }
        var html = "";
        rows.slice(0, 40).forEach(function (r) {
          var sym = String(r.symbol || r.label || "").toUpperCase();
          var lab = String(r.label || sym);
          var kind = String(r.kind || "equity");
          if (r.logo_url) {
            rememberLogo(sym, r.logo_url);
            rememberLogo(lab, r.logo_url);
          }
          var logo = r.logo_url || logoUrlFor(sym) || logoUrlFor(lab);
          var ico = logo
            ? '<img class="cx-search-view-logo" src="' +
              escAttr(logo) +
              '" alt="" width="28" height="28" loading="lazy" decoding="async" />'
            : '<span class="cx-search-view-logo is-empty" aria-hidden="true"></span>';
          html +=
            '<button type="button" class="cx-search-view-row" data-search-pick="' +
            escAttr(sym) +
            '">' +
            ico +
            '<span class="cx-search-view-sym">' +
            escHtml(lab) +
            "</span>" +
            '<span class="cx-search-view-kind">' +
            escHtml(kind) +
            "</span></button>";
        });
        box.innerHTML = html;
      })
      .catch(function () {
        box.innerHTML =
          '<div class="cx-search-view-empty">' +
          "<strong>Symbol not available</strong>" +
          "<p>It will be soon available.</p></div>";
      });
  }

  function pickSearchSymbol(sym) {
    sym = String(sym || "").trim();
    if (!sym) return;
    if (isPhoneNav()) blockChartHitsBriefly();
    setSymbol(sym, { force: true });
    if (isPhoneNav()) setMobileNavTab("chart");
    else setSearchViewOpen(false);
  }

  function setMobileNavTab(tab, opts) {
    opts = opts || {};
    var nav = document.getElementById("cxMobileNav");
    var shell = document.getElementById("cxShell");
    mobileNavTab = tab || "chart";
    if (nav) {
      nav.querySelectorAll("[data-mnav]").forEach(function (el) {
        var on = el.getAttribute("data-mnav") === mobileNavTab;
        el.classList.toggle("is-active", on);
        if (on) el.setAttribute("aria-current", "page");
        else el.removeAttribute("aria-current");
      });
    }
    if (shell) shell.setAttribute("data-mnav", mobileNavTab);
    if (opts.uiOnly) return;
    mobileNavSyncing = true;
    try {
      if (mobileNavTab === "watchlist") {
        ocApi.close();
        setMobileMenuOpen(false);
        setSearchViewOpen(false);
        setWlOpen(true);
      } else if (mobileNavTab === "search") {
        ocApi.close();
        setMobileMenuOpen(false);
        setWlOpen(false);
        setSearchViewOpen(true);
      } else if (mobileNavTab === "options") {
        setWlOpen(false);
        setMobileMenuOpen(false);
        setSearchViewOpen(false);
        ocApi.open();
      } else if (mobileNavTab === "menu") {
        setWlOpen(false);
        ocApi.close();
        setSearchViewOpen(false);
        setMobileMenuOpen(true);
      } else {
        setWlOpen(false);
        ocApi.close();
        setMobileMenuOpen(false);
        setSearchViewOpen(false);
      }
    } finally {
      mobileNavSyncing = false;
      try {
        bumpAllWidgetSizes();
        window.dispatchEvent(new Event("resize"));
      } catch (_) {}
    }
  }

  function setWlOpen(open) {
    var shell = document.getElementById("cxShell");
    var btn = document.getElementById("wlToggleBtn");
    if (shell) {
      shell.classList.toggle("wl-collapsed", !open);
      if (!open) shell.classList.remove("wl-expanded");
    }
    if (btn) {
      btn.classList.toggle("is-on", open);
      btn.setAttribute("aria-pressed", open ? "true" : "false");
      btn.title = open ? "Hide watchlist" : "Show watchlist";
    }
    try {
      localStorage.setItem("ki_charts_wl_open", open ? "1" : "0");
    } catch (_) {}
    try {
      bumpAllWidgetSizes();
      window.dispatchEvent(new Event("resize"));
    } catch (_) {}
    if (!mobileNavSyncing && isPhoneNav()) {
      if (open && mobileNavTab !== "watchlist") {
        setMobileNavTab("watchlist", { uiOnly: true });
        if (shell) shell.setAttribute("data-mnav", "watchlist");
      } else if (!open && mobileNavTab === "watchlist") {
        setMobileNavTab("chart", { uiOnly: true });
        if (shell) shell.setAttribute("data-mnav", "chart");
      }
    }
  }

  /** Prevent ghost taps into TradingView toolbar after watchlist → chart. */
  function blockChartHitsBriefly() {
    var shell = document.getElementById("cxShell");
    if (!shell) return;
    shell.classList.add("cx-block-chart-hit");
    var done = false;
    function clear() {
      if (done) return;
      done = true;
      shell.classList.remove("cx-block-chart-hit");
      document.removeEventListener("pointerup", clear, true);
      document.removeEventListener("pointercancel", clear, true);
      document.removeEventListener("touchend", clear, true);
    }
    document.addEventListener("pointerup", clear, true);
    document.addEventListener("pointercancel", clear, true);
    document.addEventListener("touchend", clear, true);
    setTimeout(clear, 650);
  }

  /** On phones, reclaim chart after picking a symbol from the watchlist. */
  function maybeAutoCollapseWlMobile() {
    try {
      if (isPhoneNav()) {
        blockChartHitsBriefly();
        setMobileNavTab("chart");
        // Ensure cursor tool (not a drawing tool) after the switch.
        setTimeout(function () {
          try {
            if (widget && typeof widget.activeChart === "function") {
              var ch = widget.activeChart();
              if (ch && typeof ch.selectLineTool === "function") ch.selectLineTool("cursor");
            }
          } catch (_) {}
        }, 80);
      }
    } catch (_) {}
  }

  function wireSearchView() {
    var input = document.getElementById("cxSearchInput");
    var results = document.getElementById("cxSearchResults");
    var closeBtn = document.getElementById("cxSearchClose");
    var openBtn = document.getElementById("cxSearchOpenBtn");
    if (input) {
      input.addEventListener("input", function () {
        var q = String(input.value || "");
        if (searchViewTimer) clearTimeout(searchViewTimer);
        searchViewTimer = setTimeout(function () {
          renderSearchViewResults(q);
        }, 140);
      });
      input.addEventListener("keydown", function (e) {
        if (e.key === "Escape") {
          if (isPhoneNav()) setMobileNavTab("chart");
          else setSearchViewOpen(false);
        }
        if (e.key === "Enter") {
          var first = results && results.querySelector("[data-search-pick]");
          if (first) {
            e.preventDefault();
            pickSearchSymbol(first.getAttribute("data-search-pick"));
          } else {
            var q = String(input.value || "").trim();
            if (q) {
              // No catalog hit — keep the unavailable message visible.
              renderSearchViewResults(q);
            }
          }
        }
      });
    }
    if (results) {
      results.addEventListener("click", function (e) {
        var row = e.target && e.target.closest ? e.target.closest("[data-search-pick]") : null;
        if (!row) return;
        e.preventDefault();
        pickSearchSymbol(row.getAttribute("data-search-pick"));
      });
    }
    if (closeBtn) {
      closeBtn.addEventListener("click", function () {
        if (isPhoneNav()) setMobileNavTab("chart");
        else setSearchViewOpen(false);
      });
    }
    if (openBtn) {
      openBtn.addEventListener("click", function (e) {
        e.preventDefault();
        if (isPhoneNav()) setMobileNavTab("search");
        else setSearchViewOpen(true);
      });
    }
  }

  function wireMobileNav() {
    var nav = document.getElementById("cxMobileNav");
    if (!nav) return;
    nav.addEventListener("click", function (e) {
      var btn = e.target && e.target.closest ? e.target.closest("[data-mnav]") : null;
      if (!btn) return;
      e.preventDefault();
      var tab = btn.getAttribute("data-mnav") || "chart";
      if (tab === mobileNavTab && tab === "chart") return;
      if (tab === mobileNavTab && (tab === "watchlist" || tab === "options" || tab === "menu" || tab === "search")) {
        setMobileNavTab("chart");
        return;
      }
      setMobileNavTab(tab);
    });

    var menu = document.getElementById("cxMobileMenu");
    if (menu) {
      menu.addEventListener("click", function (e) {
        if (e.target === menu) setMobileNavTab("chart");
      });
    }
    var closeBtn = document.getElementById("cxMobileMenuClose");
    if (closeBtn) {
      closeBtn.addEventListener("click", function () {
        setMobileNavTab("chart");
      });
    }
    var themeBtn = document.getElementById("cxMobileThemeBtn");
    if (themeBtn) {
      themeBtn.addEventListener("click", function () {
        toggleChartTheme();
      });
    }
    var layoutBtn = document.getElementById("cxMobileLayoutBtn");
    if (layoutBtn) {
      layoutBtn.addEventListener("click", function () {
        setMobileMenuOpen(false);
        var layoutToggle = document.getElementById("chartsLayoutSettingsBtn");
        if (layoutToggle) layoutToggle.click();
        setMobileNavTab("chart", { uiOnly: true });
      });
    }
  }

  function wireUi() {
    var shell = document.getElementById("cxShell");
    try {
      var isNarrow = window.matchMedia && window.matchMedia("(max-width: 900px)").matches;
      var pref = isNarrow
        ? localStorage.getItem("ki_charts_wl_open") === "1"
        : localStorage.getItem("ki_charts_wl_open") !== "0";
      // Phones default to chart tab (full chart); bottom nav opens watchlist.
      if (isPhoneNav()) pref = false;
      setWlOpen(pref);
      if (shell && isPhoneNav()) shell.setAttribute("data-mnav", "chart");
    } catch (_) {
      setWlOpen(true);
    }

    wireMobileNav();
    wireSearchView();

    var btn = document.getElementById("cxUserBtn");
    var menu = document.getElementById("cxUserMenu");
    if (btn && menu) {
      function setUserMenuOpen(open) {
        menu.hidden = !open;
        menu.classList.toggle("is-open", open);
        btn.setAttribute("aria-expanded", open ? "true" : "false");
      }
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (isPhoneNav()) {
          setMobileNavTab("menu");
          return;
        }
        setUserMenuOpen(menu.hidden || !menu.classList.contains("is-open"));
      });
      document.addEventListener("click", function () {
        setUserMenuOpen(false);
      });
      menu.addEventListener("click", function (e) {
        e.stopPropagation();
      });
    }

    (function initLeadModal() {
      var modal = document.getElementById("cxLeadModal");
      var form = document.getElementById("cxLeadForm");
      var card = modal ? modal.querySelector(".cx-lead-card") : null;
      var thanks = document.getElementById("cxLeadThanks");
      var openBtn = document.getElementById("wlAdOpen");
      if (!modal || !form) return;
      var statusEl = document.getElementById("cxLeadStatus");
      function showThanks(on) {
        if (card) card.classList.toggle("is-thanks", !!on);
        if (thanks) thanks.hidden = !on;
      }
      function setOpen(open) {
        modal.hidden = !open;
        modal.classList.toggle("is-open", !!open);
        if (open) {
          showThanks(false);
          var tEl = document.getElementById("cxLeadT");
          if (tEl) tEl.value = String(Date.now());
          if (statusEl) {
            statusEl.hidden = true;
            statusEl.textContent = "";
            statusEl.classList.remove("is-err");
          }
          try {
            var phone = document.getElementById("cxLeadPhone");
            if (phone && !String(phone.value || "").trim()) setTimeout(function () { phone.focus(); }, 40);
          } catch (_) {}
        } else {
          showThanks(false);
        }
      }
      if (openBtn) {
        openBtn.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        });
      }
      var cancel = document.getElementById("cxLeadCancel");
      if (cancel) cancel.addEventListener("click", function () { setOpen(false); });
      var thanksClose = document.getElementById("cxLeadThanksClose");
      if (thanksClose) thanksClose.addEventListener("click", function () { setOpen(false); });
      modal.addEventListener("click", function (e) {
        if (e.target === modal) setOpen(false);
      });
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && !modal.hidden) setOpen(false);
      });
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        var submitBtn = document.getElementById("cxLeadSubmit");
        if (submitBtn) submitBtn.disabled = true;
        var fd = new FormData(form);
        fetch("/api/leads.php", {
          method: "POST",
          body: fd,
          credentials: "include",
        })
          .then(function (r) {
            return r.json().then(function (j) {
              return { ok: r.ok, j: j };
            });
          })
          .then(function (res) {
            if (res.ok && res.j && res.j.ok) {
              showThanks(true);
              try {
                if (thanksClose) setTimeout(function () { thanksClose.focus(); }, 40);
              } catch (_) {}
            } else {
              var err =
                (res.j && res.j.error) ||
                (res.j && res.j.errors && (res.j.errors.phone || res.j.errors.email || res.j.errors.name)) ||
                "Could not submit. Please try again.";
              if (statusEl) {
                statusEl.hidden = false;
                statusEl.classList.add("is-err");
                statusEl.textContent = err;
              }
            }
          })
          .catch(function () {
            if (statusEl) {
              statusEl.hidden = false;
              statusEl.classList.add("is-err");
              statusEl.textContent = "Network error. Please try again.";
            }
          })
          .finally(function () {
            if (submitBtn) submitBtn.disabled = false;
          });
      });
    })();

    (function initFeedbackModal() {
      var modal = document.getElementById("cxFeedbackModal");
      var form = document.getElementById("cxFeedbackForm");
      if (!modal || !form) return;
      var statusEl = document.getElementById("cxFeedbackStatus");
      var openedAt = 0;
      function setOpen(open) {
        modal.hidden = !open;
        modal.classList.toggle("is-open", !!open);
        if (open) {
          openedAt = Date.now();
          var tEl = document.getElementById("cxFeedbackT");
          if (tEl) tEl.value = String(openedAt);
          var symEl = document.getElementById("cxFeedbackSymbol");
          if (symEl) symEl.value = activeSymbol || "";
          if (statusEl) {
            statusEl.hidden = true;
            statusEl.textContent = "";
            statusEl.classList.remove("is-err");
          }
          try {
            var msg = document.getElementById("cxFeedbackMessage");
            if (msg) setTimeout(function () { msg.focus(); }, 40);
          } catch (_) {}
        }
      }
      function openFeedback(e) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        try {
          var um = document.getElementById("cxUserMenu");
          if (um) {
            um.hidden = true;
            um.classList.remove("is-open");
          }
          var mm = document.getElementById("cxMobileMenu");
          if (mm && !mm.hidden) setMobileMenuOpen(false);
        } catch (_) {}
        setOpen(true);
      }
      document.querySelectorAll("[data-feedback-open], #cxFeedbackOpen, #cxFeedbackOpenGuest").forEach(function (el) {
        el.addEventListener("click", openFeedback);
      });
      var cancel = document.getElementById("cxFeedbackCancel");
      if (cancel) cancel.addEventListener("click", function () { setOpen(false); });
      modal.addEventListener("click", function (e) {
        if (e.target === modal) setOpen(false);
      });
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && !modal.hidden) setOpen(false);
      });
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        var submitBtn = document.getElementById("cxFeedbackSubmit");
        if (submitBtn) submitBtn.disabled = true;
        var fd = new FormData(form);
        fetch("/api/feedback.php", {
          method: "POST",
          body: fd,
          credentials: "include",
        })
          .then(function (r) {
            return r.json().then(function (j) {
              return { ok: r.ok, j: j };
            });
          })
          .then(function (res) {
            if (statusEl) statusEl.hidden = false;
            if (res.ok && res.j && res.j.ok) {
              if (statusEl) {
                statusEl.classList.remove("is-err");
                statusEl.textContent = "Thanks — your feedback was sent.";
              }
              var keepName = (document.getElementById("cxFeedbackName") || {}).value || "";
              var keepEmail = (document.getElementById("cxFeedbackEmail") || {}).value || "";
              form.reset();
              var nameEl = document.getElementById("cxFeedbackName");
              var emailEl = document.getElementById("cxFeedbackEmail");
              if (nameEl) nameEl.value = keepName;
              if (emailEl) emailEl.value = keepEmail;
              setTimeout(function () { setOpen(false); }, 900);
            } else {
              var err =
                (res.j && res.j.error) ||
                (res.j && res.j.errors && (res.j.errors.message || res.j.errors.email || res.j.errors.name)) ||
                "Could not send. Please try again.";
              if (statusEl) {
                statusEl.classList.add("is-err");
                statusEl.textContent = err;
              }
            }
          })
          .catch(function () {
            if (statusEl) {
              statusEl.hidden = false;
              statusEl.classList.add("is-err");
              statusEl.textContent = "Network error. Please try again.";
            }
          })
          .finally(function () {
            if (submitBtn) submitBtn.disabled = false;
          });
      });
    })();

    var themeBtn = document.getElementById("cx-theme-toggle");
    if (themeBtn) {
      themeBtn.addEventListener("click", function () {
        toggleChartTheme();
      });
      updateThemeIcon();
    }

    var collapseBtn = document.getElementById("wlCollapseBtn");
    if (collapseBtn && shell) {
      collapseBtn.addEventListener("click", function (e) {
        e.stopPropagation();
        var currentlyOpen = !shell.classList.contains("wl-collapsed");
        setWlOpen(!currentlyOpen);
      });
    }

    var listBtn = document.getElementById("wlListBtn");
    var listMenu = document.getElementById("wlListMenu");
    if (listBtn && listMenu) {
      listBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        var open = listMenu.hidden;
        if (open) renderWlListMenu();
        setWlListMenuOpen(open);
      });
      listMenu.addEventListener("click", function (e) {
        e.stopPropagation();
        var action = e.target.closest("[data-wl-action]");
        if (action) {
          var act = action.getAttribute("data-wl-action");
          setWlListMenuOpen(false);
          if (act === "add") {
            openAddSymbolModal();
            return;
          }
          if (act === "rename") {
            renameActiveCustomList();
            return;
          }
          if (act === "delete") {
            deleteActiveCustomList();
            return;
          }
          return;
        }
        var sel = e.target.closest("[data-wl-select]");
        if (!sel) return;
        setWlSource(sel.getAttribute("data-wl-select"));
      });
      document.addEventListener("click", function () {
        setWlListMenuOpen(false);
      });
    }
    var customTabs = document.getElementById("wlCustomTabs");
    if (customTabs) {
      customTabs.addEventListener("click", function (e) {
        var tab = e.target.closest("[data-wl-tab]");
        if (!tab) return;
        e.preventDefault();
        e.stopPropagation();
        setWlSource(tab.getAttribute("data-wl-tab"));
      });
      customTabs.addEventListener("dblclick", function (e) {
        var tab = e.target.closest("[data-wl-tab]");
        if (!tab) return;
        e.preventDefault();
        e.stopPropagation();
        var id = tab.getAttribute("data-wl-tab");
        if (isSystemWlId(id)) return;
        if (wlActiveListId !== id) setWlSource(id);
        renameActiveCustomList();
      });
      renderCustomTabs();
    }
    (function initWlModals() {
      var newModal = document.getElementById("wlNewListModal");
      var renameModal = document.getElementById("wlRenameListModal");
      var addModal = document.getElementById("wlAddSymModal");
      var newCancel = document.getElementById("wlNewListCancel");
      var newCreate = document.getElementById("wlNewListCreate");
      var newName = document.getElementById("wlNewListName");
      var renameCancel = document.getElementById("wlRenameListCancel");
      var renameSave = document.getElementById("wlRenameListSave");
      var renameName = document.getElementById("wlRenameListName");
      var addCancel = document.getElementById("wlAddSymCancel");
      var addInput = document.getElementById("wlAddSymInput");
      var addResults = document.getElementById("wlAddSymResults");
      if (newCancel) {
        newCancel.addEventListener("click", function () {
          setWlModalOpen("wlNewListModal", false);
        });
      }
      if (newCreate) {
        newCreate.addEventListener("click", function () {
          confirmNewWatchlist();
        });
      }
      if (newName) {
        newName.addEventListener("keydown", function (e) {
          if (e.key === "Enter") {
            e.preventDefault();
            confirmNewWatchlist();
          }
        });
      }
      if (newModal) {
        newModal.addEventListener("click", function (e) {
          if (e.target === newModal) setWlModalOpen("wlNewListModal", false);
        });
      }
      if (renameCancel) {
        renameCancel.addEventListener("click", function () {
          setWlModalOpen("wlRenameListModal", false);
        });
      }
      if (renameSave) {
        renameSave.addEventListener("click", function () {
          confirmRenameWatchlist();
        });
      }
      if (renameName) {
        renameName.addEventListener("keydown", function (e) {
          if (e.key === "Enter") {
            e.preventDefault();
            confirmRenameWatchlist();
          }
        });
      }
      if (renameModal) {
        renameModal.addEventListener("click", function (e) {
          if (e.target === renameModal) setWlModalOpen("wlRenameListModal", false);
        });
      }
      if (addCancel) {
        addCancel.addEventListener("click", function () {
          setWlModalOpen("wlAddSymModal", false);
          renderWl();
        });
      }
      if (addModal) {
        addModal.addEventListener("click", function (e) {
          if (e.target === addModal) {
            setWlModalOpen("wlAddSymModal", false);
            renderWl();
          }
        });
      }
      if (addInput) {
        addInput.addEventListener("input", function () {
          if (wlAddSymSearchTimer) clearTimeout(wlAddSymSearchTimer);
          var qv = String(addInput.value || "").trim();
          wlAddSymSearchTimer = setTimeout(function () {
            renderAddSymSuggestions(qv);
          }, 140);
        });
        addInput.addEventListener("keydown", function (e) {
          if (e.key !== "Enter") return;
          var raw = String(addInput.value || "").trim().toUpperCase();
          if (!raw) return;
          e.preventDefault();
          tryAddToCustom(raw).then(function (ok) {
            if (!ok) return;
            addInput.value = "";
            renderAddSymSuggestions("");
          });
        });
      }
      if (addResults) {
        addResults.addEventListener("click", function (e) {
          var addBtn = e.target.closest("[data-add-custom]");
          if (!addBtn) return;
          e.preventDefault();
          e.stopPropagation();
          var addSym = addBtn.getAttribute("data-add-custom");
          if (isInCustom(addSym)) removeFromCustom(addSym);
          else addToCustom(addSym);
          if (addInput) addInput.value = "";
          renderAddSymSuggestions("");
          var hint = document.getElementById("wlAddSymHint");
          if (hint) {
            hint.innerHTML =
              "Added to <b>" +
              escHtml(activeWatchlistTitle()) +
              "</b> — saved. Add another?";
          }
        });
      }
      document.addEventListener("keydown", function (e) {
        if (e.key !== "Escape") return;
        if (newModal && !newModal.hidden) setWlModalOpen("wlNewListModal", false);
        if (renameModal && !renameModal.hidden) setWlModalOpen("wlRenameListModal", false);
        if (addModal && !addModal.hidden) {
          setWlModalOpen("wlAddSymModal", false);
          renderWl();
        }
      });
    })();
    var addSymBtn = document.getElementById("wlAddSymBtn");
    if (addSymBtn) {
      addSymBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        openNewWatchlistModal();
      });
    }
    var sectorBack = document.getElementById("wlSectorBack");
    if (sectorBack) {
      sectorBack.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        showSectorList();
      });
    }
    var search = document.getElementById("wlSearch");
    if (search) {
      search.addEventListener("input", function () {
        if (wlSrc === "custom") {
          var qv = String(search.value || "").trim();
          if (wlAddSearchTimer) clearTimeout(wlAddSearchTimer);
          if (qv.length >= 1) {
            wlAddSearchTimer = setTimeout(function () {
              renderCustomAddSuggestions(qv);
            }, 160);
          } else {
            renderWl();
          }
          return;
        }
        renderWl();
      });
      search.addEventListener("keydown", function (e) {
        if (e.key !== "Enter") return;
        var raw = String(search.value || "").trim().toUpperCase();
        if (!raw) return;
        if (wlSrc !== "custom") {
          beginAddToCustomList();
          return;
        }
        e.preventDefault();
        tryAddToCustom(raw).then(function (ok) {
          if (!ok) return;
          search.value = "";
          search.placeholder = "Search symbols…";
          renderWl();
        });
      });
    }
    syncWlListBtn();
    try {
      wireFavDock();
    } catch (_) {}
    var chgSortEl = document.getElementById("wlColChg");
    if (chgSortEl) {
      chgSortEl.setAttribute("role", "button");
      chgSortEl.tabIndex = 0;
      chgSortEl.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        toggleWlChgSort();
      });
      chgSortEl.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleWlChgSort();
        }
      });
      syncWlSortUi();
    }
    var colAddSym = document.getElementById("wlColAddSym");
    if (colAddSym) {
      colAddSym.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        openAddSymbolModal();
      });
    }
    var body = document.getElementById("wlBody");
    if (body) {
      var wlArmed = "";
      var wlArmedAt = 0;
      function wlPickSector(e) {
        var row = e.target.closest("[data-sector-key]");
        if (!row) return "";
        return row.getAttribute("data-sector-key") || "";
      }
      function wlPick(e) {
        if (
          e.target.closest(
            "[data-toggle-custom], [data-add-custom], [data-remove], .wl-add-custom, .wl-row-remove, [data-wl-empty-add], [data-wl-empty-rename]"
          )
        ) {
          return "";
        }
        if (wlPickSector(e)) return "";
        var row = e.target.closest("[data-symbol]");
        if (!row) return "";
        return row.getAttribute("data-symbol") || "";
      }
      body.addEventListener("click", function (e) {
        var emptyAdd = e.target.closest("[data-wl-empty-add]");
        if (emptyAdd) {
          e.preventDefault();
          e.stopPropagation();
          beginAddToCustomList();
          return;
        }
        var emptyRename = e.target.closest("[data-wl-empty-rename]");
        if (emptyRename) {
          e.preventDefault();
          e.stopPropagation();
          renameActiveCustomList();
          return;
        }
        var toggleBtn = e.target.closest("[data-toggle-custom]");
        if (toggleBtn) {
          e.preventDefault();
          e.stopPropagation();
          toggleCustom(toggleBtn.getAttribute("data-toggle-custom"));
          return;
        }
        var addBtn = e.target.closest("[data-add-custom]");
        if (addBtn) {
          e.preventDefault();
          e.stopPropagation();
          var addSym = addBtn.getAttribute("data-add-custom");
          if (isInCustom(addSym)) removeFromCustom(addSym);
          else addToCustom(addSym);
          var searchEl = document.getElementById("wlSearch");
          if (searchEl) {
            searchEl.value = "";
            searchEl.placeholder = "Search symbols…";
          }
          renderWl();
          return;
        }
        var rmBtn = e.target.closest("[data-remove]");
        if (rmBtn) {
          e.preventDefault();
          e.stopPropagation();
          removeFromCustom(rmBtn.getAttribute("data-remove"));
          return;
        }
      });
      // Mouse: fire on pointerdown so the switch starts before click (feels instant).
      // Touch/pen: wait for click — a touch that turns into a scroll never fires click.
      body.addEventListener("pointerdown", function (e) {
        if (e.pointerType && e.pointerType !== "mouse") return;
        if (e.button != null && e.button !== 0) return;
        if (e.target.closest("[data-toggle-custom], [data-add-custom], [data-remove], .wl-add-custom, .wl-row-remove, [data-wl-empty-add], [data-wl-empty-rename], .wl-add-suggest")) {
          return;
        }
        var targetSymbol = wlPick(e);
        if (!targetSymbol) return;
        e.preventDefault();
        e.stopPropagation();
        wlArmed = targetSymbol;
        wlArmedAt = performance.now();
        frontTrace("watchlist.pointerdown", {
          target: targetSymbol,
          pointer: e.pointerType || "",
        });
        // Start tip download on press so cold symbols paint as soon as setSymbol runs.
        try {
          prefetchTip(targetSymbol);
        } catch (_) {}
        setSymbolFromWatchlist(targetSymbol);
        maybeAutoCollapseWlMobile();
      });
      body.addEventListener("click", function (e) {
        if (e.target.closest("[data-toggle-custom], [data-add-custom], [data-remove], .wl-add-custom, .wl-row-remove")) {
          return;
        }
        var sectorKey = wlPickSector(e);
        if (sectorKey) {
          if (
            wlArmed === "sector:" + sectorKey &&
            wlArmedAt &&
            performance.now() - wlArmedAt < 800
          ) {
            e.preventDefault();
            return;
          }
          openSectorStocks(sectorKey);
          return;
        }
        var targetSymbol = wlPick(e);
        if (!targetSymbol) return;
        // pointerdown already switched — ignore duplicate click unless it missed.
        if (
          wlArmed === targetSymbol &&
          wlArmedAt &&
          performance.now() - wlArmedAt < 800
        ) {
          e.preventDefault();
          return;
        }
        frontTrace("watchlist.click", {
          target: targetSymbol,
          detail: e.detail,
        });
        try {
          prefetchTip(targetSymbol);
        } catch (_) {}
        setSymbolFromWatchlist(targetSymbol);
        maybeAutoCollapseWlMobile();
      });
      body.addEventListener(
        "pointerenter",
        function (e) {
          var row = e.target.closest("[data-symbol]");
          if (!row) return;
          prewarmSymbol(row.getAttribute("data-symbol"));
        },
        true
      );
    }
    var wlToggle = document.getElementById("wlToggleBtn");
    var edge = document.getElementById("wlEdgeToggle");
    if (wlToggle) {
      wlToggle.addEventListener("click", function () {
        var currentlyOpen = shell && !shell.classList.contains("wl-collapsed");
        setWlOpen(!currentlyOpen);
      });
    }
    if (edge) {
      edge.addEventListener("click", function () {
        setWlOpen(true);
      });
    }
    var sheetHandle = document.getElementById("wlSheetHandle");
    if (sheetHandle && shell) {
      sheetHandle.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (shell.classList.contains("wl-collapsed")) {
          setWlOpen(true);
          return;
        }
        shell.classList.toggle("wl-expanded");
        try {
          bumpAllWidgetSizes();
          window.dispatchEvent(new Event("resize"));
        } catch (_) {}
      });
    }
  }

  function waitTv(cb) {
    var n = 0;
    (function tick() {
      if (typeof TradingView !== "undefined" && TradingView.widget) {
        cb();
        return;
      }
      if (++n > 200) {
        log("TV timeout");
        return;
      }
      setTimeout(tick, 25);
    })();
  }

  function labelsOf(list) {
    var out = [];
    for (var i = 0; i < (list || []).length; i++) {
      var lab = String((list[i] && (list[i].label || list[i].symbol)) || "").trim();
      if (lab) out.push(lab);
    }
    return out;
  }

  function uniqueLabels(list) {
    var seen = {};
    var out = [];
    (list || []).forEach(function (lab) {
      var k = String(lab || "")
        .trim()
        .toUpperCase();
      if (!k || seen[k]) return;
      seen[k] = true;
      out.push(String(lab).trim());
    });
    return out;
  }

  function registerHarness() {
    window.__kiChartsHarness = {
      ready: function () {
        return !!(
          widget &&
          (wlData.indices.length || wlData.sectors.length || wlData.fno.length)
        );
      },
      /** Full watchlist universe: every index, sector index, sector stock, F&O. */
      pool: function () {
        var sectorStocks = [];
        var chartableSectors = [];
        (wlData.sectors || []).forEach(function (sec) {
          // Skip sector indexes with no Dhan security_id (CEMENT/CHEMICALS).
          if (sec && String(sec.security_id || "").trim()) {
            var lab = String((sec.label || sec.symbol || "") || "").trim();
            if (lab) chartableSectors.push(lab);
          }
          (sec.stocks || []).forEach(function (s) {
            var stockLab = String((s && (s.label || s.symbol)) || "").trim();
            if (stockLab) sectorStocks.push(stockLab);
          });
        });
        var custom = labelsOf(wlData.custom);
        return {
          indices: uniqueLabels(labelsOf(wlData.indices)),
          sectors: uniqueLabels(chartableSectors),
          stocks: uniqueLabels(sectorStocks.length ? sectorStocks.concat(custom) : custom),
          fno: uniqueLabels(labelsOf(wlData.fno)),
        };
      },
      poolCounts: function () {
        var p = this.pool();
        return {
          indices: (p.indices || []).length,
          sectors: (p.sectors || []).length,
          stocks: (p.stocks || []).length,
          fno: (p.fno || []).length,
          total:
            (p.indices || []).length +
            (p.sectors || []).length +
            (p.stocks || []).length +
            (p.fno || []).length,
        };
      },
      ensureFull: function () {
        return loadWatchlist().then(function () {
          var p = window.__kiChartsHarness.pool();
          return {
            indices: (p.indices || []).length,
            sectors: (p.sectors || []).length,
            stocks: (p.stocks || []).length,
            fno: (p.fno || []).length,
          };
        });
      },
      ensureFno: function () {
        if (wlData.fno && wlData.fno.length) {
          return Promise.resolve(wlData.fno.length);
        }
        return loadWatchlist().then(function () {
          return (wlData.fno || []).length;
        });
      },
      setSource: function (src) {
        setWlSource(src || "indices");
      },
      open: function (symbol, kind, resolution) {
        var q = String(symbol || "").trim();
        if (!q) return false;
        kind = String(kind || "").toLowerCase();
        try {
          var chartSym = q;
          if (kind === "index" || kind === "indices") this.setSource("indices");
          else if (kind === "sector") {
            this.setSource("sectors");
            var sec = findSectorByKey(q);
            if (sec) {
              if (!String(sec.security_id || "").trim()) {
                log("harness.open", "skip empty SID sector " + q);
                return false;
              }
              // Always chart the sector index — Autobot must not stop on the stock list.
              openSectorStocks(q);
              chartSym = sec.index_symbol || sec.symbol || sec.label || q;
            }
          } else if (kind === "stock") {
            this.setSource("sectors");
          } else if (kind === "fno") this.setSource("fno");
          else this.setSource("indices");
          if (resolution) this.setResolution(resolution);
          setSymbol(chartSym);
          return true;
        } catch (err) {
          log("harness.open", err);
          return false;
        }
      },
      setResolution: function (res) {
        var r = String(res || "5");
        if (r === "1H") r = "60";
        if (r === "4H") r = "240";
        if (r === "D") r = "1D";
        if (r === "1" || r === "3" || r === "25") r = "5";
        activeRes = r;
        try {
          if (widget && typeof widget.activeChart === "function") {
            var ch = widget.activeChart();
            if (ch && typeof ch.setResolution === "function") {
              ch.setResolution(r);
            }
          }
        } catch (e) {
          log("harness.setResolution", e);
        }
        return activeRes;
      },
      getResolution: function () {
        return activeRes;
      },
      waitForWidgetReady: function (maxMs) {
        maxMs = maxMs == null ? 8000 : Number(maxMs);
        return new Promise(function (resolve) {
          var t0 = Date.now();
          function tick() {
            try {
              if (widget && typeof widget.activeChart === "function") {
                var chart = widget.activeChart();
                if (chart && typeof chart.getVisibleRange === "function") {
                  var vr = chart.getVisibleRange();
                  if (vr && vr.from != null && vr.to != null) {
                    resolve({ ok: true, ms: Date.now() - t0 });
                    return;
                  }
                }
              }
            } catch (_) {}
            if (Date.now() - t0 >= maxMs) {
              resolve({ ok: false, ms: Date.now() - t0, why: "widget_timeout" });
              return;
            }
            setTimeout(tick, 100);
          }
          tick();
        });
      },
      /**
       * Chart series snapshot for October / QA bots — MEM tip, TV edge, today count, holes,
       * dense/solid/sparse flags (0.5.362+ paint gate + 0.5.363 server tip continuity).
       * Does not hit the network.
       */
      snapshot: function (symbol, resolution) {
        var sym = String(symbol || activeSymbol || "").trim();
        var res = String(resolution || activeRes || "5");
        if (!sym) return { ok: false, why: "no_symbol" };
        var key = subKey(sym, res);
        var bars = barsFromFivePool(sym, res) || histCache[key] || [];
        var edge = tvEdge[key] || null;
        var live = liveBar[key] || null;
        // Continuity metrics score *today* only (0.5.366+). Full-span hist holes stay in holes_hist.
        var holes = [];
        var holesHist = [];
        try {
          holes =
            typeof findLiveSessionHoles === "function"
              ? findLiveSessionHoles(bars, res, sym)
              : findIntradayHoles(bars, res, sym);
        } catch (_) {}
        try {
          holesHist = findIntradayHoles(bars, res, sym);
        } catch (_) {}
        var today = 0;
        try {
          today = todayBarCount(bars, sym);
        } catch (_) {}
        var tipDays = 0;
        try {
          tipDays = tradingDayLabels(bars).length;
        } catch (_) {}
        var lastT = bars.length ? Number(bars[bars.length - 1].time) : 0;
        var edgeT = edge ? Number(edge.time) : 0;
        var openB = 0;
        try {
          openB = floorBarTimeMs(Date.now(), res, sym);
        } catch (_) {}
        var stepMs = resMinutes(res) * 60000 || 300000;
        var closedEdge = 0;
        try {
          closedEdge = typeof seriesClosedEdgeMs === "function" ? seriesClosedEdgeMs(sym, res) : 0;
        } catch (_) {}
        var edgeLagMs =
          closedEdge > 0 && openB > 0 ? Math.max(0, openB - closedEdge) : lastT > 0 ? Math.max(0, openB - lastT) : null;
        var dense = false;
        try {
          dense =
            typeof poolIsDenseForPaint === "function"
              ? poolIsDenseForPaint(bars, sym, res)
              : typeof sessionTipSolid === "function"
                ? sessionTipSolid(bars, sym, res)
                : holes.length === 0 && today > 0;
        } catch (_) {}
        var continuous = false;
        try {
          continuous = typeof poolIsContinuous === "function" ? poolIsContinuous(bars) : tipDays >= 8;
        } catch (_) {}
        var enoughToday =
          typeof poolHasEnoughToday === "function" ? poolHasEnoughToday(bars, sym, res) : today > 0;
        var thinFlash =
          typeof isThinSessionFlash === "function" ? isThinSessionFlash(bars, sym, res) : false;
        var solid = !!solidFirstPaint[key];
        var sparse = !!sparseFirstPaint[key];
        var paintAgeMs =
          paintCommittedAt[key] > 0 ? Math.max(0, Date.now() - paintCommittedAt[key]) : null;
        return {
          ok: true,
          symbol: sym,
          resolution: res,
          n: bars.length,
          today: today,
          tip_days: tipDays,
          holes: holes.length,
          holes_hist: holesHist.length,
          hole_sample: holes.slice(0, 4).map(function (h) {
            return { from: h.from, to: h.to, min: Math.round((h.to - h.from) / 60000) };
          }),
          last_t: lastT || null,
          edge_t: edgeT || null,
          live_t: live ? Number(live.time) : null,
          live_c: live ? Number(live.close) : null,
          open_bucket: openB || null,
          closed_edge: closedEdge || null,
          edge_lag_ms: edgeLagMs,
          edge_lag_steps: edgeLagMs != null ? Math.round(edgeLagMs / stepMs) : null,
          paint_committed: !!paintCommittedAt[key],
          paint_age_ms: paintAgeMs,
          solid: solid,
          sparse: sparse,
          dense: dense,
          continuous: continuous,
          enough_today: enoughToday,
          thin_flash: thinFlash,
          // Compact grade hints for bots / reports
          metrics: {
            dense: dense,
            solid: solid,
            sparse: sparse,
            continuous: continuous,
            holes: holes.length,
            holes_hist: holesHist.length,
            today: today,
            tip_days: tipDays,
            edge_lag_steps: edgeLagMs != null ? Math.round(edgeLagMs / stepMs) : null,
            edge_lag_ms: edgeLagMs,
            thin_flash: thinFlash,
            live: !!(live && Number(live.close) > 0),
            n: bars.length,
          },
        };
      },
      scrollLeftOne: function (opts) {
        opts = opts || {};
        try {
          if (!widget || typeof widget.activeChart !== "function") {
            return { ok: false, why: "no_widget" };
          }
          var chart = widget.activeChart();
          if (!chart || typeof chart.getVisibleRange !== "function") {
            return { ok: false, why: "no_range" };
          }
          var vr = chart.getVisibleRange();
          if (!vr || vr.from == null || vr.to == null) return { ok: false, why: "empty_range" };
          if (opts.dryRun) {
            return { ok: true, dryRun: true, from: vr.from, to: vr.to };
          }
          var span = Math.max(1, vr.to - vr.from);
          var jumpDays = Number(opts.jumpDays) || 0;
          var pastMs = opts.pastMs != null ? Number(opts.pastMs) : null;
          var next;
          // Jump to a window ending before pastMs (or current from), spanning jumpDays or 1 viewport.
          if (pastMs != null && isFinite(pastMs) && pastMs > 0) {
            var endSec = Math.floor(pastMs / 1000) - 60;
            var winSec = jumpDays > 0 ? jumpDays * 86400 : Math.max(span, 3 * 86400);
            next = { from: endSec - winSec, to: endSec };
          } else if (jumpDays > 0) {
            var shift = jumpDays * 86400;
            next = { from: vr.from - shift, to: vr.to - shift };
          } else {
            next = { from: vr.from - span, to: vr.to - span };
          }
          if (typeof chart.setVisibleRange === "function") {
            chart.setVisibleRange(next);
          }
          return { ok: true, from: next.from, to: next.to, jumpDays: jumpDays || null };
        } catch (err) {
          return { ok: false, why: (err && err.message) || "scroll_err" };
        }
      },
    };
    log("harness registered");
  }

  // Cache clear button handler
  function initLiveLogButton() {
    var btn = document.getElementById("bcLiveLogBtn");
    if (!btn) return;
    btn.addEventListener("click", function () {
      try {
        localStorage.setItem("ki_logbot", "1");
      } catch (_) {}
      function open() {
        if (window.KiLogbot && typeof window.KiLogbot.openPanel === "function") {
          window.KiLogbot.openPanel();
        }
      }
      if (window.KiLogbot) {
        open();
        return;
      }
      var s = document.createElement("script");
      s.src = "/assets/js/ki_logbot.js?v=" + encodeURIComponent((window.__BC && window.__BC.assetV) || "");
      s.onload = open;
      document.body.appendChild(s);
    });
  }

  function registerChartSw() {
    try {
      if (!("serviceWorker" in navigator)) return;
      navigator.serviceWorker
        .register("/sw-charts.js?v=" + encodeURIComponent((window.__BC && window.__BC.assetV) || ""), { scope: "/" })
        .catch(function () {});
    } catch (_) {}
  }

  function ocGuessUnder(sym) {
    var s = String(sym || "").toUpperCase();
    if (s.indexOf("BANK") >= 0) return "BANK NIFTY";
    if (s.indexOf("FIN") >= 0) return "FIN NIFTY";
    if (s.indexOf("SENSEX") >= 0) return "SENSEX";
    if (s.indexOf("NIFTY") >= 0) return "NIFTY 50";
    return "NIFTY 50";
  }

  function ocBestPx(c) {
    if (!c) return 0;
    var ltp = Number(c.ltp);
    if (ltp > 0) return ltp;
    var ask = Number(c.ask);
    if (ask > 0) return ask;
    var bid = Number(c.bid);
    if (bid > 0) return bid;
    var prev = Number(c.prev_close);
    if (prev > 0) return prev;
    return 0.05;
  }

  var _ocTipBatchAt = 0;
  var _ocTipBatchKey = "";

  function prefetchOptWingTips(payload) {
    try {
      var rows = (payload && payload.rows) || [];
      var atm = Number(payload && payload.atm) || 0;
      if (!rows.length || !(atm > 0)) return;
      var underLabel = "";
      try {
        underLabel =
          (payload.under && (payload.under.label || payload.under)) ||
          ocGuessUnder(activeSymbol);
      } catch (_) {
        underLabel = ocGuessUnder(activeSymbol);
      }
      underLabel = String(underLabel || "NIFTY 50");
      var expiry = String((payload && payload.expiry) || "");
      var wing = 15;
      try {
        wing = Math.max(8, Math.min(20, Number(payload.wing) || 15));
      } catch (_) {}
      var batchKey = underLabel + "|" + expiry + "|" + wing;
      var now = Date.now();
      // OC live refresh re-fires onChain — only batch once per under/expiry per 45s.
      if (batchKey === _ocTipBatchKey && now - _ocTipBatchAt < 45000) return;
      _ocTipBatchKey = batchKey;
      _ocTipBatchAt = now;
      var url =
        "/api/oc-tip-batch.php?symbol=" +
        encodeURIComponent(underLabel) +
        "&expiry=" +
        encodeURIComponent(expiry) +
        "&wing=" +
        encodeURIComponent(String(wing)) +
        "&resolution=5&max_bars=600&sessions=7&budget_ms=8000";
      ocLog("cache.start", { under: underLabel, expiry: expiry, wing: wing, atm: atm, sessions: 7 });
      fetch(url, { credentials: "include", cache: "no-store" })
        .then(function (r) {
          return r.json();
        })
        .then(function (j) {
          if (!j || !j.tips) {
            ocLog("cache.empty", { under: underLabel, ms: j && j.ms });
            return;
          }
          var tips = j.tips;
          var keys = Object.keys(tips);
          var loaded = 0;
          for (var i = 0; i < keys.length; i++) {
            var sym = keys[i];
            var pack = tips[sym] || {};
            var bars = pack.bars || [];
            if (!bars.length) continue;
            if (bars[0].time < 1e12) bars = toTvBars(bars);
            var key = subKey(sym, activeRes);
            // Prefer deeper tip already in MEM; otherwise take batch pack (up to 7 sessions).
            if (histCache[key] && histCache[key].length >= bars.length && histCache[key].length >= 40) {
              continue;
            }
            histCache[key] = bars;
            netPaintReady[key] = symbolSwitchGen;
            solidFirstPaint[key] = true;
            bridgeReady[key] = true;
            lastHistoricalBar[key] = bars[bars.length - 1];
            loaded++;
          }
          ocLog("cache.ready", {
            under: underLabel,
            n: loaded,
            hit: j.n,
            miss: j.miss,
            skipped: j.skipped,
            ms: j.ms,
            sessions: 7,
          });
        })
        .catch(function (err) {
          ocLog("cache.err", {
            under: underLabel,
            err: String(err && err.message ? err.message : err),
          });
        });
    } catch (_) {}
  }

  function initOptionChainModal() {
    var backdrop = document.getElementById("ocModal");
    var btn = document.getElementById("cxOcBtn");
    // Wire the button even if option_chain.js is still loading (defer race).
    if (!backdrop) return;
    var root = backdrop.querySelector(".oc-shell") || backdrop;
    var ctrl = null;
    var lastSelectSym = "";
    var lastSelectAt = 0;
    var openPending = false;

    function closeOc() {
      backdrop.hidden = true;
      openPending = false;
      if (ctrl && typeof ctrl.destroy === "function") {
        try {
          ctrl.destroy();
        } catch (_) {}
        ctrl = null;
      }
      if (!mobileNavSyncing && isPhoneNav() && mobileNavTab === "options") {
        setMobileNavTab("chart", { uiOnly: true });
      }
    }

    function mountOc(under) {
      if (!window.BcOptionChain || typeof window.BcOptionChain.mount !== "function") return false;
      if (!ctrl) {
        ctrl = window.BcOptionChain.mount(root, {
          symbol: under,
          embedded: true,
          wsUrl: (window.__BC && window.__BC.ocWsUrl) || undefined,
          onClose: closeOc,
          onChain: function (payload) {
            prefetchOptWingTips(payload);
          },
          onSelect: function (c) {
            if (!c || !c.symbol) return;
            var now = Date.now();
            if (c.symbol === lastSelectSym && now - lastSelectAt < 500) return;
            lastSelectSym = c.symbol;
            lastSelectAt = now;
            var clickT0 = performance.now();
            var displaySym = registerOptLabel(c.symbol, c.label || c.symbol);
            ocLog("click", {
              symbol: c.symbol,
              label: displaySym,
              px: ocBestPx(c),
              pane: activePane || "sector",
            });
            var px = ocBestPx(c);
            try {
              if (px > 0 && TickHub && typeof TickHub.applyQuote === "function") {
                var seed = {
                  ltp: px,
                  open: px,
                  high: px,
                  low: px,
                  ts: Date.now(),
                  security_id: c.security_id || String(c.symbol).replace(/^OPT:/i, ""),
                };
                TickHub.applyQuote(c.symbol, seed);
                if (displaySym !== c.symbol) TickHub.applyQuote(displaySym, seed);
              }
            } catch (_) {}
            // Close OC so the CE/PE chart is front — not the chain overlay.
            closeOc();
            // Paint into the selected (active) grid pane — not always the first pane.
            setSymbol(displaySym, {
              force: true,
              ltp: px,
              pane: activePane || "sector",
            });
            ocLog("setSymbol", {
              symbol: displaySym,
              wire: c.symbol,
              pane: activePane || "sector",
              px: px,
              elapsed_ms: Math.round(performance.now() - clickT0),
            });
            var el = document.getElementById("cxActiveSym");
            if (el) el.textContent = displaySym;
            var title = document.getElementById("cx-left-title");
            if (title) title.textContent = displaySym;
            // Live sub after paint — fire-and-forget (wire id for the feed).
            setTimeout(function () {
              try {
                if (TickHub && typeof TickHub.sendSub === "function") {
                  TickHub.sendSub(c.symbol);
                  if (displaySym !== c.symbol) TickHub.sendSub(displaySym);
                }
              } catch (_) {}
            }, 0);
          },
        });
      } else if (typeof ctrl.setUnder === "function") {
        ctrl.setUnder(under);
      }
      return true;
    }

    function openOc() {
      backdrop.hidden = false;
      openPending = true;
      if (!mobileNavSyncing && isPhoneNav()) {
        setMobileNavTab("options", { uiOnly: true });
      }
      var under = ocGuessUnder(activeSymbol);
      if (mountOc(under)) {
        openPending = false;
        return;
      }
      // option_chain.js not ready yet — retry briefly instead of a dead click.
      var tries = 0;
      var timer = setInterval(function () {
        tries++;
        if (!openPending || backdrop.hidden) {
          clearInterval(timer);
          return;
        }
        if (mountOc(under) || tries >= 40) {
          clearInterval(timer);
          openPending = false;
          if (tries >= 40 && !ctrl) {
            ocLog("open.fail", { reason: "BcOptionChain_missing" });
          }
        }
      }, 50);
    }

    ocApi.open = openOc;
    ocApi.close = closeOc;
    ocApi.isOpen = function () {
      return !backdrop.hidden;
    };
    if (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        openOc();
      });
    }
    backdrop.addEventListener("click", function (e) {
      if (e.target === backdrop) closeOc();
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && !backdrop.hidden) closeOc();
      if (e.key === "Escape" && isPhoneNav()) {
        var m = document.getElementById("cxMobileMenu");
        if (m && !m.hidden) setMobileNavTab("chart");
      }
    });
  }

  function initOcAnalysis() {
    var btn = document.getElementById("cxAnalysisBtn");
    var panel = document.getElementById("cxAnalysisPanel");
    var wrap = document.getElementById("cxAnalysisWrap");
    if (!btn || !panel) return;

    var lastData = null;
    var shapeIds = [];
    var plotted = false;
    var loading = false;
    var ANALYSIS_URL = "/api/option-analysis.php";
    var NIFTY = "NIFTY 50";

    function fmtNum(n, d) {
      if (n == null || isNaN(Number(n))) return "—";
      return Number(n).toLocaleString("en-IN", {
        minimumFractionDigits: d == null ? 0 : d,
        maximumFractionDigits: d == null ? 0 : d,
      });
    }
    function fmtOi(n) {
      if (n == null || isNaN(Number(n))) return "—";
      var v = Number(n);
      if (v >= 1e7) return (v / 1e7).toFixed(2) + " Cr";
      if (v >= 1e5) return (v / 1e5).toFixed(2) + " L";
      return Math.round(v).toLocaleString("en-IN");
    }
    function setOpen(open) {
      panel.hidden = !open;
      panel.classList.toggle("is-open", open);
      btn.setAttribute("aria-expanded", open ? "true" : "false");
      btn.classList.toggle("is-on", open);
    }
    function levelList(el, rows, kind) {
      if (!el) return;
      el.innerHTML = "";
      (rows || []).forEach(function (lv) {
        var li = document.createElement("li");
        var chg = Number(lv.oi_chg || 0);
        var chgTxt = chg === 0 ? "" : (chg > 0 ? "↑" : "↓") + " " + fmtOi(Math.abs(chg));
        li.innerHTML =
          "<strong>" +
          fmtNum(lv.strike, 0) +
          "</strong><span>OI " +
          fmtOi(lv.oi) +
          (chgTxt ? " · " + chgTxt : "") +
          "</span>";
        li.title = (kind === "support" ? "Put wall " : "Call wall ") + fmtNum(lv.strike, 0);
        li.addEventListener("click", function () {
          plotLevels(lastData, [lv], kind === "support" ? "#34d399" : "#f87171");
        });
        el.appendChild(li);
      });
      if (!(rows || []).length) {
        el.innerHTML = "<li><span>No levels</span></li>";
      }
    }
    function render(data) {
      lastData = data;
      var meta = panel.querySelector("[data-oa-meta]");
      var spot = panel.querySelector("[data-oa-spot]");
      var atm = panel.querySelector("[data-oa-atm]");
      var pcr = panel.querySelector("[data-oa-pcr]");
      var mp = panel.querySelector("[data-oa-maxpain]");
      var bias = panel.querySelector("[data-oa-bias]");
      if (meta) {
        meta.textContent =
          (data.expiry || "—") +
          (data.cache ? " · " + data.cache : "") +
          (data.pcr_atm != null ? " · ATM PCR " + data.pcr_atm : "");
      }
      if (spot) spot.textContent = fmtNum(data.spot, 2);
      if (atm) atm.textContent = fmtNum(data.atm, 0);
      if (pcr) pcr.textContent = data.pcr_oi != null ? String(data.pcr_oi) : "—";
      if (mp) mp.textContent = fmtNum(data.max_pain, 0);
      if (bias) bias.textContent = data.bias || "—";
      levelList(panel.querySelector("[data-oa-support]"), data.support, "support");
      levelList(panel.querySelector("[data-oa-resist]"), data.resistance, "resist");
      var plotBtn = document.getElementById("cxAnalysisPlot");
      if (plotBtn) plotBtn.classList.toggle("is-on", plotted);
    }
    function fetchAnalysis() {
      if (loading) return Promise.resolve(lastData);
      loading = true;
      var meta = panel.querySelector("[data-oa-meta]");
      if (meta) meta.textContent = "Loading…";
      return fetch(ANALYSIS_URL + "?symbol=" + encodeURIComponent(NIFTY) + "&top=3", {
        credentials: "include",
        cache: "no-store",
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          loading = false;
          if (!data || !data.ok) {
            if (meta) meta.textContent = (data && data.error) || "Failed";
            return null;
          }
          render(data);
          return data;
        })
        .catch(function (e) {
          loading = false;
          if (meta) meta.textContent = "Error: " + (e && e.message ? e.message : "fetch");
          return null;
        });
    }
    function clearShapes() {
      var chart = null;
      try {
        chart = widget && widget.activeChart && widget.activeChart();
      } catch (_) {}
      if (!chart) {
        shapeIds = [];
        plotted = false;
        return;
      }
      shapeIds.forEach(function (id) {
        try {
          chart.removeEntity(id);
        } catch (_) {}
      });
      shapeIds = [];
      plotted = false;
      var plotBtn = document.getElementById("cxAnalysisPlot");
      if (plotBtn) plotBtn.classList.remove("is-on");
    }
    function addHLine(chart, price, text, color) {
      if (!chart || !(price > 0)) return Promise.resolve(null);
      var opts = {
        shape: "horizontal_line",
        text: text,
        lock: true,
        disableSelection: true,
        disableSave: true,
        disableUndo: true,
        overrides: {
          linecolor: color,
          linestyle: 2,
          linewidth: 1,
          showLabel: true,
          textcolor: color,
          fontsize: 10,
          horzLabelsAlign: "right",
          vertLabelsAlign: "middle",
        },
      };
      try {
        var p = chart.createShape({ price: Number(price) }, opts);
        if (p && typeof p.then === "function") return p;
        return Promise.resolve(p);
      } catch (_) {
        return Promise.resolve(null);
      }
    }
    function plotLevels(data, onlyLevels, forceColor) {
      if (!data) return;
      var w = widget || window._bcWidget;
      if (!w) return;
      function go() {
        var chart = null;
        try {
          chart = w.activeChart && w.activeChart();
        } catch (_) {}
        if (!chart) return;
        clearShapes();
        var jobs = [];
        if (onlyLevels && onlyLevels.length) {
          onlyLevels.forEach(function (lv) {
            jobs.push(
              addHLine(
                chart,
                lv.strike,
                (forceColor === "#34d399" ? "S " : "R ") + fmtNum(lv.strike, 0),
                forceColor || "#989ea0"
              )
            );
          });
        } else {
          (data.support || []).forEach(function (lv, i) {
            jobs.push(addHLine(chart, lv.strike, "S" + (i + 1) + " " + fmtNum(lv.strike, 0), "#34d399"));
          });
          (data.resistance || []).forEach(function (lv, i) {
            jobs.push(addHLine(chart, lv.strike, "R" + (i + 1) + " " + fmtNum(lv.strike, 0), "#f87171"));
          });
          if (data.max_pain > 0) {
            jobs.push(addHLine(chart, data.max_pain, "MaxPain " + fmtNum(data.max_pain, 0), "#f59e0b"));
          }
        }
        Promise.all(jobs).then(function (ids) {
          shapeIds = (ids || []).filter(Boolean);
          plotted = shapeIds.length > 0;
          var plotBtn = document.getElementById("cxAnalysisPlot");
          if (plotBtn) plotBtn.classList.toggle("is-on", plotted);
        });
      }
      if (String(activeSymbol).toUpperCase() !== NIFTY && String(activeSymbol).toUpperCase() !== "NIFTY") {
        setSymbol(NIFTY);
        var el = document.getElementById("cxActiveSym");
        if (el) el.textContent = NIFTY;
        var title = document.getElementById("cx-left-title");
        if (title) title.textContent = NIFTY;
        setTimeout(go, 900);
      } else {
        go();
      }
    }

    btn.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      var open = panel.hidden;
      setOpen(open);
      if (open) fetchAnalysis();
    });
    document.addEventListener("click", function (e) {
      if (!panel.hidden && wrap && !wrap.contains(e.target)) setOpen(false);
    });
    var plotBtn = document.getElementById("cxAnalysisPlot");
    var clearBtn = document.getElementById("cxAnalysisClear");
    var refreshBtn = document.getElementById("cxAnalysisRefresh");
    var smartBtn = document.getElementById("cxAnalysisSmartOi");
    if (plotBtn) {
      plotBtn.addEventListener("click", function (e) {
        e.stopPropagation();
        if (plotted) {
          clearShapes();
          return;
        }
        var run = lastData ? Promise.resolve(lastData) : fetchAnalysis();
        run.then(function (d) {
          if (d) plotLevels(d);
        });
      });
    }
    if (smartBtn) {
      smartBtn.addEventListener("click", function (e) {
        e.stopPropagation();
        var api = window.__BC_smartOi;
        if (!api) return;
        if (api.active && api.active()) {
          api.clear();
          smartBtn.classList.remove("is-on");
          return;
        }
        // Prefer Nifty for Smart OI sampling context
        if (
          String(activeSymbol).toUpperCase() !== NIFTY &&
          String(activeSymbol).toUpperCase() !== "NIFTY"
        ) {
          setSymbol(NIFTY);
          var el = document.getElementById("cxActiveSym");
          if (el) el.textContent = NIFTY;
          var title = document.getElementById("cx-left-title");
          if (title) title.textContent = NIFTY;
          setTimeout(function () {
            api.ensure(true);
            smartBtn.classList.add("is-on");
          }, 900);
        } else {
          api.ensure(true);
          smartBtn.classList.add("is-on");
        }
      });
    }
    if (clearBtn) {
      clearBtn.addEventListener("click", function (e) {
        e.stopPropagation();
        clearShapes();
        try {
          if (window.__BC_smartOi) window.__BC_smartOi.clear();
        } catch (_) {}
        if (smartBtn) smartBtn.classList.remove("is-on");
      });
    }
    if (refreshBtn) {
      refreshBtn.addEventListener("click", function (e) {
        e.stopPropagation();
        fetchAnalysis();
        try {
          if (window.__BC_smartOi) window.__BC_smartOi.fetch(true);
        } catch (_) {}
      });
    }
  }

  function barReplayFmtTime(ms) {
    try {
      var d = new Date(Number(ms) + IST_OFFSET_MS);
      var dd = String(d.getUTCDate()).padStart(2, "0");
      var mo = String(d.getUTCMonth() + 1).padStart(2, "0");
      var hh = String(d.getUTCHours()).padStart(2, "0");
      var mi = String(d.getUTCMinutes()).padStart(2, "0");
      return dd + "/" + mo + " " + hh + ":" + mi;
    } catch (_) {
      return "—";
    }
  }

  function barReplayPool() {
    var key = subKey(activeSymbol, activeRes || "5");
    // Use raw tip MEM — do not densify the full multi-week pool (that ballooned to 14k bars).
    return (histCache[key] || []).slice();
  }

  function barReplaySnapIndex(timeSecOrMs, pool) {
    var t = Number(timeSecOrMs);
    if (!(t > 0) || !pool || !pool.length) return -1;
    if (t < 1e12) t = t * 1000;
    var idx = 0;
    for (var i = 0; i < pool.length; i++) {
      var bt = Number(pool[i].time);
      if (bt < 1e12) bt *= 1000;
      if (bt <= t) idx = i;
      else break;
    }
    return idx;
  }

  function barReplayEnsureHost() {
    var line = document.getElementById("cxReplayVLine");
    if (!line) return null;
    var host = barReplayHostEl();
    if (!host) return line;
    if (getComputedStyle(host).position === "static") {
      host.style.position = "relative";
    }
    if (line.parentNode !== host) host.appendChild(line);
    barReplay.hostEl = host;
    return line;
  }

  function barReplayHideVLine() {
    var line = document.getElementById("cxReplayVLine");
    if (line) {
      line.classList.remove("is-on");
      line.hidden = true;
      line.style.left = "";
      line.style.width = "2px";
      line.style.right = "auto";
    }
    barReplayClearPickShape();
  }

  function barReplayClearPickShape() {
    try {
      var chart = barReplayChart();
      if (chart && barReplay.pickShapeId) chart.removeEntity(barReplay.pickShapeId);
    } catch (_) {}
    barReplay.pickShapeId = null;
    barReplay.pickLineTime = null;
  }

  function barReplayShowVLine(param) {
    var line = barReplayEnsureHost();
    if (!line || !param) return;
    var x = param.offsetX;
    if (x == null || !(x >= 0)) return;
    // Thin 2px guide only — never stretch across the pane.
    line.hidden = false;
    line.classList.add("is-on");
    line.style.width = "2px";
    line.style.maxWidth = "2px";
    line.style.right = "auto";
    line.style.left = Math.round(x) + "px";
  }

  function barReplaySetPickShape(_timeSecOrMs) {
    // Intentionally no-op: TV vertical_line shapes were filling the right pane green.
    // Hover guide is the DOM 2px line only.
  }

  function barReplayForceReload() {
    try {
      for (var guid in resetCacheNeededCbs) {
        if (typeof resetCacheNeededCbs[guid] === "function") {
          try {
            resetCacheNeededCbs[guid]();
          } catch (_) {}
        }
      }
    } catch (_) {}
    barReplayResetChart();
  }

  /**
   * Reload truncated series for the cut.
   * TV keeps its own bar cache — resetData alone will NOT drop right-side candles.
   * Must invalidate via onResetCacheNeededCallback (same recipe as forceHistoryReload),
   * then resetData so getBars re-paints only the prefix up to the cut.
   */
  function barReplayReloadCut() {
    var res = String(barReplay.resolution || "");
    var sym = barReplay.symbol || "";
    var key = subKey(sym, res);
    try {
      delete liveBar[key];
      delete pendingLiveSeeds[key];
      getBarsPending[key] = Date.now();
      // Drop paint commit so subscribeBars cannot seed live LTP during the reload.
      delete paintCommittedAt[key];
    } catch (_) {}
    barReplayClearPendingLive();
    barReplayApplyTruncateToCache();
    try {
      if (sym) fireResetCacheNeeded(sym, res || null);
    } catch (_) {}
    try {
      var chart = barReplayChart();
      if (chart && typeof chart.resetData === "function") chart.resetData();
    } catch (_) {}
  }

  function barReplayBarTimeSec(t) {
    var n = Number(t);
    if (!(n > 0)) return 0;
    return n < 1e12 ? Math.floor(n) : Math.floor(n / 1000);
  }

  /** Capture current chart window width so replay/Live can soft-fit without changing zoom. */
  function barReplayCaptureSpan() {
    try {
      var chart = barReplayChart();
      if (!chart || typeof chart.getVisibleRange !== "function") return 0;
      var vr = chart.getVisibleRange();
      if (!vr || vr.from == null || vr.to == null) return 0;
      var span = Number(vr.to) - Number(vr.from);
      return span > 60 ? span : 0;
    } catch (_) {
      return 0;
    }
  }

  /** Exact visible {from,to}. */
  function barReplayCaptureRange() {
    try {
      var chart = barReplayChart();
      if (!chart || typeof chart.getVisibleRange !== "function") return null;
      var vr = chart.getVisibleRange();
      if (!vr || vr.from == null || vr.to == null) return null;
      var from = Number(vr.from);
      var to = Number(vr.to);
      if (!(to > from) || to - from < 60) return null;
      return { from: from, to: to };
    } catch (_) {
      return null;
    }
  }

  function barReplayDefaultSpanSec() {
    var m = resMinutes(barReplay.resolution || activeRes || "5") || 5;
    return Math.max(m * 60 * 100, 5 * 3600);
  }

  function barReplayRestoreRange(range) {
    try {
      var chart = barReplayChart();
      if (!chart || typeof chart.setVisibleRange !== "function") return;
      var r = range || barReplay.savedRange;
      if (!r || r.from == null || r.to == null) return;
      var from = Number(r.from);
      var to = Number(r.to);
      if (!(to > from)) return;
      // No percentRightMargin — that pins the cut to the RHS and changes the view.
      chart.setVisibleRange({ from: from, to: to });
    } catch (_) {}
  }

  /** Capture zoom/pan before cut so wipe does not invent a new view. */
  function barReplayCaptureView() {
    var range = barReplayCaptureRange();
    var spacing = 0;
    var rightOffset = 0;
    try {
      var chart = barReplayChart();
      var scale = chart && typeof chart.getTimeScale === "function" ? chart.getTimeScale() : null;
      if (scale) {
        if (typeof scale.barSpacing === "function") spacing = Number(scale.barSpacing()) || 0;
        if (typeof scale.rightOffset === "function") rightOffset = Number(scale.rightOffset()) || 0;
      }
    } catch (_) {}
    if (range) {
      barReplay.savedRange = range;
      barReplay.savedSpanSec = Number(range.to) - Number(range.from);
    } else {
      var span = barReplayCaptureSpan();
      if (span > 0) barReplay.savedSpanSec = span;
    }
    if (spacing > 0) barReplay.savedSpacing = spacing;
    barReplay.savedRightOffset = rightOffset;
    return range;
  }

  /**
   * After wipe: keep the SAME zoom/pan the user had. Candles right of the cut disappear;
   * empty space stays where they were (rightOffset), instead of auto-fitting the cut to the edge.
   */
  function barReplayRestoreView() {
    if (!barReplay.active || barReplay.playing) return false;
    try {
      var chart = barReplayChart();
      if (!chart) return false;
      var range = barReplay.savedRange;
      var cut = barReplay.bars && barReplay.bars[barReplay.index];
      var cutSec = barReplayBarTimeSec(cut && cut.time);
      var scale = typeof chart.getTimeScale === "function" ? chart.getTimeScale() : null;

      if (barReplay.savedSpacing > 0 && scale && typeof scale.setBarSpacing === "function") {
        try {
          scale.setBarSpacing(barReplay.savedSpacing);
        } catch (_) {}
      }

      if (range && typeof chart.setVisibleRange === "function") {
        try {
          chart.setVisibleRange({ from: Number(range.from), to: Number(range.to) });
        } catch (_) {}
      }

      // TV often clamps `to` past the last bar — recreate the wiped right-side gap.
      if (scale && typeof scale.setRightOffset === "function" && cutSec > 0 && range) {
        var toSec = Number(range.to);
        if (toSec > 1e12) toSec = Math.floor(toSec / 1000);
        var fromSec = Number(range.from);
        if (fromSec > 1e12) fromSec = Math.floor(fromSec / 1000);
        var step = Math.max((resMinutes(barReplay.resolution) || 5) * 60, 60);
        var barsAfterCut = Math.max(0, (toSec - cutSec) / step);
        var off = barsAfterCut + (Number(barReplay.savedRightOffset) || 0);
        var visibleBars = Math.max(10, (toSec - fromSec) / step);
        if (off > visibleBars * 0.95) off = visibleBars * 0.95;
        try {
          scale.setRightOffset(off);
        } catch (_) {}
      } else if (scale && typeof scale.setRightOffset === "function") {
        try {
          scale.setRightOffset(Number(barReplay.savedRightOffset) || 0);
        } catch (_) {}
      }
      barReplay._fittedOnce = true;
      return true;
    } catch (_) {
      return false;
    }
  }

  /** Retry restore after resetData paint — TV may ignore setVisibleRange until bars land. */
  function barReplayScheduleRestoreView() {
    var token = (barReplay._fitToken = (barReplay._fitToken || 0) + 1);
    [0, 60, 150, 320, 700, 1400, 2600].forEach(function (ms) {
      setTimeout(function () {
        if (token !== barReplay._fitToken) return;
        if (!barReplay.active || barReplay.playing) return;
        barReplayRestoreView();
      }, ms);
    });
  }

  /**
   * After cut paint: put the selected bar on the RIGHT with the same zoom span.
   * Candles past the cut are already gone (truncated histCache).
   */
  function barReplayVisibleToSec() {
    try {
      var chart = barReplayChart();
      if (!chart || typeof chart.getVisibleRange !== "function") return 0;
      var vr = chart.getVisibleRange();
      if (!vr || vr.to == null) return 0;
      var toN = Number(vr.to);
      if (toN > 1e12) toN = Math.floor(toN / 1000);
      return toN > 0 ? toN : 0;
    } catch (_) {
      return 0;
    }
  }

  function barReplayCutGapSec() {
    var cut = barReplay.bars && barReplay.bars[barReplay.index];
    if (!cut) return 1e15;
    var cutSec = barReplayBarTimeSec(cut.time);
    var toN = barReplayVisibleToSec();
    if (!(cutSec > 0) || !(toN > 0)) return 1e15;
    return toN - cutSec;
  }

  function barReplayMaxCutGapSec() {
    return Math.max((resMinutes(barReplay.resolution) || 5) * 60 * 10, 3600);
  }

  function barReplayFitCutOnce(force) {
    if (barReplay._fittedOnce && !force) return false;
    if (!barReplay.active || barReplay.playing) return false;
    var cut = barReplay.bars && barReplay.bars[barReplay.index];
    if (!cut) return false;
    barReplayApplyTruncateToCache();
    barReplaySoftFitToEnd(cut.time, {
      spanSec: Number(barReplay.savedSpanSec) || barReplayDefaultSpanSec(),
    });
    var gap = barReplayCutGapSec();
    var maxGap = barReplayMaxCutGapSec();
    var ok = gap >= -60 && gap <= maxGap;
    // Only lock once the visible right edge is actually near the cut.
    if (ok) barReplay._fittedOnce = true;
    return !!ok;
  }

  /**
   * Pan only while playing (same span) when the bar leaves the right edge.
   */
  function barReplayPanToEnd(endTimeMsOrSec) {
    try {
      var chart = barReplayChart();
      if (!chart || typeof chart.setVisibleRange !== "function") return;
      var endSec = barReplayBarTimeSec(endTimeMsOrSec);
      if (!(endSec > 0)) return;
      var resM = resMinutes(barReplay.resolution || activeRes || "5") || 5;
      var span = barReplaySpanSec(barReplay.savedSpanSec);
      var pad = Math.max(Math.floor(resM * 60 / 2), 30);
      try {
        var cur = chart.getVisibleRange();
        if (cur && cur.to != null) {
          var right = Number(cur.to);
          if (right > 1e12) right = Math.floor(right / 1000);
          if (endSec + pad <= right) return;
          var curSpan = Number(cur.to) - Number(cur.from);
          if (curSpan > 1e12) curSpan = curSpan / 1000;
          if (curSpan > 60) span = barReplaySpanSec(curSpan);
        }
      } catch (_) {}
      var to = endSec + pad;
      var from = to - span;
      var range = { from: from, to: to };
      try {
        chart.setVisibleRange(range, { percentRightMargin: 2 });
      } catch (_) {
        chart.setVisibleRange(range);
      }
      barReplay.savedSpanSec = span;
      barReplay.savedRange = range;
    } catch (_) {}
  }

  /** Normalize a captured viewport width to seconds (TV uses sec; tolerate ms spans). */
  function barReplaySpanSec(raw) {
    var span = Number(raw) || 0;
    if (span > 1e10) span = Math.floor(span / 1000);
    if (!(span > 60)) span = barReplayDefaultSpanSec();
    // Cap so setVisibleRange is not rejected for an absurd window.
    if (span > 60 * 86400) span = 60 * 86400;
    return span;
  }

  /**
   * Put endTime (cut / last bar) on the RIGHT edge.
   * Use percentRightMargin so TV pins `to` to the latest bar on the chart — a raw
   * `to` past the last bar is ignored and the range stays at "now" (bot gap ~days).
   */
  function barReplaySoftFitToEnd(endTimeMsOrSec, opts) {
    opts = opts || {};
    try {
      var chart = barReplayChart();
      if (!chart || typeof chart.setVisibleRange !== "function") return false;
      var endSec = barReplayBarTimeSec(endTimeMsOrSec);
      if (!(endSec > 0)) return false;
      var span = barReplaySpanSec(opts.spanSec != null ? opts.spanSec : barReplay.savedSpanSec);
      var from = endSec - span;
      var to = endSec + 30;
      var range = { from: from, to: to };
      // percentRightMargin → library ignores `to` and uses last bar + margin (docs).
      var applied = chart.setVisibleRange(range, { percentRightMargin: 2 });
      if (applied && typeof applied.then === "function") {
        applied.then(
          function () {
            barReplay.savedSpanSec = span;
            barReplay.savedRange = range;
          },
          function () {
            try {
              chart.setVisibleRange(range);
            } catch (_) {}
          }
        );
      }
      barReplay.savedSpanSec = span;
      barReplay.savedRange = range;
      return true;
    } catch (_) {
      return false;
    }
  }

  function barReplayScheduleSoftFit(endTime, mode) {
    var span = barReplaySpanSec(barReplay.savedSpanSec);
    barReplay.savedSpanSec = span;
    var token = (barReplay._fitToken = (barReplay._fitToken || 0) + 1);
    setTimeout(function () {
      if (token !== barReplay._fitToken) return;
      if (mode === "replay" && !barReplay.active) return;
      if (mode === "live" && (barReplay.active || barReplay.selecting)) return;
      barReplaySoftFitToEnd(endTime, { spanSec: span, force: true });
    }, 200);
  }

  /** Multiple fit attempts after resetData — setVisibleRange Promise often rejects until bars land. */
  function barReplayScheduleFitCut() {
    var token = (barReplay._fitToken = (barReplay._fitToken || 0) + 1);
    [0, 80, 200, 450, 900, 1600, 2800, 4500].forEach(function (ms) {
      setTimeout(function () {
        if (token !== barReplay._fitToken) return;
        if (!barReplay.active || barReplay.playing) return;
        if (barReplay._fittedOnce && barReplayCutGapSec() <= barReplayMaxCutGapSec()) return;
        barReplayFitCutOnce(true);
      }, ms);
    });
  }

  function barReplayApplyTruncateToCache() {
    if (!barReplay.active) return;
    var key = subKey(barReplay.symbol, barReplay.resolution);
    var cut = barReplay.bars[barReplay.index];
    if (!cut) return;
    var cutMs = Number(cut.time);
    if (cutMs > 0 && cutMs < 1e12) cutMs *= 1000;
    var view = [];
    for (var i = 0; i <= barReplay.index && i < barReplay.bars.length; i++) {
      view.push(barReplay.bars[i]);
    }
    // Also drop any polluted post-cut bars that tip/5m-derive may have merged in.
    if (cutMs > 0) {
      var cleaned = [];
      for (var j = 0; j < view.length; j++) {
        var t = Number(view[j].time);
        if (!(t > 0)) continue;
        if (t < 1e12) t *= 1000;
        if (t <= cutMs + 500) cleaned.push(view[j]);
      }
      if (cleaned.length) view = cleaned;
    }
    histCache[key] = view;
    lastHistoricalBar[key] = view[view.length - 1];
    lastDeliveredTime[key] = view[view.length - 1].time;
  }

  function barReplayUi() {
    var hint = document.getElementById("cxReplayHint");
    var bar = document.getElementById("cxReplayBar");
    var btn = document.getElementById("cxReplayBtn");
    var play = document.getElementById("cxReplayPlay");
    var timeEl = document.getElementById("cxReplayTime");
    var shell = document.querySelector(".cx-shell");
    if (hint) hint.hidden = !barReplay.selecting;
    if (bar) bar.hidden = !barReplay.active;
    if (btn) btn.classList.toggle("is-on", barReplay.active || barReplay.selecting);
    if (shell) {
      shell.classList.toggle("is-replay-pick", !!barReplay.selecting);
      shell.classList.toggle("is-replay", !!barReplay.active);
    }
    if (!barReplay.selecting) barReplayHideVLine();
    if (play) {
      play.textContent = barReplay.playing ? "❚❚" : "▶";
      play.classList.toggle("is-playing", !!barReplay.playing);
      play.title = barReplay.playing ? "Pause" : "Play";
    }
    if (timeEl && barReplay.active && barReplay.bars[barReplay.index]) {
      var b = barReplay.bars[barReplay.index];
      timeEl.textContent =
        barReplayFmtTime(b.time) +
        "  ·  " +
        (barReplay.index + 1) +
        "/" +
        barReplay.bars.length;
    }
  }

  function barReplayUnsubPick() {
    try {
      if (barReplay.crossSub && barReplay.crossSub.unsubscribe && barReplay._onCross) {
        barReplay.crossSub.unsubscribe(null, barReplay._onCross);
      }
    } catch (_) {}
    try {
      var w = barReplayWidget();
      if (w && typeof w.unsubscribe === "function" && barReplay._onMouse) {
        w.unsubscribe("mouse_up", barReplay._onMouse);
        w.unsubscribe("mouse_down", barReplay._onMouse);
      }
    } catch (_) {}
    barReplay.crossSub = null;
    barReplay._onCross = null;
    barReplay._onMouse = null;
  }

  function barReplayResetChart() {
    try {
      var chart = barReplayChart();
      if (chart && typeof chart.resetData === "function") chart.resetData();
    } catch (_) {}
  }

  /** Align TV edges to the replay cut so Play is not blocked by a leftover live edge. */
  function barReplaySyncEdges() {
    if (!barReplay.active) return;
    var cut = barReplay.bars && barReplay.bars[barReplay.index];
    if (!cut) return;
    var key = subKey(barReplay.symbol, barReplay.resolution);
    var edge = copyBar(cut);
    tvEdge[key] = edge;
    lastHistoricalBar[key] = copyBar(cut);
    lastDeliveredTime[key] = cut.time;
    try {
      delete liveBar[key];
      delete pendingLiveSeeds[key];
    } catch (_) {}
    function touchGuid(guid, sym) {
      if (!guid || !sameChartSymbol(sym, barReplay.symbol)) return;
      tvEdge["g:" + guid] = copyBar(edge);
    }
    try {
      Object.keys(realtimeSubscribers || {}).forEach(function (guid) {
        var sub = realtimeSubscribers[guid];
        if (!sub || String(sub.resolution) !== String(barReplay.resolution)) return;
        var t = (sub.symbolInfo && (sub.symbolInfo.ticker || sub.symbolInfo.name)) || "";
        touchGuid(guid, t);
      });
    } catch (_) {}
    try {
      Object.keys(TickHub.listeners || {}).forEach(function (guid) {
        var L = TickHub.listeners[guid];
        if (!L || String(L.resolution) !== String(barReplay.resolution)) return;
        touchGuid(guid, L.symbol);
      });
    } catch (_) {}
  }

  function barReplayEmitBar() {
    var bar = barReplay.bars[barReplay.index];
    if (!bar) return;
    var key = subKey(barReplay.symbol, barReplay.resolution);
    var view = histCache[key] || [];
    var lastT = view.length ? Number(view[view.length - 1].time) : 0;
    if (lastT > 0 && lastT < 1e12) lastT *= 1000;
    var barT = Number(bar.time);
    if (barT > 0 && barT < 1e12) barT *= 1000;
    if (barT > lastT) {
      histCache[key] = view.concat([copyBar(bar)]);
    } else if (!view.length || Math.abs(barT - lastT) < 1000) {
      if (view.length) view[view.length - 1] = copyBar(bar);
      else view = [copyBar(bar)];
      histCache[key] = view;
    }
    lastHistoricalBar[key] = copyBar(bar);
    lastDeliveredTime[key] = bar.time;
    // Push through the same live pipe (tagged "replay") so every pane/listener gets it.
    try {
      deliverLiveBarToPanes(barReplay.symbol, barReplay.resolution, bar, "replay");
    } catch (_) {}
    // Only while playing — pan if the bar would leave the right edge. Otherwise leave the view alone.
    try {
      if (barReplay.playing) {
        var nowFit = Date.now();
        if (!barReplay._lastFitAt || nowFit - barReplay._lastFitAt > 900) {
          barReplay._lastFitAt = nowFit;
          barReplayPanToEnd(bar.time);
        }
      }
    } catch (_) {}
    barReplayUi();
  }

  function barReplayStopTimer() {
    if (barReplay.timer) {
      clearInterval(barReplay.timer);
      barReplay.timer = null;
    }
    barReplay.playing = false;
  }

  function barReplayIntervalMs() {
    var base = 520;
    var sp = Number(barReplay.speed) || 1;
    return Math.max(40, Math.round(base / sp));
  }

  function barReplayPlay() {
    if (!barReplay.active) return;
    barReplayStopTimer();
    if (barReplay.index >= barReplay.bars.length - 1) {
      barReplayUi();
      return;
    }
    barReplay.playing = true;
    barReplayUi();
    barReplay.timer = setInterval(function () {
      if (barReplay.index >= barReplay.bars.length - 1) {
        barReplayStopTimer();
        barReplayUi();
        return;
      }
      barReplay.index += 1;
      barReplayEmitBar();
    }, barReplayIntervalMs());
  }

  function barReplayPause() {
    barReplayStopTimer();
    barReplayUi();
  }

  function barReplayStep(delta) {
    if (!barReplay.active) return;
    barReplayPause();
    var next = barReplay.index + delta;
    if (next < 0) next = 0;
    if (next > barReplay.bars.length - 1) next = barReplay.bars.length - 1;
    if (next === barReplay.index) {
      barReplayUi();
      return;
    }
    if (delta < 0) {
      barReplay.index = next;
      barReplayApplyTruncateToCache();
      barReplay._fittedOnce = false;
      barReplaySyncEdges();
      barReplayClearPendingLive();
      barReplayReloadCut();
      setTimeout(function () {
        barReplayUi();
      }, 200);
      return;
    }
    while (barReplay.index < next) {
      barReplay.index += 1;
      barReplayEmitBar();
    }
  }

  function barReplayExit(quiet) {
    barReplayStopTimer();
    barReplay._pickToken = (barReplay._pickToken || 0) + 1;
    barReplayCancelNativeSelect();
    barReplayUnsubPick();
    barReplayHideVLine();
    // Keep zoom width from replay (or current) so Live doesn't snap to a default view.
    var span = barReplayCaptureSpan() || Number(barReplay.savedSpanSec) || 0;
    if (span > 0) barReplay.savedSpanSec = span;
    var key = barReplay.symbol ? subKey(barReplay.symbol, barReplay.resolution) : "";
    var liveEnd = null;
    if (key && barReplay.bars && barReplay.bars.length) {
      histCache[key] = barReplay.bars.slice();
      lastHistoricalBar[key] = barReplay.bars[barReplay.bars.length - 1];
      liveEnd = lastHistoricalBar[key].time;
    }
    barReplay.active = false;
    barReplay.selecting = false;
    barReplay.bars = [];
    barReplay.index = 0;
    barReplay.hoverTime = null;
    barReplay.hoverMs = null;
    barReplay.savedRange = null;
    barReplay.savedSpacing = 0;
    barReplay.savedRightOffset = 0;
    barReplay._fittedOnce = false;
    if (barReplay._guardTimer) {
      clearInterval(barReplay._guardTimer);
      barReplay._guardTimer = null;
    }
    barReplayUi();
    if (!quiet) {
      barReplayForceReload();
      if (liveEnd) barReplayScheduleSoftFit(liveEnd, "live");
    }
  }

  function barReplayStartAt(timeSecOrMs) {
    var pool = barReplayPool();
    if (pool.length < 3) {
      try {
        pageSaved("Need more bars to replay");
      } catch (_) {}
      return;
    }
    var idx = barReplaySnapIndex(timeSecOrMs, pool);
    if (idx < 0) return;
    // Must keep bars AFTER the cut so Play can advance (never end at N/N on start).
    if (idx >= pool.length - 1) idx = Math.max(0, pool.length - 2);

    // Keep the exact chart view from before the pick — only wipe candles past the cut.
    if (!barReplay.savedRange) barReplayCaptureView();
    else {
      var spanKeep = Number(barReplay.savedRange.to) - Number(barReplay.savedRange.from);
      if (spanKeep > 60) barReplay.savedSpanSec = barReplaySpanSec(spanKeep);
    }

    barReplay._pickToken = (barReplay._pickToken || 0) + 1;
    barReplayCancelNativeSelect();
    barReplayUnsubPick();
    barReplayHideVLine();

    var sym = activeSymbol;
    var res = String(activeRes || "5");
    var key = subKey(sym, res);

    // FULL history stays in barReplay.bars for playback; histCache is only the prefix.
    barReplay.symbol = sym;
    barReplay.resolution = res;
    barReplay.bars = pool.slice();
    barReplay.index = idx;
    barReplay.active = true;
    barReplay.selecting = false;
    barReplay.hoverTime = null;
    barReplay.hoverMs = null;
    barReplay.playing = false;
    barReplayStopTimer();

    var truncated = pool.slice(0, idx + 1);
    histCache[key] = truncated;
    lastHistoricalBar[key] = truncated[truncated.length - 1];
    lastDeliveredTime[key] = lastHistoricalBar[key].time;
    try {
      delete liveBar[key];
      delete pendingLiveSeeds[key];
    } catch (_) {}
    barReplaySyncEdges();
    barReplayClearPendingLive();
    barReplay._fittedOnce = false;

    try {
      viewportUserFree = true;
    } catch (_) {}

    barReplayUi();
    try {
      lb("replay.cut", sym, {
        resolution: res,
        index: idx,
        n: pool.length,
        cut_t: truncated[truncated.length - 1].time,
        span_sec: barReplay.savedSpanSec,
      });
    } catch (_) {}
    // Invalidate TV cache → resetData → truncated getBars → restore the same view.
    barReplayReloadCut();
    barReplayScheduleRestoreView();
  }

  function barReplayCancelNativeSelect() {
    try {
      var chart = barReplayChart();
      if (chart && typeof chart.cancelSelectBar === "function" && typeof chart.isSelectBarRequested === "function") {
        if (chart.isSelectBarRequested()) chart.cancelSelectBar();
      } else if (chart && typeof chart.cancelSelectBar === "function") {
        chart.cancelSelectBar();
      }
    } catch (_) {}
  }

  function barReplayEnterSelect() {
    if (barReplay.active) barReplayPause();
    // Pin the active pane widget before picking candles.
    try {
      setActivePane(activePane || "sector");
    } catch (_) {}
    // Remember exact zoom/pan while user is picking — click must not change scale.
    barReplayCaptureView();
    barReplay.selecting = true;
    barReplay.hoverTime = null;
    barReplay.hoverMs = null;
    barReplay.pickReadyAt = Date.now() + 220;
    barReplayEnsureHost();
    barReplayUi();

    var w = barReplayWidget();
    if (!w || typeof w.activeChart !== "function") {
      try {
        pageSaved("Chart not ready");
      } catch (_) {}
      barReplay.selecting = false;
      barReplayUi();
      return;
    }

    var chart = null;
    try {
      chart = w.activeChart();
    } catch (_) {
      chart = null;
    }

    // Prefer TV's native bar-select (same API Bar Replay uses) — reliable candle hit-test.
    if (chart && typeof chart.requestSelectBar === "function") {
      barReplayCancelNativeSelect();
      try {
        var pickToken = (barReplay._pickToken = (barReplay._pickToken || 0) + 1);
        chart
          .requestSelectBar()
          .then(function (time) {
            if (pickToken !== barReplay._pickToken) return;
            if (!barReplay.selecting) return;
            if (!(Number(time) > 0)) return;
            barReplayStartAt(time);
          })
          .catch(function () {
            if (pickToken !== barReplay._pickToken) return;
            if (barReplay.selecting && !barReplay.active) barReplayCancelSelect();
          });
        return;
      } catch (_) {
        /* fall through to custom pick */
      }
    }

    try {
      barReplay._onCross = function (param) {
        if (!barReplay.selecting) return;
        if (!param || param.time == null) {
          barReplayHideVLine();
          return;
        }
        barReplay.hoverTime = param.time;
        var pool = barReplay.active && barReplay.bars.length ? barReplay.bars : barReplayPool();
        var idx = barReplaySnapIndex(param.time, pool);
        if (idx >= 0) {
          barReplay.hoverMs = Number(pool[idx].time);
        }
        // Thin DOM guide only (no TV shape — that filled the right pane green).
        barReplayShowVLine(param);
      };
      barReplay.crossSub = chart && chart.crossHairMoved ? chart.crossHairMoved() : null;
      if (barReplay.crossSub && typeof barReplay.crossSub.subscribe === "function") {
        barReplay.crossSub.subscribe(null, barReplay._onCross);
      }
    } catch (_) {}

    barReplay._onMouse = function () {
      if (!barReplay.selecting) return;
      if (Date.now() < barReplay.pickReadyAt) return;
      var t = barReplay.hoverMs || barReplay.hoverTime;
      if (!(t > 0)) return;
      barReplayStartAt(t);
    };
    try {
      if (typeof w.subscribe === "function") {
        w.subscribe("mouse_up", barReplay._onMouse);
        w.subscribe("mouse_down", barReplay._onMouse);
      }
    } catch (_) {}
    barReplayBindFrames();
  }

  function barReplayCancelSelect() {
    barReplay._pickToken = (barReplay._pickToken || 0) + 1;
    barReplayCancelNativeSelect();
    barReplay.selecting = false;
    barReplayUnsubPick();
    barReplayHideVLine();
    barReplayUi();
  }

  /** Esc cancels picking first, then leaves replay. Returns true when handled. */
  function barReplayOnKey(e) {
    if (!barReplay.active && !barReplay.selecting) return false;
    if (e.key === "Escape" || e.key === "Esc") {
      e.preventDefault();
      if (barReplay.selecting) barReplayCancelSelect();
      else barReplayExit(false);
      return true;
    }
    if (!barReplay.active || barReplay.selecting || e.metaKey || e.ctrlKey || e.altKey) return false;
    var tag = e.target && e.target.tagName ? String(e.target.tagName).toUpperCase() : "";
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return false;
    if (e.key === " " || e.code === "Space") {
      e.preventDefault();
      if (barReplay.playing) barReplayPause();
      else barReplayPlay();
      return true;
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      barReplayStep(1);
      return true;
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      barReplayStep(-1);
      return true;
    }
    return false;
  }

  /** Candle time under a click/tap point inside the chart iframe (null if not over the price pane). */
  function barReplayTimeAtPoint(doc, clientX, clientY) {
    try {
      var chart = barReplayChart();
      if (!chart || typeof chart.getTimeScale !== "function") return null;
      var scale = chart.getTimeScale();
      if (!scale || typeof scale.coordinateToTime !== "function") return null;
      var el = doc.elementFromPoint(clientX, clientY);
      if (!el) return null;
      var canvas =
        el.tagName === "CANVAS"
          ? el
          : el.closest
            ? el.closest("canvas")
            : el.querySelector
              ? el.querySelector("canvas")
              : null;
      // Prefer price-pane canvas, but fall back to any canvas under the pointer.
      if (!canvas) return null;
      var pane = canvas.closest
        ? canvas.closest(".chart-markup-table.pane, .chart-gui-wrapper, .chart-container")
        : null;
      if (!pane && canvas.closest && canvas.closest(".price-axis")) return null;
      var r = canvas.getBoundingClientRect();
      var x = clientX - r.left;
      if (!(x >= 0) || x > r.width) return null;
      var t = scale.coordinateToTime(x);
      return t != null && Number(t) > 0 ? Number(t) : null;
    } catch (_) {
      return null;
    }
  }

  /**
   * Keyboard focus and touches inside the TradingView iframe never reach this document,
   * so Esc / Space / arrows and click/tap candle-picks are also bound on each chart iframe.
   */
  function barReplayBindFrames() {
    var host = barReplayHostEl();
    var frames = document.querySelectorAll(".cx-main iframe, .cx-chart iframe");
    for (var i = 0; i < frames.length; i++) {
      var frame = frames[i];
      var win = null;
      var doc = null;
      try {
        win = frame.contentWindow;
        doc = frame.contentDocument || (win && win.document);
      } catch (_) {
        continue;
      }
      if (!win || !doc) continue;
      if (!win.__bcReplayKeyBound) {
        win.__bcReplayKeyBound = true;
        win.addEventListener("keydown", barReplayOnKey, true);
      }
      // Bind candle picks on every chart pane (active multi-grid), not only sector.
      if (win.__bcReplayPickBound) continue;
      win.__bcReplayPickBound = true;
      (function (frameDoc, frameWin, frameEl) {
        var down = null;
        function inActiveHost() {
          if (!host) return true;
          try {
            return !!(frameEl.closest && (frameEl.closest("#" + host.id) || host.contains(frameEl)));
          } catch (_) {
            return true;
          }
        }
        frameWin.addEventListener(
          "pointerdown",
          function (e) {
            if (!barReplay.selecting || (e.button != null && e.button !== 0)) {
              down = null;
              return;
            }
            if (!inActiveHost()) {
              down = null;
              return;
            }
            down = { x: e.clientX, y: e.clientY, id: e.pointerId };
          },
          true
        );
        frameWin.addEventListener(
          "pointerup",
          function (e) {
            var d = down;
            down = null;
            if (!d || !barReplay.selecting || e.pointerId !== d.id) return;
            if (!inActiveHost()) return;
            if (Date.now() < barReplay.pickReadyAt) return;
            if (Math.abs(e.clientX - d.x) > 10 || Math.abs(e.clientY - d.y) > 10) return;
            var t = barReplayTimeAtPoint(frameDoc, e.clientX, e.clientY);
            if (!(t > 0)) {
              // Fallback: use last crosshair hover time on this pane.
              t = barReplay.hoverMs || barReplay.hoverTime;
            }
            if (!(t > 0)) return;
            barReplayStartAt(t);
          },
          true
        );
        frameWin.addEventListener(
          "pointercancel",
          function () {
            down = null;
          },
          true
        );
      })(doc, win, frame);
    }
  }

  function initBarReplay() {
    var btn = document.getElementById("cxReplayBtn");
    var play = document.getElementById("cxReplayPlay");
    var back = document.getElementById("cxReplayBack");
    var fwd = document.getElementById("cxReplayFwd");
    var speed = document.getElementById("cxReplaySpeed");
    var exit = document.getElementById("cxReplayExit");
    var repick = document.getElementById("cxReplayRepick");
    var close = document.getElementById("cxReplayClose");
    var hintClose = document.getElementById("cxReplayHintClose");
    if (!btn) return;

    btn.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (barReplay.selecting) {
        barReplayCancelSelect();
        return;
      }
      if (barReplay.active) {
        barReplayEnterSelect();
        return;
      }
      barReplayEnterSelect();
    });
    if (play) {
      play.addEventListener("click", function (e) {
        e.preventDefault();
        if (!barReplay.active) return;
        if (barReplay.playing) barReplayPause();
        else barReplayPlay();
      });
    }
    if (back) {
      back.addEventListener("click", function (e) {
        e.preventDefault();
        barReplayStep(-1);
      });
    }
    if (fwd) {
      fwd.addEventListener("click", function (e) {
        e.preventDefault();
        barReplayStep(1);
      });
    }
    if (speed) {
      speed.addEventListener("change", function () {
        barReplay.speed = Number(speed.value) || 1;
        if (barReplay.playing) barReplayPlay();
      });
    }
    if (exit) {
      exit.addEventListener("click", function (e) {
        e.preventDefault();
        barReplayExit(false);
      });
    }
    if (repick) {
      repick.addEventListener("click", function (e) {
        e.preventDefault();
        barReplayEnterSelect();
      });
    }
    if (close) {
      close.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        barReplayExit(false);
      });
    }
    if (hintClose) {
      hintClose.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (barReplay.selecting) barReplayCancelSelect();
      });
    }
    document.addEventListener("keydown", barReplayOnKey, true);
    barReplayUi();

    /** Replay bot / console: window.__bcReplay.status() / .check() */
    try {
      window.__bcReplay = {
        status: function () {
          // Re-assert truncate so the bot reads what paint should show (not a polluted merge).
          try {
            if (barReplay.active) barReplayApplyTruncateToCache();
          } catch (_) {}
          var key = barReplay.symbol ? subKey(barReplay.symbol, barReplay.resolution) : "";
          var cut = barReplay.bars && barReplay.bars[barReplay.index];
          var mem = key ? histCache[key] || [] : [];
          var memLast = mem.length ? mem[mem.length - 1] : null;
          var vr = null;
          try {
            var ch = barReplayChart();
            if (ch && typeof ch.getVisibleRange === "function") vr = ch.getVisibleRange();
          } catch (_) {}
          return {
            active: !!barReplay.active,
            selecting: !!barReplay.selecting,
            playing: !!barReplay.playing,
            symbol: barReplay.symbol || "",
            resolution: String(barReplay.resolution || ""),
            index: barReplay.index,
            n: (barReplay.bars && barReplay.bars.length) || 0,
            cut_t: cut ? cut.time : null,
            mem_n: mem.length,
            mem_last_t: memLast ? memLast.time : null,
            liveBar: key && liveBar[key] ? liveBar[key].time : null,
            span_sec: Number(barReplay.savedSpanSec) || 0,
            fitted: !!barReplay._fittedOnce,
            visible: vr,
          };
        },
        check: function () {
          // Re-assert truncate + restore the user's view (do not force cut-to-right).
          try {
            if (barReplay.active) {
              barReplayApplyTruncateToCache();
              barReplayRestoreView();
            }
          } catch (_) {}
          var s = this.status();
          var checks = [];
          function add(id, ok, detail) {
            checks.push({ id: id, ok: !!ok, detail: detail || "" });
          }
          add("active", s.active, s.active ? "replay on" : "not in replay — pick a candle first");
          if (!s.active) {
            return { ok: false, checks: checks, status: s };
          }
          add("forward", s.index < s.n - 1, s.index + 1 + "/" + s.n + (s.index >= s.n - 1 ? " — no bars ahead to play" : ""));
          function tMs(t) {
            var n = Number(t);
            if (!(n > 0)) return 0;
            return n < 1e12 ? n * 1000 : n;
          }
          var memMs = tMs(s.mem_last_t);
          var cutMs = tMs(s.cut_t);
          add(
            "truncated",
            memMs > 0 && cutMs > 0 && Math.abs(memMs - cutMs) < 2000,
            "mem_last=" + s.mem_last_t + " cut=" + s.cut_t + " (right-side candles must be gone)"
          );
          add("no_live", !s.liveBar, s.liveBar ? "liveBar still set @ " + s.liveBar : "live muted");
          var viewOk = !!barReplay.savedRange;
          var viewDetail = "no saved range";
          if (barReplay.savedRange && s.visible && s.visible.from != null) {
            var sf = Number(barReplay.savedRange.from);
            var vf = Number(s.visible.from);
            if (sf > 1e12) sf = Math.floor(sf / 1000);
            if (vf > 1e12) vf = Math.floor(vf / 1000);
            var drift = Math.abs(sf - vf);
            viewOk = drift < Math.max((resMinutes(s.resolution) || 5) * 60 * 8, 1800);
            viewDetail = "from drift " + Math.round(drift) + "s (keep pick zoom/pan)";
          }
          add("view_kept", viewOk, viewDetail);
          var ok = checks.every(function (c) {
            return c.ok;
          });
          return { ok: ok, checks: checks, status: s };
        },
        /** Async: restore view then check (bot should prefer this). */
        checkAfterFit: function (cb) {
          var self = this;
          try {
            if (barReplay.active) {
              barReplayApplyTruncateToCache();
              barReplayRestoreView();
            }
          } catch (_) {}
          setTimeout(function () {
            try {
              barReplayRestoreView();
            } catch (_) {}
            var r = self.check();
            if (typeof cb === "function") cb(r);
          }, 350);
        },
      };
    } catch (_) {}
  }

  function initRefreshButton() {
    var btn = document.getElementById("bcRefreshBtn");
    if (!btn) return;
    btn.addEventListener("click", function () {
      btn.classList.add("is-spin");
      btn.disabled = true;
      try {
        // Soft refresh: re-pull tip for active symbol, then reload page cleanly.
        var sym = activeSymbol || (paneSymbols && paneSymbols.sector) || "NIFTY 50";
        invalidateSymbolTip(sym, activeRes);
      } catch (_) {}
      setTimeout(function () {
        location.reload();
      }, 180);
    });
  }

  wireUi();
  initRefreshButton();
  initBarReplay();
  initOptionChainModal();
  initOcAnalysis();
  try {
    alertCtl = initAlerts({
      getWidget: function () {
        return widget;
      },
      getActiveSymbol: function () {
        return activeSymbol || "NIFTY 50";
      },
      getLtp: function (sym) {
        var q = TickHub && TickHub.lastQuotes ? TickHub.lastQuotes[sym] || TickHub.lastQuotes[String(sym || "").toUpperCase()] : null;
        return q && Number(q.ltp) > 0 ? Number(q.ltp) : 0;
      },
      getBars: function (sym) {
        try {
          var s = String(sym || "").trim();
          var pool = barsFromFivePool(s, "5") || histCache[subKey(s, "5")] || histCache[subKey(s, "1")] || [];
          var out = [];
          for (var i = 0; i < pool.length; i++) {
            var b = pool[i];
            if (!b) continue;
            var c = Number(b.close);
            if (!(c > 0)) continue;
            out.push({
              open: Number(b.open) || c,
              high: Number(b.high) || c,
              low: Number(b.low) || c,
              close: c,
              volume: Number(b.volume) || 0,
            });
          }
          return out;
        } catch (_) {
          return [];
        }
      },
      ensureBars: function (sym) {
        try {
          var s = String(sym || "").trim();
          if (s && sameChartSymbol(s, activeSymbol)) prefetchTip(s);
        } catch (_) {}
      },
      openSymbol: function (sym) {
        openSymbolOnPane(sym, "sector", { force: true });
      },
      showToast: function () {},
    });
    heatmapCtl = initHeatmap({
      watchlistUrl: WL_URL,
      openSymbol: function (sym) {
        openSymbolOnPane(sym, "sector", { force: true });
      },
      getQuotesBag: function () {
        return (TickHub && TickHub.lastQuotes) || {};
      },
    });
  } catch (e) {
    console.warn("[alerts/heatmap]", e);
  }
  registerChartSw();
  initPush();
  refreshMarketBadgeFromClock();
  loadLogosIndex().then(function () {
    return loadWatchlist();
  }).then(function () {
    TickHub.start();
    startWatchlistQuotePoll();
    startHoverTipPrefetch();
    registerHarness();
  });
  waitTv(function () {
    // Do not block chart boot on logos — legend icons fill in when the index returns.
    bootWidget();
    registerHarness();
    loadLogosIndex();
    try {
      if (alertCtl && typeof alertCtl.redrawLines === "function") {
        setTimeout(function () {
          alertCtl.redrawLines();
        }, 1200);
      }
    } catch (_) {}
  });
})();
